import React from "react";
import useAccess from "../hooks/useAccess.jsx";
import { ACCESS_PERMISSION_KEYS, canShowReportCompartments } from "../lib/accessControl.js";
import DispatchGanttViewMenu from "./resultsPageChrome/DispatchGanttViewMenu.jsx";
import useFleetTimelinePerspective from "../hooks/useFleetTimelinePerspective.js";
import { FleetTimelinePerspective } from "./FleetTimelinePerspective.jsx";
import { TimelinePerspectiveIndicators } from "./FleetTimelinePresentation.jsx";
import FleetSelectionHeading from "./FleetSelectionHeading.jsx";
import { createPortal } from "react-dom";
import { getReactPortalRoot } from "../utils/reactPortalRoot.js";
import { applyCompartmentOccupancyChange, cloneCompartmentOccupancy } from "../utils/compartmentOccupancy.js";
import { offset } from "@floating-ui/react-dom";
import { AlertTriangle, Bell, BellOff, ChevronDown, Expand, Minimize2, Moon, Pause, RotateCcw, Route, Smartphone, Sun, Utensils, Wrench } from "lucide-react";
import {
  ActionButton,
  EmptyState,
  HoverCard,
  Pill,
  StatusBadge,
  joinClasses,
  DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME,
  useToast,
} from "./ResultsPrimitives.jsx";
import ConfirmationModal from "./ConfirmationModal.jsx";
import { ConnectedDispatchPeriodToggle } from "./DispatchPeriodToggle.jsx";
import TruckIncidentsDialog from "./TruckIncidentsDialog.jsx";
import TruckLoadView from "./TruckLoadView.jsx";
import LunchBreakTimelineBlock from "./LunchBreakTimelineBlock.jsx";
import JoinedLoadsTimelineMarks from "./JoinedLoadsTimelineMarks.jsx";
import GeotabRealTimelineMarks, { GeotabRealProgressChip } from "./GeotabRealTimelineMarks.jsx";
import DispatchDisplayMenu from "./resultsPageChrome/DispatchDisplayMenu.jsx";
import { geotabUrl } from "./geotab/geotabLinks.js";
import { buildGeotabRealMarks, summarizeGeotabRealProgress } from "../utils/geotabRealTimeline.js";
import { alertsEnabled, assignmentAlertStatus } from "../utils/geotabLiveAlerts.js";
import { getLocalTodayDispatchDate } from "../utils/dispatchDayCalendarUtils.js";
import useDispatchDisplayPreferences, { useDispatchProductTags } from "../hooks/useDispatchDisplayPreferences.js";
import useDispatchGeotabReal from "../hooks/useDispatchGeotabReal.js";
import { useDispatchGeotabGaps } from "../hooks/useDispatchGeotabCheck.js";
import useGeotabTolerance from "../hooks/useGeotabTolerance.js";
import { isLunchBreakSegment, resolveLunchBreakTimeline, resolveLunchBreakWindow } from "../utils/lunchBreakTimeline.js";
import FloatingRouteMapSurface from "./FloatingRouteMapSurface.jsx";
import ColumnHeaderDragOverlay from "./ColumnHeaderDragOverlay.jsx";
import RouteMetaChipRow from "./RouteMetaChipRow.jsx";
import RouteMapPanel from "./RouteMapPanel.jsx";
import RouteMapPictureInPicturePanel from "./RouteMapPictureInPicturePanel.jsx";
import DispatchFullscreenActions from "./resultsPageChrome/DispatchFullscreenActions.jsx";
import SuspendedDeliveriesList, { SuspendedTonnageSummary } from "./SuspendedDeliveriesList.jsx";
import GanttAddUnitButton from "./GanttAddUnitButton.jsx";
import GanttStandaloneUnitRows from "./GanttStandaloneUnitRows.jsx";
import useStandaloneUnitsCatalog from "../hooks/useStandaloneUnitsCatalog.js";
import OptimizationProgressCard, { isFleetOptimizationProgressCardVisible } from "./OptimizationProgressCard.jsx";
import { formatOptimizationProgressDuration } from "../utils/optimizationProgress.js";
import FleetTruckStatusIndicator from "./FleetTruckStatusIndicator.jsx";
import FleetTruckStatusIconHoverCard from "./FleetTruckStatusIconHoverCard.jsx";
import useFleetFloatingMapState from "../hooks/useFleetFloatingMapState.js";
import useFleetReassignmentDrag, {
  hasReassignmentPointerMoved,
  REASSIGNMENT_POINTER_HOLD_DELAY_MS,
  REASSIGNMENT_POINTER_MOVE_THRESHOLD_PX,
} from "../hooks/useFleetReassignmentDrag.js";
import useFleetSegmentReorderDrag from "../hooks/useFleetSegmentReorderDrag.js";
import useFleetSegmentPopover from "../hooks/useFleetSegmentPopover.js";
import useFleetTruckFilter from "../hooks/useFleetTruckFilter.js";
import { useOptimizationJobTracker } from "../hooks/useOptimizationJobTracker.js";
import { useTruckSheetPdfExportTracker } from "../hooks/useTruckSheetPdfExportTracker.jsx";
import { useUserUiPreferences } from "../hooks/useUserUiPreferences.js";
import useEveningRailWidth from "../hooks/useEveningRailWidth.js";
import useSuspendedArrivalHighlight from "../hooks/useSuspendedArrivalHighlight.js";
import useDataManagerColumnPreferences from "../hooks/useDataManagerColumnPreferences.js";
import useSortableColumnHeaderDrag from "../hooks/useSortableColumnHeaderDrag.js";
import { fetchColumnPreferences, saveColumnPreferences } from "../lib/dataManagerApi.js";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import {
  buildCompartmentFillIndicatorLabel,
  normalizeCompartmentFillPercent,
  summarizeCompartmentFillSegments,
} from "../lib/productCompartmentFillRules.js";
import { findPortalPublicationRoute } from "../lib/portalPublicationsApi.js";
import { MISSING_TRACTOR_LABEL } from "../lib/tractorAssignmentsApi.js";
import {
  DRIVER_PORTAL_FLUORESCENT_BADGE_CLASS_NAME,
  DRIVER_PORTAL_FLUORESCENT_ICON_CLASS_NAME,
} from "../utils/driverPortalPublicationStyles.js";
import {
  buildDispatchDisabledHoverCardContent,
  buildDriverPortalStatusHoverCardContent,
  buildTruckProtectionHoverCardContent,
} from "../utils/fleetTruckStatusHoverCardContent.js";
import {
  buildExcludedTruckRefreshPresentation,
  buildHaversineRouteWarningPresentation,
  buildFleetSegmentComparisonCacheKey,
  buildFleetSequenceDisplayItems,
  buildSequenceTruckLoadByKey,
  buildFleetTimelineDisplaySegments,
  buildSegmentPositionData,
  buildSequenceTableRenderItems,
  buildSameSiteSequenceDisplayOverrides,
  buildTimelineRenderItems,
  formatTimelineClockLabel,
  getTimelineTickLabelStyle,
  getSameSiteSequenceDisplayDistanceLabel,
  getRowActiveTrip,
  getTruckFilterMetaLabel,
  isMergedOperationalMissionSegment,
  resolveAssignmentBackendDurationBreakdown,
  resolveAssignmentBackendTotalDurationMinutes,
  resolveDisplayedTimelineFrontendDurationBreakdown,
  resolveFrontendCanonicalTotalDurationMinutes,
  resolveDisplayedTimelineSpanLabel,
  resolveDisplayedTimelineSpanMinutes,
  resolveAspiratorMissionRouteSegmentIndexes,
  resolveGroupedHoverRouteSegmentIndex,
  resolveDisplayedOperationalSequenceLabel,
  resolveSameSiteSequenceDisplayedTravelDuration,
  resolveTimelineGroupSyncTarget,
  resolveTimelineHourTicks,
  resolveTimelineBounds,
  resolveAssignmentDurationLabel,
  buildGroupedSegmentOrderHoverEntries,
  shouldSyncDisplayedTravelDurationChip,
  shouldDisplaySameSiteDurationChips,
} from "../utils/fleetTimelinePresentation.js";
import {
  resolveGroupedSegmentProductLabels,
  resolveProductRunStartLabels,
  resolveProductTabRoomPercents,
  resolveTimelineLegendProductLabels,
  buildSegmentHoverCardTitle,
  getSegmentClassName,
  getSegmentHoverCardAccentColor,
  getSequenceTableMarkerStyle,
  isLinkedCommandCompanionSegment,
  normalizeSegmentMetaValue,
  resolveEndOfDayLoadDisplayMode,
  resolveEndOfDayLoadVisualMode,
  resolveSegmentPrimaryAddress,
  resolveSegmentWorkOrderLabel,
} from "../utils/fleetSegmentPresentation.js";
import {
  getEndOfDayLoadModeLongLabel,
} from "../utils/endOfDayLoadModeLabels.js";
import { buildAssignmentShellViewModel, buildFleetSearchValue, buildSuspendedSearchValue } from "../utils/resultsFleetCardShell.js";
import {
  buildReassignmentVisibleTruckCapabilityIndex,
  buildTimelineGroupActionPresentation,
  findSegmentContextByActionKey,
  getTimelineSegmentActionKey,
  mergeTimelineSegmentActionPresentation,
  normalizeGroupDragMembers,
} from "../utils/fleetReassignmentHelpers.js";
import { FLOATING_MAP_INITIAL_POSITION, resolveFloatingMapPreset } from "../utils/floatingRouteMapLayout.js";
import { FLEET_FLOATING_ROUTE_MAP_STORAGE_KEY } from "../utils/floatingRouteMapStorage.js";
import {
  closeRouteMapPictureInPictureWindow,
  focusRouteMapPictureInPictureWindow,
  openRouteMapPictureInPictureWindow,
  resolveRouteMapPictureInPictureDefaultSize,
  resolveRouteMapPictureInPictureScreenSize,
} from "../utils/routeMapPictureInPictureWindow.js";
import {
  clearRouteMapPictureInPictureRestoreState,
  persistRouteMapPictureInPictureRestoreState,
  readRouteMapPictureInPictureRestoreState,
} from "../utils/routeMapPictureInPictureRestoreState.js";
import { buildSegmentTimePriorityResetUpdates } from "../utils/segmentReorderTimePriority.js";
import {
  buildRoutingIssueByTruckId,
  collectOptimizationPreviewReserveState,
  selectDispatchRibbonData,
  selectSuspendedPanelData,
} from "../utils/resultsChromeData.js";
import {
  compareAssignmentsByDisplayedWorkload,
  getAssignmentDisplayedWorkloadMinutes,
  getAssignmentOvertimeState,
  resolvePlanningResetTruckPresentation,
  shouldShowAssignmentOvertimeChrome,
} from "../utils/resultsFleetData.js";
import { buildFleetRowDurationKey, reportFleetRowDisplayedMinutes } from "../utils/rightPanelData.js";
import {
  buildResultsState,
  buildPlanningOptimizeConfirmationContent,
  shouldKeepExpandedTimelineGroupOpenOnPointerDown,
} from "../utils/resultsWorkspaceData.js";
import { buildFleetTimelineData, formatDurationShort, resolveVisibleRouteSequenceItems } from "../utils/routeModelsData.js";
import { setFleetFullscreenOverlayActive } from "../utils/fleetFullscreenOverlayState.js";
import {
  getActivePlanningMutationCount,
  subscribePlanningMutationActivity,
} from "../utils/planningMutationActivity.js";
import { appendDispatchDateToUrl, readCurrentDispatchDate } from "../utils/dispatchDateUrls.js";
import { mergeDayAndEveningTimelineRows } from "../utils/eveningGanttRail.js";
import {
  buildOptimizationPreviewMotionPlan,
  condenseOptimizationPreviewMotionQueue,
  isCanonicalOptimizationTerminalPreview,
  resolveOptimizationPreviewMotionDuration,
  resolveOptimizationPreviewVisualPhase,
  shouldDeferOptimizationTerminalPreview,
  shouldHoldOptimizationTerminalPreview,
  shouldHideOptimizationPreviewInspection,
  shouldPrioritizeOptimizationTerminalPreview,
  updateOptimizationPreviewPresentation,
  updateOptimizationPreviewVisitState,
  OPTIMIZATION_PREVIEW_MOTION_DURATION_MS,
  OPTIMIZATION_PREVIEW_MOTION_CATCH_UP_DURATION_MS,
  OPTIMIZATION_PREVIEW_MOTION_QUEUE_LIMIT,
  OPTIMIZATION_PREVIEW_TERMINAL_HANDOFF_TIMEOUT_MS,
  OPTIMIZATION_PREVIEW_TERMINAL_HOLD_DURATION_MS,
  OPTIMIZATION_PREVIEW_TERMINAL_QUEUE_DELAY_MS,
} from "../utils/optimizationPreviewMotion.js";
import {
  applyOptimizationPreviewReserveFallback,
  collectBoundedOptimizationPreviewOrderIds,
  collectOptimizationPreviewRetainedOrderIds,
  filterOptimizationPreviewRetainedGroups,
  mergeOptimizationPreviewAssignments,
  mergeOptimizationPreviewTimelineBounds,
  timelineRowHasOptimizationPreview,
} from "../utils/optimizationPreviewTimeline.js";
import {
  enrichRouteSequenceDurationChipMetadata,
  buildRouteSequenceOpeningIndicator,
  buildRouteSequenceOpeningVisibilityByItemKey,
  buildRouteSequenceClosingVisibilityByItemKey,
  filterRouteSequenceOpeningIndicators,
  shouldDisplayRouteSequenceOpeningForItem,
} from "../utils/routeSequenceHoverCards.js";
import {
  FLEET_SEQUENCE_COLUMN_PREFERENCE_KEY,
  buildFleetSequencePreferenceColumns,
} from "../utils/fleetSequenceColumnPreferences.js";
import {
  FLEET_SEGMENT_HOVER_CARD_LAYOUT_PROPS,
  FLEET_TIMELINE_ZOOM_OPTIONS,
  DEFAULT_FLEET_TIMELINE_ZOOM_KEY,
  resolveEndOfDayLoadLabelOptions,
  formatSegmentBoundaryLabel,
  formatPreviousDaySequenceStartLabel,
  resolveSegmentBusinessStartMinutes,
  formatDisplayedSegmentEndLabel,
  formatSegmentBusinessEndLabel,
  getSegmentStartLabel,
  normalizeFleetSearch,
  getFleetSequenceDisplayKey,
  getSequenceOperationLabel,
  buildSequenceCounterpartyInfoItems,
  FleetSequenceCompartmentPill,
  withVirtualTruckCompartment,
  resolveFleetSequenceMeunerieExtraMinutes,
  resolveFleetSequenceAspiratorServiceMinutes,
  resolveFleetSequenceGroupOpeningTime,
  buildFleetSequenceMeunerieChip,
  buildFleetSequenceAspiratorTimeChip,
  buildFleetSequenceManualBufferChip,
  getSequenceGroupCompartmentLabel,
  parseSequenceDistanceKm,
  compactFleetSequenceDurationLabel,
  isInformationalSequenceLoadItem,
  formatFleetSequenceDurationChipLabel,
  buildSequenceSegmentServiceDurationChip,
  buildSequenceGroupDurationChips,
  normalizeFleetSegmentDurationMinutes,
  GroupedSegmentHoverCardContent,
  resolveGroupedSegmentDragCandidate,
  TimelineSegmentStatusMarkers,
  TimelineSequenceBadge,
  TimelineProductRunStripe,
  TimelineSegmentTabs,
  InitialPreloadFleetRibbon,
  EndOfDayLoadFleetRibbon,
  TimelinePriorityBadge,
  TimelineContextBadge,
  TimelineInlineIndicators,
  FleetTimelineSearchField,
  FleetTimelineToolbar,
  FleetTimelineZoomSelect,
  FleetTimelineShortcutsHelp,
  FleetTimelineNowLine,
  FleetTimelineScale,
  FleetTruckFilterControls,
  FleetReassignmentDragNotice,
  normalizeTruckProductLabel,
  getTruckProductTone,
  FleetProductPill,
  TruckHoverCardContent,
  renderSegmentHoverCardOrderTitleTrailing,
  fetchFleetSegmentComparison,
  normalizeFleetRouteSegmentIndexes,
  buildFleetTimelineSourceSegments,
  buildFleetTimelineBoundRows,
  SegmentHoverCardContent,
  CompartmentFillHoverCard,
} from "./FleetTimelinePresentation.jsx";
import {
  formatTruckTotalHoverDistance,
  SegmentActionPopover,
  SuspendedTransferAction,
  TruckActionsPopover,
} from "./FleetSegmentActions.jsx";

const EMPTY_TRUCK_ID_SET = new Set();
const DISPATCH_TRUCK_SHEET_PDF_REVISION = "report_v42";

function buildDispatchTruckSheetPdfFingerprint(baseFingerprint, {
  dispatchDate = "",
  dispatchPeriod = "day",
  fitOnePage = false,
  generationNonce = "",
  notes = "",
  orientation = "landscape",
  showCompartments = true,
  showMap = true,
  truckId = "",
} = {}) {
  const normalizedBaseFingerprint = typeof baseFingerprint === "string" ? baseFingerprint.trim() : "";
  const normalizedDispatchDate = typeof dispatchDate === "string" ? dispatchDate.trim() : "";
  const normalizedDispatchPeriod = dispatchPeriod === "evening" ? "evening" : "day";
  const normalizedGenerationNonce = typeof generationNonce === "string" ? generationNonce.trim() : "";
  const normalizedNotes = typeof notes === "string" ? notes.trim().replace(/\s+/g, " ") : "";
  const normalizedOrientation = orientation === "portrait" ? "portrait" : "landscape";
  const normalizedTruckId = typeof truckId === "string" ? truckId.trim() : "";
  const fingerprintParts = [
    normalizedBaseFingerprint || normalizedDispatchDate || "dispatch",
    "truck_sheet_pdf",
    DISPATCH_TRUCK_SHEET_PDF_REVISION,
    normalizedDispatchPeriod,
    "single",
    normalizedTruckId || "unknown",
    normalizedOrientation,
    fitOnePage ? "fit1" : "fit0",
    showCompartments ? "cmp" : "nocmp",
    showMap ? "map" : "nomap",
  ];
  if (normalizedNotes) {
    fingerprintParts.push(`notes::${normalizedNotes}`);
  }
  if (normalizedGenerationNonce) {
    fingerprintParts.push(`run::${normalizedGenerationNonce}`);
  }
  return fingerprintParts.join("::");
}

function buildDispatchTruckSheetPdfReadyRoute(truckId = "", dispatchPeriod = "day") {
  const searchParams = new URLSearchParams();
  searchParams.set("downloadTruckSheetPdf", "selected");
  const normalizedTruckId = typeof truckId === "string" ? truckId.trim() : "";
  if (normalizedTruckId) {
    searchParams.set("selected_truck_id", normalizedTruckId);
  }
  if (dispatchPeriod === "evening") {
    searchParams.set("dispatch_period", "evening");
  }
  return `/reports?${searchParams.toString()}`;
}

const FLEET_SEQUENCE_DURATION_CHIP_WIDTH_PX = 56;
const FLEET_TIMELINE_TRUCK_COLUMN_GRID = "min-[1120px]:grid-cols-[155px_minmax(0,1fr)]";
const FLEET_TIMELINE_ROW_ARTICLE_BASE_CLASS_NAME = "relative grid gap-1 rounded-[4px] border px-1 py-0 transition duration-150";
const FLEET_TIMELINE_ROW_TRACK_HEIGHT_CLASS_NAME = "h-9";
const FLEET_TIMELINE_ROWS_CANVAS_CLASS_NAME = "grid gap-0.5 overflow-y-auto pr-px";
const FLEET_TIMELINE_ROWS_EMBEDDED_MAX_HEIGHT_CLASS_NAME = "max-h-205"; // 15 camions sans défilement
const FLEET_TIMELINE_TRUCK_TITLE_CLASS_NAME = "m-0 min-w-0 truncate text-[0.78rem] font-bold leading-none tracking-[-0.02em]";
const FLEET_TIMELINE_TRUCK_DRIVER_CLASS_NAME = "min-w-0 break-words text-[0.68rem] font-medium leading-tight text-content-copy";
const FLEET_TIMELINE_TRUCK_HEADER_BUTTON_BASE_CLASS_NAME = "grid w-full min-w-0 gap-0.5 rounded-[4px] py-0.5 text-left transition focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2";
const FLEET_TIMELINE_TRUCK_HEADER_INNER_GRID_CLASS_NAME = "col-span-2 row-span-3 grid min-w-0 grid-cols-subgrid grid-rows-subgrid";

const FLEET_TIMELINE_TRUCK_PRODUCT_PILL_CLASS_NAME = "min-h-4 px-1.25 py-0.25 text-[0.58rem]";
const FLEET_TIMELINE_TRUCK_TRIPLE_PRODUCT_PILL_CLASS_NAME = "min-h-4 px-px py-0.25 text-[0.58rem]";

const FLEET_SEQUENCE_DURATION_CHIP_CLASS_NAME = `justify-center px-0.75 text-[0.58rem] tabular-nums tracking-tight ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`;
const FLEET_SEQUENCE_DURATION_CHIP_LABEL_CLASS_NAME = "whitespace-nowrap";






































const EMBEDDED_PROMOTED_SEGMENT_PRODUCT_CHIP_COLUMN_CLASS_NAME_MAP = { grain: "" };



const FLEET_SEQUENCE_NUMERIC_VALUE_CLASS_NAME = "text-[0.81rem] font-bold leading-tight text-text tabular-nums whitespace-nowrap";
const FLEET_SEQUENCE_TONNAGE_CELL_CLASS_NAME = `px-2.5 py-1.25 align-top text-right ${FLEET_SEQUENCE_NUMERIC_VALUE_CLASS_NAME}`;
const FLEET_SEQUENCE_DISTANCE_VALUE_GRID_CLASS_NAME = "grid w-full grid-cols-[minmax(0,1fr)_auto] items-baseline gap-x-0.75";
const FLEET_SEQUENCE_DISTANCE_CELL_CLASS_NAME = `py-1.25 pl-2.5 pr-6 align-top text-left ${FLEET_SEQUENCE_NUMERIC_VALUE_CLASS_NAME}`;
const FLEET_SEQUENCE_STEP_HEADER_CLASS_NAME = "sticky top-0 border-b border-[var(--shell-chrome-line)] py-1.5 pl-2.5 pr-1 text-left";
const FLEET_SEQUENCE_ROUTE_HEADER_CLASS_NAME = "sticky top-0 border-b border-[var(--shell-chrome-line)] py-1.5 pl-1 pr-2.5 text-left";
const FLEET_SEQUENCE_STEP_CELL_CLASS_NAME = "py-1.5 pl-2.5 pr-1 align-top";
const FLEET_SEQUENCE_ROUTE_CELL_CLASS_NAME = "py-1.5 pl-1 pr-2.5 align-top";
const FLEET_SEQUENCE_STEP_CELL_COMPACT_CLASS_NAME = "py-1.25 pl-2.5 pr-1 align-top";
const FLEET_SEQUENCE_ROUTE_CELL_COMPACT_CLASS_NAME = "py-1.25 pl-1 pr-2.5 align-top";
const FLEET_SEQUENCE_OCCUPANCY_HEADER_CLASS_NAME = "sticky top-0 border-b border-[var(--shell-chrome-line)] px-1 py-1.5 text-center";
const FLEET_SEQUENCE_OCCUPANCY_CELL_CLASS_NAME = "px-1 py-1.5 align-top text-center text-[0.73rem] font-semibold text-text tabular-nums";
const FLEET_SEQUENCE_OCCUPANCY_CELL_COMPACT_CLASS_NAME = "px-1 py-1.25 align-top text-center text-[0.73rem] font-semibold text-text tabular-nums";

const FLOATING_MAP_INLINE_OPEN_GAP_PX = 24;
const FLEET_FLOATING_MAP_SIZE_PRESETS = {
  compact: { width: 620, mapHeight: 220 },
  standard: { width: 760, mapHeight: 300 },
  large: { width: 920, mapHeight: 390 },
  xlarge: { width: 1080, mapHeight: 480 },
};
const FLOATING_MAP_COMPACT_WIDTH = resolveFloatingMapPreset("compact", FLEET_FLOATING_MAP_SIZE_PRESETS).width;

const FLEET_SEQUENCE_COLUMN_DEFAULT_WIDTHS = Object.freeze({
  step: 82,
  route: 196,
  silo: 88,
  order: 104,
  workOrder: 88,
  product: 60,
  code: 68,
  compartment: 280,
  occupancy: 50,
  fillRate: 62,
  tonnage: 50,
  charge: 64,
  distance: 68,
  service: FLEET_SEQUENCE_DURATION_CHIP_WIDTH_PX,
  buffer: 50,
  travel: 68,
  meunerie: FLEET_SEQUENCE_DURATION_CHIP_WIDTH_PX,
  aspirator: FLEET_SEQUENCE_DURATION_CHIP_WIDTH_PX,
});

const FLEET_SEQUENCE_COLUMN_MIN_WIDTHS = Object.freeze({
  step: 74,
  route: 152,
  silo: 56,
  order: 72,
  workOrder: 64,
  product: 36,
  code: 36,
  compartment: 180,
  occupancy: 42,
  fillRate: 54,
  tonnage: 50,
  charge: 58,
  distance: 52,
  service: 48,
  buffer: 42,
  travel: 56,
  meunerie: 48,
  aspirator: 48,
});

const FLEET_SEQUENCE_COLUMN_LABELS = Object.freeze({
  step: "#",
  route: "Départ → Destination",
  silo: "Silo",
  order: "N° commande",
  workOrder: "Bon de travail",
  product: "Produit",
  code: "Code Pr.",
  compartment: "Compartiment",
  occupancy: "OC",
  fillRate: "Taux",
  tonnage: "Tn",
  charge: "Chg",
  distance: "Dist.",
  service: "C/D",
  buffer: "Buf.",
  travel: "Route",
  meunerie: "Meun.",
  aspirator: "Aspi.",
});

const FLEET_SEQUENCE_BASE_COLUMNS = Object.freeze(buildFleetSequencePreferenceColumns({
  defaultWidths: FLEET_SEQUENCE_COLUMN_DEFAULT_WIDTHS,
  labels: FLEET_SEQUENCE_COLUMN_LABELS,
  minWidths: FLEET_SEQUENCE_COLUMN_MIN_WIDTHS,
}));

const FLEET_SEQUENCE_COLUMN_ALIGNMENTS = Object.freeze({
  aspirator: "center",
  buffer: "center",
  charge: "right",
  fillRate: "center",
  meunerie: "center",
  occupancy: "center",
  service: "center",
  tonnage: "right",
  travel: "center",
});

const FLEET_SEQUENCE_COLUMN_HEADER_CLASS_NAMES = Object.freeze({
  step: FLEET_SEQUENCE_STEP_HEADER_CLASS_NAME,
  route: FLEET_SEQUENCE_ROUTE_HEADER_CLASS_NAME,
  occupancy: FLEET_SEQUENCE_OCCUPANCY_HEADER_CLASS_NAME,
});

const FLEET_SEQUENCE_DEFAULT_HEADER_CLASS_NAME = "sticky top-0 border-b border-[var(--shell-chrome-line)] px-1.5 py-1.5 text-left";
const FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME = "py-1.25 px-1 align-top text-center";
const FLEET_SEQUENCE_DURATION_CELL_FALLBACK_CLASS_NAME = "text-[0.73rem] font-medium text-text";

const FLEET_SEQUENCE_COLUMN_CELL_CLASS_NAMES = Object.freeze({
  step: {
    group: FLEET_SEQUENCE_STEP_CELL_CLASS_NAME,
    segment: FLEET_SEQUENCE_STEP_CELL_COMPACT_CLASS_NAME,
  },
  route: {
    group: FLEET_SEQUENCE_ROUTE_CELL_CLASS_NAME,
    segment: FLEET_SEQUENCE_ROUTE_CELL_COMPACT_CLASS_NAME,
  },
  silo: {
    group: "max-w-0 overflow-hidden px-2.5 py-1.5 align-top text-[0.73rem] text-content-copy",
    segment: "max-w-0 overflow-hidden px-2.5 py-1.25 align-top text-[0.73rem] text-content-copy",
  },
  order: {
    group: "px-2.5 py-1.5 align-top text-[0.73rem] text-content-copy",
    segment: "max-w-0 overflow-hidden px-2.5 py-1.25 align-top text-[0.73rem] text-content-copy",
  },
  workOrder: {
    group: "px-2.5 py-1.5 align-top text-[0.73rem] text-content-copy",
    segment: "max-w-0 overflow-hidden px-2.5 py-1.25 align-top text-[0.73rem] text-content-copy",
  },
  product: {
    group: "px-2.5 py-1.5 align-top text-[0.73rem] text-content-copy",
    segment: "px-2.5 py-1.25 align-top text-[0.73rem] text-content-copy",
  },
  code: {
    group: "px-2.5 py-1.5 align-top text-[0.73rem] text-content-copy",
    segment: "px-2.5 py-1.25 align-top text-[0.73rem] text-content-copy",
  },
  compartment: {
    group: "px-2.5 py-1.5 align-top text-[0.73rem] text-content-copy",
    segment: "px-2.5 py-1.25 align-top text-[0.73rem] text-content-copy",
  },
  occupancy: {
    group: FLEET_SEQUENCE_OCCUPANCY_CELL_CLASS_NAME,
    segment: FLEET_SEQUENCE_OCCUPANCY_CELL_COMPACT_CLASS_NAME,
  },
  fillRate: {
    group: "px-2.5 py-1.5 align-top text-center text-[0.73rem] font-semibold text-text tabular-nums",
    segment: "px-2.5 py-1.25 align-top text-center text-[0.73rem] font-semibold text-text tabular-nums",
  },
  tonnage: {
    group: FLEET_SEQUENCE_TONNAGE_CELL_CLASS_NAME,
    segment: FLEET_SEQUENCE_TONNAGE_CELL_CLASS_NAME,
  },
  charge: {
    group: "px-2.5 py-1.5 align-top text-right text-[0.73rem] font-semibold text-text",
    segment: "px-2.5 py-1.25 align-top text-right text-[0.73rem] font-semibold text-text",
  },
  distance: {
    group: FLEET_SEQUENCE_DISTANCE_CELL_CLASS_NAME,
    segment: FLEET_SEQUENCE_DISTANCE_CELL_CLASS_NAME,
  },
  service: {
    group: FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME,
    segment: FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME,
  },
  meunerie: {
    group: FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME,
    segment: FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME,
  },
  aspirator: {
    group: FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME,
    segment: FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME,
  },
  buffer: {
    group: FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME,
    segment: FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME,
  },
  travel: {
    group: FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME,
    segment: FLEET_SEQUENCE_DURATION_CELL_CLASS_NAME,
  },
});

const FLEET_SEQUENCE_DURATION_CHIP_KIND_BY_COLUMN_KEY = Object.freeze({
  service: "svc",
  meunerie: "meunerie",
  aspirator: "aspirator_time",
  buffer: "manual_buffer",
  travel: "dur",
});






const TRUCK_TOTAL_DURATION_HOVERCARD_DELAY_MS = 2000;
const HAVERSINE_ROUTE_WARNING_PERSISTENCE_DELAY_MS = 15000;
const RENDERED_ACTIVATION_HIGHLIGHT_DURATION_MS = 4600;
const TIMELINE_EDGE_SNAP_PERCENT = 1.25;
const TIMELINE_PRELOAD_GUTTER_RETAINED_RATIO = 0.3;
const TIMELINE_PRELOAD_GUTTER_BASE_PERCENT = 6 * TIMELINE_PRELOAD_GUTTER_RETAINED_RATIO;
const TIMELINE_INFORMATIONAL_SEGMENT_WIDTH_PERCENT = 3.3;



const MONTREAL_RISK_BADGE_TITLE = "Passe par l’île de Montréal, prévoir un buffer de temps.";








function getEndOfDayLoadModeLongLabelForSegment(segment = null) {
  return getEndOfDayLoadModeLongLabel(
    resolveEndOfDayLoadDisplayMode(segment),
    resolveEndOfDayLoadLabelOptions(segment),
  );
}

function resolveFleetTimelineRowTruckKey(row) {
  return String(row?.assignmentId || row?.truckId || row?.assignment?.truck_id || "").trim();
}

function normalizeFleetIncidentText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function resolveFleetIncidentMinutes(value) {
  if (Number.isFinite(Number(value))) {
    return Number(value);
  }
  const match = /^(\d{2}):(\d{2})$/.exec(normalizeFleetIncidentText(value));
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59
    ? hour * 60 + minute
    : null;
}

function resolveFleetRowIncidents(row = null) {
  const incidents = Array.isArray(row?.incidents)
    ? row.incidents
    : Array.isArray(row?.assignment?.truck_incidents)
      ? row.assignment.truck_incidents
      : [];
  return incidents
    .filter((incident) => incident && typeof incident === "object")
    .slice()
    .sort((left, right) => (
      (resolveFleetIncidentMinutes(left?.display_start_minutes ?? left?.start_minutes ?? left?.start_time) ?? Number.MAX_SAFE_INTEGER)
      - (resolveFleetIncidentMinutes(right?.display_start_minutes ?? right?.start_minutes ?? right?.start_time) ?? Number.MAX_SAFE_INTEGER)
      || normalizeFleetIncidentText(left?.id).localeCompare(normalizeFleetIncidentText(right?.id))
    ));
}

function formatFleetIncidentTime(value, fallbackMinutes = null) {
  const match = /^(\d{2}):(\d{2})$/.exec(normalizeFleetIncidentText(value));
  if (match) {
    return `${Number(match[1])} h ${match[2]}`;
  }
  return Number.isFinite(Number(fallbackMinutes))
    ? formatTimelineClockLabel(Number(fallbackMinutes))
    : "--";
}

function formatFleetIncidentUpdatedAt(value) {
  const normalizedValue = normalizeFleetIncidentText(value);
  if (!normalizedValue) {
    return "Date inconnue";
  }
  const parsedValue = new Date(normalizedValue);
  if (Number.isNaN(parsedValue.getTime())) {
    return normalizedValue;
  }
  return new Intl.DateTimeFormat("fr-CA", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsedValue);
}

function buildFleetIncidentPlacement(incident, {
  leadingPaddingPercent = 0,
  scaleMinutes = 0,
  timelineStartMinutes = 0,
  trailingPaddingPercent = 0,
} = {}) {
  const startMinutes = resolveFleetIncidentMinutes(
    incident?.display_start_minutes ?? incident?.start_minutes ?? incident?.start_time,
  );
  const endMinutes = resolveFleetIncidentMinutes(
    incident?.display_end_minutes ?? incident?.end_minutes ?? incident?.end_time,
  );
  const safeScaleMinutes = Number(scaleMinutes);
  const safeTimelineStart = Number(timelineStartMinutes);
  if (
    startMinutes == null
    || endMinutes == null
    || endMinutes <= startMinutes
    || !Number.isFinite(safeScaleMinutes)
    || safeScaleMinutes <= 0
    || !Number.isFinite(safeTimelineStart)
  ) {
    return null;
  }
  const leading = Math.max(0, Math.min(Number(leadingPaddingPercent) || 0, 100));
  const trailing = Math.max(0, Math.min(Number(trailingPaddingPercent) || 0, 100 - leading));
  const availablePercent = Math.max(100 - leading - trailing, 0);
  const rawLeft = leading + ((startMinutes - safeTimelineStart) / safeScaleMinutes) * availablePercent;
  const rawRight = leading + ((endMinutes - safeTimelineStart) / safeScaleMinutes) * availablePercent;
  const leftPercent = Math.max(0, Math.min(rawLeft, 100));
  const rightPercent = Math.max(leftPercent, Math.min(rawRight, 100));
  return {
    endMinutes,
    leftPercent,
    startMinutes,
    widthPercent: Math.max(rightPercent - leftPercent, 0.8),
  };
}

function FleetIncidentsSummaryHoverCardContent({ incidents = [] }) {
  return (
    <div className="grid min-w-72 max-w-[26rem] gap-2">
      {incidents.map((incident, index) => (
        <div key={normalizeFleetIncidentText(incident?.id) || `${incident?.start_time}-${index}`} className="grid gap-1 rounded-[8px] border border-red-100 bg-red-50/60 px-2.5 py-2">
          <div className="text-[0.7rem] font-black text-red-800">
            {formatFleetIncidentTime(incident?.start_time, incident?.display_start_minutes)} — {formatFleetIncidentTime(incident?.end_time, incident?.display_end_minutes)}
          </div>
          <p className="m-0 line-clamp-3 whitespace-pre-wrap text-[0.68rem] leading-4.5 text-content-copy">
            {normalizeFleetIncidentText(incident?.description) || "Description non disponible"}
          </p>
          <p className="m-0 border-t border-red-100 pt-1 text-[0.62rem] font-medium leading-4 text-content-muted">
            Dernière modification : {formatFleetIncidentUpdatedAt(incident?.updated_at || incident?.created_at)}
            {normalizeFleetIncidentText(incident?.updated_by) ? ` · ${normalizeFleetIncidentText(incident.updated_by)}` : ""}
          </p>
        </div>
      ))}
    </div>
  );
}

function countInformationalTimelinePills(segments, { edge = "leading" } = {}) {
  return (Array.isArray(segments) ? segments : []).filter((segment) => {
    const isLeading = Boolean(segment?.isInitialPreload || segment?.isEndOfDayLoadSourcePreload);
    const isTrailing = Boolean(segment?.isEndOfDayLoad);
    if (edge === "leading" ? !isLeading : !isTrailing) {
      return false;
    }

    if (segment?.isSameSiteSequenceGroup && Number(segment?.sameSiteGroupSize) > 1) {
      return segment?.sameSiteGroupIndex === 0;
    }

    return true;
  }).length;
}

export function resolveInformationalTimelineLayout(rows = []) {
  let maxLeadingPillCount = 0;
  let maxTrailingPillCount = 0;

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const segments = Array.isArray(row?.segments) ? row.segments : [];
    maxLeadingPillCount = Math.max(maxLeadingPillCount, countInformationalTimelinePills(segments, { edge: "leading" }));
    maxTrailingPillCount = Math.max(maxTrailingPillCount, countInformationalTimelinePills(segments, { edge: "trailing" }));
  });

  const maxPillCount = Math.max(maxLeadingPillCount, maxTrailingPillCount);
  if (maxPillCount <= 0) {
    return { gutterPercent: 0, segmentWidthPercent: 0 };
  }

  const segmentWidthPercent = TIMELINE_INFORMATIONAL_SEGMENT_WIDTH_PERCENT;
  const gutterPercent = Math.max(
    TIMELINE_PRELOAD_GUTTER_BASE_PERCENT,
    maxPillCount * segmentWidthPercent,
  );

  return {
    gutterPercent,
    segmentWidthPercent,
    // Marge de droite réservée aux seuls chargements de fin de journée.
    trailingGutterPercent: maxTrailingPillCount > 0 ? gutterPercent : 0,
  };
}













function formatSegmentExactTimelineStartLabel(segment) {
  return formatPreviousDaySequenceStartLabel(segment)
    || formatSegmentBoundaryLabel(segment, "visualSegmentStartAt", "visualSegmentStartMinutes")
    || formatSegmentBoundaryLabel(segment, "segmentStartAt", "segmentStartMinutes")
    || formatSegmentBoundaryLabel(segment, "scheduledAt", "scheduledAtMinutes");
}

function formatSequenceStepTimeLabel(segment) {
  if (segment?.isTerminalRouteMarker) {
    return formatSegmentBusinessEndLabel(segment)
      || formatDisplayedSegmentEndLabel(segment)
      || formatSegmentExactTimelineStartLabel(segment);
  }

  return formatSegmentExactTimelineStartLabel(segment);
}











function formatHovercardComparisonEndLabel(
  segment,
  {
    currentHereTrafficDurationMinutes = null,
    hovercardContext = null,
    manualBufferMinutes = null,
    serviceDurationMinutes = null,
  } = {},
) {
  if (hovercardContext?.here_time_aware_routing_enabled !== true) {
    return "";
  }

  if (segment?.isTerminalRouteMerged === true || segment?.stopType === "depot_return") {
    return "";
  }

  const startMinutes = resolveSegmentBusinessStartMinutes(segment);
  const resolvedCurrentHereTrafficDurationMinutes = normalizeFleetSegmentDurationMinutes(currentHereTrafficDurationMinutes);

  if (startMinutes == null || resolvedCurrentHereTrafficDurationMinutes == null) {
    return "";
  }

  const resolvedServiceDurationMinutes = normalizeFleetSegmentDurationMinutes(serviceDurationMinutes) ?? 0;
  const resolvedManualBufferMinutes = normalizeFleetSegmentDurationMinutes(manualBufferMinutes) ?? 0;

  return formatTimelineClockLabel(
    startMinutes
      + resolvedCurrentHereTrafficDurationMinutes
      + resolvedServiceDurationMinutes
      + resolvedManualBufferMinutes,
  );
}



function formatSegmentPriorityArrivalLabel(segment) {
  return formatSegmentBoundaryLabel(segment, "arrivalAt", "arrivalAtMinutes");
}

function formatSegmentPriorityWaitLabel(segment) {
  const waitMinutes = Number(segment?.waitMinutes);
  if (!Number.isFinite(waitMinutes) || waitMinutes <= 0) {
    return "";
  }

  return formatDurationShort(waitMinutes) || "0 min";
}

// Un chargement du jour peut recevoir un autre chargement « à la suite » (pas la veille ni un Chargé PM).
function isJoinableLoadSegment(segment) {
  return segment?.stopType === "loading"
    && !segment.isPreloadedPreviousDay
    && !segment.isInitialPreload
    && !segment.isPmOperationalLoading
    && !segment.isEndOfDayLoadSourcePreload;
}

// Glisser d'un chargement : barre au bord (avant/après), cadre au milieu (charger à la suite), en vert Grain.
function resolveSegmentReorderInsertionStyle(placement) {
  if (placement === "join") {
    return { outline: "2px solid var(--color-accent)", outlineOffset: "1px", zIndex: 40 };
  }
  // Séparer (fin de ligne) : la barre reste après le dernier segment.
  return placement === "after" || placement === "separate"
    ? { borderRight: "12px solid var(--color-accent)" }
    : { borderLeft: "12px solid var(--color-accent)" };
}

function resolveTimelineItemPlacement(leftPercent, widthPercent, {
  snapLeading = false,
  snapTrailing = false,
} = {}) {
  let resolvedLeftPercent = Number.isFinite(Number(leftPercent)) ? Number(leftPercent) : 0;
  let resolvedWidthPercent = Number.isFinite(Number(widthPercent)) && Number(widthPercent) > 0 ? Number(widthPercent) : 0;

  if (snapLeading && resolvedLeftPercent <= TIMELINE_EDGE_SNAP_PERCENT) {
    resolvedWidthPercent += resolvedLeftPercent;
    resolvedLeftPercent = 0;
  }

  const maxVisibleWidthPercent = Math.max(100 - resolvedLeftPercent, 0);
  if (resolvedWidthPercent > maxVisibleWidthPercent) {
    resolvedWidthPercent = maxVisibleWidthPercent;
  }

  if (snapTrailing && (100 - (resolvedLeftPercent + resolvedWidthPercent)) <= TIMELINE_EDGE_SNAP_PERCENT) {
    resolvedWidthPercent = Math.max(100 - resolvedLeftPercent, 0);
  }

  return {
    leftPercent: Math.max(resolvedLeftPercent, 0),
    widthPercent: Math.max(resolvedWidthPercent, 0),
  };
}





const GROUPED_SEGMENT_CLASS_NAMES = {
  mixed: "border border-[rgba(148,163,184,0.24)] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(241,245,249,0.96))] text-slate-700 shadow-none",
  mixedSelected: "border border-[rgba(71,85,105,0.28)] bg-[linear-gradient(180deg,rgba(255,255,255,1),rgba(226,232,240,0.98))] text-slate-800 shadow-none",
};

const MERGED_SAME_ADDRESS_SEGMENT_CLASS_NAMES = {
  base: "border-[rgba(110,118,95,0.38)] bg-[linear-gradient(90deg,rgba(162,186,178,0.78)_0%,rgba(147,170,163,0.68)_44%,rgba(219,209,172,0.7)_56%,rgba(194,181,129,0.82)_100%)] text-[rgb(66,73,71)] shadow-none",
  selected: "border-[rgba(15,92,87,0.5)] bg-[linear-gradient(90deg,rgba(148,173,165,0.9)_0%,rgba(132,156,149,0.8)_44%,rgba(214,202,161,0.82)_56%,rgba(187,172,116,0.92)_100%)] text-[rgb(57,63,62)] shadow-none ring-2 ring-[rgba(15,92,87,0.18)]",
};

const FROZEN_TIMELINE_SEGMENT_CLASS_NAMES = {
  delivery: "border border-[rgba(180,124,24,0.92)] bg-[linear-gradient(180deg,rgba(254,243,199,1),rgba(248,225,138,1))] text-[rgb(106,74,17)] shadow-none ring-2 ring-[rgba(180,124,24,0.22)]",
  deliverySelected: "border border-[rgba(168,114,18,0.96)] bg-[linear-gradient(180deg,rgba(253,230,178,1),rgba(245,212,108,1))] text-[rgb(96,65,12)] shadow-none ring-2 ring-[rgba(180,124,24,0.3)]",
  loading: "border border-[rgb(63,85,133)] bg-[linear-gradient(180deg,rgb(122,148,198),rgb(74,102,158))] text-[rgb(22,36,64)] shadow-none ring-2 ring-[rgba(63,85,133,0.22)]",
  loadingSelected: "border border-[rgb(55,76,122)] bg-[linear-gradient(180deg,rgb(108,135,186),rgb(63,85,133))] text-[rgb(18,30,56)] shadow-none ring-2 ring-[rgba(63,85,133,0.30)]",
  mixed: "border border-[rgba(180,124,24,0.78)] bg-[linear-gradient(90deg,rgb(122,148,198)_0%,rgb(74,102,158)_42%,rgba(254,243,199,1)_58%,rgba(248,225,138,1)_100%)] text-[rgb(78,70,44)] shadow-none ring-2 ring-[rgba(180,124,24,0.22)]",
  mixedSelected: "border border-[rgba(168,114,18,0.86)] bg-[linear-gradient(90deg,rgb(108,135,186)_0%,rgb(63,85,133)_42%,rgba(253,230,178,1)_58%,rgba(245,212,108,1)_100%)] text-[rgb(70,63,39)] shadow-none ring-2 ring-[rgba(180,124,24,0.3)]",
};

const MERGED_SAME_ADDRESS_BADGE_CLASS_NAME = `inline-flex min-h-4.5 shrink-0 items-center border border-[rgba(95,103,83,0.28)] bg-[linear-gradient(90deg,rgba(94,121,114,0.96)_0%,rgba(167,145,84,0.96)_100%)] px-1.5 py-[2px] text-[0.46rem] font-extrabold uppercase tracking-[0.11em] text-white shadow-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`;

const GROUPED_SEGMENT_DRAG_COLLAPSE_DELAY_MS = 300;
const BLOCKED_PLANNING_MOVEMENT_NOTICE_DURATION_MS = 2500;

function getIsPlanningMutationActivityActive() {
  return getActivePlanningMutationCount() > 0;
}

function resolveFleetTimelinePointerDragIntent(deltaX, deltaY, moveThresholdPx = REASSIGNMENT_POINTER_MOVE_THRESHOLD_PX) {
  if (Math.hypot(deltaX, deltaY) < moveThresholdPx) {
    return "";
  }

  return Math.abs(deltaX) > Math.abs(deltaY) ? "reorder" : "reassignment";
}

function isFleetSuspendedDropZoneClientPoint(clientX, clientY) {
  if (typeof document === "undefined" || !Number.isFinite(clientX) || !Number.isFinite(clientY)) {
    return false;
  }

  const target = document.elementFromPoint(clientX, clientY);
  return target instanceof Element
    && target.closest('[data-fleet-suspended-drop-zone="true"]') instanceof HTMLElement;
}

function resolveFleetTimelineDispatchPeriodFromClientPoint(clientX, clientY) {
  if (typeof document === "undefined" || !Number.isFinite(clientX) || !Number.isFinite(clientY)) {
    return "";
  }

  const target = document.elementFromPoint(clientX, clientY);
  if (!(target instanceof Element)) {
    return "";
  }

  const timelineRow = target.closest('[data-fleet-timeline-row="true"]');
  if (!(timelineRow instanceof HTMLElement)) {
    return "";
  }

  return timelineRow.dataset.dispatchPeriod === "evening" ? "evening" : "day";
}

function isFleetCrossPeriodTimelineDropTargetClientPoint(clientX, clientY, sourceDispatchPeriod = "day") {
  const targetDispatchPeriod = resolveFleetTimelineDispatchPeriodFromClientPoint(clientX, clientY);
  if (!targetDispatchPeriod) {
    return false;
  }

  const normalizedSourceDispatchPeriod = sourceDispatchPeriod === "evening" ? "evening" : "day";
  return targetDispatchPeriod !== normalizedSourceDispatchPeriod;
}

const TIMELINE_PRIORITY_SORT_WEIGHT = {
  P1: 3,
  P2: 2,
  P3: 1,
};





const TIMELINE_SEGMENT_OUTLINE_CLASS_NAMES = {
  schedule: "border-[rgba(220,38,38,0.92)] shadow-[0_0_0_1px_rgba(254,226,226,0.94)]",
};

const TIMELINE_SEGMENT_ASPIRATION_ARCH_CLASS_NAME = "absolute inset-x-[1px] top-[1px] h-[50%] rounded-t-[3px] rounded-b-none border-x-2 border-t-2 border-[rgba(245,158,11,0.96)] bg-[linear-gradient(180deg,rgba(255,237,213,0.36),rgba(255,237,213,0.08)_72%,transparent)] shadow-[inset_0_1px_0_rgba(255,251,235,0.72)]";

function getTimelineOutlineLayerClassNames(tone = "") {
  if (tone === "schedule-aspiration") {
    return [
      `absolute inset-0 rounded-[3px] border-2 ${TIMELINE_SEGMENT_OUTLINE_CLASS_NAMES.schedule}`,
      TIMELINE_SEGMENT_ASPIRATION_ARCH_CLASS_NAME,
    ];
  }

  if (tone === "aspiration") {
    return [TIMELINE_SEGMENT_ASPIRATION_ARCH_CLASS_NAME];
  }

  if (tone === "schedule") {
    return [`absolute inset-0 rounded-[3px] border-2 ${TIMELINE_SEGMENT_OUTLINE_CLASS_NAMES.schedule}`];
  }

  return [];
};

const TIMELINE_PRIORITY_STRIPE_CLASS_NAMES = {
  P1: "bg-[rgba(194,65,12,0.88)]",
  P2: "bg-[rgba(180,83,9,0.82)]",
  P3: "bg-[rgba(202,138,4,0.78)]",
};



function getTimelineSequenceBadgeTone(markers = []) {
  if (!Array.isArray(markers) || !markers.length) {
    return "default";
  }

  if (markers.some((marker) => marker?.tone === "warning")) {
    return "warning";
  }

  if (markers.some((marker) => marker?.tone === "protection-reordered")) {
    return "protection-reordered";
  }

  if (markers.some((marker) => marker?.tone === "manual")) {
    return "manual";
  }

  if (markers.some((marker) => marker?.tone === "success")) {
    return "success";
  }

  return "default";
}

function getGroupedSegmentClassName(stopType, isSelected = false, isInitialPreload = false, isEndOfDayLoad = false, endOfDayLoadVisualMode = "") {
  if (stopType === "mixed") {
    return isSelected ? GROUPED_SEGMENT_CLASS_NAMES.mixedSelected : GROUPED_SEGMENT_CLASS_NAMES.mixed;
  }

  return getSegmentClassName(stopType, isSelected, isInitialPreload, isEndOfDayLoad, endOfDayLoadVisualMode);
}

function getGroupedSegmentAccentColor(stopType, isInitialPreload = false, isEndOfDayLoad = false, endOfDayLoadVisualMode = "") {
  return stopType === "mixed" ? "var(--hovercard-accent-neutral)" : getSegmentHoverCardAccentColor(stopType, isInitialPreload, isEndOfDayLoad, endOfDayLoadVisualMode);
}

function getMergedSameAddressSegmentClassName(isSelected = false) {
  return [
    "border",
    isSelected
      ? MERGED_SAME_ADDRESS_SEGMENT_CLASS_NAMES.selected
      : MERGED_SAME_ADDRESS_SEGMENT_CLASS_NAMES.base,
    isSelected
      ? ""
      : "hover:border-[rgba(15,92,87,0.4)]",
  ].filter(Boolean).join(" ");
}



function hasTimelineStatusMarker(markers = [], key = "") {
  return Array.isArray(markers) && markers.some((marker) => marker?.key === key);
}

function isDispatcherFrozenOrDeliveredTimeline(markers = []) {
  if (!Array.isArray(markers) || !markers.length) {
    return false;
  }

  return markers.some((marker) => (
    marker?.key === "frozen"
    || marker?.key === "delivered"
    || marker?.key === "portal"
    || marker?.portalHighlight === true
    || marker?.icon === "smartphone"
  ));
}

function getTimelinePortalStatusMarker(markers = []) {
  if (!Array.isArray(markers)) {
    return null;
  }

  return markers.find((marker) => marker?.portalHighlight === true || marker?.icon === "smartphone") || null;
}

function getTimelineProtectionStatusMarker(markers = []) {
  if (!Array.isArray(markers)) {
    return null;
  }

  if (getTimelinePortalStatusMarker(markers)) {
    return null;
  }

  const reorderedProtectionMarker = markers.find((marker) => marker?.key === "protected-reordered");
  if (reorderedProtectionMarker) {
    return reorderedProtectionMarker;
  }

  return markers.find((marker) => (
    marker?.category === "protected"
    || marker?.key === "protected"
    || marker?.key === "blocked"
  )) || markers.find((marker) => marker?.key === "reassigned") || null;
}

function getTimelineFrozenLockStatusMarker(markers = []) {
  if (!Array.isArray(markers)) {
    return null;
  }

  return markers.find((marker) => marker?.key === "frozen" && marker?.icon === "lock") || null;
}

function getFrozenTimelineSegmentClassName(stopType = "", isSelected = false, isMergedSameAddress = false) {
  if (isMergedSameAddress || stopType === "mixed") {
    return isSelected ? FROZEN_TIMELINE_SEGMENT_CLASS_NAMES.mixedSelected : FROZEN_TIMELINE_SEGMENT_CLASS_NAMES.mixed;
  }

  if (stopType === "loading") {
    return isSelected ? FROZEN_TIMELINE_SEGMENT_CLASS_NAMES.loadingSelected : FROZEN_TIMELINE_SEGMENT_CLASS_NAMES.loading;
  }

  return isSelected ? FROZEN_TIMELINE_SEGMENT_CLASS_NAMES.deliverySelected : FROZEN_TIMELINE_SEGMENT_CLASS_NAMES.delivery;
}



function getTimelineServiceOverlayClassName(stopType, isFrozen = false) {
  if (isFrozen) {
    if (stopType === "loading") {
      return "bg-[linear-gradient(180deg,rgba(63,85,133,0.28),rgba(74,102,158,0.14))]";
    }

    if (stopType === "delivery") {
      return "bg-[linear-gradient(180deg,rgba(248,225,138,0.55),rgba(254,243,199,0.32))]";
    }

    return "bg-[linear-gradient(180deg,rgba(63,85,133,0.18),rgba(248,225,138,0.32))]";
  }

  if (stopType === "loading") {
    return "bg-[image:var(--dispatch-segment-loading-service-overlay)]";
  }

  if (stopType === "delivery") {
    return "bg-[image:var(--dispatch-segment-delivery-service-overlay)]";
  }

  if (stopType === "depot_start") {
    return "bg-[image:var(--dispatch-segment-inspection-service-overlay)]";
  }

  if (stopType === "depot_return") {
    return "bg-[image:var(--dispatch-segment-return-service-overlay)]";
  }

  return "bg-[image:var(--dispatch-segment-neutral-service-overlay)]";
}

function getGroupedTimelineServiceOverlayClassName(stopType, isFrozen = false) {
  return getTimelineServiceOverlayClassName(stopType, isFrozen);
}

function getGroupedTimelineServiceOverlayWidth(servicePercent) {
  const normalizedPercent = Number(servicePercent);

  if (!Number.isFinite(normalizedPercent) || normalizedPercent <= 0) {
    return "0%";
  }

  return `${Math.min(Math.max(normalizedPercent, 0), 100)}%`;
}











function buildMutableSetSignature(values) {
  if (!(values instanceof Set) || values.size === 0) {
    return "";
  }

  return Array.from(values)
    .map((value) => `${value ?? ""}`)
    .sort()
    .join("|");
}





function getGroupedSegmentTypeSummary(groupItem) {
  if (groupItem?.sameSiteGroupContainsMixedStopTypes) {
    return "Chargements et déchargements au même arrêt";
  }

  if (groupItem?.stopType === "loading") {
    return "Chargements successifs au même arrêt";
  }

  if (groupItem?.stopType === "delivery") {
    return "Déchargements successifs au même arrêt";
  }

  return "Étapes successives au même arrêt";
}

function getGroupedSegmentSequenceLabel(groupItem) {
  const startSequence = Number(groupItem?.sameSiteGroupDisplayStartSequence ?? groupItem?.startSequence) || 0;
  const endSequence = Number(groupItem?.sameSiteGroupDisplayEndSequence ?? groupItem?.endSequence) || startSequence;

  if (!startSequence) {
    return "";
  }

  return startSequence === endSequence ? String(startSequence) : `${startSequence}-${endSequence}`;
}

function getSameSiteSequenceGroupLabel(segment) {
  if (!segment?.isSameSiteSequenceGroup) {
    return "";
  }

  const startSequence = Number(segment?.sameSiteGroupDisplayStartSequence ?? segment?.sameSiteGroupStartSequence) || 0;
  const groupSize = Number(segment?.sameSiteGroupSize) || 0;

  if (!startSequence || groupSize <= 1) {
    return "";
  }

  const endSequence = Number(segment?.sameSiteGroupDisplayEndSequence) || (startSequence + groupSize - 1);
  return startSequence === endSequence ? String(startSequence) : `${startSequence}-${endSequence}`;
}

function getMergedSameAddressTimelineOrderCount(segment) {
  if (!isMergedOperationalMissionSegment(segment)) {
    return 0;
  }

  return buildGroupedSegmentOrderHoverEntries(segment?.mergedMemberSegments).length;
}

function getMergedSameAddressTimelineStepLabel(segment) {
  return typeof segment?.mergedSequenceLabel === "string"
    ? segment.mergedSequenceLabel.trim()
    : "";
}

function getDisplayedTimelineSequenceLabel(segment) {
  return resolveDisplayedOperationalSequenceLabel(segment);
}

function getTimelineSegmentSequenceAnnouncement(segment, displaySequenceLabel = "") {
  if (isMergedOperationalMissionSegment(segment)) {
    const mergedOrderCount = getMergedSameAddressTimelineOrderCount(segment);
    const mergedOrderLabel = mergedOrderCount > 0
      ? `${mergedOrderCount} commande${mergedOrderCount > 1 ? "s" : ""} regroupée${mergedOrderCount > 1 ? "s" : ""}`
      : "Commandes regroupées";
    const mergedOrderSequenceLabel = typeof displaySequenceLabel === "string"
      ? displaySequenceLabel.trim()
      : "";
    const mergedTechnicalStepLabel = getMergedSameAddressTimelineStepLabel(segment);
    const mergedSequenceParts = [
      mergedOrderSequenceLabel ? `commandes ${mergedOrderSequenceLabel}` : "",
      mergedTechnicalStepLabel && mergedTechnicalStepLabel !== mergedOrderSequenceLabel
        ? `étapes techniques ${mergedTechnicalStepLabel}`
        : "",
    ].filter(Boolean);
    const mergedSequenceAnnouncement = mergedSequenceParts.length
      ? ` (${mergedSequenceParts.join("; ")})`
      : "";
    return segment?.isAspiratorMissionMerged === true
      ? `${mergedOrderLabel}${mergedSequenceAnnouncement}: mission Aspiration complète regroupée dans un segment unique.`
      : `${mergedOrderLabel}${mergedSequenceAnnouncement}: segment unique chargement et déchargement à la même adresse.`;
  }

  return `Étape ${displaySequenceLabel}:`;
}

function getSameAddressMergedTimelineBadgeLabel(segment, widthPercent = 0) {
  if (segment?.isSameAddressLoadingDeliveryMerged !== true) {
    return "";
  }

  const normalizedWidthPercent = Number(widthPercent);
  return Number.isFinite(normalizedWidthPercent) && normalizedWidthPercent < 5.5 ? "C/D" : "C + D";
}





function getSequenceOperationDisambiguatorLabel(item, fallbackItem = null) {
  const label = typeof item?.operationDisambiguatorLabel === "string"
    ? item.operationDisambiguatorLabel.trim()
    : "";

  if (label) {
    return label;
  }

  return typeof fallbackItem?.operationDisambiguatorLabel === "string"
    ? fallbackItem.operationDisambiguatorLabel.trim()
    : "";
}

function getSequenceRouteSummaryLabel({
  isTerminalRouteMarker = false,
  fromLabel = "",
  routeFromLabel = "",
  routeToLabel = "",
  toLabel = "",
} = {}) {
  if (isTerminalRouteMarker) {
    return "";
  }

  const resolvedFromLabel = routeFromLabel || fromLabel;
  const resolvedToLabel = routeToLabel || toLabel;

  if (!resolvedFromLabel || !resolvedToLabel) {
    return "";
  }

  return `${resolvedFromLabel} → ${resolvedToLabel}`;
}

function getSequenceInlineSummaryLabel(summaryLabel = "", addressLabel = "") {
  const trimmedSummaryLabel = typeof summaryLabel === "string" ? summaryLabel.trim() : "";
  if (trimmedSummaryLabel) {
    return trimmedSummaryLabel;
  }

  return typeof addressLabel === "string" ? addressLabel.trim() : "";
}



function resolveFleetSequenceColumnWidth(columnKey = "", widths = null) {
  const candidateWidth = Number(widths?.[columnKey]);
  const fallbackWidth = Number(FLEET_SEQUENCE_COLUMN_DEFAULT_WIDTHS[columnKey]);
  const minWidth = Number(FLEET_SEQUENCE_COLUMN_MIN_WIDTHS[columnKey]) || 0;
  const resolvedWidth = Number.isFinite(candidateWidth) ? candidateWidth : fallbackWidth;
  return Math.max(minWidth, Math.round(Number.isFinite(resolvedWidth) ? resolvedWidth : minWidth));
}

function getFleetSequenceTableWidth(widths = null, columns = FLEET_SEQUENCE_BASE_COLUMNS) {
  const resolvedColumns = Array.isArray(columns) && columns.length ? columns : FLEET_SEQUENCE_BASE_COLUMNS;
  return resolvedColumns.reduce(
    (totalWidth, column) => totalWidth + resolveFleetSequenceColumnWidth(column?.key, widths),
    0,
  );
}

function buildFleetSequenceColumnStyle(width) {
  const resolvedWidth = Math.max(0, Math.round(Number(width) || 0));

  return {
    minWidth: `${resolvedWidth}px`,
    width: `${resolvedWidth}px`,
  };
}





function buildSequenceCounterpartySummaryLines(items = [], fallbackItem = null) {
  return buildSequenceCounterpartyInfoItems(items, fallbackItem)
    .map((counterparty) => `${counterparty.label}: ${counterparty.value}`);
}









function FleetSequenceColumnResizeHandle({ columnKey = "", label = "", onPointerDown = null }) {
  if (!columnKey) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label={`Redimensionner la colonne ${label}`}
      className="absolute inset-y-0 right-0 z-10 w-2 cursor-col-resize touch-none select-none bg-transparent p-0 after:absolute after:inset-y-1 after:right-0 after:w-px after:rounded-full after:bg-[rgba(148,163,184,0.38)] hover:after:bg-[rgba(59,130,246,0.72)] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-[rgba(59,130,246,0.28)]"
      onClick={(event) => {
        event.preventDefault();
      }}
      onPointerDown={(event) => {
        onPointerDown?.(event, columnKey);
      }}
      title={`Redimensionner la colonne ${label}`}
    />
  );
}

function FleetSequenceSortableHeaderCell({
  align = "left",
  className = "",
  columnKey = "",
  draggable = true,
  label = "",
  onColumnMove = null,
  onResizePointerDown = null,
}) {
  const thRef = React.useRef(null);
  const { dragOverlay, isDragging } = useSortableColumnHeaderDrag({
    columnKey,
    columnKeyAttribute: "data-fleet-sequence-column-key",
    draggable,
    label,
    onMove: onColumnMove,
    thRef,
  });

  return (
    <th
      ref={thRef}
      data-fleet-sequence-column-key={draggable ? columnKey : undefined}
      className={joinClasses([
        className,
        align === "center" ? "text-center" : "",
        align === "right" ? "text-right" : "",
        draggable ? "cursor-grab select-none" : "",
        isDragging ? "border border-dashed border-accent/40 bg-slate-50/30 opacity-30" : "",
      ])}
    >
      <div
        className={joinClasses([
          "relative flex w-full min-w-0 items-center pr-2.5",
          align === "center" ? "justify-center" : "",
          align === "right" ? "justify-end" : "",
        ])}
      >
        <span className="truncate">{label}</span>
        <FleetSequenceColumnResizeHandle columnKey={columnKey} label={label} onPointerDown={onResizePointerDown} />
      </div>
      <ColumnHeaderDragOverlay dragOverlay={dragOverlay} />
    </th>
  );
}

function resolveFleetSequenceColumnCellClassName(columnKey = "", rowKind = "segment") {
  const cellClassNames = FLEET_SEQUENCE_COLUMN_CELL_CLASS_NAMES[columnKey];
  if (!cellClassNames) {
    return rowKind === "group"
      ? "px-2.5 py-1.5 align-top text-[0.73rem] text-content-muted"
      : "px-2.5 py-1.25 align-top text-[0.73rem] text-content-muted";
  }
  return cellClassNames[rowKind] || cellClassNames.segment || "";
}

function resolveFleetSequenceHeaderClassName(columnKey = "", align = "left") {
  return joinClasses([
    FLEET_SEQUENCE_COLUMN_HEADER_CLASS_NAMES[columnKey] || FLEET_SEQUENCE_DEFAULT_HEADER_CLASS_NAME,
    align === "center" ? "text-center" : "",
    align === "right" ? "text-right" : "",
  ]);
}

function getFleetSequenceDurationChip(chips = [], columnKey = "") {
  const chipKind = FLEET_SEQUENCE_DURATION_CHIP_KIND_BY_COLUMN_KEY[columnKey];
  if (!chipKind) {
    return null;
  }
  return (Array.isArray(chips) ? chips : []).find((chip) => (
    chip?.kind === chipKind || chip?.key === chipKind
  )) || null;
}

function FleetSequenceDurationColumnContent({ chip = null, showFallback = true }) {
  if (chip) {
    return (
      <RouteMetaChipRow
        chips={[chip]}
        chipClassName={FLEET_SEQUENCE_DURATION_CHIP_CLASS_NAME}
        chipLabelClassName={FLEET_SEQUENCE_DURATION_CHIP_LABEL_CLASS_NAME}
        className="justify-center gap-0"
        gridClassName=""
      />
    );
  }

  return showFallback ? <span className={FLEET_SEQUENCE_DURATION_CELL_FALLBACK_CLASS_NAME}>—</span> : null;
}

































function parseFleetSequenceDistanceDisplayLabel(label = "") {
  if (typeof label !== "string") {
    return null;
  }

  const trimmedLabel = label.trim();
  if (!trimmedLabel || trimmedLabel === "—") {
    return null;
  }

  const match = trimmedLabel.match(/^(-?\d+(?:[.,]\d+)?)\s*km$/iu);
  if (!match) {
    return { fallbackLabel: trimmedLabel };
  }

  return { valueLabel: match[1].replace(",", ".") };
}

function FleetSequenceLoadCell({ label = "", stopType = "", signed = false }) {
  if (!label || label === "—" || !["loading", "delivery"].includes(stopType)) {
    return label;
  }
  // Comme le Gantt : chargement en bleu, livraison en brun.
  return (
    <span className="whitespace-nowrap tabular-nums" style={{ color: stopType === "loading" ? "#1e40af" : "#92400e" }}>
      {signed ? (stopType === "loading" ? "+" : "−") : ""}{label}
    </span>
  );
}

function FleetSequenceDistanceCell({ label = "" }) {
  const parsedLabel = parseFleetSequenceDistanceDisplayLabel(label);

  if (!parsedLabel) {
    return <span className="text-content-muted">—</span>;
  }

  if (parsedLabel.fallbackLabel) {
    return <span className="whitespace-nowrap">{parsedLabel.fallbackLabel}</span>;
  }

  return (
    <div className={FLEET_SEQUENCE_DISTANCE_VALUE_GRID_CLASS_NAME}>
      <span className="text-right tabular-nums whitespace-nowrap">{parsedLabel.valueLabel}</span>
      <span className="whitespace-nowrap">km</span>
    </div>
  );
}

// Pause dîner : une ligne à part, aux heures du bloc « Dîner » du Gantt. Les arrêts suivants la comptent déjà.
function FleetSequenceLunchBreakRow({ columns = [], lunchBreak }) {
  const startLabel = formatTimelineClockLabel(lunchBreak.startMinutes);
  const cells = {
    step: <span className="text-[0.72rem] font-bold tabular-nums text-text">{startLabel}</span>,
    route: (
      <div className="flex items-start gap-2">
        <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] ${getSegmentClassName("depot_start", false)}`} aria-hidden="true">
          <Utensils className="h-2.5 w-2.5" strokeWidth={2.4} />
        </span>
        <div className="grid min-w-0 gap-0.5">
          <span className="text-[0.79rem] font-bold leading-4.5 text-text">Dîner</span>
          <span className="text-[0.63rem] leading-4 tabular-nums text-content-copy">
            {startLabel} → {formatTimelineClockLabel(lunchBreak.endMinutes)} · {lunchBreak.minutes} min · hors temps travaillé
          </span>
        </div>
      </div>
    ),
  };

  return (
    <tr className="border-b border-[var(--shell-chrome-line)] text-text last:border-b-0" data-fleet-sequence-lunch-break="true">
      {columns.map((column) => (
        <td key={column.key} className={resolveFleetSequenceColumnCellClassName(column.key, "segment")}>
          {cells[column.key] ?? null}
        </td>
      ))}
    </tr>
  );
}

function FleetSequenceStepCell({ hideStartLabel = false, indicators = [], startLabel = "", stepLabel = "" }) {
  const resolvedStartLabel = typeof startLabel === "string" ? startLabel.trim() : "";
  const resolvedStepLabel = typeof stepLabel === "string" ? stepLabel.trim() : "";
  const resolvedIndicators = hideStartLabel
    ? []
    : (Array.isArray(indicators) ? indicators.filter(Boolean) : []);
  const shouldShowStartLabel = !hideStartLabel;

  if (!resolvedStartLabel && !resolvedStepLabel && shouldShowStartLabel) {
    return <span className="text-[0.72rem] font-semibold text-content-copy">—</span>;
  }

  return (
    <div className="grid min-w-0 gap-0.5">
      <div className="grid grid-cols-[2.65rem_minmax(0,1fr)] items-baseline gap-0.75 tabular-nums">
        {shouldShowStartLabel ? (
          <span className="text-left text-[0.72rem] font-bold text-text">{resolvedStartLabel || "—"}</span>
        ) : (
          <span aria-hidden="true" />
        )}
        <span className="whitespace-nowrap text-left text-[0.73rem] font-semibold text-content-copy">{resolvedStepLabel || "—"}</span>
      </div>
      {resolvedIndicators.length ? (
        <SequenceTableIndicatorPills
          indicators={resolvedIndicators}
          className="w-full justify-start gap-0.5"
          pillClassName="px-1.5 py-px text-[0.58rem] tracking-[0.04em]"
        />
      ) : null}
    </div>
  );
}

function getGroupedSegmentStatusMarkers(memberSegments = []) {
  const seenMarkers = new Set();
  let hasGroupedPortalSmartphone = false;
  const groupedMarkers = memberSegments.flatMap((segment) => {
    const statusMarkers = Array.isArray(segment?.statusMarkers) ? segment.statusMarkers : [];

    return statusMarkers.filter((marker) => {
      const isPortalSmartphone = marker?.portalHighlight === true || marker?.icon === "smartphone";
      if (isPortalSmartphone) {
        if (hasGroupedPortalSmartphone) {
          return false;
        }
        hasGroupedPortalSmartphone = true;
      }

      const markerKey = `${marker?.key || marker?.icon || "marker"}::${marker?.label || ""}::${marker?.tone || ""}`;

      if (seenMarkers.has(markerKey)) {
        return false;
      }

      seenMarkers.add(markerKey);
      return true;
    });
  });
  const portalMarkerIndex = groupedMarkers.findIndex((marker) => (
    marker?.portalHighlight === true
    || marker?.icon === "smartphone"
  ));

  if (portalMarkerIndex <= 0) {
    return groupedMarkers;
  }

  const [portalMarker] = groupedMarkers.splice(portalMarkerIndex, 1);
  return [portalMarker, ...groupedMarkers.filter((marker) => marker?.key !== "approved")];
}

function getGroupedSequenceTableMarkerStyle(groupItem, fallbackColor = "") {
  if (groupItem?.stopType === "mixed") {
    return {
      background: "linear-gradient(135deg, var(--dispatch-sequence-loading-marker-fill, rgb(90 121 190)) 0%, var(--dispatch-sequence-delivery-marker-fill, rgb(150 138 62)) 100%)",
      boxShadow: "0 0 0 1px rgba(100,116,139,0.28), 0 0 0 4px rgba(148,163,184,0.16), 0 3px 8px rgba(15,23,42,0.12)",
    };
  }

  return getSequenceTableMarkerStyle(
    groupItem?.stopType,
    fallbackColor,
    groupItem?.isInitialPreload,
    groupItem?.isEndOfDayLoad || groupItem?.isEndOfDayLoadSourcePreload,
    resolveEndOfDayLoadVisualMode(groupItem),
  );
}

function resolveAssignmentMaxWorkMinutes(assignment) {
  const maxWorkHours = Number(assignment?.max_work_hours);

  if (!Number.isFinite(maxWorkHours) || maxWorkHours <= 0) {
    return 0;
  }

  return Math.round(maxWorkHours * 60);
}

function resolveRowEstimatedTotalDurationMinutes(row) {
  if (row?.timelineRenderState?.status === "unrenderable" || row?.timelineRenderState?.status === "empty") {
    return 0;
  }

  const activeTrip = getRowActiveTrip(row);
  const candidateDurations = [
    getAssignmentDisplayedWorkloadMinutes(row?.assignment),
    row?.assignment?.total_estimated_duration_minutes,
    activeTrip?.route?.estimated_duration_minutes,
    activeTrip?.estimated_duration_minutes,
    row?.totalDurationMinutes,
  ];

  for (const candidateDuration of candidateDurations) {
    const parsedMinutes = Math.round(Number(candidateDuration));

    if (Number.isFinite(parsedMinutes) && parsedMinutes > 0) {
      return parsedMinutes;
    }
  }

  return 0;
}

function resolveRowEstimatedTotalDurationLabel(row) {
  if (row?.timelineRenderState?.status === "unrenderable") {
    return "Durée non calculable";
  }

  if (row?.timelineRenderState?.status === "empty") {
    return "0 min";
  }

  const assignmentDurationLabel = typeof row?.assignment?.total_estimated_duration_label === "string"
    ? row.assignment.total_estimated_duration_label.trim()
    : "";

  if (assignmentDurationLabel) {
    return assignmentDurationLabel;
  }

  const estimatedTotalDurationMinutes = resolveRowEstimatedTotalDurationMinutes(row);

  if (estimatedTotalDurationMinutes > 0) {
    return formatDurationShort(estimatedTotalDurationMinutes) || "Durée estimée indisponible";
  }

  const rowDurationLabel = typeof row?.totalDurationLabel === "string"
    ? row.totalDurationLabel.trim()
    : "";

  if (rowDurationLabel) {
    return rowDurationLabel;
  }

  return resolveAssignmentDurationLabel(row?.assignment, row?.totalDurationMinutes);
}

function formatTruckTotalHoverDuration(minutes) {
  return formatDurationShort(minutes) || "0 min";
}

// « charg. 1 h 47 min (dont 40 min fixes) » : temps fixes des Paramètres système compris dans le service.
function formatTruckTotalHoverServicePart(label, minutes, fixedMinutes) {
  const fixedLabel = fixedMinutes > 0 ? ` (dont ${formatTruckTotalHoverDuration(fixedMinutes)} fixes)` : "";
  return `${label} ${formatTruckTotalHoverDuration(minutes)}${fixedLabel}`;
}



function formatTruckTotalHoverSpeed(speedKmh) {
  const normalizedSpeedKmh = Number(speedKmh);
  if (!Number.isFinite(normalizedSpeedKmh) || normalizedSpeedKmh <= 0) {
    return "";
  }

  return `${Number.isInteger(normalizedSpeedKmh) ? normalizedSpeedKmh : normalizedSpeedKmh.toFixed(1)} km/h`;
}

function formatTruckTotalHoverSignedDuration(minutes) {
  const normalizedMinutes = Number(minutes);
  if (!Number.isFinite(normalizedMinutes)) {
    return "";
  }

  const prefix = normalizedMinutes > 0 ? "+" : normalizedMinutes < 0 ? "-" : "";
  return `${prefix}${formatTruckTotalHoverDuration(Math.abs(normalizedMinutes))}`;
}

function formatTruckTotalGanttGapSegmentLabel(segmentSummary = null) {
  if (!segmentSummary) {
    return "le début de la ligne";
  }

  const operationLabel = typeof segmentSummary.operationLabel === "string"
    ? segmentSummary.operationLabel.trim()
    : "";
  const stepLabel = typeof segmentSummary.stepLabel === "string"
    ? segmentSummary.stepLabel.trim()
    : "";

  if (stepLabel && operationLabel) {
    return `étape ${stepLabel} (${operationLabel})`;
  }

  return operationLabel || (stepLabel ? `étape ${stepLabel}` : "segment");
}

function buildVisibleBackendDeltaExplanation({
  backendDurationBreakdown = null,
  frontendDurationBreakdown = null,
  hasTimelineSpanBeyondProductiveTotal = false,
}) {
  const deltaMinutes = Number(backendDurationBreakdown?.visibleDeltaMinutes);
  if (!Number.isFinite(deltaMinutes) || Math.abs(deltaMinutes) < 0.5) {
    return null;
  }

  const ganttGapMinutes = Number(frontendDurationBreakdown?.ganttGapMinutes) || 0;
  const frontendDistanceKm = Number(frontendDurationBreakdown?.distanceKm);
  const backendDistanceKm = Number(backendDurationBreakdown?.distanceKm);
  const frontendDrivingMinutes = Number(frontendDurationBreakdown?.drivingMinutes);
  const backendDrivingMinutes = Number(backendDurationBreakdown?.drivingMinutes);
  const routesMatch = Number.isFinite(frontendDistanceKm)
    && Number.isFinite(backendDistanceKm)
    && Math.round(frontendDistanceKm) === Math.round(backendDistanceKm)
    && Math.round(frontendDrivingMinutes) === Math.round(backendDrivingMinutes);
  const frontendServiceTotalMinutes = (frontendDurationBreakdown?.inspectionMinutes || 0)
    + (frontendDurationBreakdown?.loadingMinutes || 0)
    + (frontendDurationBreakdown?.unloadingMinutes || 0)
    + (frontendDurationBreakdown?.aspiratorMinutes || 0)
    + (frontendDurationBreakdown?.waitMinutes || 0);
  const backendServiceTotalMinutes = (backendDurationBreakdown?.inspectionMinutes || 0)
    + (backendDurationBreakdown?.loadingMinutes || 0)
    + (backendDurationBreakdown?.unloadingMinutes || 0)
    + (backendDurationBreakdown?.aspiratorMinutes || 0)
    + (backendDurationBreakdown?.waitMinutes || 0);
  const serviceDeltaMinutes = frontendServiceTotalMinutes - backendServiceTotalMinutes;
  const manualBufferDeltaMinutes = (frontendDurationBreakdown?.manualBufferMinutes || 0)
    - (backendDurationBreakdown?.manualBufferMinutes || 0);
  const distanceLabel = formatTruckTotalHoverDistance(frontendDistanceKm);

  if (routesMatch && ganttGapMinutes > 0 && Math.abs(serviceDeltaMinutes - ganttGapMinutes) < 1) {
    return null;
  }

  if (routesMatch && Math.abs(manualBufferDeltaMinutes) < 1 && Math.abs(deltaMinutes) < 1) {
    return null;
  }

  if (routesMatch && Math.abs(deltaMinutes + manualBufferDeltaMinutes) < 1) {
    return "L'écart vient du temps ajouté à la main.";
  }

  if (hasTimelineSpanBeyondProductiveTotal) {
    return "Début → fin dépasse le temps travaillé quand un trajet s'étale sur deux blocs.";
  }

  return "« Affiché » compte les vides entre les blocs. « Calculé » ne les compte pas.";
}

function TruckTotalDurationHoverCardContent({
  backendDurationBreakdown = null,
  frontendDurationBreakdown = null,
  frontendTimelineSpanMinutes = null,
  displayedTotalDurationLabel = "",
  showCalculated = false,
}) {
  if (!backendDurationBreakdown) {
    return null;
  }

  const frontendDistanceLabel = formatTruckTotalHoverDistance(frontendDurationBreakdown?.distanceKm);
  const frontendDrivingDurationLabel = formatTruckTotalHoverDuration(frontendDurationBreakdown?.drivingMinutes);
  const frontendDrivingDetailRows = Array.isArray(frontendDurationBreakdown?.drivingSegments)
    ? frontendDurationBreakdown.drivingSegments
    : [];
  const frontendBusinessServiceTotalMinutes = (frontendDurationBreakdown?.inspectionMinutes || 0)
    + (frontendDurationBreakdown?.loadingMinutes || 0)
    + (frontendDurationBreakdown?.unloadingMinutes || 0)
    + (frontendDurationBreakdown?.aspiratorMinutes || 0)
    + (frontendDurationBreakdown?.ganttGapMinutes > 0
      ? (frontendDurationBreakdown?.stopWaitMinutes || 0)
      : (frontendDurationBreakdown?.waitMinutes || 0));
  const frontendBusinessServiceTotalLabel = formatTruckTotalHoverDuration(frontendBusinessServiceTotalMinutes);
  const frontendGanttGapMinutes = Number(frontendDurationBreakdown?.ganttGapMinutes) || 0;
  const frontendGanttGapRows = Array.isArray(frontendDurationBreakdown?.ganttGaps)
    ? frontendDurationBreakdown.ganttGaps.filter((gap) => Number(gap?.gapMinutes) > 0)
    : [];
  const frontendGanttGapLabel = formatTruckTotalHoverDuration(frontendGanttGapMinutes);
  const frontendSommeBeforeGanttGapMinutes = (frontendDurationBreakdown?.drivingMinutes || 0)
    + frontendBusinessServiceTotalMinutes
    + (frontendDurationBreakdown?.manualBufferMinutes || 0);
  const frontendSommeBeforeGanttGapLabel = formatTruckTotalHoverDuration(frontendSommeBeforeGanttGapMinutes);
  const frontendSegmentTotalLabel = formatTruckTotalHoverDuration(frontendDurationBreakdown?.segmentTotalMinutes);
  const frontendManualBufferLabel = frontendDurationBreakdown?.manualBufferMinutes > 0
    ? formatTruckTotalHoverDuration(frontendDurationBreakdown.manualBufferMinutes)
    : "";
  const hasFrontendDrivingDetails = frontendDrivingDetailRows.length > 0;
  const frontendDrivingSummaryLabel = frontendDistanceLabel
    ? (
        <span className="text-[11px]">
          {frontendDistanceLabel} visibles → <span className="font-semibold text-content-ink">{frontendDrivingDurationLabel}</span>
        </span>
      )
    : frontendDrivingDurationLabel;
  const frontendDrivingFormulaLabel = frontendDrivingSummaryLabel;
  const frontendDrivingDetailsPanel = hasFrontendDrivingDetails
    ? (
        <section className="flex h-full min-w-0 flex-col gap-2 rounded-[8px] border border-[rgba(58,82,120,0.12)] bg-[rgba(246,248,252,0.84)] p-2.5">
          <div className="flex items-center justify-between gap-2 border-b-[0.5px] border-[rgba(58,82,120,0.12)] pb-1.5">
            <span className="text-[10px] font-black uppercase tracking-[0.14em] text-content-muted">Étapes visibles</span>
            <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-content-ink shadow-[inset_0_0_0_1px_rgba(58,82,120,0.08)]">
              Gantt
            </span>
          </div>
          <div className="grid gap-1 rounded-[8px] border border-[rgba(58,82,120,0.08)] bg-white/78 px-2 py-1.5">
            {frontendDrivingDetailRows.map((detail, detailIndex) => (
              <div
                key={`${detail.stepLabel || detail.operationLabel || "segment"}-${detailIndex + 1}`}
                className="grid gap-1 min-[520px]:grid-cols-[minmax(0,1fr)_auto] min-[520px]:items-center"
              >
                <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
                  {detail.stepLabel ? (
                    <span className="shrink-0 rounded-full bg-[rgba(58,82,120,0.08)] px-1.5 py-0.5 text-[9px] font-black uppercase tracking-[0.08em] text-content-muted">
                      Étape {detail.stepLabel}
                    </span>
                  ) : null}
                  <span className="truncate text-[10px] font-semibold text-content-ink">{detail.operationLabel}</span>
                  {detail.routeSummaryLabel ? <span className="truncate text-[10px] text-content-muted">{detail.routeSummaryLabel}</span> : null}
                </div>
                <div className="shrink-0 whitespace-nowrap text-[10px] font-semibold text-content-ink min-[520px]:justify-self-end min-[520px]:text-right">
                  {detail.distanceKm != null ? `${formatTruckTotalHoverDistance(detail.distanceKm)} → ` : ""}
                  {formatTruckTotalHoverDuration(detail.travelMinutes)}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-auto ml-auto flex max-w-full items-center gap-2 rounded-full border border-[rgba(37,99,235,0.14)] bg-[rgba(239,246,255,0.9)] px-2.5 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.78)]">
            <span className="truncate whitespace-nowrap text-[10px] font-black tracking-[0.04em] text-[rgb(37,99,235)]">
              Étapes 
            </span>
            <span className="shrink-0 whitespace-nowrap text-[12px] font-bold text-[rgb(30,64,175)]">{frontendDrivingDurationLabel}</span>
          </div>
        </section>
      )
    : null;
  const frontendServiceFormulaParts = [
    frontendDurationBreakdown?.inspectionMinutes > 0
      ? `Inspection ${formatTruckTotalHoverDuration(frontendDurationBreakdown.inspectionMinutes)}`
      : "",
    frontendDurationBreakdown?.loadingMinutes > 0
      ? formatTruckTotalHoverServicePart("charg.", frontendDurationBreakdown.loadingMinutes, frontendDurationBreakdown.loadingFixedMinutes)
      : "",
    frontendDurationBreakdown?.unloadingMinutes > 0
      ? formatTruckTotalHoverServicePart("décharg.", frontendDurationBreakdown.unloadingMinutes, frontendDurationBreakdown.unloadingFixedMinutes)
      : "",
    frontendDurationBreakdown?.aspiratorMinutes > 0
      ? `aspir. ${formatTruckTotalHoverDuration(frontendDurationBreakdown.aspiratorMinutes)}`
      : "",
    frontendDurationBreakdown?.ganttGapMinutes <= 0 && frontendDurationBreakdown?.stopWaitMinutes > 0
      ? `attente ${formatTruckTotalHoverDuration(frontendDurationBreakdown.stopWaitMinutes)}`
      : frontendDurationBreakdown?.ganttGapMinutes <= 0 && frontendDurationBreakdown?.waitMinutes > 0
        ? `attente ${formatTruckTotalHoverDuration(frontendDurationBreakdown.waitMinutes)}`
        : "",
  ].filter(Boolean);
  const frontendServiceFormulaLabel = frontendServiceFormulaParts.length > 0
    ? `${frontendServiceFormulaParts.join(" + ")} = ${frontendBusinessServiceTotalLabel}`
    : "Aucun service";
  const frontendSegmentFormulaParts = [
    frontendDrivingDurationLabel,
    frontendBusinessServiceTotalLabel,
    frontendManualBufferLabel ? `ajout ${frontendManualBufferLabel}` : "",
  ].filter(Boolean);
  const frontendSegmentFormulaLabel = frontendSegmentFormulaParts.length > 0
    ? `${frontendSegmentFormulaParts.join(" + ")} = ${frontendSommeBeforeGanttGapLabel}`
    : frontendSommeBeforeGanttGapLabel;
  const frontendGanttGapDetailPanel = frontendGanttGapMinutes > 0
    ? (
        <div className="rounded-[8px] border border-[rgba(37,99,235,0.24)] bg-[rgba(239,246,255,0.82)] px-2.5 py-2">
          <div className="flex items-start justify-between gap-3 text-[11px]">
            <span className="font-semibold text-[rgb(30,64,175)]">Vides entre blocs</span>
            <span className="shrink-0 whitespace-nowrap font-bold text-content-ink">
              + {frontendGanttGapLabel}
            </span>
          </div>
          {frontendGanttGapRows.length > 0 ? (
            <ul className="mb-0 mt-1.5 grid list-none gap-1 p-0">
              {frontendGanttGapRows.map((gap, gapIndex) => {
                const travelAbsorption = gap?.travelAbsorption;
                const travelDistanceLabel = travelAbsorption?.distanceKm != null
                  ? formatTruckTotalHoverDistance(travelAbsorption.distanceKm)
                  : "";

                return (
                  <li
                    key={`${gap?.beforeSegment?.stepLabel || "segment"}-${gap?.afterSegment?.stepLabel || "start"}-${gapIndex + 1}`}
                    className="grid gap-0.5 text-[10px] leading-[1.45] text-content-ink"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <span className="min-w-0">
                        Entre {formatTruckTotalGanttGapSegmentLabel(gap?.afterSegment)} et {formatTruckTotalGanttGapSegmentLabel(gap?.beforeSegment)}
                      </span>
                      <span className="shrink-0 whitespace-nowrap font-semibold text-[rgb(30,64,175)]">
                        + {formatTruckTotalHoverDuration(gap.gapMinutes)}
                      </span>
                    </div>
                    {travelAbsorption ? (
                      <div className="text-content-muted">
                        {travelDistanceLabel ? `${travelDistanceLabel} · ` : ""}
                        Durée route affichée : {formatTruckTotalHoverDuration(travelAbsorption.travelMinutesBefore)} → ≈ {formatTruckTotalHoverDuration(travelAbsorption.travelMinutesAfter)}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : null}
          <p className="mb-0 mt-1.5 text-[10px] leading-[1.55] text-content-muted">
            Ces minutes comblent un vide entre deux blocs. Elles s'ajoutent au trajet suivant, sans changer les kilomètres.
          </p>
        </div>
      )
    : null;
  const normalizedTimelineSpanMinutes = Number(frontendTimelineSpanMinutes);
  const normalizedSegmentTotalMinutes = Number(frontendDurationBreakdown?.segmentTotalMinutes);
  // La pause dîner allonge Début → Fin sans être du temps travaillé.
  const lunchBreakMinutes = Number(frontendDurationBreakdown?.lunchBreakMinutes) || 0;
  const hasTimelineSpanBeyondProductiveTotal = Number.isFinite(normalizedTimelineSpanMinutes)
    && normalizedTimelineSpanMinutes > 0
    && Number.isFinite(normalizedSegmentTotalMinutes)
    && normalizedTimelineSpanMinutes > normalizedSegmentTotalMinutes + lunchBreakMinutes + 0.5;
  const frontendTimelineSpanLabel = hasTimelineSpanBeyondProductiveTotal
    ? formatTruckTotalHoverDuration(normalizedTimelineSpanMinutes)
    : "";
  const frontendTimelineSpanDeltaLabel = hasTimelineSpanBeyondProductiveTotal
    ? formatTruckTotalHoverSignedDuration(normalizedTimelineSpanMinutes - normalizedSegmentTotalMinutes - lunchBreakMinutes)
    : "";
  const distanceLabel = formatTruckTotalHoverDistance(backendDurationBreakdown.distanceKm);
  const speedLabel = formatTruckTotalHoverSpeed(backendDurationBreakdown.speedKmh);
  const truckSpeedLabel = formatTruckTotalHoverSpeed(backendDurationBreakdown.truckSpeedKmh);
  const drivingDurationLabel = formatTruckTotalHoverDuration(backendDurationBreakdown.drivingMinutes);
  const backendServiceTotalMinutes = backendDurationBreakdown.inspectionMinutes
    + backendDurationBreakdown.loadingMinutes
    + backendDurationBreakdown.unloadingMinutes
    + (backendDurationBreakdown.aspiratorMinutes || 0)
    + backendDurationBreakdown.waitMinutes;
  const backendServiceTotalLabel = formatTruckTotalHoverDuration(backendServiceTotalMinutes);
  const backendManualBufferLabel = backendDurationBreakdown?.manualBufferMinutes > 0
    ? formatTruckTotalHoverDuration(backendDurationBreakdown.manualBufferMinutes)
    : "";
  const backendSommeBeforeTotalMinutes = (backendDurationBreakdown?.drivingMinutes || 0)
    + backendServiceTotalMinutes
    + (backendDurationBreakdown?.manualBufferMinutes || 0);
  const backendSegmentFormulaParts = [
    drivingDurationLabel,
    backendServiceTotalLabel,
    backendManualBufferLabel ? `ajout ${backendManualBufferLabel}` : "",
  ].filter(Boolean);
  const backendSegmentFormulaLabel = backendSegmentFormulaParts.length > 0
    ? `${backendSegmentFormulaParts.join(" + ")} = ${formatTruckTotalHoverDuration(backendSommeBeforeTotalMinutes)}`
    : formatTruckTotalHoverDuration(backendSommeBeforeTotalMinutes);
  const drivingFormulaLabel = distanceLabel && speedLabel
    ? (
        <span className="whitespace-nowrap text-[11px]">
          {distanceLabel} / {speedLabel} = <span className="font-semibold text-content-ink">{drivingDurationLabel}</span>
        </span>
      )
    : drivingDurationLabel;
  const visibleDeltaLabel = formatTruckTotalHoverSignedDuration(backendDurationBreakdown.visibleDeltaMinutes);
  const serviceFormulaParts = [
    backendDurationBreakdown.inspectionMinutes > 0
      ? `Inspection ${formatTruckTotalHoverDuration(backendDurationBreakdown.inspectionMinutes)}`
      : "",
    backendDurationBreakdown.loadingMinutes > 0
      ? formatTruckTotalHoverServicePart("charg.", backendDurationBreakdown.loadingMinutes, backendDurationBreakdown.loadingFixedMinutes)
      : "",
    backendDurationBreakdown.unloadingMinutes > 0
      ? formatTruckTotalHoverServicePart("décharg.", backendDurationBreakdown.unloadingMinutes, backendDurationBreakdown.unloadingFixedMinutes)
      : "",
    backendDurationBreakdown.aspiratorMinutes > 0
      ? `aspir. ${formatTruckTotalHoverDuration(backendDurationBreakdown.aspiratorMinutes)}`
      : "",
    backendDurationBreakdown.waitMinutes > 0
      ? `attente ${formatTruckTotalHoverDuration(backendDurationBreakdown.waitMinutes)}`
      : "",
  ].filter(Boolean);
  const serviceFormulaLabel = serviceFormulaParts.length > 0
    ? `${serviceFormulaParts.join(" + ")} = ${backendServiceTotalLabel}`
    : "Aucun service";
  const speedExplanation = truckSpeedLabel
    ? "Temps de route = distance ÷ vitesse du camion."
    : "Vitesse du camion inconnue.";
  const visibleBackendDeltaExplanation = buildVisibleBackendDeltaExplanation({
    backendDurationBreakdown,
    frontendDurationBreakdown,
    hasTimelineSpanBeyondProductiveTotal,
  });
  const frontendRows = frontendDurationBreakdown
    ? [
        {
          label: "Route visible",
          value: frontendDrivingFormulaLabel,
          fullWidth: true,
        },
        {
          label: "Services visibles",
          value: frontendServiceFormulaLabel,
          fullWidth: true,
        },
        {
          label: "Somme des étapes",
          value: frontendSegmentFormulaLabel,
          fullWidth: true,
        },
        lunchBreakMinutes > 0
          ? { label: "Pause dîner", value: `${formatTruckTotalHoverDuration(lunchBreakMinutes)} · hors temps travaillé`, fullWidth: true }
          : null,
        hasTimelineSpanBeyondProductiveTotal
          ? {
              label: "Début → fin",
              value: (
                <span className="text-[11px]">
                  <span className="font-semibold text-content-ink">{frontendTimelineSpanLabel}</span>
                  {frontendTimelineSpanDeltaLabel ? (
                    <>
                      {" "}
                      <span className="text-content-muted">({frontendTimelineSpanDeltaLabel} de plus : un trajet s'étale sur deux blocs)</span>
                    </>
                  ) : null}
                </span>
              ),
              fullWidth: true,
            }
          : null,
      ].filter(Boolean)
    : [
        {
          label: "Amplitude",
          value: displayedTotalDurationLabel || formatTruckTotalHoverDuration(backendDurationBreakdown.displayedDurationMinutes),
        },
        {
          label: "Source",
          value: "Segments visibles du Gantt",
        },
        {
          label: "Calcul",
          value: "Lecture des blocs du Gantt.",
          fullWidth: true,
        },
      ];
  const backendRows = [
    {
      label: "Route",
      value: drivingFormulaLabel,
      fullWidth: true,
    },
    {
      label: "Services",
      value: serviceFormulaLabel,
      fullWidth: true,
    },
    {
      label: "Somme",
      value: (
        <span className="whitespace-nowrap text-[11px]">
          {backendSegmentFormulaLabel}
        </span>
      ),
      fullWidth: true,
    },
  ].filter(Boolean);
  const renderRows = (rows) => rows.map((row) => {
    const ValueTag = row.fullWidth ? "div" : "span";

    return (
      <div
        key={row.label}
        className={joinClasses([
          "grid items-start gap-1.5 text-[12px]",
          row.fullWidth ? "" : "min-[520px]:grid-cols-[112px_1fr]",
        ])}
      >
        <span className="font-semibold text-content-ink">{row.label}</span>
        <ValueTag className={joinClasses(["min-w-0 text-content-muted", row.fullWidth ? "" : "wrap-break-word"])}>{row.value}</ValueTag>
      </div>
    );
  });

  return (
    <div
      className="flex flex-col gap-2"
      style={{ width: hasFrontendDrivingDetails ? "min(54rem, calc(100vw - 2rem))" : "min(38rem, calc(100vw - 2rem))" }}
    >
      <div
        className={joinClasses([
          "grid gap-2",
          hasFrontendDrivingDetails ? "min-[860px]:grid-cols-[16rem_minmax(0,1fr)]" : "",
        ])}
      >
        {frontendDrivingDetailsPanel}

        <div className={joinClasses(["grid min-w-0 items-stretch gap-2", showCalculated ? "min-[520px]:grid-cols-2" : ""])}>
          <section className="flex h-full min-w-0 flex-col gap-2 rounded-[8px] border border-[rgba(58,82,120,0.12)] bg-[rgba(246,248,252,0.84)] p-2.5">
            <div className="flex items-center justify-between gap-2 border-b-[0.5px] border-[rgba(58,82,120,0.12)] pb-1.5">
              <span className="text-[10px] font-black uppercase tracking-[0.14em] text-content-muted">Affiché</span>
              <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-black text-content-ink shadow-[inset_0_0_0_1px_rgba(58,82,120,0.08)]">
                Gantt
              </span>
            </div>
            <div className="grid gap-1.5">{renderRows(frontendRows)}</div>
            {frontendGanttGapDetailPanel}
            <div className="mt-auto flex items-center justify-between border-t-[0.5px] border-[rgba(58,82,120,0.12)] pt-2">
              <span className="text-[11px] text-content-muted">
                {hasTimelineSpanBeyondProductiveTotal ? "Temps travaillé" : "Total affiché"}
              </span>
              <span className="text-[14px] font-bold text-content-ink">
                {displayedTotalDurationLabel || formatTruckTotalHoverDuration(frontendDurationBreakdown?.segmentTotalMinutes ?? backendDurationBreakdown.displayedDurationMinutes)}
              </span>
            </div>
          </section>

          {showCalculated ? (
          <section className="flex h-full min-w-0 flex-col gap-2 rounded-[8px] border border-[rgba(15,92,87,0.16)] bg-[rgba(236,248,245,0.72)] p-2.5">
            <div className="flex items-center justify-between gap-2 border-b-[0.5px] border-[rgba(15,92,87,0.14)] pb-1.5">
              <span className="text-[10px] font-black uppercase tracking-[0.14em] text-[rgb(15,92,87)]">Calculé</span>
              <span className="rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-black text-[rgb(15,92,87)] shadow-[inset_0_0_0_1px_rgba(15,92,87,0.12)]">
                Calcul
              </span>
            </div>
            <div className="grid gap-1.5">{renderRows(backendRows)}</div>
            <div className="mt-auto flex items-center justify-between border-t-[0.5px] border-[rgba(15,92,87,0.14)] pt-2">
              <span className="text-[11px] text-content-muted">Total calculé</span>
              <span className="text-[14px] font-bold text-content-ink">
                {formatTruckTotalHoverDuration(backendDurationBreakdown.backendTotalMinutes)}
              </span>
            </div>
          </section>
          ) : null}
        </div>
      </div>

      {showCalculated ? (
      <div className="border-t-[0.5px] border-[rgba(0,0,0,0.08)] pt-2">
        {visibleDeltaLabel ? (
          <div className="mb-1.5 flex items-center justify-between gap-3 text-[12px]">
            <span className="font-semibold text-content-ink">Écart</span>
            <span className="shrink-0 whitespace-nowrap font-bold text-content-ink">{visibleDeltaLabel}</span>
          </div>
        ) : null}
        <p className="m-0 text-[11px] leading-[1.55] text-content-muted">
          {visibleBackendDeltaExplanation || (
            <>
              À gauche, chaque trajet du Gantt. Les vides entre blocs sont dans l'encadré bleu.
              {hasTimelineSpanBeyondProductiveTotal
                ? " Début → fin dépasse le temps travaillé quand un trajet s'étale sur deux blocs."
                : " « Calculé » est le total du dispatch."}
            </>
          )}
          {" "}{speedExplanation}
        </p>
      </div>
      ) : null}
    </div>
  );
}

function resolveRowVisibleDurationMinutes(row) {
  const timelineStartMinutes = Number(row?.timelineStartMinutes);
  const timelineEndMinutes = Number(row?.timelineEndMinutes);

  if (Number.isFinite(timelineStartMinutes) && Number.isFinite(timelineEndMinutes)) {
    return Math.max(timelineEndMinutes - timelineStartMinutes, 0);
  }

  const totalDurationMinutes = Number(row?.totalDurationMinutes);
  return Number.isFinite(totalDurationMinutes) && totalDurationMinutes >= 0 ? totalDurationMinutes : null;
}

function resolveRowFrontendDisplayedTotalDurationMinutes(row, options = {}) {
  const displayedSegments = buildFleetTimelineDisplaySegments(
    buildFleetTimelineSourceSegments(row, options),
  );
  const timelineSpanMinutes = resolveDisplayedTimelineSpanMinutes(displayedSegments, row?.timelineStartMinutes);
  const frontendDurationBreakdown = resolveDisplayedTimelineFrontendDurationBreakdown(displayedSegments, {
    rowStartMinutes: row?.timelineStartMinutes,
  });

  return resolveFrontendCanonicalTotalDurationMinutes(frontendDurationBreakdown, {
    rowVisibleDurationMinutes: resolveRowVisibleDurationMinutes(row),
    timelineSpanDurationMinutes: timelineSpanMinutes,
  });
}

function resolveRowDisplayedTotalDurationLabel(row, options = {}) {
  const displayedTotalDurationMinutes = resolveRowFrontendDisplayedTotalDurationMinutes(row, options);
  const displayedDurationLabel = displayedTotalDurationMinutes != null
    ? formatDurationShort(displayedTotalDurationMinutes)
    : null;

  if (displayedDurationLabel) {
    return displayedDurationLabel;
  }

  const displayedSegments = buildFleetTimelineDisplaySegments(
    buildFleetTimelineSourceSegments(row, options),
  );
  const spanLabel = resolveDisplayedTimelineSpanLabel(displayedSegments, row?.timelineStartMinutes);

  if (spanLabel) {
    return spanLabel;
  }

  return resolveRowEstimatedTotalDurationLabel(row);
}

function compareTimelineRowsByDisplayedWorkload(leftRow, rightRow) {
  const sharedAssignmentOrder = compareAssignmentsByDisplayedWorkload(leftRow?.assignment, rightRow?.assignment);
  if (sharedAssignmentOrder !== 0) {
    return sharedAssignmentOrder;
  }

  return resolveRowEstimatedTotalDurationMinutes(rightRow) - resolveRowEstimatedTotalDurationMinutes(leftRow);
}

function getSequenceGroupSharedValue(memberSegments, fieldName) {
  const values = memberSegments
    .map((segment) => (typeof segment?.[fieldName] === "string" ? segment[fieldName].trim() : ""))
    .filter(Boolean);

  if (!values.length) {
    return "—";
  }

  return values.every((value) => value === values[0]) ? values[0] : "Multiple";
}

function resolveSequenceSegmentSiloLabel(segment) {
  const siloCandidates = [
    segment?.siloLabel,
    segment?.loading_silo,
    segment?.silo,
    segment?.businessStep?.orderDetails?.editableOrder?.loading_silo,
    segment?.businessStep?.orderDetails?.editableOrder?.silo,
  ];

  for (const candidate of siloCandidates) {
    const normalized = normalizeSegmentMetaValue(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return "";
}



function parseSequenceCompartmentTokens(label = "") {
  if (typeof label !== "string") {
    return [];
  }

  return Array.from(new Set(
    label
      .split(",")
      .map((token) => token.trim())
      .filter(Boolean),
  ));
}

function getSequenceSegmentCompartmentTokens(segment = null) {
  const chips = Array.isArray(segment?.chips) ? segment.chips : [];
  const compartmentChip = chips.find((chip) => chip?.key === "compartment" || chip?.kind === "compartment");

  if (!compartmentChip) {
    return [];
  }

  return parseSequenceCompartmentTokens(compartmentChip.label);
}

function applySequenceCompartmentOccupancyDelta(occupiedCompartments, segment = null) {
  if (!segment) {
    return occupiedCompartments;
  }

  const chips = Array.isArray(segment?.chips) ? segment.chips : [];
  const compartmentChip = chips.find((chip) => chip?.key === "compartment" || chip?.kind === "compartment");
  return applyCompartmentOccupancyChange(occupiedCompartments, {
    tokens: getSequenceSegmentCompartmentTokens(segment),
    stopType: typeof segment?.stopType === "string" ? segment.stopType.trim() : "",
    occupantOrderIdsByToken: compartmentChip?.occupantOrderIdsByToken,
  });
}

function formatSequenceCompartmentOccupancyLabel(occupiedCount = 0, totalCompartments = 0) {
  const safeTotalCompartments = Math.max(Number(totalCompartments) || 0, 0);

  if (safeTotalCompartments <= 0) {
    return "—";
  }

  const safeOccupiedCount = Math.max(
    Math.min(Number(occupiedCount) || 0, safeTotalCompartments),
    0,
  );

  return `${safeOccupiedCount}/${safeTotalCompartments}`;
}

function buildSequenceCompartmentOccupancyMap(renderItems = [], totalCompartments = 0) {
  const occupancyByItemKey = new Map();
  const occupiedCompartments = new Map();
  const applySegments = (targetOccupancy, segments = []) => {
    segments.forEach((segment) => {
      applySequenceCompartmentOccupancyDelta(targetOccupancy, segment);
    });
  };

  renderItems.forEach((item) => {
    if (item?.kind === "group") {
      const previewOccupancy = cloneCompartmentOccupancy(occupiedCompartments);
      applySegments(previewOccupancy, Array.isArray(item.memberSegments) ? item.memberSegments : []);
      occupancyByItemKey.set(
        item.key,
        formatSequenceCompartmentOccupancyLabel(previewOccupancy.size, totalCompartments),
      );

      if (!item.isExpanded) {
        occupiedCompartments.clear();
        previewOccupancy.forEach((orderIds, token) => {
          occupiedCompartments.set(token, orderIds);
        });
      }

      return;
    }

    const segment = item?.segment || null;
    applySequenceCompartmentOccupancyDelta(occupiedCompartments, segment);
    occupancyByItemKey.set(
      item?.key,
      formatSequenceCompartmentOccupancyLabel(occupiedCompartments.size, totalCompartments),
    );
  });

  return occupancyByItemKey;
}

function buildSequenceCompartmentCapacityByPosition(compartments = []) {
  return (Array.isArray(compartments) ? compartments : []).reduce((capacityByPosition, compartment) => {
    const position = Number(compartment?.position);
    const capacityTons = Number(compartment?.capacity_tons);

    if (Number.isFinite(position) && position > 0 && Number.isFinite(capacityTons) && capacityTons > 0) {
      capacityByPosition.set(position, capacityTons);
    }

    return capacityByPosition;
  }, new Map());
}

function getSequenceSegmentCompartmentAllocations(segment = null) {
  const allocations = Array.isArray(segment?.compartmentAllocations)
    ? segment.compartmentAllocations
    : (Array.isArray(segment?.compartment_allocations) ? segment.compartment_allocations : []);

  return allocations
    .map((allocation) => ({
      position: Number(allocation?.position),
      quantityTons: Number(allocation?.quantity_tons ?? allocation?.quantityTons),
    }))
    .filter((allocation) => Number.isFinite(allocation.position) && allocation.position > 0 && Number.isFinite(allocation.quantityTons) && allocation.quantityTons > 0);
}

function buildSequenceStepLoadByPosition(segments = []) {
  return (Array.isArray(segments) ? segments : []).reduce((loadByPosition, segment) => {
    const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
    if (stopType !== "loading") {
      return loadByPosition;
    }

    getSequenceSegmentCompartmentAllocations(segment).forEach((allocation) => {
      const currentQuantityTons = Number(loadByPosition.get(allocation.position)) || 0;
      loadByPosition.set(allocation.position, Number((currentQuantityTons + allocation.quantityTons).toFixed(3)));
    });

    return loadByPosition;
  }, new Map());
}

function formatSequenceCompartmentFillRateLabel(loadByPosition = new Map(), capacityByPosition = new Map()) {
  if (!(loadByPosition instanceof Map) || !(capacityByPosition instanceof Map) || !loadByPosition.size || !capacityByPosition.size) {
    return "—";
  }

  let loadedTons = 0;
  let capacityTons = 0;

  for (const [position, quantityTons] of loadByPosition.entries()) {
    if (!Number.isFinite(quantityTons) || quantityTons <= 0.000001) {
      continue;
    }

    const compartmentCapacityTons = Number(capacityByPosition.get(position));
    if (!Number.isFinite(compartmentCapacityTons) || compartmentCapacityTons <= 0) {
      return "—";
    }

    loadedTons += quantityTons;
    capacityTons += compartmentCapacityTons;
  }

  if (loadedTons <= 0.000001 || capacityTons <= 0) {
    return "—";
  }

  return `${Math.max(0, Math.min(Math.round((loadedTons / capacityTons) * 100), 999))}%`;
}

function buildSequenceCompartmentFillRateMap(renderItems = [], truckCompartments = []) {
  const fillRateByItemKey = new Map();
  const capacityByPosition = buildSequenceCompartmentCapacityByPosition(truckCompartments);

  renderItems.forEach((item) => {
    if (item?.kind === "group") {
      const previewLoadByPosition = buildSequenceStepLoadByPosition(item.memberSegments);
      fillRateByItemKey.set(
        item.key,
        formatSequenceCompartmentFillRateLabel(previewLoadByPosition, capacityByPosition),
      );

      return;
    }

    const segment = item?.segment || null;
    const loadByPosition = buildSequenceStepLoadByPosition(segment ? [segment] : []);
    fillRateByItemKey.set(
      item?.key,
      formatSequenceCompartmentFillRateLabel(loadByPosition, capacityByPosition),
    );
  });

  return fillRateByItemKey;
}

function buildTruckCompartmentsByTruckId(rawTrucks = []) {
  return (Array.isArray(rawTrucks) ? rawTrucks : []).reduce((compartmentsByTruckId, truck) => {
    const truckId = typeof truck?.truck_id === "string" ? truck.truck_id.trim() : "";
    const compartments = Array.isArray(truck?.compartments) ? truck.compartments.filter(Boolean) : [];

    if (truckId && compartments.length) {
      compartmentsByTruckId.set(truckId, compartments);
    }

    return compartmentsByTruckId;
  }, new Map());
}

function shouldDisplaySequenceCompartmentOccupancy(item = null) {
  const stopType = typeof item?.stopType === "string" ? item.stopType.trim() : "";

  return stopType !== "depot_start"
    && stopType !== "depot_return"
    && item?.isTerminalRouteMarker !== true;
}

function shouldDisplaySequenceLoadMetrics(item = null) {
  const stopType = typeof item?.stopType === "string" ? item.stopType.trim() : "";

  return stopType !== "depot_start"
    && stopType !== "depot_return"
    && item?.isTerminalRouteMarker !== true;
}

function shouldDisplaySequenceCompartmentFillRate(item = null) {
  if (item?.kind === "group") {
    const memberSegments = Array.isArray(item?.memberSegments) ? item.memberSegments.filter(Boolean) : [];
    return memberSegments.length > 0 && memberSegments.every((segment) => shouldDisplaySequenceCompartmentFillRate(segment));
  }

  const stopType = typeof item?.stopType === "string" ? item.stopType.trim() : "";
  return stopType === "loading" && item?.isTerminalRouteMarker !== true;
}

function formatSequenceGroupTonnageLabel(memberSegments) {
  const totalTons = memberSegments.reduce((total, segment) => {
    const quantityTons = Number(segment?.quantityTons);
    return Number.isFinite(quantityTons) && quantityTons > 0 ? total + quantityTons : total;
  }, 0);

  if (totalTons <= 0) {
    return "—";
  }

  return `${totalTons.toFixed(1)} t`;
}

function formatSequenceGroupChargeLabel(memberSegments, sequenceTruckLoadByKey) {
  if (!(sequenceTruckLoadByKey instanceof Map) || !memberSegments.length) {
    return "—";
  }

  const loadEntries = memberSegments
    .map((segment) => sequenceTruckLoadByKey.get(getFleetSequenceDisplayKey(segment)))
    .filter((entry) => entry && Number.isFinite(entry.valueTons));

  if (!loadEntries.length) {
    return "—";
  }

  const isLoadingGroup = memberSegments.every((segment) => {
    const stopType = typeof segment?.stopType === "string" ? segment.stopType.trim() : "";
    return stopType === "loading";
  });

  if (isLoadingGroup) {
    const peakLoadTons = Math.max(...loadEntries.map((entry) => entry.valueTons));
    return `${peakLoadTons.toFixed(1)} t`;
  }

  return loadEntries[loadEntries.length - 1].label;
}









function normalizeTimelinePriorityLabel(value = "") {
  const normalizedValue = typeof value === "string" ? value.trim().toUpperCase() : "";

  return normalizedValue === "P1" || normalizedValue === "P2" || normalizedValue === "P3"
    ? normalizedValue
    : "";
}

function getSegmentPriorityLabel(segment) {
  return normalizeTimelinePriorityLabel(segment?.dominantPriority);
}

function getGroupedSegmentPriorityLabels(groupItem) {
  const memberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];

  return Array.from(new Set(memberSegments.map((segment) => getSegmentPriorityLabel(segment)).filter(Boolean)))
    .sort((leftPriority, rightPriority) => {
      return (TIMELINE_PRIORITY_SORT_WEIGHT[rightPriority] || 0) - (TIMELINE_PRIORITY_SORT_WEIGHT[leftPriority] || 0);
    });
}

function buildAspiratorTimelineBadge(segment) {
  if (!segment?.requiresAspirator) {
    return null;
  }

  return {
    key: "aspirator",
    label: "ASP",
    title: segment?.aspiratorRequirementLabel || "Aspiration requise",
    tone: "aspiration",
  };
}

function buildAspiratorSequenceIndicator(segment) {
  if (!segment?.requiresAspirator) {
    return null;
  }

  return {
    key: "aspirator",
    label: "Aspiration",
    active: false,
    title: segment?.aspiratorRequirementLabel || "Aspiration requise",
    tone: "aspiration",
  };
}

function buildCompartmentFillTimelineBadge(segment) {
  const percent = normalizeCompartmentFillPercent(segment?.compartmentFillPercent);
  if (percent === null) {
    return null;
  }

  return {
    key: "compartment-fill",
    label: "EXP",
    tone: "expansion",
  };
}

function buildCompartmentFillSequenceIndicator(segment, compartmentFillTruck = null) {
  const percent = normalizeCompartmentFillPercent(segment?.compartmentFillPercent);
  if (percent === null) {
    return null;
  }

  return {
    key: "compartment-fill",
    label: buildCompartmentFillIndicatorLabel(percent),
    active: false,
    compartmentFill: { segments: [segment], ...compartmentFillTruck },
    tone: "expansion",
  };
}

function buildCompartmentFillGroupTimelineBadge(memberSegments = []) {
  const summary = summarizeCompartmentFillSegments(memberSegments);
  if (summary.percent === null) {
    return null;
  }

  return {
    key: "compartment-fill",
    label: "EXP",
    tone: "expansion",
  };
}

function buildCompartmentFillGroupSequenceIndicator(memberSegments = [], compartmentFillTruck = null) {
  const summary = summarizeCompartmentFillSegments(memberSegments);
  if (summary.percent === null) {
    return null;
  }

  return {
    key: "compartment-fill",
    label: buildCompartmentFillIndicatorLabel(summary.percent),
    active: false,
    compartmentFill: { segments: memberSegments, ...compartmentFillTruck },
    tone: "expansion",
  };
}

function getManualMixSequenceIndicatorLabel(segment) {
  const currentLabel = typeof segment?.manualMixCurrentProductLabel === "string"
    ? segment.manualMixCurrentProductLabel.trim()
    : "";
  const previousLabel = typeof segment?.manualMixPreviousProductLabel === "string"
    ? segment.manualMixPreviousProductLabel.trim()
    : "";

  return currentLabel || previousLabel || "Mix produit";
}

function buildManualMixSequenceIndicator(segment) {
  if (!segment?.hasManualMixBoundaryBefore) {
    return null;
  }

  const productLabel = normalizeTruckProductLabel(segment?.manualMixCurrentProductLabel || segment?.manualMixPreviousProductLabel);
  const accentColor = productLabel ? getTruckProductTone(productLabel).accentColor : "";

  return {
    key: `product-change:${getManualMixSequenceIndicatorLabel(segment)}`,
    label: getManualMixSequenceIndicatorLabel(segment),
    active: false,
    // Même couleur pleine que les onglets produit du Gantt.
    style: accentColor ? { backgroundColor: accentColor, borderColor: accentColor, boxShadow: "none", color: "#fff" } : undefined,
    title: segment?.manualMixBoundaryTitle || "Changement de produit",
    tone: "product-change",
  };
}

function isSorelTracyRouteAlternativesFeatureEnabled(hovercardContext = null) {
  if (!hovercardContext || typeof hovercardContext !== "object") {
    return true;
  }

  return hovercardContext.route_alternatives_sorel_tracy_enabled !== false;
}

function buildMontrealRiskSequenceIndicator(segment, { routeAlternativesFeatureEnabled = true } = {}) {
  if (!routeAlternativesFeatureEnabled) {
    return null;
  }

  const alert = segment?.montrealRiskAlert || null;
  if (!segment?.hasMontrealRiskAlert && !alert) {
    return null;
  }

  return {
    key: "route-alert:montreal",
    label: "Île Mtl",
    active: false,
    title: alert?.suggestedAction || MONTREAL_RISK_BADGE_TITLE,
    tone: "warning",
  };
}

function buildRouteAlternativeSequenceIndicator(segment) {
  if (!segment?.routeAlternativeApplied) {
    return null;
  }

  const routeAlternativeLabel = segment?.routeAlternativeLabel || "Traverse Sorel-Tracy";

  return {
    key: "route-alternative:applied",
    label: "Bateau",
    active: false,
    title: `${routeAlternativeLabel} appliquée`,
    tone: "ferry",
  };
}

function hasTimelineScheduledTimeAnchor(segment) {
  return Boolean(segment?.hasPriorityTimeAnchor) && Number.isFinite(Number(segment?.scheduledAtMinutes));
}

function getTimelineOutlineTone({ hasScheduledTimeAnchor = false, requiresAspirator = false } = {}) {
  if (hasScheduledTimeAnchor && requiresAspirator) {
    return "schedule-aspiration";
  }

  if (hasScheduledTimeAnchor) {
    return "schedule";
  }

  if (requiresAspirator) {
    return "aspiration";
  }

  return "";
}

function buildManualBufferTimelineBadge(minutes = 0, { titlePrefix = "Buffer manuel" } = {}) {
  const normalizedMinutes = Number(minutes);

  if (!Number.isFinite(normalizedMinutes) || normalizedMinutes <= 0) {
    return null;
  }

  const roundedMinutes = Math.round(normalizedMinutes);

  return {
    key: `manual-buffer:${roundedMinutes}`,
    label: `+${roundedMinutes}m`,
    title: `${titlePrefix}: +${roundedMinutes} min`,
    tone: "buffer",
  };
}

function buildMontrealRiskTimelineBadge(segment, { routeAlternativesFeatureEnabled = true } = {}) {
  if (!routeAlternativesFeatureEnabled) {
    return null;
  }

  if (segment?.routeAlternativeApplied) {
    const routeAlternativeLabel = segment?.routeAlternativeLabel || "Traverse Sorel-Tracy";
    return {
      icon: "sailboat",
      key: "route-alternative:applied",
      label: "",
      title: `${routeAlternativeLabel} appliquée; cliquer pour comparer avec le trajet via Montréal.`,
      tone: "ferry",
    };
  }

  if (segment?.routeAlternativeAvailable) {
    const routeAlternativeLabel = segment?.routeAlternativeLabel || "Traverse Sorel-Tracy";
    return {
      icon: "bridge",
      key: "route-alternative:available",
      label: "",
      title: `Trajet actuel via Montréal; ${routeAlternativeLabel} disponible.`,
      tone: "warning",
    };
  }

  const alert = segment?.montrealRiskAlert || null;
  if (!segment?.hasMontrealRiskAlert && !alert) {
    return null;
  }

  return {
    icon: "bridge",
    key: "route-alert:montreal",
    label: "",
    title: alert?.suggestedAction || MONTREAL_RISK_BADGE_TITLE,
    tone: "warning",
  };
}

function buildManualMixBoundaryTimelineBadge(segment) {
  if (!segment?.hasManualMixBoundaryBefore) {
    return null;
  }

  const productLabel = normalizeTruckProductLabel(segment?.manualMixCurrentProductLabel) || "Mix";
  const productTone = getTruckProductTone(productLabel);

  return {
    key: `product-change:${productLabel}`,
    className: productTone.badgeClassName,
    label: productLabel,
    style: productTone.badgeStyle,
    title: segment?.manualMixBoundaryTitle || "Changement de produit",
    tone: "product-change",
  };
}

function buildTimelineContextBadges(segment, { routeAlternativesFeatureEnabled = true } = {}) {
  const badges = [];
  const manualMixBoundaryBadge = buildManualMixBoundaryTimelineBadge(segment);
  const aspiratorBadge = buildAspiratorTimelineBadge(segment);
  const compartmentFillBadge = buildCompartmentFillTimelineBadge(segment);
  const scheduledAtMinutes = Number(segment?.scheduledAtMinutes);
  // La pause dîner a son propre bloc « Dîner » au Gantt.
  const manualBufferBadge = isLunchBreakSegment(segment) ? null : buildManualBufferTimelineBadge(segment?.manualBufferMinutes);
  const montrealRiskBadge = buildMontrealRiskTimelineBadge(segment, { routeAlternativesFeatureEnabled });

  if (manualMixBoundaryBadge) {
    badges.push(manualMixBoundaryBadge);
  }

  if (manualBufferBadge) {
    badges.push(manualBufferBadge);
  }

  if (montrealRiskBadge) {
    badges.push(montrealRiskBadge);
  }

  if (aspiratorBadge) {
    badges.push(aspiratorBadge);
  }

  if (compartmentFillBadge) {
    badges.push(compartmentFillBadge);
  }

  if (segment?.hasPriorityTimeAnchor && Number.isFinite(scheduledAtMinutes)) {
    const formattedTime = formatTimelineClockLabel(scheduledAtMinutes);
    if (formattedTime) {
      badges.push({
        key: "target-time",
        label: formattedTime,
        title: `${getSegmentStartLabel(segment)} ${formattedTime}`,
        tone: "schedule",
      });
    }
  }

  return badges;
}

function buildGroupedTimelineContextBadges(memberSegments = [], { routeAlternativesFeatureEnabled = true } = {}) {
  const resolvedSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const badges = [];
  const manualMixBoundarySegment = resolvedSegments.find((segment) => segment?.hasManualMixBoundaryBefore) || null;
  const totalManualBufferMinutes = resolvedSegments.reduce((totalMinutes, segment) => {
    const segmentManualBufferMinutes = Number(segment?.manualBufferMinutes);
    return Number.isFinite(segmentManualBufferMinutes) && segmentManualBufferMinutes > 0 && !isLunchBreakSegment(segment)
      ? totalMinutes + segmentManualBufferMinutes
      : totalMinutes;
  }, 0);
  const aspiratorSegment = resolvedSegments.find((segment) => segment?.requiresAspirator) || null;
  const routeAlternativeSegment = routeAlternativesFeatureEnabled
    ? resolvedSegments.find((segment) => segment?.routeAlternativeApplied || segment?.routeAlternativeAvailable) || null
    : null;
  const montrealRiskSegment = routeAlternativeSegment
    || (routeAlternativesFeatureEnabled
      ? resolvedSegments.find((segment) => segment?.hasMontrealRiskAlert || segment?.montrealRiskAlert)
      : null)
    || null;
  const scheduledSegment = resolvedSegments.find((segment) => segment?.hasPriorityTimeAnchor && Number.isFinite(Number(segment?.scheduledAtMinutes))) || null;

  if (manualMixBoundarySegment) {
    badges.push(buildManualMixBoundaryTimelineBadge(manualMixBoundarySegment));
  }

  if (totalManualBufferMinutes > 0) {
    badges.push(buildManualBufferTimelineBadge(totalManualBufferMinutes, { titlePrefix: "Buffers manuels" }));
  }

  if (montrealRiskSegment) {
    badges.push(buildMontrealRiskTimelineBadge(montrealRiskSegment, { routeAlternativesFeatureEnabled }));
  }

  if (aspiratorSegment) {
    badges.push(buildAspiratorTimelineBadge(aspiratorSegment));
  }

  const compartmentFillGroupBadge = buildCompartmentFillGroupTimelineBadge(resolvedSegments);
  if (compartmentFillGroupBadge) {
    badges.push(compartmentFillGroupBadge);
  }

  if (scheduledSegment?.hasPriorityTimeAnchor && Number.isFinite(Number(scheduledSegment?.scheduledAtMinutes))) {
    const formattedTime = formatTimelineClockLabel(scheduledSegment.scheduledAtMinutes);
    if (formattedTime) {
      badges.push({
        key: `time:${formattedTime}`,
        label: formattedTime,
        title: `${getSegmentStartLabel(scheduledSegment)} ${formattedTime}`,
        tone: "schedule",
      });
    }
  }

  return badges.filter(Boolean);
}

function buildSequenceTableInlineIndicators(segment, timeWindowOverride = null, { compartmentFillTruck = null, routeAlternativesFeatureEnabled = true } = {}) {
  const indicators = [];
  const priorityLabel = getSegmentPriorityLabel(segment);
  const manualMixBoundaryIndicator = buildManualMixSequenceIndicator(segment);
  const aspiratorIndicator = buildAspiratorSequenceIndicator(segment);
  const compartmentFillIndicator = buildCompartmentFillSequenceIndicator(segment, compartmentFillTruck);
  const routeAlternativeIndicator = routeAlternativesFeatureEnabled
    ? buildRouteAlternativeSequenceIndicator(segment)
    : null;
  const montrealRiskIndicator = routeAlternativeIndicator
    ? null
    : buildMontrealRiskSequenceIndicator(segment, { routeAlternativesFeatureEnabled });

  if (manualMixBoundaryIndicator) {
    indicators.push(manualMixBoundaryIndicator);
  }

  if (aspiratorIndicator) {
    indicators.push(aspiratorIndicator);
  }

  if (compartmentFillIndicator) {
    indicators.push(compartmentFillIndicator);
  }

  if (routeAlternativeIndicator) {
    indicators.push(routeAlternativeIndicator);
  }

  if (montrealRiskIndicator) {
    indicators.push(montrealRiskIndicator);
  }

  if (priorityLabel) {
    indicators.push({ key: `priority:${priorityLabel}`, label: priorityLabel, active: true, title: `Priorité ${priorityLabel}` });
  }

  if (segment?.hasPriorityTimeAnchor && Number.isFinite(Number(segment?.scheduledAtMinutes))) {
    const formattedTime = formatTimelineClockLabel(segment.scheduledAtMinutes);
    if (formattedTime) {
      indicators.push({ key: `time:${formattedTime}`, label: formattedTime, active: false, title: `${getSegmentStartLabel(segment)} ${formattedTime}`, tone: "schedule" });
    }
  }

  if (timeWindowOverride?.label) {
    indicators.push({
      key: `window:${timeWindowOverride.label}`,
      label: timeWindowOverride.label,
      active: false,
      title: timeWindowOverride.reason
        ? `Motif: ${timeWindowOverride.reason}`
        : (timeWindowOverride.description || timeWindowOverride.modeLabel || timeWindowOverride.label),
    });
  }

  const openingIndicator = buildRouteSequenceOpeningIndicator(segment?.client_loading_opening_time);
  if (openingIndicator) {
    indicators.push(openingIndicator);
  }

  return indicators;
}

function buildSequenceTableGroupInlineIndicators(memberSegments = [], { compartmentFillTruck = null, routeAlternativesFeatureEnabled = true } = {}) {
  const resolvedSegments = Array.isArray(memberSegments) ? memberSegments.filter(Boolean) : [];
  const indicators = [];
  const manualMixBoundarySegment = resolvedSegments.find((segment) => segment?.hasManualMixBoundaryBefore) || null;
  const aspiratorSegment = resolvedSegments.find((segment) => segment?.requiresAspirator) || null;
  const routeAlternativeAppliedSegment = routeAlternativesFeatureEnabled
    ? resolvedSegments.find((segment) => segment?.routeAlternativeApplied) || null
    : null;
  const montrealRiskSegment = routeAlternativesFeatureEnabled
    ? resolvedSegments.find((segment) => segment?.hasMontrealRiskAlert || segment?.montrealRiskAlert) || null
    : null;
  const priorityLabels = Array.from(new Set(resolvedSegments.map((segment) => getSegmentPriorityLabel(segment)).filter(Boolean)));
  const timeLabel = resolvedSegments
    .map((segment) => {
      if (!segment?.hasPriorityTimeAnchor || !Number.isFinite(Number(segment?.scheduledAtMinutes))) {
        return "";
      }

      return formatTimelineClockLabel(segment.scheduledAtMinutes);
    })
    .find(Boolean);

  if (manualMixBoundarySegment) {
    indicators.push(buildManualMixSequenceIndicator(manualMixBoundarySegment));
  }

  if (aspiratorSegment) {
    indicators.push(buildAspiratorSequenceIndicator(aspiratorSegment));
  }

  const compartmentFillGroupIndicator = buildCompartmentFillGroupSequenceIndicator(resolvedSegments, compartmentFillTruck);
  if (compartmentFillGroupIndicator) {
    indicators.push(compartmentFillGroupIndicator);
  }

  if (routeAlternativeAppliedSegment) {
    indicators.push(buildRouteAlternativeSequenceIndicator(routeAlternativeAppliedSegment));
  }

  if (!routeAlternativeAppliedSegment && montrealRiskSegment) {
    indicators.push(buildMontrealRiskSequenceIndicator(montrealRiskSegment, { routeAlternativesFeatureEnabled }));
  }

  priorityLabels.forEach((priorityLabel) => {
    indicators.push({ key: `priority:${priorityLabel}`, label: priorityLabel, active: true, title: `Priorité ${priorityLabel}` });
  });

  if (timeLabel) {
    indicators.push({ key: `time:${timeLabel}`, label: timeLabel, active: false, title: `Début ${timeLabel}`, tone: "schedule" });
  }

  const groupOpeningIndicator = buildRouteSequenceOpeningIndicator(resolveFleetSequenceGroupOpeningTime(resolvedSegments));
  if (groupOpeningIndicator) {
    indicators.push(groupOpeningIndicator);
  }

  return indicators;
}

function hasSequencePriorityTimeAnchor(items = []) {
  const resolvedItems = Array.isArray(items) ? items.filter(Boolean) : [items].filter(Boolean);

  return resolvedItems.some((item) => item?.hasPriorityTimeAnchor && Number.isFinite(Number(item?.scheduledAtMinutes)));
}

function isSequencePriorityScheduleIndicator(indicator = null) {
  const indicatorKey = typeof indicator?.key === "string" ? indicator.key.trim() : "";
  return indicatorKey.startsWith("priority:") || indicatorKey.startsWith("time:");
}

function isSequencePromotedStepIndicator(indicator = null, { promotePrioritySchedule = false } = {}) {
  return promotePrioritySchedule && isSequencePriorityScheduleIndicator(indicator);
}

function splitSequenceTableInlineIndicators(indicators = [], { promotePrioritySchedule = false } = {}) {
  const resolvedIndicators = Array.isArray(indicators) ? indicators.filter(Boolean) : [];
  const promotedIndicators = [];
  const inlineIndicators = [];

  resolvedIndicators.forEach((indicator) => {
    if (isSequencePromotedStepIndicator(indicator, { promotePrioritySchedule })) {
      promotedIndicators.push(indicator);
      return;
    }

    inlineIndicators.push(indicator);
  });

  return {
    inlineIndicators,
    promotedIndicators,
  };
}







































function buildSequenceSegmentDurationChips(segment, displayOverrides, fallbackIndex = 0, { assignment = null, hovercardContext = null, sequenceItems = [] } = {}) {
  if (!shouldDisplaySameSiteDurationChips(segment)) {
    return [];
  }

  if (isInformationalSequenceLoadItem(segment)) {
    return [];
  }

  const segmentChips = Array.isArray(segment?.chips) ? segment.chips.filter(Boolean) : [];
  const displayTravelDuration = resolveSameSiteSequenceDisplayedTravelDuration(
    segment,
    displayOverrides,
    fallbackIndex,
    hovercardContext,
  );
  const displayTravelDurationMinutes = displayTravelDuration.minutes;
  const shouldOverrideTravelChip = shouldSyncDisplayedTravelDurationChip(
    segment,
    displayTravelDurationMinutes,
    { showsHereTrafficDuration: displayTravelDuration.usesHereTraffic === true },
  );

  const displayDistanceLabel = getSameSiteSequenceDisplayDistanceLabel(segment, displayOverrides, fallbackIndex);
  const showsHereTrafficDuration = displayTravelDuration.usesHereTraffic === true;

  const mappedChips = segmentChips
    .filter((chip) => chip?.key === "dur" || chip?.key === "svc")
    .map((chip) => {
      if (chip?.key === "dur") {
        const displayLabel = shouldOverrideTravelChip
          ? formatFleetSequenceDurationChipLabel(displayTravelDurationMinutes, { approximate: true })
          : compactFleetSequenceDurationLabel(chip.label);
        return {
          ...chip,
          ...enrichRouteSequenceDurationChipMetadata(
            segment,
            displayDistanceLabel,
            displayTravelDurationMinutes,
            sequenceItems,
            { showsHereTrafficDuration },
          ),
          label: displayLabel,
        };
      }

      if (chip?.key === "svc") {
        return buildSequenceSegmentServiceDurationChip(chip, segment, { assignment });
      }

      return {
        ...chip,
        label: compactFleetSequenceDurationLabel(chip.label),
      };
    });

  const meunerieChip = buildFleetSequenceMeunerieChip(
    resolveFleetSequenceMeunerieExtraMinutes(segment),
    getFleetSequenceDisplayKey(segment, fallbackIndex),
  );
  const aspiratorTimeChip = buildFleetSequenceAspiratorTimeChip(
    resolveFleetSequenceAspiratorServiceMinutes(segment),
    getFleetSequenceDisplayKey(segment, fallbackIndex),
  );
  const manualBufferChip = buildFleetSequenceManualBufferChip(
    segment,
    getFleetSequenceDisplayKey(segment, fallbackIndex),
  );
  const extraChips = [meunerieChip, aspiratorTimeChip, manualBufferChip].filter(Boolean);
  if (!extraChips.length) {
    return mappedChips;
  }

  const serviceChips = mappedChips.filter((chip) => chip?.kind === "svc" || chip?.key === "svc");
  const travelChips = mappedChips.filter((chip) => chip?.kind === "dur" || chip?.key === "dur");
  const manualBufferChips = extraChips.filter((chip) => chip?.kind === "manual_buffer" || chip?.key === "manual_buffer");
  const otherExtraChips = extraChips.filter((chip) => !manualBufferChips.includes(chip));

  return [...serviceChips, ...otherExtraChips, ...manualBufferChips, ...travelChips];
}

function formatSequenceGroupDistanceLabel(memberSegments, displayOverrides) {
  const parsedDistances = memberSegments
    .map((segment, memberIndex) => {
      return parseSequenceDistanceKm(getSameSiteSequenceDisplayDistanceLabel(segment, displayOverrides, memberIndex));
    })
    .filter((distanceKm) => distanceKm != null);

  if (!parsedDistances.length) {
    return "—";
  }

  const totalDistanceKm = parsedDistances.reduce((total, distanceKm) => total + distanceKm, 0);
  return `${Math.round(totalDistanceKm)} km`;
}































































































































































































































































function getFleetGroupedRouteHoverSegmentIndex(groupItem) {
  const memberSegments = Array.isArray(groupItem?.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];
  return resolveGroupedHoverRouteSegmentIndex(memberSegments);
}

const FLEET_TIMELINE_SEGMENT_TEXT_CLASS_NAME = "text-[10px] font-bold leading-none tracking-normal [font-synthesis:none] text-[color-mix(in_srgb,currentColor_80%,var(--color-content-ink))]";
// Segment saisi : contour noir épais, tracé à l'extérieur pour rester visible au-dessus des alertes.
const FLEET_SEGMENT_PRESS_CLASS_NAME = "z-20 scale-[1.045] shadow-[0_10px_20px_rgba(15,23,42,0.24)] brightness-[1.04] saturate-[1.06] outline outline-2 outline-offset-0 outline-black";
const FLEET_SEGMENT_DRAG_SOURCE_CLASS_NAME = "z-10 opacity-75 outline outline-2 outline-offset-0 outline-black";
const FLEET_SEGMENT_BLOCKED_CLASS_NAME = "z-30 rounded-[4px]! border-border-danger-strong! outline outline-2 outline-offset-[-2px] outline-[var(--color-danger)]! cursor-not-allowed!";
const BLOCKED_PLANNING_MOVEMENT_PULSE_CLASS_NAME = "pointer-events-none absolute inset-0 z-30 rounded-[4px] border-2 border-border-danger-strong motion-safe:animate-[pulse_420ms_ease-in-out_2] motion-reduce:animate-none";

function isFleetTimelineBlockedPlanningMovementSource({
  blockedPlanningMovementFeedback = null,
  assignmentId = "",
  displayDispatchPeriod = "day",
  stepId = "",
  groupKey = "",
} = {}) {
  const normalizedDisplayDispatchPeriod = displayDispatchPeriod === "evening" ? "evening" : "day";
  if (
    blockedPlanningMovementFeedback?.sourceSurface !== "gantt"
    || blockedPlanningMovementFeedback?.assignmentId !== assignmentId
    || blockedPlanningMovementFeedback?.dispatchPeriod !== normalizedDisplayDispatchPeriod
  ) {
    return false;
  }

  if (groupKey) {
    return blockedPlanningMovementFeedback?.sourceType === "group"
      && blockedPlanningMovementFeedback.groupKey === groupKey;
  }

  return blockedPlanningMovementFeedback?.sourceType === "segment"
    && Boolean(stepId && blockedPlanningMovementFeedback.stepId === stepId);
}

function getFleetTimelineSegmentInteractionClassName(options = {}) {
  if (isFleetTimelineBlockedPlanningMovementSource(options)) {
    return FLEET_SEGMENT_BLOCKED_CLASS_NAME;
  }

  if (isFleetTimelineSegmentPressing(options)) {
    return FLEET_SEGMENT_PRESS_CLASS_NAME;
  }

  if (
    isFleetTimelineSegmentReorderDragSource(options)
    || isFleetTimelineSegmentReassignmentDragSource(options)
  ) {
    return FLEET_SEGMENT_DRAG_SOURCE_CLASS_NAME;
  }

  return "";
}

function isFleetTimelineSegmentReorderDragSource({
  segmentReorderDragState = null,
  assignmentId = "",
  displayDispatchPeriod = "day",
  stepId = "",
  groupKey = "",
} = {}) {
  const sourceDispatchPeriod = segmentReorderDragState?.sourceDispatchPeriod === "evening" ? "evening" : "day";
  const normalizedDisplayDispatchPeriod = displayDispatchPeriod === "evening" ? "evening" : "day";
  if (
    !segmentReorderDragState?.sourceAssignmentId
    || segmentReorderDragState.sourceAssignmentId !== assignmentId
    || sourceDispatchPeriod !== normalizedDisplayDispatchPeriod
  ) {
    return false;
  }

  if (groupKey) {
    return Boolean(segmentReorderDragState?.sourceGroupKey && segmentReorderDragState.sourceGroupKey === groupKey);
  }

  return Boolean(stepId && stepId === segmentReorderDragState?.sourceStepId);
}

function isFleetTimelineSegmentReassignmentDragSource({
  reassignmentDragState = null,
  assignmentId = "",
  displayDispatchPeriod = "day",
  stepId = "",
  groupKey = "",
} = {}) {
  const sourceDispatchPeriod = reassignmentDragState?.sourceDispatchPeriod === "evening" ? "evening" : "day";
  const normalizedDisplayDispatchPeriod = displayDispatchPeriod === "evening" ? "evening" : "day";
  if (
    reassignmentDragState?.dragKind !== "assignment"
    || reassignmentDragState?.sourceAssignmentId !== assignmentId
    || sourceDispatchPeriod !== normalizedDisplayDispatchPeriod
  ) {
    return false;
  }

  if (groupKey) {
    return Boolean(reassignmentDragState?.sourceGroupKey && reassignmentDragState.sourceGroupKey === groupKey);
  }

  return Boolean(stepId && stepId === reassignmentDragState?.stepId);
}

function isFleetTimelineSegmentPressing({
  fleetSegmentPressState = null,
  assignmentId = "",
  stepId = "",
  groupKey = "",
} = {}) {
  if (!fleetSegmentPressState || fleetSegmentPressState.assignmentId !== assignmentId) {
    return false;
  }

  if (groupKey) {
    return fleetSegmentPressState.groupKey === groupKey;
  }

  return Boolean(stepId && stepId === fleetSegmentPressState.stepId);
}

const EVENING_TIMELINE_ACTION_KEY_PREFIX = "evening::";
const EVENING_TIMELINE_TRUCK_COLUMN_GRID = "grid-cols-[minmax(5.5rem,6.5rem)_minmax(0,1fr)]";

function getEveningTimelineTickLabelStyle(tick) {
  const percent = typeof tick === "object" && tick != null ? Number(tick.percent) : Number(tick);
  // Le rail Soir est étroit et clipé : garder les libellés entièrement dans la piste.
  if (Number.isFinite(percent) && percent >= 100) {
    return { left: "100%", transform: "translateX(-100%)" };
  }
  if (Number.isFinite(percent) && percent <= 0) {
    return { left: 0, transform: "none" };
  }
  if (typeof tick === "object" && tick != null && (tick.labelExtendRight || tick.labelAlignment === "trailing-edge")) {
    return { left: `${percent}%`, transform: "translateX(-100%)" };
  }
  return getTimelineTickLabelStyle(tick);
}

function readOptimizationPreviewValue(candidate, ...keys) {
  if (!candidate || typeof candidate !== "object") {
    return undefined;
  }

  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(candidate, key)) {
      return candidate[key];
    }
  }

  return undefined;
}

function normalizeOptimizationPreviewText(value) {
  return typeof value === "string" ? value.trim() : "";
}

function resolveOptimizationPreviewCount(...values) {
  for (const value of values) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue >= 0) {
      return Math.round(numericValue);
    }
  }
  return null;
}

function normalizeOptimizationPreviewOrderIds(...values) {
  const orderIds = [];
  const seen = new Set();
  const collect = (value) => {
    if (Array.isArray(value)) {
      value.forEach(collect);
      return;
    }
    const orderId = normalizeOptimizationPreviewText(value);
    if (orderId && !seen.has(orderId)) {
      seen.add(orderId);
      orderIds.push(orderId);
    }
  };
  values.forEach(collect);
  return orderIds;
}

const OPTIMIZATION_PREVIEW_SOURCE_SEGMENT_SELECTOR = [
  "[data-segment-reorder-order-id]",
  "[data-segment-reorder-order-ids]",
  "[data-optimization-preview-source-order-id]",
  "[data-optimization-preview-source-order-ids]",
].join(", ");

function readOptimizationPreviewSourceOrderIds(element) {
  if (!(element instanceof HTMLElement)) {
    return [];
  }

  return normalizeOptimizationPreviewOrderIds(
    element.getAttribute("data-segment-reorder-order-id"),
    element.getAttribute("data-segment-reorder-order-ids")?.split(","),
    element.getAttribute("data-optimization-preview-source-order-id"),
    element.getAttribute("data-optimization-preview-source-order-ids")?.split(","),
  );
}

function splitOptimizationPreviewLabel(label, fallbackOrderId = "") {
  const normalizedLabel = normalizeOptimizationPreviewText(label) || normalizeOptimizationPreviewText(fallbackOrderId);
  const labelParts = normalizedLabel.split("·").map((part) => part.trim()).filter(Boolean);
  return {
    clientLabel: labelParts.length > 1 ? labelParts[0] : "",
    label: normalizedLabel,
    orderLabel: labelParts.length > 1 ? labelParts.slice(1).join(" · ") : normalizedLabel,
  };
}

function normalizeOptimizationPreviewPosition(candidate) {
  const position = readOptimizationPreviewValue(candidate, "position", "timeline_position", "timelinePosition");
  const timeline = readOptimizationPreviewValue(candidate, "timeline");
  const source = position && typeof position === "object"
    ? position
    : timeline && typeof timeline === "object"
      ? timeline
      : candidate;
  const readNumber = (...keys) => {
    const value = Number(readOptimizationPreviewValue(source, ...keys));
    return Number.isFinite(value) ? value : null;
  };
  const left = readNumber(
    "left_percent",
    "leftPercent",
    "start_percent",
    "startPercent",
    "left",
    "start",
  );
  const right = readNumber(
    "right_percent",
    "rightPercent",
    "end_percent",
    "endPercent",
    "right",
    "end",
  );
  const width = readNumber("width_percent", "widthPercent", "width");
  if (left === null || (width === null && right === null)) {
    return null;
  }
  const resolvedWidth = width === null ? right - left : width;
  if (!Number.isFinite(resolvedWidth) || resolvedWidth <= 0) {
    return null;
  }
  return {
    leftPercent: Math.max(0, Math.min(100, left)),
    widthPercent: Math.max(0, Math.min(100 - Math.max(0, left), resolvedWidth)),
  };
}

function normalizeOptimizationPreviewItems(rawItems, fallbackLabel = "", fallbackOrderIds = []) {
  const items = Array.isArray(rawItems) ? rawItems : rawItems && typeof rawItems === "object" ? [rawItems] : [];
  const normalizedItems = items.map((item, index) => {
    const orderId = normalizeOptimizationPreviewText(readOptimizationPreviewValue(item, "order_id", "orderId", "id"));
    const labels = splitOptimizationPreviewLabel(
      readOptimizationPreviewValue(item, "label", "order_label", "orderLabel"),
      orderId,
    );
    return {
      ...labels,
      orderId,
      sequence: Math.max(1, Number(readOptimizationPreviewValue(item, "sequence", "order_sequence", "orderSequence")) || index + 1),
      truckId: normalizeOptimizationPreviewText(readOptimizationPreviewValue(item, "truck_id", "truckId")),
    };
  }).filter((item) => item.orderId);
  if (normalizedItems.length) {
    return normalizedItems;
  }
  return normalizeOptimizationPreviewOrderIds(fallbackOrderIds).map((orderId, index) => {
    const labels = splitOptimizationPreviewLabel(fallbackLabel, orderId);
    return { ...labels, orderId, sequence: index + 1 };
  });
}

function normalizeOptimizationPreviewGroup(rawGroup, index, layoutKind = "groups") {
  if (!rawGroup || typeof rawGroup !== "object") {
    return null;
  }
  const nestedItems = readOptimizationPreviewValue(
    rawGroup,
    "items",
    "members",
    "assignments",
    "assignment",
    "orders",
  );
  const directOrderIds = readOptimizationPreviewValue(
    rawGroup,
    "order_ids",
    "orderIds",
    "member_order_ids",
    "memberOrderIds",
    "delivery_ids",
    "deliveryIds",
  );
  const items = normalizeOptimizationPreviewItems(
    nestedItems,
    readOptimizationPreviewValue(rawGroup, "label", "client_label", "clientLabel"),
    normalizeOptimizationPreviewOrderIds(directOrderIds, readOptimizationPreviewValue(rawGroup, "order_id", "orderId")),
  );
  const orderIds = normalizeOptimizationPreviewOrderIds(
    directOrderIds,
    items.map((item) => item.orderId),
    readOptimizationPreviewValue(rawGroup, "order_id", "orderId"),
  );
  if (!orderIds.length) {
    return null;
  }
  const firstItem = items[0] || null;
  const labels = splitOptimizationPreviewLabel(
    readOptimizationPreviewValue(rawGroup, "label", "client_label", "clientLabel") || firstItem?.label,
    firstItem?.orderId || orderIds[0],
  );
  const sequence = Math.max(1, Number(readOptimizationPreviewValue(rawGroup, "sequence", "group_sequence", "groupSequence")) || firstItem?.sequence || index + 1);
  const protectedOrderIds = normalizeOptimizationPreviewOrderIds(
    readOptimizationPreviewValue(rawGroup, "protected_order_ids", "protectedOrderIds"),
  ).filter((orderId) => orderIds.includes(orderId));
  const readFiniteNumber = (...keys) => {
    const value = Number(readOptimizationPreviewValue(rawGroup, ...keys));
    return Number.isFinite(value) ? value : null;
  };
  return {
    clientLabel: labels.clientLabel || firstItem?.clientLabel || "",
    groupId: normalizeOptimizationPreviewText(readOptimizationPreviewValue(rawGroup, "group_id", "groupId", "id", "key")) || `preview-group-${index + 1}`,
    items: items.length ? items : [{ ...labels, orderId: orderIds[0], sequence }],
    label: labels.label,
    layoutKind,
    moved: readOptimizationPreviewValue(rawGroup, "moved", "is_moved") === true,
    orderIds,
    orderLabel: labels.orderLabel || firstItem?.orderLabel || labels.label,
    position: normalizeOptimizationPreviewPosition(rawGroup),
    protected: readOptimizationPreviewValue(rawGroup, "protected", "is_protected", "isProtected") === true,
    protectedOrderIds,
    revealed: readOptimizationPreviewValue(rawGroup, "revealed", "is_revealed", "released", "is_released") === true,
    reordered: readOptimizationPreviewValue(rawGroup, "reordered", "is_reordered") === true,
    sameSiteGroupKey: normalizeOptimizationPreviewText(readOptimizationPreviewValue(
      rawGroup,
      "same_site_group_key",
      "sameSiteGroupKey",
      "same_site_key",
      "sameSiteKey",
    )),
    sequenceEnd: Math.max(
      sequence,
      Number(readOptimizationPreviewValue(rawGroup, "sequence_end", "sequenceEnd"))
        || sequence + Math.max(items.length, orderIds.length) - 1,
    ),
    sequence,
    startMinutes: readFiniteNumber("display_start_minutes", "displayStartMinutes", "start_minutes", "startMinutes"),
    endMinutes: readFiniteNumber("display_end_minutes", "displayEndMinutes", "end_minutes", "endMinutes"),
    stepType: normalizeOptimizationPreviewText(readOptimizationPreviewValue(rawGroup, "step_type", "stepType")).toLowerCase(),
    truckId: normalizeOptimizationPreviewText(
      readOptimizationPreviewValue(rawGroup, "truck_id", "truckId") || firstItem?.truckId,
    ),
  };
}

function groupOptimizationPreviewV1Assignments(assignments) {
  const groupedByTruckId = new Map();
  assignments.forEach((assignment, index) => {
    const truckId = normalizeOptimizationPreviewText(readOptimizationPreviewValue(assignment, "truck_id", "truckId"));
    const orderId = normalizeOptimizationPreviewText(readOptimizationPreviewValue(assignment, "order_id", "orderId"));
    if (!truckId || !orderId) {
      return;
    }
    const sequence = Math.max(1, Number(readOptimizationPreviewValue(assignment, "sequence")) || index + 1);
    const labels = splitOptimizationPreviewLabel(readOptimizationPreviewValue(assignment, "label"), orderId);
    const isProtected = readOptimizationPreviewValue(assignment, "protected", "is_protected", "isProtected") === true;
    const truckGroups = groupedByTruckId.get(truckId) || [];
    const previousGroup = truckGroups[truckGroups.length - 1];
    if (previousGroup && previousGroup.clientLabel && previousGroup.clientLabel === labels.clientLabel
      && sequence <= previousGroup.sequenceEnd + 1) {
      previousGroup.orderIds.push(orderId);
      previousGroup.items.push({ ...labels, orderId, sequence });
      if (isProtected) {
        previousGroup.protected = true;
        previousGroup.protectedOrderIds.push(orderId);
      }
      previousGroup.sequenceEnd = Math.max(previousGroup.sequenceEnd, sequence);
      previousGroup.orderLabel = previousGroup.items.map((item) => item.orderLabel).join(" · ");
    } else {
      truckGroups.push({
        clientLabel: labels.clientLabel,
        groupId: `preview-group-${truckId}-${sequence}`,
        items: [{ ...labels, orderId, sequence }],
        label: labels.label,
        layoutKind: "assignments",
        moved: false,
        orderIds: [orderId],
        orderLabel: labels.orderLabel,
        position: null,
        protected: isProtected,
        protectedOrderIds: isProtected ? [orderId] : [],
        reordered: false,
        sameSiteGroupKey: "",
        sequenceEnd: sequence,
        sequence,
        truckId,
      });
    }
    groupedByTruckId.set(truckId, truckGroups);
  });
  return Array.from(groupedByTruckId.values()).flat();
}

function buildOptimizationPreviewGroups(candidate) {
  if (!candidate || typeof candidate !== "object") {
    return [];
  }
  const rawLayoutKind = normalizeOptimizationPreviewText(readOptimizationPreviewValue(candidate, "layout_kind", "layoutKind")).toLowerCase();
  const layoutKind = rawLayoutKind === "timeline"
    ? "timeline"
    : rawLayoutKind === "sequence"
      ? "sequence"
      : rawLayoutKind === "groups"
        ? "groups"
        : "assignment";
  // v2 keeps `groups` authoritative for assignment and sequence layouts too.
  // Only fall back to v1 when there is no usable group list at all.
  const rawGroups = readOptimizationPreviewValue(candidate, "groups");
  const timelineGroups = rawLayoutKind === "timeline"
    ? readOptimizationPreviewValue(candidate, "timeline", "positions", "items")
    : null;
  const groupsCandidate = Array.isArray(rawGroups) ? rawGroups : timelineGroups;
  if (Array.isArray(groupsCandidate)) {
    const normalizedGroups = groupsCandidate
      .map((group, index) => normalizeOptimizationPreviewGroup(group, index, layoutKind))
      .filter(Boolean)
      .sort((left, right) => left.truckId.localeCompare(right.truckId, "fr", { numeric: true }) || left.sequence - right.sequence);
    if (normalizedGroups.length) {
      return normalizedGroups;
    }
  }
  return groupOptimizationPreviewV1Assignments(
    Array.isArray(candidate.assignments) ? candidate.assignments.slice().sort((left, right) => Number(left?.sequence || 0) - Number(right?.sequence || 0)) : [],
  );
}

function collectOptimizationPreviewOrderIds(candidate) {
  return Array.from(new Set(buildOptimizationPreviewGroups(candidate).flatMap((group) => group.orderIds)));
}

function collectOptimizationPreviewProtectedOrderIds(candidate) {
  return normalizeOptimizationPreviewOrderIds(
    buildOptimizationPreviewGroups(candidate).flatMap((group) => (
      group.protectedOrderIds?.length
        ? group.protectedOrderIds
        : group.protected ? group.orderIds : []
    )),
  );
}

function collectOptimizationPreviewRevealedOrderIds(candidate) {
  if (!candidate || typeof candidate !== "object") {
    return [];
  }

  const stageKey = normalizeOptimizationPreviewText(
    readOptimizationPreviewValue(candidate, "stage_key", "stageKey"),
  ).toLowerCase();
  if (["preparation", "data_loading", "validation"].includes(stageKey)) {
    return [];
  }

  const groups = buildOptimizationPreviewGroups(candidate);
  if (!groups.length) {
    return [];
  }

  const explicitGroups = groups.filter((group) => (
    readOptimizationPreviewValue(group, "revealed", "is_revealed", "released", "is_released") === true
  ));
  if (explicitGroups.length) {
    return Array.from(new Set(explicitGroups.flatMap((group) => group.orderIds)));
  }

  const assignedOrderCount = Number(readOptimizationPreviewValue(
    candidate,
    "assigned_order_count",
    "assignedOrderCount",
  ));
  if (!Number.isFinite(assignedOrderCount) || assignedOrderCount <= 0) {
    return [];
  }

  // A same-site group is revealed as one visual unit.  This keeps the reserve
  // count stable while a group is being assembled and avoids claiming every
  // assignment merely because the payload also carries the full v1 fallback.
  return collectBoundedOptimizationPreviewOrderIds(
    groups
      .slice()
      .sort((left, right) => left.sequence - right.sequence || left.groupId.localeCompare(right.groupId)),
    assignedOrderCount,
  );
}

export function buildOptimizationPreviewByTruckId(candidate, {
  dispatchDate = "",
  dispatchPeriod = "day",
  jobDispatchDate = "",
  jobDispatchPeriod = "",
  retainedOrderIds = [],
} = {}) {
  const normalizedDate = typeof dispatchDate === "string" ? dispatchDate.trim() : "";
  const normalizedJobDate = typeof jobDispatchDate === "string" ? jobDispatchDate.trim() : "";
  const normalizedPeriod = dispatchPeriod === "evening" ? "evening" : "day";
  const normalizedJobPeriod = jobDispatchPeriod === "evening" ? "evening" : jobDispatchPeriod === "day" ? "day" : "";
  const previewByTruckId = new Map();
  const activityPhase = resolveOptimizationPreviewVisualPhase(candidate);

  if (!normalizedDate || !normalizedJobDate || normalizedDate !== normalizedJobDate
    || !normalizedJobPeriod || normalizedJobPeriod !== normalizedPeriod
    || !candidate || typeof candidate !== "object") {
    return previewByTruckId;
  }

  filterOptimizationPreviewRetainedGroups(buildOptimizationPreviewGroups(candidate), retainedOrderIds).forEach((group) => {
    const truckId = group.truckId;
    if (!truckId) {
      return;
    }

    const existing = previewByTruckId.get(truckId) || {
      groups: [],
      items: [],
      labels: [],
      layoutKind: group.layoutKind,
      activityPhase,
      revision: Number(candidate.revision || 0),
      truckId,
    };
    const labels = group.clientLabel
      ? [
        { kind: "client", label: group.clientLabel },
        { kind: "order", label: group.orderLabel },
      ]
      : [{ kind: "order", label: group.orderLabel }];
    labels.forEach((previewLabel) => {
      if (!existing.labels.some((entry) => entry.kind === previewLabel.kind && entry.label === previewLabel.label)) {
        existing.labels.push(previewLabel);
      }
    });
    existing.groups.push(group);
    existing.items.push(...group.items);
    existing.items.sort((left, right) => left.sequence - right.sequence);
    existing.labels = existing.labels.slice(0, 6);
    previewByTruckId.set(truckId, existing);
  });

  return previewByTruckId;
}

function readOptimizationPreviewAssignments(candidate) {
  return buildOptimizationPreviewGroups(candidate).flatMap((group) => group.items.map((item) => ({
    groupId: group.groupId,
    label: item.label,
    orderId: item.orderId,
    truckId: group.truckId,
  })));
}

function readOptimizationPreviewGroups(candidate) {
  return buildOptimizationPreviewGroups(candidate).map((group) => ({
    ...group,
    orderIds: normalizeOptimizationPreviewOrderIds(group.orderIds),
  }));
}

function findOptimizationOrderSegment(root, orderIds) {
  const normalizedOrderIds = normalizeOptimizationPreviewOrderIds(orderIds);
  if (!(root instanceof HTMLElement) || !normalizedOrderIds.length) {
    return null;
  }
  const candidates = Array.from(root.querySelectorAll("[data-segment-reorder-order-id], [data-segment-reorder-order-ids]"))
    .filter((element) => {
      const elementOrderIds = normalizeOptimizationPreviewOrderIds(
        element.getAttribute("data-segment-reorder-order-id"),
        element.getAttribute("data-segment-reorder-order-ids")?.split(","),
      );
      return normalizedOrderIds.some((orderId) => elementOrderIds.includes(orderId));
    })
    .map((element) => ({ element, rect: element.getBoundingClientRect() }))
    .filter(({ rect }) => rect.width > 0 && rect.height > 0)
    .sort((left, right) => right.rect.width - left.rect.width);
  return candidates[0] || null;
}

function findOptimizationPreviewTarget(root, group) {
  if (!(root instanceof HTMLElement) || !group) {
    return null;
  }
  const orderIdSet = new Set(group.orderIds || []);
  return Array.from(root.querySelectorAll("[data-optimization-preview-group-id], [data-optimization-preview-order-ids]"))
    .find((element) => element.getAttribute("data-optimization-preview-group-id") === group.groupId
      || normalizeOptimizationPreviewOrderIds(element.getAttribute("data-optimization-preview-order-ids")?.split(","))
        .some((orderId) => orderIdSet.has(orderId))) || null;
}

function isOptimizationMotionRectVisible(rect) {
  return rect && rect.right > 0 && rect.bottom > 0
    && rect.left < window.innerWidth && rect.top < window.innerHeight;
}

function clampOptimizationMotionRectToViewport(rect) {
  const width = Math.max(30, Math.min(Number(rect?.width || 90), window.innerWidth * 0.35));
  const height = Math.max(22, Math.min(Number(rect?.height || 26), 44));
  return {
    height,
    left: Math.max(-width * 0.35, Math.min(Number(rect?.left || 0), window.innerWidth - width * 0.65)),
    top: Math.max(-height * 0.35, Math.min(Number(rect?.top || 0), window.innerHeight - height * 0.65)),
    width,
  };
}

function restoreOptimizationPreviewSourceDimming(root = document) {
  root?.querySelectorAll?.('[data-optimization-preview-source-dim="true"]').forEach((element) => {
    element.style.opacity = element.getAttribute("data-optimization-preview-source-opacity") || "";
    element.style.filter = element.getAttribute("data-optimization-preview-source-filter") || "";
    element.removeAttribute("data-optimization-preview-source-dim");
    element.removeAttribute("data-optimization-preview-source-opacity");
    element.removeAttribute("data-optimization-preview-source-filter");
  });
}

function dimOptimizationPreviewSourceSegments(root, protectedOrderIds = []) {
  if (!(root instanceof HTMLElement)) {
    return;
  }
  const protectedIds = new Set(normalizeOptimizationPreviewOrderIds(protectedOrderIds));
  const dimElement = (element) => {
    if (element.getAttribute("data-optimization-preview-source-dim") === "true") {
      return;
    }
    element.setAttribute("data-optimization-preview-source-dim", "true");
    element.setAttribute("data-optimization-preview-source-opacity", element.style.opacity || "");
    element.setAttribute("data-optimization-preview-source-filter", element.style.filter || "");
    element.style.opacity = "0";
    element.style.filter = "saturate(0.65)";
  };
  root.querySelectorAll(OPTIMIZATION_PREVIEW_SOURCE_SEGMENT_SELECTOR).forEach((element) => {
    const orderIds = readOptimizationPreviewSourceOrderIds(element);
    // A protected member keeps the whole DOM group visible; it is never
    // included in a preview flight that could make it appear to move.
    if (!orderIds.length || orderIds.some((orderId) => protectedIds.has(orderId))) {
      return;
    }
    dimElement(element);
    // L'onglet produit / PM au-dessus du segment disparaît avec lui.
    const itemKey = element.getAttribute("data-fleet-timeline-item-key");
    element.closest('[data-fleet-timeline-track="true"]')?.querySelectorAll("[data-fleet-segment-tabs-item-key]").forEach((tab) => {
      if (itemKey && tab.getAttribute("data-fleet-segment-tabs-item-key") === itemKey) {
        dimElement(tab);
      }
    });
  });
}

function animateOptimizationPreviewRetraction({
  durationMs = OPTIMIZATION_PREVIEW_MOTION_DURATION_MS,
  index,
  sourceElement,
  sourceRect,
  stagingRect,
}) {
  if (!(sourceElement instanceof HTMLElement) || !sourceElement.animate || !document.body
    || !isOptimizationMotionRectVisible(sourceRect) || !isOptimizationMotionRectVisible(stagingRect)) {
    return Promise.resolve();
  }
  const from = clampOptimizationMotionRectToViewport(sourceRect);
  const to = clampOptimizationMotionRectToViewport(stagingRect);
  const clone = sourceElement.cloneNode(true);
  clone.removeAttribute?.("id");
  clone.setAttribute("aria-hidden", "true");
  clone.setAttribute("data-optimization-preview-flying-segment", `reserve-${index}`);
  Object.assign(clone.style, {
    borderColor: "var(--color-accent)",
    boxShadow: "0 14px 30px rgba(8, 90, 85, 0.28), 0 0 0 3px rgba(20, 184, 166, 0.15)",
    height: `${from.height}px`,
    left: `${from.left}px`,
    margin: "0",
    maxWidth: "none",
    minWidth: "0",
    opacity: "0.98",
    pointerEvents: "none",
    position: "fixed",
    top: `${from.top}px`,
    transformOrigin: "center",
    width: `${from.width}px`,
    zIndex: "2147483000",
  });
  document.body.appendChild(clone);
  const travel = Math.hypot(to.left - from.left, to.top - from.top);
  const arcHeight = Math.min(68, Math.max(22, travel * 0.12));
  const animation = clone.animate([
    {
      height: `${from.height}px`, left: `${from.left}px`, opacity: 0.96,
      top: `${from.top}px`, transform: "scale(1)", width: `${from.width}px`,
    },
    {
      height: `${(from.height + to.height) / 2}px`, left: `${(from.left + to.left) / 2}px`, opacity: 1,
      top: `${(from.top + to.top) / 2 - arcHeight}px`, transform: "scale(1.08)", width: `${(from.width + to.width) / 2}px`,
    },
    {
      height: `${to.height}px`, left: `${to.left}px`, opacity: 0,
      top: `${to.top}px`, transform: "scale(0.82)", width: `${to.width}px`,
    },
  ], {
    delay: Math.min(index, 10) * 35,
    duration: durationMs,
    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
    fill: "forwards",
  });
  return animation.finished.catch(() => {}).finally(() => clone.remove());
}

function animateOptimizationPreviewOrder({
  durationMs = OPTIMIZATION_PREVIEW_MOTION_DURATION_MS,
  index,
  label,
  motionKind = "move",
  orderId,
  sourceElement,
  sourceRect,
  sourceTruckId = "",
  stagingRect = null,
  targetElement,
  targetRect,
  targetTruckId = "",
}) {
  if (!targetElement?.animate || !document.body) {
    return Promise.resolve();
  }
  const isSourceVisible = isOptimizationMotionRectVisible(sourceRect);
  const isTargetVisible = isOptimizationMotionRectVisible(targetRect);
  if (!isSourceVisible || !isTargetVisible) {
    const localElement = isTargetVisible
      ? targetElement
      : sourceElement instanceof HTMLElement && isSourceVisible
        ? sourceElement
        : null;
    const localAnimation = localElement?.animate?.([
      { opacity: 0.35 },
      { opacity: 1 },
    ], { duration: OPTIMIZATION_PREVIEW_MOTION_CATCH_UP_DURATION_MS, easing: "ease-out" });
    return localAnimation?.finished?.catch?.(() => {}) || Promise.resolve();
  }
  const from = clampOptimizationMotionRectToViewport(sourceRect);
  const to = clampOptimizationMotionRectToViewport(targetRect);
  const staging = stagingRect ? clampOptimizationMotionRectToViewport(stagingRect) : null;
  const clone = sourceElement instanceof HTMLElement
    ? sourceElement.cloneNode(true)
    : document.createElement("div");
  if (!(sourceElement instanceof HTMLElement)) {
    clone.textContent = label || orderId;
    clone.className = "flex items-center rounded-[5px] border border-[var(--color-accent)] bg-white px-2 text-[0.6rem] font-black text-[var(--color-accent)]";
  }
  clone.removeAttribute?.("id");
  clone.setAttribute("aria-hidden", "true");
  clone.setAttribute("data-optimization-preview-flying-segment", orderId);
  clone.setAttribute("data-optimization-preview-motion-kind", motionKind);
  if (sourceTruckId) {
    clone.setAttribute("data-optimization-preview-source-truck-id", sourceTruckId);
  }
  if (targetTruckId) {
    clone.setAttribute("data-optimization-preview-target-truck-id", targetTruckId);
  }
  const isTruckChange = motionKind === "truck-change";
  Object.assign(clone.style, {
    borderColor: "var(--color-accent)",
    boxShadow: isTruckChange
      ? "0 18px 38px rgba(8, 90, 85, 0.38), 0 0 0 4px rgba(45, 212, 191, 0.26)"
      : "0 14px 30px rgba(8, 90, 85, 0.28), 0 0 0 3px rgba(20, 184, 166, 0.15)",
    height: `${from.height}px`,
    left: `${from.left}px`,
    margin: "0",
    maxWidth: "none",
    minWidth: "0",
    opacity: "0.98",
    pointerEvents: "none",
    position: "fixed",
    top: `${from.top}px`,
    transformOrigin: "center",
    width: `${from.width}px`,
    zIndex: "2147483000",
  });
  document.body.appendChild(clone);

  const motionToken = `${orderId}:${Date.now()}:${index}`;
  targetElement.setAttribute("data-optimization-motion-token", motionToken);
  targetElement.style.opacity = "0";
  const runTravel = (start, destination, delay = 0) => {
    const travel = Math.hypot(destination.left - start.left, destination.top - start.top);
    const arcHeight = Math.min(68, Math.max(22, travel * 0.12));
    return clone.animate([
      {
        height: `${start.height}px`, left: `${start.left}px`, offset: 0, opacity: 0.96,
        top: `${start.top}px`, transform: "scale(1) rotate(0deg)", width: `${start.width}px`,
      },
      {
        height: `${(start.height + destination.height) / 2}px`, left: `${(start.left + destination.left) / 2}px`, offset: 0.55, opacity: 1,
        top: `${(start.top + destination.top) / 2 - arcHeight}px`, transform: "scale(1.08) rotate(-1deg)", width: `${(start.width + destination.width) / 2}px`,
      },
      {
        height: `${destination.height}px`, left: `${destination.left}px`, offset: 1, opacity: 1,
        top: `${destination.top}px`, transform: "scale(1) rotate(0deg)", width: `${destination.width}px`,
      },
    ], {
      delay,
      duration: durationMs,
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
      fill: "forwards",
    });
  };
  const firstAnimation = runTravel(from, staging || to, Math.min(index, 10) * 35);
  const finish = () => {
    clone.remove();
    if (targetElement.getAttribute("data-optimization-motion-token") === motionToken) {
      targetElement.style.opacity = "";
      targetElement.removeAttribute("data-optimization-motion-token");
      targetElement.animate([
        { filter: "brightness(1.35)", transform: "scale(1.08)" },
        { filter: "brightness(1)", transform: "scale(1)" },
      ], { duration: 320, easing: "ease-out" });
    }
  };
  if (staging && isOptimizationMotionRectVisible(targetRect)) {
    return firstAnimation.finished.catch(() => {}).then(() => {
      Object.assign(clone.style, {
        height: `${staging.height}px`,
        left: `${staging.left}px`,
        top: `${staging.top}px`,
        width: `${staging.width}px`,
      });
      return runTravel(staging, to, 35).finished.catch(() => {});
    }).finally(finish);
  }
  return firstAnimation.finished.catch(() => {}).finally(finish);
}

function animateOptimizationPreviewDeparture({
  durationMs = OPTIMIZATION_PREVIEW_MOTION_DURATION_MS,
  index,
  label,
  sourceElement,
  sourceRect,
  stagingRect,
}) {
  if (!document.body || !isOptimizationMotionRectVisible(sourceRect) || !isOptimizationMotionRectVisible(stagingRect)) {
    const localElement = sourceElement instanceof HTMLElement && isOptimizationMotionRectVisible(sourceRect)
      ? sourceElement
      : null;
    const localAnimation = localElement?.animate?.([
      { opacity: 0.75 },
      { opacity: 0 },
    ], { duration: OPTIMIZATION_PREVIEW_MOTION_CATCH_UP_DURATION_MS, easing: "ease-out", fill: "forwards" });
    if (!localAnimation || !(localElement instanceof HTMLElement)) {
      return Promise.resolve();
    }
    const localMotionToken = `departure-local:${Date.now()}:${index}`;
    localElement.setAttribute("data-optimization-motion-token", localMotionToken);
    return localAnimation.finished.catch(() => {}).finally(() => {
      localAnimation.cancel();
      if (localElement.getAttribute("data-optimization-motion-token") === localMotionToken) {
        localElement.removeAttribute("data-optimization-motion-token");
      }
    });
  }
  const from = clampOptimizationMotionRectToViewport(sourceRect);
  const to = clampOptimizationMotionRectToViewport(stagingRect);
  const clone = sourceElement instanceof HTMLElement
    ? sourceElement.cloneNode(true)
    : document.createElement("div");
  if (!(sourceElement instanceof HTMLElement)) {
    clone.textContent = label || "Commande";
    clone.className = "flex items-center rounded-[5px] border border-[var(--color-accent)] bg-white px-2 text-[0.6rem] font-black text-[var(--color-accent)]";
  }
  clone.removeAttribute?.("id");
  clone.setAttribute("aria-hidden", "true");
  clone.setAttribute("data-optimization-preview-flying-segment", `departure-${index}`);
  Object.assign(clone.style, {
    borderColor: "var(--color-accent)",
    boxShadow: "0 14px 30px rgba(8, 90, 85, 0.28), 0 0 0 3px rgba(20, 184, 166, 0.15)",
    height: `${from.height}px`,
    left: `${from.left}px`,
    margin: "0",
    maxWidth: "none",
    minWidth: "0",
    opacity: "0.98",
    pointerEvents: "none",
    position: "fixed",
    top: `${from.top}px`,
    transformOrigin: "center",
    width: `${from.width}px`,
    zIndex: "2147483000",
  });
  document.body.appendChild(clone);
  const travel = Math.hypot(to.left - from.left, to.top - from.top);
  const arcHeight = Math.min(68, Math.max(22, travel * 0.12));
  const animation = clone.animate([
    {
      height: `${from.height}px`, left: `${from.left}px`, opacity: 0.96,
      top: `${from.top}px`, transform: "scale(1)", width: `${from.width}px`,
    },
    {
      height: `${(from.height + to.height) / 2}px`, left: `${(from.left + to.left) / 2}px`, opacity: 1,
      top: `${(from.top + to.top) / 2 - arcHeight}px`, transform: "scale(1.08)", width: `${(from.width + to.width) / 2}px`,
    },
    {
      height: `${to.height}px`, left: `${to.left}px`, opacity: 0,
      top: `${to.top}px`, transform: "scale(0.82)", width: `${to.width}px`,
    },
  ], {
    delay: Math.min(index, 3) * 35,
    duration: durationMs,
    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
    fill: "forwards",
  });
  return animation.finished.catch(() => {}).finally(() => clone.remove());
}

function optimizationPreviewStageAllowsMotion(assignmentPreview) {
  const stageKey = normalizeOptimizationPreviewText(
    readOptimizationPreviewValue(assignmentPreview, "stage_key", "stageKey"),
  ).toLowerCase();
  return ["assignment", "assignments", "dispatch_improvement", "routes", "final_verification"].includes(stageKey);
}

function useOptimizationPreviewMotion({
  active,
  assignmentPreview,
  jobId,
  operationKind = "optimization",
  prioritizeTerminal = false,
  protectedOrderIds = [],
  retainedOrderIds = [],
  queueDepth = 0,
}) {
  const previousPositionsRef = React.useRef(new Map());
  const previousTemplatesRef = React.useRef(new Map());
  const previousGroupsRef = React.useRef([]);
  const previousJobIdRef = React.useRef("");
  const retractedJobIdRef = React.useRef("");
  const motionQueueRef = React.useRef([]);
  const motionRunnerActiveRef = React.useRef(false);
  const motionGenerationRef = React.useRef(0);
  const motionExecutorRef = React.useRef(null);
  const prioritizedTerminalRevisionRef = React.useRef("");
  const revision = Number(assignmentPreview?.revision || 0);

  React.useLayoutEffect(() => {
    const resetMotion = () => {
      motionGenerationRef.current += 1;
      motionQueueRef.current = [];
      motionRunnerActiveRef.current = false;
      motionExecutorRef.current = null;
      const root = document.querySelector('[data-fleet-timeline-panel="true"]');
      document.querySelectorAll?.("[data-optimization-preview-flying-segment]").forEach((element) => element.remove());
      document.querySelectorAll?.("[data-optimization-motion-token]").forEach((element) => {
        element.getAnimations?.().forEach((animation) => animation.cancel());
        element.style.opacity = "";
        element.removeAttribute("data-optimization-motion-token");
      });
      restoreOptimizationPreviewSourceDimming(root || document);
    };
    if (!active) {
      resetMotion();
      previousPositionsRef.current = new Map();
      previousTemplatesRef.current = new Map();
      previousGroupsRef.current = [];
      previousJobIdRef.current = "";
      prioritizedTerminalRevisionRef.current = "";
      retractedJobIdRef.current = "";
      return undefined;
    }
    if (previousJobIdRef.current !== jobId) {
      resetMotion();
      previousPositionsRef.current = new Map();
      previousTemplatesRef.current = new Map();
      previousGroupsRef.current = [];
      prioritizedTerminalRevisionRef.current = "";
      retractedJobIdRef.current = "";
      previousJobIdRef.current = jobId;
    }
    const terminalRevisionKey = prioritizeTerminal && revision ? `${jobId}:${revision}` : "";
    if (terminalRevisionKey && prioritizedTerminalRevisionRef.current !== terminalRevisionKey) {
      // The canonical boundary is another ordered event. Keep every revision
      // already waiting in front of it; terminal priority only controls the
      // handoff after that backlog has drained.
      prioritizedTerminalRevisionRef.current = terminalRevisionKey;
    }

    const frameId = window.requestAnimationFrame(() => {
      const root = document.querySelector('[data-fleet-timeline-panel="true"]');
      if (!(root instanceof HTMLElement)) {
        return;
      }
      const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
      const previewGroups = readOptimizationPreviewGroups(assignmentPreview);
      const previewProtectedOrderIds = collectOptimizationPreviewProtectedOrderIds(assignmentPreview);
      const effectiveProtectedOrderIds = normalizeOptimizationPreviewOrderIds(
        retainedOrderIds,
        operationKind === "planning_reset" ? [] : protectedOrderIds,
        operationKind === "planning_reset" ? [] : previewProtectedOrderIds,
      );
      const protectedIds = new Set(effectiveProtectedOrderIds);
      const stagingElement = document.querySelector('[data-optimization-preview-staging="true"]');
      const stagingRect = stagingElement?.getBoundingClientRect?.() || null;
      const visibleStagingRect = isOptimizationMotionRectVisible(stagingRect) ? stagingRect : null;
      const generation = motionGenerationRef.current;
      const drainMotionQueue = () => {
        if (generation !== motionGenerationRef.current || motionRunnerActiveRef.current) {
          return;
        }
        const motion = motionQueueRef.current.shift();
        if (!motion || !Array.isArray(motion.motions) || typeof motionExecutorRef.current !== "function") {
          return;
        }
        motionRunnerActiveRef.current = true;
        const queueDepth = motionQueueRef.current.length;
        Promise.all(motion.motions.map((item) => motionExecutorRef.current(item, {
          queueDepth,
          revision: motion.revision,
        }))).catch(() => {}).finally(() => {
          if (generation !== motionGenerationRef.current) {
            return;
          }
          motionRunnerActiveRef.current = false;
          drainMotionQueue();
        });
      };
      const enqueueMotionQueue = (motions, executor) => {
        motionExecutorRef.current = executor;
        motionQueueRef.current = condenseOptimizationPreviewMotionQueue(
          motionQueueRef.current,
          motions,
          OPTIMIZATION_PREVIEW_MOTION_QUEUE_LIMIT,
        );
        drainMotionQueue();
      };
      const isFirstJobFrame = retractedJobIdRef.current !== jobId;
      if (isFirstJobFrame) {
        retractedJobIdRef.current = jobId;
        const initialRetractionMotions = [];
        const seenElements = new Set();
        Array.from(root.querySelectorAll(OPTIMIZATION_PREVIEW_SOURCE_SEGMENT_SELECTOR))
          .forEach((element) => {
            if (!(element instanceof HTMLElement) || seenElements.has(element)) {
              return;
            }
            const orderIds = readOptimizationPreviewSourceOrderIds(element);
            if (!orderIds.length || orderIds.some((orderId) => protectedIds.has(orderId))) {
              return;
            }
            seenElements.add(element);
            initialRetractionMotions.push({
              animate: !reducedMotion && Boolean(visibleStagingRect),
              group: { groupId: `initial:${orderIds.join(",")}`, orderIds },
              groupId: `initial:${orderIds.join(",")}`,
              identity: `orders:${orderIds.slice().sort().join(",")}`,
              kind: "initial-retraction",
              motion: "initial-retraction",
              orderIds,
              sourceElement: element,
              sourceRect: element.getBoundingClientRect(),
              to: "reserve",
            });
          });
        if (initialRetractionMotions.length && visibleStagingRect && !reducedMotion) {
          // The initial handoff is one visual transition. It is deliberately
          // not condensed with revision batches: every committed segment gets
          // a chance to reach the reserve.
          initialRetractionMotions.forEach((motion, index) => {
            void animateOptimizationPreviewRetraction({
              durationMs: resolveOptimizationPreviewMotionDuration({
                queueDepth,
              }),
              index,
              sourceElement: motion.sourceElement,
              sourceRect: motion.sourceRect,
              stagingRect: visibleStagingRect,
            });
          });
        } else if (initialRetractionMotions.length) {
          // If the reserve is outside the viewport (or motion is reduced),
          // preserve the one-time handoff with a short local fade instead of
          // making the committed segments disappear abruptly.
          initialRetractionMotions.forEach((motion, index) => {
            motion.sourceElement?.animate?.([
              { opacity: 1 },
              { opacity: 0 },
            ], {
              delay: Math.min(index, 3) * 20,
              duration: 160,
              easing: "ease-out",
            });
          });
        }
        dimOptimizationPreviewSourceSegments(root, effectiveProtectedOrderIds);
        if (!revision) {
          return;
        }
      }
      if (!revision) {
        dimOptimizationPreviewSourceSegments(root, effectiveProtectedOrderIds);
        return;
      }
      if (!optimizationPreviewStageAllowsMotion(assignmentPreview)) {
        previousPositionsRef.current = new Map();
        previousTemplatesRef.current = new Map();
        previousGroupsRef.current = [];
        return;
      }
      dimOptimizationPreviewSourceSegments(root, effectiveProtectedOrderIds);
      const previousPositions = previousPositionsRef.current;
      const previousTemplates = previousTemplatesRef.current;
      const previousGroups = previousGroupsRef.current;
      const nextPositions = new Map();
      const nextTemplates = new Map();
      const groups = previewGroups;
      const motionPlan = buildOptimizationPreviewMotionPlan({
        nextGroups: groups,
        operationKind,
        previousGroups,
        protectedOrderIds: effectiveProtectedOrderIds,
        retainedOrderIds,
        maxQueue: OPTIMIZATION_PREVIEW_MOTION_QUEUE_LIMIT,
      });
      groups.forEach((group, index) => {
        const targetElement = findOptimizationPreviewTarget(root, group);
        const targetRect = targetElement?.getBoundingClientRect?.() || null;
        if (!targetRect) {
          return;
        }
        nextPositions.set(group.groupId, targetRect);
        group.orderIds.forEach((orderId) => nextPositions.set(orderId, targetRect));
        if (targetElement instanceof HTMLElement) {
          const template = targetElement.cloneNode(true);
          nextTemplates.set(group.groupId, template);
          group.orderIds.forEach((orderId) => nextTemplates.set(orderId, template));
        }
      });
      previousGroupsRef.current = groups;
      previousPositionsRef.current = nextPositions;
      previousTemplatesRef.current = nextTemplates;
      if (!motionPlan.motions.length) {
        return;
      }
      if (reducedMotion) {
        motionPlan.motions.forEach((motion, index) => {
          const localElement = motion.group
            ? findOptimizationPreviewTarget(root, motion.group)
            : stagingElement;
          localElement?.animate?.([
            { opacity: 0.55 },
            { opacity: 1 },
          ], {
            delay: Math.min(index, 3) * 20,
            duration: 160,
            easing: "ease-out",
          });
        });
        return;
      }

      const pendingMotions = motionPlan.motions.map((motion, index) => {
        const group = motion.group;
        const previousGroup = motion.previousGroup;
        const orderIds = group?.orderIds?.length ? group.orderIds : previousGroup?.orderIds || [];
        const targetElement = group ? findOptimizationPreviewTarget(root, group) : null;
        const targetRect = targetElement?.getBoundingClientRect?.() || visibleStagingRect;
        const sourceTemplate = previousGroup
          ? previousTemplates.get(previousGroup.groupId)
            || previousGroup.orderIds.map((orderId) => previousTemplates.get(orderId)).find(Boolean)
          : null;
        const source = sourceTemplate instanceof HTMLElement
          ? { element: sourceTemplate }
          : findOptimizationOrderSegment(root, orderIds);
        const previousRect = previousGroup
          ? previousPositions.get(previousGroup.groupId)
            || previousGroup.orderIds.map((orderId) => previousPositions.get(orderId)).find(Boolean)
          : null;
        const sourceRect = motion.kind === "reveal"
          ? visibleStagingRect || source?.rect || previousRect
          : previousRect || source?.rect || visibleStagingRect;
        return {
          ...motion,
          index,
          label: group?.label || previousGroup?.label || orderIds[0] || "Commande",
          sourceTruckId: previousGroup?.truckId || "",
          sourceElement: source?.element,
          sourceRect,
          targetElement,
          targetRect,
          targetTruckId: group?.truckId || "",
        };
      }).filter((motion) => motion.sourceRect || motion.targetRect);
      if (!pendingMotions.length) {
        return;
      }
      const revisionBatch = {
        jobId,
        key: `${jobId}:${revision}`,
        motions: pendingMotions,
        revision,
      };
      enqueueMotionQueue([revisionBatch], (motion, { queueDepth: batchQueueDepth = 0 } = {}) => {
        const currentRoot = document.querySelector('[data-fleet-timeline-panel="true"]');
        const orderIds = motion.group?.orderIds?.length
          ? motion.group.orderIds
          : motion.previousGroup?.orderIds || [];
        const resolvedTargetElement = motion.targetElement?.isConnected
          ? motion.targetElement
          : motion.group && currentRoot instanceof HTMLElement
            ? findOptimizationPreviewTarget(currentRoot, motion.group)
            : null;
        const resolvedSource = motion.sourceElement instanceof HTMLElement
          ? { element: motion.sourceElement, rect: motion.sourceRect }
          : currentRoot instanceof HTMLElement
            ? findOptimizationOrderSegment(currentRoot, orderIds)
            : null;
        const resolvedTargetRect = resolvedTargetElement?.getBoundingClientRect?.()
          || (motion.kind === "retract"
            ? document.querySelector('[data-optimization-preview-staging="true"]')?.getBoundingClientRect?.()
            : motion.targetRect);
        // The geometry belongs to the previously displayed revision. The DOM
        // element is only a clone template and may still sit in the committed
        // dispatch, so recomputing its rectangle would make every revision
        // appear to restart from that stale position.
        const resolvedSourceRect = motion.sourceRect
          || resolvedSource?.element?.getBoundingClientRect?.()
          || null;
        const runDurationMs = resolveOptimizationPreviewMotionDuration({
          motionKind: motion.kind,
          queueDepth: Math.max(queueDepth, Number(batchQueueDepth) || 0),
        });
        return motion.kind === "retract"
          ? animateOptimizationPreviewDeparture({
            durationMs: runDurationMs,
            index: motion.index,
            label: motion.label,
            sourceElement: resolvedSource?.element || motion.sourceElement,
            sourceRect: resolvedSourceRect,
            stagingRect: resolvedTargetRect || visibleStagingRect,
          })
          : animateOptimizationPreviewOrder({
            durationMs: runDurationMs,
            index: motion.index,
            label: motion.label,
            motionKind: motion.kind,
            orderId: motion.groupId,
            sourceElement: resolvedSource?.element || motion.sourceElement,
            sourceRect: resolvedSourceRect,
            sourceTruckId: motion.sourceTruckId,
            stagingRect: null,
            targetElement: resolvedTargetElement || motion.targetElement,
            targetRect: resolvedTargetRect || motion.targetRect,
            targetTruckId: motion.targetTruckId,
          });
      });
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [active, assignmentPreview, jobId, operationKind, prioritizeTerminal, protectedOrderIds, retainedOrderIds, queueDepth, revision]);

  React.useEffect(() => () => {
    motionGenerationRef.current += 1;
    motionQueueRef.current = [];
    motionRunnerActiveRef.current = false;
    motionExecutorRef.current = null;
    const root = document.querySelector('[data-fleet-timeline-panel="true"]');
    document.querySelectorAll?.("[data-optimization-preview-flying-segment]").forEach((element) => element.remove());
    document.querySelectorAll?.("[data-optimization-motion-token]").forEach((element) => {
      element.getAnimations?.().forEach((animation) => animation.cancel());
      element.style.opacity = "";
      element.removeAttribute("data-optimization-motion-token");
    });
    restoreOptimizationPreviewSourceDimming(root || document);
  }, []);
}

function useDisplayedOptimizationPreview({
  active,
  assignmentPreview,
  assignmentPreviewEvents = [],
  jobId,
  prioritizeTerminal = false,
}) {
  const timerRef = React.useRef(null);
  const reducedMotion = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
  const [presentation, setPresentation] = React.useState(() => ({
    displayedAt: 0,
    jobId: "",
    preview: null,
    queue: [],
  }));
  const revision = Number(assignmentPreview?.revision || 0);

  const resolveStreamPreview = (event) => {
    if (!event || typeof event !== "object" || Array.isArray(event)) {
      return null;
    }
    const nestedPreview = event.preview
      || event.assignmentPreview
      || event.assignment_preview;
    return nestedPreview && typeof nestedPreview === "object" && !Array.isArray(nestedPreview)
      ? nestedPreview
      : event;
  };

  React.useEffect(() => {
    if (!active || !jobId) {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setPresentation({ displayedAt: 0, jobId: "", preview: null, queue: [] });
      return;
    }
    const incomingPreviews = Array.isArray(assignmentPreviewEvents) && assignmentPreviewEvents.length
      ? assignmentPreviewEvents.map(resolveStreamPreview).filter((preview) => (
        preview && typeof preview === "object" && Number(preview.revision || 0) > 0
      ))
      : [assignmentPreview];
    if (!incomingPreviews.length) {
      return;
    }

    const now = Date.now();
    setPresentation((current) => incomingPreviews.reduce((nextPresentation, preview) => {
      const previewIsTerminal = prioritizeTerminal
        && isCanonicalOptimizationTerminalPreview(preview);
      return updateOptimizationPreviewPresentation(nextPresentation, {
        assignmentPreview: preview,
        deferTerminal: previewIsTerminal && !reducedMotion,
        jobId,
        now,
        prioritizeTerminal: previewIsTerminal,
      });
    }, current));
  }, [active, assignmentPreview, assignmentPreviewEvents, jobId, prioritizeTerminal, reducedMotion, revision]);

  React.useEffect(() => {
    if (!active
      || !jobId
      || presentation.jobId !== jobId
      || !presentation.preview
      || !presentation.queue.length) {
      return undefined;
    }
    const durationMs = isCanonicalOptimizationTerminalPreview(presentation.queue[0])
      ? OPTIMIZATION_PREVIEW_TERMINAL_QUEUE_DELAY_MS
      : resolveOptimizationPreviewMotionDuration({
        queueDepth: presentation.queue.length,
      });
    const elapsedMs = Math.max(Date.now() - presentation.displayedAt, 0);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setPresentation((current) => {
        if (current.jobId !== jobId || !current.queue.length) {
          return current;
        }
        const [nextPreview, ...remainingQueue] = current.queue;
        return {
          ...current,
          displayedAt: Date.now(),
          preview: nextPreview,
          queue: remainingQueue,
        };
      });
    }, Math.max(durationMs - elapsedMs, 0));
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, jobId, presentation.displayedAt, presentation.jobId, presentation.preview, presentation.queue]);

  React.useEffect(() => () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const matchesActiveJob = active && presentation.jobId === jobId;
  return {
    displayedAt: matchesActiveJob ? presentation.displayedAt : 0,
    preview: matchesActiveJob ? presentation.preview : null,
    queueDepth: matchesActiveJob ? presentation.queue.length : 0,
  };
}

function useOptimizationPreviewTerminalHandoff({
  assignmentPreview,
  authoritativePaintSeen = false,
  displayedAt = 0,
  displayedPreview = null,
  jobId = "",
  observedActive = true,
  previewMode = "off",
  resultCommitted = false,
  status = "idle",
}) {
  const reducedMotion = typeof window !== "undefined"
    && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
  const terminalPreviewActive = shouldPrioritizeOptimizationTerminalPreview({
    assignmentPreview,
    observedActive,
    previewMode,
    resultCommitted,
    status,
  });
  const terminalPreviewRevision = Number(assignmentPreview?.revision || 0);
  const terminalKey = terminalPreviewActive
    ? `${typeof jobId === "string" ? jobId.trim() : ""}:${terminalPreviewRevision}`
    : "";
  const terminalPreviewDisplayed = terminalPreviewActive
    && isCanonicalOptimizationTerminalPreview(displayedPreview)
    && Number(displayedPreview?.revision || 0) === terminalPreviewRevision;
  const [handoffState, setHandoffState] = React.useState(() => ({
    key: "",
    minimumDisplayElapsed: false,
    timedOut: false,
  }));

  React.useEffect(() => {
    if (!terminalKey) {
      setHandoffState((current) => (
        current.key || current.minimumDisplayElapsed || current.timedOut
          ? { key: "", minimumDisplayElapsed: false, timedOut: false }
          : current
      ));
      return undefined;
    }

    setHandoffState((current) => ({
      key: terminalKey,
      minimumDisplayElapsed: current.key === terminalKey
        ? current.minimumDisplayElapsed
        : false,
      timedOut: false,
    }));
    const safetyTimeoutId = window.setTimeout(() => {
      setHandoffState((current) => (
        current.key === terminalKey
          ? { ...current, timedOut: true }
          : current
      ));
    }, OPTIMIZATION_PREVIEW_TERMINAL_HANDOFF_TIMEOUT_MS);
    return () => window.clearTimeout(safetyTimeoutId);
  }, [terminalKey]);

  React.useEffect(() => {
    if (!terminalKey || !terminalPreviewDisplayed) {
      return undefined;
    }
    if (reducedMotion) {
      setHandoffState((current) => (
        current.key === terminalKey
          ? { ...current, minimumDisplayElapsed: true }
          : current
      ));
      return undefined;
    }
    setHandoffState((current) => (
      current.key === terminalKey
        ? { ...current, minimumDisplayElapsed: false }
        : current
    ));
    const minimumDisplayTimeoutId = window.setTimeout(() => {
      setHandoffState((current) => (
        current.key === terminalKey
          ? { ...current, minimumDisplayElapsed: true }
          : current
      ));
    }, OPTIMIZATION_PREVIEW_TERMINAL_HOLD_DURATION_MS);
    return () => window.clearTimeout(minimumDisplayTimeoutId);
  }, [displayedAt, reducedMotion, terminalKey, terminalPreviewDisplayed]);

  const matchesTerminalPreview = handoffState.key === terminalKey;
  return shouldHoldOptimizationTerminalPreview({
    assignmentPreview,
    authoritativePaintSeen,
    minimumDisplayElapsed: reducedMotion
      || (matchesTerminalPreview && handoffState.minimumDisplayElapsed),
    observedActive,
    previewMode,
    reducedMotion,
    resultCommitted,
    status,
    terminalPreviewDisplayed,
    timedOut: matchesTerminalPreview && handoffState.timedOut,
  });
}

function usePlanningResetGanttWaiting({
  dispatchDate, dispatchPeriod, launchScope, jobState, hasDisplayedPreview, resultInstalled,
}) {
  const observedJobRef = React.useRef(null);
  const previewSeenJobRef = React.useRef(null);
  const matchesScope = (scope) => Boolean(dispatchDate)
    && scope?.dispatchDate === dispatchDate && scope?.dispatchPeriod === dispatchPeriod;
  const matchesJob = matchesScope(jobState) && jobState?.operationKind === "planning_reset";
  const active = matchesJob && ["running", "cancelling"].includes(jobState.status);
  const jobId = jobState?.jobId;

  React.useLayoutEffect(() => {
    if (active && jobId) observedJobRef.current = jobId;
    if (matchesJob && hasDisplayedPreview && jobId) previewSeenJobRef.current = jobId;
  }, [active, hasDisplayedPreview, jobId, matchesJob]);

  // A new launch takes precedence over the previous job's displayed preview.
  if (matchesScope(launchScope)) return true;
  if (!matchesJob || hasDisplayedPreview || (jobId && previewSeenJobRef.current === jobId)) return false;
  return active || (jobState.status === "completed"
    && jobState.resultCommitted !== false
    && Boolean(jobId) && observedJobRef.current === jobId && !resultInstalled);
}

function OptimizationGanttWaitingState({ animationEnabled = true, jobState = null, orderCount = 0, isPlanningReset = false }) {
  const normalizedOrderCount = Number.isFinite(Number(orderCount))
    ? Math.max(0, Math.round(Number(orderCount)))
    : 0;
  const isActiveJob = ["running", "cancelling"].includes(jobState?.status);
  const activeStep = isActiveJob
    ? jobState?.progress?.steps?.find((step) => step.key === jobState.progress.currentStepKey)
    : null;
  const startedAtMs = activeStep?.startedAt ? Date.parse(activeStep.startedAt) : Number.NaN;
  const shouldTick = activeStep?.status === "running" && Number.isFinite(startedAtMs);
  const [nowMs, setNowMs] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (!shouldTick) {
      return undefined;
    }
    setNowMs(Date.now());
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [activeStep?.key, shouldTick]);

  const durationLabel = formatOptimizationProgressDuration(
    shouldTick ? Math.max(nowMs - startedAtMs, 0) : activeStep?.durationMs,
  );
  const activityLabel = jobState?.status === "cancelling"
    ? "Arrêt demandé"
    : activeStep?.label || "";
  const segmentAnimationClassName = animationEnabled
    ? "motion-safe:animate-[optimization-preview-waiting-segment_2s_ease-in-out_infinite]"
    : "";

  return (
    <div
      className="grid min-w-0 gap-3 rounded-[4px] border border-slate-200 bg-white p-4"
      data-optimization-gantt-waiting="true"
      aria-busy={isActiveJob || isPlanningReset}
    >
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="m-0 text-[13px] font-bold text-accent">
            {isPlanningReset ? "Réinitialisation en cours…" : "En attente du premier aperçu…"}
          </p>
          <p className="m-0 mt-1 text-xs leading-5 text-slate-600">
            Les segments apparaîtront dès que les premières affectations seront disponibles.
          </p>
          {activityLabel ? (
            <p className="m-0 mt-1 text-xs font-semibold text-slate-700" data-optimization-gantt-waiting-activity="true">
              <span role="status">{activityLabel}</span>
              {durationLabel ? <span className="whitespace-nowrap" aria-live="off"> · {durationLabel}</span> : null}
            </p>
          ) : null}
        </div>
        {normalizedOrderCount > 0 ? (
          <span className="rounded-[4px] border border-slate-200 bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-600">
            {normalizedOrderCount} commande{normalizedOrderCount === 1 ? "" : "s"} à positionner
          </span>
        ) : null}
      </div>

      <div className="grid gap-1.5" aria-hidden="true">
        {[0, 1, 2, 3, 4].map((rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-center gap-3 sm:grid-cols-[6rem_minmax(0,1fr)]">
            <div className="h-2 rounded-[4px] bg-slate-200" />
            <div className="relative h-9 overflow-hidden rounded-[4px] border border-slate-100 bg-slate-50">
              <span className="absolute inset-y-2 rounded-[4px] bg-slate-200/80" style={{ left: `${6 + rowIndex * 3}%`, width: "24%" }} />
              <span className="absolute inset-y-2 rounded-[4px] bg-slate-200/80" style={{ left: `${40 + rowIndex * 4}%`, width: "18%" }} />
              <span
                className={joinClasses([
                  "absolute inset-y-0 left-0 w-1/3 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.85),transparent)] opacity-0",
                  segmentAnimationClassName,
                ])}
                style={{ animationDelay: `${rowIndex * -150}ms` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OptimizationPreviewOverlay({
  belowRetainedSegments = false,
  preview = null,
  leadingPaddingPercent = 0,
  scaleMinutes = 0,
  timelineStartMinutes = 0,
  trailingPaddingPercent = 0,
}) {
  if (!preview || typeof preview !== "object") {
    return null;
  }

  const groups = Array.isArray(preview.groups) ? preview.groups : [];
  const itemWidthPercent = groups.length ? 100 / groups.length : 100;
  const activityPhase = resolveOptimizationPreviewVisualPhase(preview.activityPhase);

  return (
    <>
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-[12] rounded-[5px]"
        data-optimization-previous-dispatch-dim="true"
      />
      {groups.length ? (
        <div
          className={joinClasses([
            "pointer-events-none absolute inset-x-0.5 bottom-0.5 z-[25] min-w-0 overflow-hidden rounded-[4px]",
            belowRetainedSegments ? "top-9" : "top-0.5",
          ])}
          data-optimization-preview-activity={activityPhase}
          data-optimization-preview-animated="true"
          data-optimization-preview-segments="true"
          data-optimization-preview-layout-kind={preview.layoutKind || undefined}
        >
          {groups.map((group, index) => {
            const providedPosition = group.position;
			const timelineEndMinutes = Number(group.endMinutes) === Number(group.startMinutes)
				&& Number.isFinite(Number(group.startMinutes))
				? Number(group.startMinutes) + 0.01
				: group.endMinutes;
            const timelinePosition = group.layoutKind === "timeline"
              ? buildFleetIncidentPlacement({
                display_start_minutes: group.startMinutes,
				display_end_minutes: timelineEndMinutes,
              }, {
                leadingPaddingPercent,
                scaleMinutes,
                timelineStartMinutes,
                trailingPaddingPercent,
              })
              : null;
            const position = providedPosition || timelinePosition || (group.layoutKind === "timeline"
              ? null
              : { leftPercent: index * itemWidthPercent, widthPercent: Math.max(0, itemWidthPercent - 0.35) });
            if (!position) {
              return null;
            }
            const firstItem = group.items?.[0] || null;
            const protectedOrderCount = Array.isArray(group.protectedOrderIds)
              ? group.protectedOrderIds.length
              : 0;
            const groupRenderKey = `${group.groupId}:${group.orderIds.join(",")}`;
            return (
            <span
              key={groupRenderKey}
              className={[
                "absolute inset-y-0.5 flex min-w-0 items-center gap-1 overflow-hidden rounded-[5px] px-1 text-[0.55rem] font-extrabold transition-[left,width,filter,transform,opacity] duration-500",
                group.stepType === "loading"
                  ? FROZEN_TIMELINE_SEGMENT_CLASS_NAMES.loading
                  : group.stepType === "delivery"
                    ? FROZEN_TIMELINE_SEGMENT_CLASS_NAMES.delivery
                    : "border border-[var(--color-accent)] bg-[linear-gradient(135deg,rgba(204,251,241,0.98),rgba(219,234,254,0.98))] text-[rgb(8,64,60)] shadow-[0_3px_8px_rgba(8,90,85,0.14)]",
              ].filter(Boolean).join(" ")}
              data-optimization-preview-group-id={group.groupId}
              data-optimization-preview-step-type={group.stepType || "neutral"}
              data-optimization-preview-truck-id={group.truckId || undefined}
              data-optimization-preview-order-id={firstItem?.orderId || group.orderIds[0]}
              data-optimization-preview-order-ids={group.orderIds.join(",")}
              data-optimization-preview-moved={group.moved ? "true" : undefined}
              data-optimization-preview-reordered={group.reordered ? "true" : undefined}
              style={{
                "--optimization-preview-activity-index": index,
                left: `${position.leftPercent}%`,
                width: `${Math.max(0, position.widthPercent - 0.35)}%`,
              }}
              title={`${group.clientLabel ? `${group.clientLabel} · ` : ""}${group.orderLabel}`}
            >
              <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-[4px] bg-white/90 px-0.5 text-[0.5rem] font-black text-[var(--color-accent)]">
                {group.sequenceEnd > group.sequence ? `${group.sequence}-${group.sequenceEnd}` : group.sequence}
              </span>
              <span className="min-w-0 truncate">{group.clientLabel || group.orderLabel}</span>
              {protectedOrderCount ? (
                <span className="ml-auto shrink-0 rounded-[4px] bg-white/85 px-1 text-[0.45rem] font-black text-[var(--color-accent)]">
                  {protectedOrderCount} prot.
                </span>
              ) : null}
            </span>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

// Ligne sans tracteur pour ce Dispatch : mention colorée à côté du nom (publication refusée).
const FLEET_TRACTOR_MISSING_CHIP_CLASS_NAME = "mr-1 inline-flex h-4 items-center whitespace-nowrap rounded-[4px] border border-amber-300 bg-amber-50 px-1 align-middle text-[0.58rem] font-bold leading-none text-amber-800";

export function FleetTimelineRow({
  activeDispatchDate = "",
  isInclined = false,
  consumeGroupedSegmentClickSuppression = null,
  consumeSegmentReorderClickSuppression = null,
  disableHoverCards = false,
  consumeReassignmentClickSuppression = null,
  expandedTimelineGroupKey = "",
  fleetSegmentComparisonByKey = null,
  fleetSegmentHovercardContext = null,
  fleetSegmentPressState = null,
  geotabReal = null,
  geotabGap = null,
  blockedPlanningMovementFeedback = null,
  haversineRouteWarningIssue = null,
  linkedSegmentSelection = null,
  openSegmentActionKey = "",
  onDropReassignment = null,
  onDoubleClickSegment = null,
  onExpandTimelineGroup = null,
  onGroupedSegmentPointerDown = null,
  onRouteHoverEnd = null,
  onRouteSegmentHoverStart = null,
  onRouteSequenceSelect = null,
  onSegmentPointerDown = null,
  onSelectAssignment,
  onOpenSegmentPopover,
  onOpenError = null,
  onTimelineGanttInteraction = null,
  onReassignmentDragEnd = null,
  onReassignmentDragOver = null,
  onReassignmentPointerDown = null,
  onReassignmentDragStart = null,
  onSelectSegment,
  onOptimizeTruckRoutes = null,
  onRecalculateTruckRoute = null,
  onManageTruckIncidents = null,
  onPrintTruckSheet = null,
  onPublishTruckPortal = null,
  onReassignAllTruckOrders = null,
  onSetTruckPreloadTime = null,
  onUpdateTruckDriverName = null,
  onUpdateTruckTractor = null,
  onUpdateTruckStartTime = null,
  onUpdateTruckTimeSettings = null,
  onToggleTruckDispatchDisabled = null,
  onToggleTruckDompeurMixedLoad = null,
  onToggleTruckOrdersProtection = null,
  onToggleTruckOvertime = null,
  onToggleTruckProtection = null,
  onUnpublishTruckPortal = null,
  optimizationPreview = null,
  optimizationPreviewMode = false,
  portalPublications = null,
  registerSegmentActionAnchor,
  reassignmentDragState = null,
  segmentReorderDragState = null,
  row,
  scaleMinutes,
  segmentActionKeyPrefix = "",
  leadingPaddingPercent = 0,
  trailingPaddingPercent = 0,
  informationalSegmentWidthPercent = 0,
  isCompactTimelineRail = false,
  displayDispatchPeriod = "day",
  timelineStartMinutes = null,
  truckLabelById = null,
  truckReassignmentTargets = [],
  truckSourceChanges = null,
  truckSheetPrintDisabledReason = "",
  truckSheetPrintIsBusy = false,
  truckTimeSettingsDisabledReason = "",
  selectedClientId = "",
  showHaversineRouteWarning = false,
  showProductTags = true,
  alertDisplay = null,
}) {
  const { hasPermission } = useAccess();
  const normalizedDisplayDispatchPeriod = displayDispatchPeriod === "evening" ? "evening" : "day";
  const isEveningTimelineRail = normalizedDisplayDispatchPeriod === "evening";
  const isSegmentReorderDragVisibleForDisplayPeriod = Boolean(segmentReorderDragState)
    && (segmentReorderDragState?.sourceDispatchPeriod === "evening" ? "evening" : "day") === normalizedDisplayDispatchPeriod;
  const resolveRowSegmentActionKey = (segment) => {
    const segmentActionKey = getTimelineSegmentActionKey(row.assignmentId, segment);
    return segmentActionKey ? `${segmentActionKeyPrefix}${segmentActionKey}` : "";
  };
  const shell = buildAssignmentShellViewModel({
    assignment: row.suppressPreviousTruckStatus
      ? { ...row.assignment, is_truck_protected: false }
      : row.assignment,
    assignmentId: row.assignmentId,
    distanceSummary: row.distanceLabel || "Distance estimée indisponible",
    timelineRenderState: row.timelineRenderState,
  });
  const canManageDriverPortalPublication = typeof onPublishTruckPortal === "function"
    || typeof onUnpublishTruckPortal === "function";
  const portalPublicationRoute = canManageDriverPortalPublication
    ? findPortalPublicationRoute(
        portalPublications,
        row?.assignment?.truck_id || row?.assignmentId,
      )
    : null;
  const driverPortalStatusHoverCard = portalPublicationRoute
    ? buildDriverPortalStatusHoverCardContent(
        portalPublicationRoute.status,
        portalPublicationRoute.isDispatchDisabled,
      )
    : null;
  const dispatchDisabledHoverCard = buildDispatchDisabledHoverCardContent(shell.dispatchDisabledBadge);
  const truckProtectionHoverCard = buildTruckProtectionHoverCardContent(shell.truckProtectionBadge);
  const rowActiveTrip = getRowActiveTrip(row);
  const timelineRenderStatus = typeof row?.timelineRenderState?.status === "string"
    ? row.timelineRenderState.status
    : "";
  const isTimelineUnrenderable = timelineRenderStatus === "unrenderable";
  const hasRenderableTimeline = !timelineRenderStatus || timelineRenderStatus === "ready";
  const unrenderableTimelineItems = Array.isArray(row?.timelineRenderState?.items)
    ? row.timelineRenderState.items.filter(Boolean)
    : [];
  const unrenderableTimelineItemCount = Number.isFinite(Number(row?.timelineRenderState?.assignedItemCount))
    ? Math.max(Math.round(Number(row.timelineRenderState.assignedItemCount)), 0)
    : unrenderableTimelineItems.length;
  const unrenderableTimelineItemLabels = unrenderableTimelineItems
    .map((item) => (typeof item?.label === "string" ? item.label.trim() : ""))
    .filter(Boolean);
  const unrenderableTimelineListLabel = unrenderableTimelineItemLabels.join(" · ");
  const unrenderableTimelineHoverTitle = [
    typeof row?.timelineRenderState?.title === "string" ? row.timelineRenderState.title.trim() : "",
    unrenderableTimelineListLabel,
    typeof row?.timelineRenderState?.message === "string" ? row.timelineRenderState.message.trim() : "",
  ].filter(Boolean).join("\n");
  const rowEstimatedTotalDurationMinutes = resolveRowEstimatedTotalDurationMinutes(row);
  const routeAlternativesFeatureEnabled = isSorelTracyRouteAlternativesFeatureEnabled(fleetSegmentHovercardContext);
  const timelineSourceSegments = React.useMemo(
    () => buildFleetTimelineSourceSegments(row, {
      activeDispatchDate,
      fleetSegmentComparisonByKey,
      fleetSegmentHovercardContext,
    }),
    [activeDispatchDate, fleetSegmentComparisonByKey, fleetSegmentHovercardContext?.here_time_aware_routing_enabled, row?.segments],
  );
  const positionedSegments = buildSegmentPositionData(
    timelineSourceSegments,
    scaleMinutes,
    rowEstimatedTotalDurationMinutes,
    timelineStartMinutes,
    {
      leadingFixedPercent: leadingPaddingPercent,
      trailingFixedPercent: trailingPaddingPercent,
      informationalSegmentWidthPercent,
    },
  );
  const displayedPositionedSegments = React.useMemo(
    () => buildFleetTimelineDisplaySegments(positionedSegments),
    [positionedSegments],
  );
  const rowIncidents = resolveFleetRowIncidents(row);
  const hasMutedIncidentBars = timelineSourceSegments.length === 0;
  const hasRowIncidents = row?.hasIncidents === true || rowIncidents.length > 0;
  const positionedIncidents = rowIncidents
    .map((incident) => ({
      incident,
      placement: buildFleetIncidentPlacement(incident, {
        leadingPaddingPercent,
        scaleMinutes,
        timelineStartMinutes,
        trailingPaddingPercent,
      }),
    }))
    .filter((item) => item.placement);
  const sequenceDisplayOverrides = React.useMemo(
    () => buildSameSiteSequenceDisplayOverrides(buildFleetSequenceDisplayItems(timelineSourceSegments)),
    [timelineSourceSegments],
  );
  const sequenceRenderItems = React.useMemo(
    () => buildSequenceTableRenderItems(buildFleetSequenceDisplayItems(timelineSourceSegments)),
    [timelineSourceSegments],
  );
  const sequenceOpeningVisibilityByItemKey = React.useMemo(
    () => buildRouteSequenceOpeningVisibilityByItemKey(sequenceRenderItems),
    [sequenceRenderItems],
  );
  const sequenceClosingVisibilityByItemKey = React.useMemo(
    () => buildRouteSequenceClosingVisibilityByItemKey(sequenceRenderItems),
    [sequenceRenderItems],
  );
  const shouldDisableHoverCards = disableHoverCards || Boolean(openSegmentActionKey);
  const hasOptimizationPreview = optimizationPreviewMode && Boolean(optimizationPreview);
  const hasPlanningResetPreview = hasOptimizationPreview && optimizationPreview.operationKind === "planning_reset";
  // Keep the stationary PM segments readable even when a provisional route
  // occupies their previous time slots. The extra lane ends with the preview.
  const needsRetainedPmLane = hasOptimizationPreview
    && optimizationPreview.groups?.length > 0
    && collectOptimizationPreviewRetainedOrderIds([row.assignment], { operationKind: optimizationPreview.operationKind }).length > 0;
  // Pause dîner : l'arrêt qui la porte s'arrête au bloc « Dîner ».
  const { block: lunchBreakBlock, items: timelineRenderItems } = resolveLunchBreakTimeline(
    buildTimelineRenderItems(displayedPositionedSegments, {
      expandedGroupKey: expandedTimelineGroupKey,
      preserveAbsoluteTimelinePositions: true,
      timelineStartMinutes,
    }),
    row?.assignment,
    { leadingPaddingPercent, scaleMinutes, timelineStartMinutes, trailingPaddingPercent },
  );
  const timelineItemCount = timelineRenderItems.length;
  const geotabRealMarks = geotabReal?.truck ? buildGeotabRealMarks(timelineRenderItems, geotabReal.truck, geotabReal.tolerance) : [];
  const geotabRealSummary = geotabReal?.showProgress ? summarizeGeotabRealProgress(geotabReal.truck) : null;
  const productRunStartLabels = resolveProductRunStartLabels(timelineRenderItems);
  // Onglet produit d'un élément : premier produit d'un arrêt mixte, sinon début d'une suite du même produit.
  const resolveItemProductTabLabel = (item) => {
    const labels = item.kind === "group" ? resolveGroupedSegmentProductLabels(item.memberSegments) : [];
    return !showProductTags ? "" : labels.length > 1 ? labels[0] : productRunStartLabels.get(item) || "";
  };
  const productTabRoomPercents = resolveProductTabRoomPercents(
    timelineRenderItems,
    resolveItemProductTabLabel,
    (item) => resolveTimelineItemPlacement((item.kind === "group" ? item : item.segment)?.leftPercent, 0).leftPercent,
  );
  const timelineTickLines = resolveTimelineHourTicks(
    timelineStartMinutes,
    Number.isFinite(Number(timelineStartMinutes)) && Number.isFinite(Number(scaleMinutes))
      ? Number(timelineStartMinutes) + Number(scaleMinutes)
      : null,
    { leadingPaddingPercent, trailingPaddingPercent },
  ).filter((tick) => tick.percent > 0 && tick.percent < 100);
  const frontendDurationBreakdown = resolveDisplayedTimelineFrontendDurationBreakdown(displayedPositionedSegments, {
    rowStartMinutes: row?.timelineStartMinutes,
  });
  const timelineSpanDurationMinutes = resolveDisplayedTimelineSpanMinutes(displayedPositionedSegments, row?.timelineStartMinutes);
  const rowVisibleDurationMinutes = resolveRowVisibleDurationMinutes(row);
  const displayedTotalDurationMinutes = resolveFrontendCanonicalTotalDurationMinutes(
    frontendDurationBreakdown,
    {
      rowVisibleDurationMinutes,
      timelineSpanDurationMinutes,
    },
  ) ?? row?.totalDurationMinutes ?? null;
  const backendTotalDurationMinutes = hasRenderableTimeline
    ? resolveAssignmentBackendTotalDurationMinutes(row?.assignment)
    : null;
  const canonicalDisplayedTotalDurationMinutes = displayedTotalDurationMinutes ?? backendTotalDurationMinutes;
  const fleetRowDurationKey = isCompactTimelineRail ? "" : buildFleetRowDurationKey(activeDispatchDate, resolveFleetTimelineRowTruckKey(row));
  const reportedFleetRowMinutes = isTimelineUnrenderable ? null : canonicalDisplayedTotalDurationMinutes;
  // La marge de droite affiche la même durée que cette ligne.
  React.useEffect(() => {
    reportFleetRowDisplayedMinutes(fleetRowDurationKey, reportedFleetRowMinutes);
  }, [fleetRowDurationKey, reportedFleetRowMinutes]);
  const displayedTotalDurationLabel = isTimelineUnrenderable
    ? "Durée non calculable"
    : formatDurationShort(canonicalDisplayedTotalDurationMinutes)
      || resolveDisplayedTimelineSpanLabel(displayedPositionedSegments, row?.timelineStartMinutes)
      || resolveRowDisplayedTotalDurationLabel(row, {
        activeDispatchDate,
        fleetSegmentComparisonByKey,
        fleetSegmentHovercardContext,
      });
  const truckDistanceKm = parseSequenceDistanceKm(row.distanceLabel);
  const displayedTruckDistanceLabel = truckDistanceKm != null
    ? formatTruckTotalHoverDistance(truckDistanceKm)
    : row.distanceLabel;
  const backendDurationBreakdown = hasRenderableTimeline
    ? resolveAssignmentBackendDurationBreakdown(row?.assignment, {
        displayedDurationMinutes: displayedTotalDurationMinutes,
      })
    : null;
  const totalDurationHoverCardMaxWidth = Array.isArray(frontendDurationBreakdown?.drivingSegments)
    && frontendDurationBreakdown.drivingSegments.length > 0
    ? 896
    : 640;
  const totalDurationHoverCardContent = backendDurationBreakdown ? (
    <TruckTotalDurationHoverCardContent
      backendDurationBreakdown={backendDurationBreakdown}
      displayedTotalDurationLabel={displayedTotalDurationLabel}
      frontendDurationBreakdown={frontendDurationBreakdown}
      frontendTimelineSpanMinutes={timelineSpanDurationMinutes}
      showCalculated={hasPermission(ACCESS_PERMISSION_KEYS.systemDiagnosticsManage)}
    />
  ) : null;
  // Sans commande, ni durée ni produits : les icônes d'état se centrent sur toute la hauteur du camion.
  const truckStatusIconRowClassName = displayedTotalDurationLabel === "0 min" ? "row-start-1 row-span-3" : "row-start-2 row-span-2";
  const truckProductLabel = normalizeTruckProductLabel(shell?.grainTypeLabel);
  const manualMixProductLabels = Array.isArray(shell?.manualMixBadge?.products)
    ? shell.manualMixBadge.products
      .map((productLabel) => normalizeTruckProductLabel(productLabel))
      .filter(Boolean)
    : [];
  const truckProductLabels = [...new Set([truckProductLabel, ...(shell?.chargedPmProductLabels || [])
    .map((productLabel) => normalizeTruckProductLabel(productLabel))].filter(Boolean))];
  const timelineTruckProductPillClassName = (shell.manualMixBadge ? manualMixProductLabels : truckProductLabels).length === 3
    ? FLEET_TIMELINE_TRUCK_TRIPLE_PRODUCT_PILL_CLASS_NAME
    : FLEET_TIMELINE_TRUCK_PRODUCT_PILL_CLASS_NAME;
  const timelineTruckProductPills = shell.manualMixBadge || truckProductLabels.length ? (
    <div className="flex min-w-0 overflow-hidden [&_span]:rounded-[2px]!">
      <div className="flex min-w-0 flex-nowrap items-center gap-px">
        {shell.manualMixBadge ? (
          manualMixProductLabels.length ? (
            manualMixProductLabels.map((productLabel, productIndex) => (
              <FleetProductPill
                key={`${productLabel}-${productIndex}`}
                label={productLabel}
                maxLength={7}
                paddingClassName={timelineTruckProductPillClassName}
                title=""
              />
            ))
          ) : (
            <span
              className={joinClasses([
                `inline-flex shrink-0 items-center border border-border-warning-muted bg-amber-soft px-1 py-0.25 text-[0.58rem] font-extrabold uppercase tracking-[0.04em] text-warning-ink ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`,
              ])}
              data-route-pill="true"
            >
              {shell.manualMixBadge.label}
            </span>
          )
        ) : (
          truckProductLabels.map((productLabel) => (
            <FleetProductPill
              key={productLabel}
              label={productLabel}
              paddingClassName={timelineTruckProductPillClassName}
              title=""
            />
          ))
        )}
      </div>
    </div>
  ) : null;
  const excludedTruckRefreshWarning = row?.excludedTruckRefresh?.refreshStatus === "failed"
    ? row.excludedTruckRefresh
    : null;
  const fleetWarningTruckId = resolveFleetTimelineRowTruckKey(row);
  const excludedTruckRefreshPresentation = excludedTruckRefreshWarning
    ? buildExcludedTruckRefreshPresentation(excludedTruckRefreshWarning, {
        truckId: fleetWarningTruckId,
        truckName: shell.truckTitle,
      })
    : null;
  const excludedTruckRefreshReasonDetail = excludedTruckRefreshPresentation?.reasonDetail || "";
  const excludedTruckRefreshImpactedLabels = excludedTruckRefreshPresentation?.impactedLocationLabels || [];
  const excludedTruckRefreshSolutionLines = excludedTruckRefreshPresentation?.actionLines || [];
  const excludedTruckRefreshIsDanger = excludedTruckRefreshPresentation?.tone === "danger";
  const excludedTruckRefreshButtonClassName = joinClasses([
    "inline-flex h-6 w-6 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-full p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2",
    excludedTruckRefreshIsDanger
      ? "border border-[rgba(248,113,113,0.72)] bg-[rgba(254,226,226,0.98)] text-[rgb(185,28,28)] shadow-[0_0_0_3px_rgba(248,113,113,0.16),0_7px_14px_rgba(185,28,28,0.18)] ring-2 ring-[rgba(254,202,202,0.72)] focus-visible:outline-red-600"
      : "border border-[rgba(217,119,6,0.38)] bg-[rgba(255,247,237,0.98)] text-[rgb(154,52,18)] shadow-[0_0_0_3px_rgba(251,191,36,0.14),0_7px_14px_rgba(180,83,9,0.14)] ring-2 ring-[rgba(254,215,170,0.72)] focus-visible:outline-amber-700",
    excludedTruckRefreshPresentation?.animation === "pulse" ? "motion-safe:animate-pulse" : "",
  ]);
  const excludedTruckRefreshHoverCardContent = excludedTruckRefreshPresentation ? (
    <div className="grid min-w-80 max-w-[28rem] gap-2">
      <div className={joinClasses([
        "grid gap-0.5 rounded-[8px] border bg-white px-2.5 py-2",
        excludedTruckRefreshIsDanger ? "border-red-200/80" : "border-[rgba(217,119,6,0.14)]",
      ])}>
        <p className={joinClasses([
          "m-0 text-[0.58rem] font-black uppercase tracking-[0.12em]",
          excludedTruckRefreshIsDanger ? "text-red-700/75" : "text-[rgba(154,52,18,0.72)]",
        ])}>Problématique</p>
        <p className={joinClasses([
          "m-0 text-[0.7rem] font-semibold leading-5",
          excludedTruckRefreshIsDanger ? "text-red-700" : "text-[rgb(154,52,18)]",
        ])}>
          {excludedTruckRefreshPresentation.problemText}
        </p>
        {excludedTruckRefreshReasonDetail ? (
          <p className="m-0 text-[0.68rem] leading-5 text-content-muted">
            {excludedTruckRefreshReasonDetail}
          </p>
        ) : null}
      </div>
      {excludedTruckRefreshPresentation.impactText ? (
        <div className={joinClasses([
          "grid gap-0.5 rounded-[8px] border px-2.5 py-2",
          excludedTruckRefreshIsDanger
            ? "border-red-200/80 bg-red-50/70"
            : "border-[rgba(217,119,6,0.16)] bg-[rgba(255,247,237,0.72)]",
        ])}>
          <p className={joinClasses([
            "m-0 text-[0.58rem] font-black uppercase tracking-[0.12em]",
            excludedTruckRefreshIsDanger ? "text-red-700/75" : "text-[rgba(154,52,18,0.72)]",
          ])}>Impact</p>
          <p className={joinClasses([
            "m-0 text-[0.68rem] leading-5",
            excludedTruckRefreshIsDanger ? "text-red-700" : "text-[rgb(154,52,18)]",
          ])}>
            {excludedTruckRefreshPresentation.impactText}
          </p>
        </div>
      ) : null}
      {excludedTruckRefreshImpactedLabels.length ? (
        <div className="flex flex-wrap gap-1">
          {excludedTruckRefreshImpactedLabels.slice(0, 4).map((label, index) => (
            <span
              key={`${label}-${index}`}
              className="inline-flex max-w-full rounded-full border border-[rgba(217,119,6,0.18)] bg-white/88 px-1.75 py-0.5 text-[0.58rem] font-black uppercase tracking-[0.08em] text-[rgb(154,52,18)]"
            >
              <span className="truncate">{label}</span>
            </span>
          ))}
          {excludedTruckRefreshImpactedLabels.length > 4 ? (
            <span className="inline-flex rounded-full border border-[rgba(217,119,6,0.14)] bg-white/72 px-1.75 py-0.5 text-[0.58rem] font-black uppercase tracking-[0.08em] text-[rgb(154,52,18)]">
              +{excludedTruckRefreshImpactedLabels.length - 4}
            </span>
          ) : null}
        </div>
      ) : null}
      {excludedTruckRefreshSolutionLines.length ? (
        <div className="grid gap-1 rounded-[8px] border border-[rgba(15,92,87,0.14)] bg-[rgba(236,248,245,0.76)] px-2.5 py-2">
          <p className="m-0 text-[0.58rem] font-black uppercase tracking-[0.12em] text-[rgba(15,92,87,0.72)]">Solutions</p>
          <div className="grid gap-1">
            {excludedTruckRefreshSolutionLines.map((line, index) => (
              <p key={`${line}-${index}`} className="m-0 text-[0.68rem] leading-5 text-[rgb(17,94,89)]">
                {line}
              </p>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  ) : null;
  const hasHaversineRouteWarning = Boolean(showHaversineRouteWarning);
  const haversineRouteWarningPresentation = hasHaversineRouteWarning
    ? buildHaversineRouteWarningPresentation(haversineRouteWarningIssue, {
        summary: row.haversineRouteSummary,
        truckName: shell.truckTitle,
      })
    : null;
  const haversineRouteWarningContent = haversineRouteWarningPresentation ? (
    <div className="grid min-w-80 max-w-[28rem] gap-2">
      {[
        ["Problème", haversineRouteWarningPresentation.problemRows],
        ["Pourquoi", [["Cause", haversineRouteWarningPresentation.causeText]]],
      ].map(([label, rows]) => (
        <div key={label} className="grid gap-0.5 rounded-[8px] border border-[rgba(217,119,6,0.16)] bg-[rgba(255,247,237,0.72)] px-2.5 py-2">
          <p className="m-0 text-[0.58rem] font-black uppercase tracking-[0.12em] text-[rgba(154,52,18,0.72)]">{label}</p>
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-0.5 text-[0.68rem] leading-5">
            {rows.map(([term, value]) => (
              <React.Fragment key={term}>
                <dt className="font-semibold text-content-muted">{term}</dt>
                <dd className="m-0 font-medium text-[rgb(154,52,18)]">{value}</dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
      ))}
      <div className="grid gap-1 rounded-[8px] border border-[rgba(15,92,87,0.14)] bg-[rgba(236,248,245,0.76)] px-2.5 py-2">
        <p className="m-0 text-[0.58rem] font-black uppercase tracking-[0.12em] text-[rgba(15,92,87,0.72)]">À faire</p>
        {haversineRouteWarningPresentation.actionLines.map((line, index) => (
          <p key={`${line}-${index}`} className="m-0 text-[0.68rem] leading-5 text-[rgb(17,94,89)]">
            {line}
          </p>
        ))}
        {typeof onRecalculateTruckRoute === "function" ? (
          <button
            type="button"
            className="inline-flex h-6 w-fit cursor-pointer items-center rounded-[4px] border border-accent bg-accent px-2.5 py-0 text-[0.68rem] font-bold text-white hover:border-accent-strong hover:bg-accent-strong focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            data-fleet-recalculate-route="true"
            onClick={(event) => {
              event.stopPropagation();
              onRecalculateTruckRoute({ truckId: fleetWarningTruckId, truckName: shell.truckTitle });
            }}
          >
            Recalculer la route
          </button>
        ) : null}
      </div>
    </div>
  ) : null;
  const isSuspendedDeliveryDrag = reassignmentDragState?.dragKind === "suspended";
  const isReassignmentDragActive = Boolean(reassignmentDragState);
  const [isTruckActionsOpen, setIsTruckActionsOpen] = React.useState(false);
  // La fiche Camion couvrirait la bulle d'un triangle de la ligne (route, mise à jour Data manager).
  const [isTruckWarningHovered, setIsTruckWarningHovered] = React.useState(false);
  const truckActionsAnchorRef = React.useRef(null);
  const truckHoldTimerRef = React.useRef(null);
  const truckHoldStartPointRef = React.useRef(null);
  const suppressNextTruckSelectClickRef = React.useRef(false);
  const isReassignmentDragReady = reassignmentDragState?.status === "ready";
  const canShowSuspendedLoadingPreviewTargets = reassignmentDragState?.dragKind === "suspended"
    && reassignmentDragState?.status === "loading"
    && Object.keys(reassignmentDragState?.compatibilityByTruckId || {}).length > 0;
  const canRenderReassignmentPreviewTargets = isReassignmentDragReady || canShowSuspendedLoadingPreviewTargets;
  const reassignmentSourceDispatchPeriod = reassignmentDragState?.sourceDispatchPeriod === "evening" ? "evening" : "day";
  const isDragSourceRow = Boolean(reassignmentDragState?.sourceAssignmentId)
    && reassignmentDragState.sourceAssignmentId === row.assignmentId
    && reassignmentSourceDispatchPeriod === normalizedDisplayDispatchPeriod;
  const isBlockedPlanningMovementSourceRow = Boolean(
    blockedPlanningMovementFeedback?.sourceSurface === "gantt"
    && blockedPlanningMovementFeedback?.assignmentId === row.assignmentId
    && blockedPlanningMovementFeedback?.dispatchPeriod === normalizedDisplayDispatchPeriod,
  );
  const visibleCompatibilityByTruckId = isEveningTimelineRail
    ? reassignmentDragState?.eveningCompatibilityByTruckId
    : reassignmentDragState?.compatibilityByTruckId;
  const actionableCompatibilityByTruckId = isEveningTimelineRail
    ? reassignmentDragState?.eveningActionableCompatibilityByTruckId
    : reassignmentDragState?.actionableCompatibilityByTruckId;
  const visibleCandidateTarget = canRenderReassignmentPreviewTargets
    ? visibleCompatibilityByTruckId?.[row.assignmentId] || null
    : null;
  const actionableDropTarget = isReassignmentDragReady
    ? actionableCompatibilityByTruckId?.[row.assignmentId] || null
    : null;
  const isVisibleCandidateTarget = Boolean(visibleCandidateTarget);
  const isActionableDropTarget = Boolean(actionableDropTarget);
  const isDisabledReassignmentCandidate = isReassignmentDragReady && !isActionableDropTarget && !isDragSourceRow;
  const isHoveredDropTarget = isActionableDropTarget && reassignmentDragState?.hoveredTruckId === row.assignmentId;
  const shouldDimInactiveReassignmentRow = isReassignmentDragReady
    ? !isActionableDropTarget && !isDragSourceRow
    : canShowSuspendedLoadingPreviewTargets
      ? !isVisibleCandidateTarget && !isDragSourceRow
      : false;
  const hasTruckProtectionChrome = row.isTruckProtected && !row.suppressPreviousTruckStatus && !isReassignmentDragActive;
  const hasFleetStatusRail = Boolean(
    shell.dispatchDisabledBadge || hasTruckProtectionChrome || row.hasOvertimeWarning,
  );
  const hasGanttPortalIcon = Boolean(portalPublicationRoute);
  // Chargé PM multi-jours : camion chargé pendant le transit. Fond rouge réservé à ce cas.
  const isEndOfDayLoadTransitRow = Boolean(shell.dispatchDisabledBadge?.isEndOfDayLoadTransit);
  const endOfDayLoadTransitArticleStyle = isEndOfDayLoadTransitRow
    ? {
        borderColor: row.isSelected ? "rgba(220,38,38,0.42)" : "rgba(220,38,38,0.3)",
        backgroundImage: "linear-gradient(180deg, rgba(254,242,242,0.99), rgba(254,226,226,0.96))",
        boxShadow: "none",
      }
    : undefined;
  const endOfDayLoadTransitTrackStyle = isEndOfDayLoadTransitRow
    ? {
        borderColor: "rgba(220,38,38,0.26)",
        backgroundImage: "linear-gradient(180deg, rgba(254,242,242,0.98), rgba(254,226,226,0.92))",
        boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.38)",
      }
    : undefined;
  const canShowTruckActions = typeof onToggleTruckDispatchDisabled === "function"
    || typeof onManageTruckIncidents === "function"
    || typeof onPrintTruckSheet === "function"
    || typeof onUpdateTruckDriverName === "function"
    || typeof onUpdateTruckStartTime === "function"
    || typeof onUpdateTruckTimeSettings === "function"
    || typeof onToggleTruckProtection === "function"
    || typeof onToggleTruckOrdersProtection === "function"
    || typeof onToggleTruckDompeurMixedLoad === "function"
    || typeof onToggleTruckOvertime === "function"
    || typeof onSetTruckPreloadTime === "function"
    || typeof onOptimizeTruckRoutes === "function"
    || typeof onReassignAllTruckOrders === "function"
    || typeof onPublishTruckPortal === "function"
    || typeof onUnpublishTruckPortal === "function";

  const clearTruckActionHold = React.useCallback(() => {
    if (truckHoldTimerRef.current && typeof window !== "undefined") {
      window.clearTimeout(truckHoldTimerRef.current);
    }
    truckHoldTimerRef.current = null;
    truckHoldStartPointRef.current = null;
  }, []);

  React.useEffect(() => () => {
    clearTruckActionHold();
  }, [clearTruckActionHold]);

  React.useEffect(() => {
    if (isReassignmentDragActive) {
      setIsTruckActionsOpen(false);
      clearTruckActionHold();
    }
  }, [clearTruckActionHold, isReassignmentDragActive]);

  const openTruckActions = React.useCallback(() => {
    suppressNextTruckSelectClickRef.current = true;
    onSelectAssignment?.(row.assignmentId);
    setIsTruckActionsOpen(true);
  }, [onSelectAssignment, row.assignmentId]);

  const closeTruckActions = React.useCallback(() => {
    setIsTruckActionsOpen(false);
  }, []);
  // Ferme le menu puis agit ; une action absente reste null, et le menu grise son bouton.
  const closeThen = (handler) => (typeof handler === "function" ? (...args) => { closeTruckActions(); return handler(...args); } : null);

  const notifyTimelineGanttInteraction = React.useCallback(() => {
    onTimelineGanttInteraction?.({
      assignmentId: row.assignmentId,
      truckLabel: row.truckLabel || shell.truckTitle || "",
    });
  }, [onTimelineGanttInteraction, row.assignmentId, row.truckLabel, shell.truckTitle]);

  const handleTruckHeaderClick = React.useCallback(() => {
    if (suppressNextTruckSelectClickRef.current) {
      suppressNextTruckSelectClickRef.current = false;
      return;
    }

    closeTruckActions();
    notifyTimelineGanttInteraction();
    onSelectAssignment?.(row.assignmentId);
  }, [closeTruckActions, notifyTimelineGanttInteraction, onSelectAssignment, row.assignmentId]);

  const handleTruckHeaderContextMenu = React.useCallback((event) => {
    if (!canShowTruckActions) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    clearTruckActionHold();
    openTruckActions();
  }, [canShowTruckActions, clearTruckActionHold, openTruckActions]);

  const handleTruckHeaderPointerDown = React.useCallback((event) => {
    if (!canShowTruckActions || event.button !== 0 || !event.isPrimary || typeof window === "undefined") {
      return;
    }

    suppressNextTruckSelectClickRef.current = false;
    clearTruckActionHold();
    truckHoldStartPointRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
    };
    truckHoldTimerRef.current = window.setTimeout(() => {
      truckHoldTimerRef.current = null;
      openTruckActions();
    }, REASSIGNMENT_POINTER_HOLD_DELAY_MS);
  }, [canShowTruckActions, clearTruckActionHold, openTruckActions]);

  const handleTruckHeaderPointerMove = React.useCallback((event) => {
    if (!truckHoldTimerRef.current || !truckHoldStartPointRef.current) {
      return;
    }

    const deltaX = event.clientX - truckHoldStartPointRef.current.clientX;
    const deltaY = event.clientY - truckHoldStartPointRef.current.clientY;
    if (Math.hypot(deltaX, deltaY) > REASSIGNMENT_POINTER_MOVE_THRESHOLD_PX) {
      clearTruckActionHold();
    }
  }, [clearTruckActionHold]);

  const handleTruckHeaderPointerEnd = React.useCallback(() => {
    clearTruckActionHold();
  }, [clearTruckActionHold]);

  return (
    <>
    <article
      data-end-of-day-load-transit={isEndOfDayLoadTransitRow ? "true" : undefined}
      style={isBlockedPlanningMovementSourceRow ? undefined : endOfDayLoadTransitArticleStyle}
      className={joinClasses([
        FLEET_TIMELINE_ROW_ARTICLE_BASE_CLASS_NAME,
        row.isSelected
          ? "border-border-accent-strong bg-slate-100 shadow-none"
          : "border-[rgba(148,163,184,0.16)] bg-white shadow-none",
        isEveningTimelineRail
          ? "border-[rgba(99,102,241,0.24)] bg-[linear-gradient(180deg,rgba(248,248,255,0.99),rgba(241,243,255,0.96))]"
          : "",
        hasOptimizationPreview ? "relative" : "",
        isReassignmentDragActive && isHoveredDropTarget
          ? "border-[rgba(217,119,6,0.34)] bg-[rgba(255,247,237,0.98)] shadow-[0_16px_30px_rgba(217,119,6,0.12)]"
          : isReassignmentDragReady && isActionableDropTarget
            ? "border-[rgba(217,119,6,0.2)] bg-[rgba(255,251,235,0.96)]"
            : isReassignmentDragReady && isDisabledReassignmentCandidate
              ? "border-[rgba(148,163,184,0.18)] bg-[rgba(248,250,252,0.94)]"
            : isReassignmentDragActive && isDragSourceRow
              ? "border-[rgba(217,119,6,0.18)] bg-[rgba(255,251,235,0.94)]"
              : "",
        shouldDimInactiveReassignmentRow ? "opacity-40" : "",
        isBlockedPlanningMovementSourceRow
          ? "border-border-danger-strong! outline outline-2 outline-offset-[-2px] outline-[var(--color-danger)]"
          : row.isSelected
          ? "outline outline-1 outline-offset-[-1px] outline-accent"
          : "",
      ])}
      data-fleet-timeline-row="true"
      data-assignment-id={row.assignmentId}
      data-dispatch-period={normalizedDisplayDispatchPeriod}
      data-selection-state={row.isSelected ? "selected" : "idle"}
      data-truck-protection-state={hasTruckProtectionChrome ? "protected" : "open"}
      data-reassignment-drop-state={isBlockedPlanningMovementSourceRow ? "blocked" : isHoveredDropTarget ? "hovered" : isActionableDropTarget ? "compatible" : isDisabledReassignmentCandidate ? "disabled" : isDragSourceRow ? "source" : "inactive"}
      aria-disabled={isBlockedPlanningMovementSourceRow || isDisabledReassignmentCandidate ? "true" : undefined}
      aria-current={row.isSelected ? "true" : undefined}
      onDragOver={(event) => {
        if (isInclined) { event.preventDefault(); return; }
        onReassignmentDragOver?.(event, row.assignmentId, normalizedDisplayDispatchPeriod);
      }}
      onDrop={(event) => {
        if (isInclined) { event.preventDefault(); return; }
        onDropReassignment?.(event, row.assignmentId, normalizedDisplayDispatchPeriod);
      }}
    >
      {shell.dispatchDisabledBadge ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-1.25 left-0 z-[1] w-1 rounded-[2px] bg-[rgb(153,27,27)]"
        />
      ) : hasTruckProtectionChrome ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-1.25 left-0 z-[1] w-1 rounded-[2px] bg-[rgb(8,64,60)]"
        />
      ) : row.hasOvertimeWarning ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-1.25 left-0 z-[1] w-1 rounded-[2px] bg-[rgb(161,74,7)]"
        />
      ) : row.isSelected ? (
        <span aria-hidden="true" className="pointer-events-none absolute inset-y-1.25 left-0 z-[1] w-0.75 rounded-none bg-accent" />
      ) : null}

      {!isBlockedPlanningMovementSourceRow && isReassignmentDragActive && (isDragSourceRow || isActionableDropTarget) ? (
        <span className="pointer-events-none absolute right-1.5 top-1.5 z-20 inline-flex items-center rounded-full border border-[rgba(217,119,6,0.18)] bg-white/96 px-2 py-0.75 text-[0.6rem] font-extrabold uppercase tracking-widest text-[rgb(154,52,18)] shadow-[0_8px_18px_rgba(217,119,6,0.08)]">
          {isHoveredDropTarget
            ? (isSuspendedDeliveryDrag ? "Réactiver ici" : "Déposer ici")
            : isDragSourceRow
              ? "Source"
              : (isSuspendedDeliveryDrag ? "Cible" : "Compatible")}
        </span>
      ) : null}

      <div className={joinClasses([
        "grid gap-px min-[1120px]:items-center",
        isCompactTimelineRail ? EVENING_TIMELINE_TRUCK_COLUMN_GRID : FLEET_TIMELINE_TRUCK_COLUMN_GRID,
      ])}>
        {!isCompactTimelineRail ? (
        <div className="relative grid min-w-0 w-full grid-cols-[minmax(0,1fr)_auto] grid-rows-[auto_auto_auto] gap-x-1 gap-y-px self-center pr-1">
        <HoverCard
          title=""
          content={<TruckHoverCardContent row={row} shell={shell} trip={rowActiveTrip} truckSourceChanges={truckSourceChanges} />}
          accentColor="var(--hovercard-accent-neutral)"
          disabled={shouldDisableHoverCards || isTruckActionsOpen || isTruckWarningHovered}
          maxWidth={560}
          placement="top-start"
          referenceAsChild
        >
          <button
            type="button"
            data-fleet-truck-identity="true"
            ref={truckActionsAnchorRef}
            onClick={handleTruckHeaderClick}
            data-dispatch-truck-context="true"
            onContextMenu={handleTruckHeaderContextMenu}
            onPointerCancel={handleTruckHeaderPointerEnd}
            onPointerDown={handleTruckHeaderPointerDown}
            onPointerLeave={handleTruckHeaderPointerEnd}
            onPointerMove={handleTruckHeaderPointerMove}
            onPointerUp={handleTruckHeaderPointerEnd}
            className={joinClasses([
              FLEET_TIMELINE_TRUCK_HEADER_BUTTON_BASE_CLASS_NAME,
              "col-span-2 col-start-1 row-span-3 row-start-1 grid-cols-subgrid grid-rows-subgrid",
              hasFleetStatusRail || row.isSelected ? "pl-1.5" : "pl-0",
              "pr-0.5",
              row.isSelected
                  ? "bg-slate-100"
                  : isTruckActionsOpen
                    ? "bg-slate-100"
                    : "hover:bg-[rgba(248,250,252,0.86)]",
            ])}
          >
            <div className={FLEET_TIMELINE_TRUCK_HEADER_INNER_GRID_CLASS_NAME}>
              <div className="col-start-1 row-start-1 flex min-w-0 items-center gap-1">
                <h3 className={joinClasses([FLEET_TIMELINE_TRUCK_TITLE_CLASS_NAME, "text-text"])}>
                  {shell.truckTitle}
                </h3>
                {hasRowIncidents ? (
                  <HoverCard
                    title="Bris mécaniques / Contretemps"
                    content={<FleetIncidentsSummaryHoverCardContent incidents={rowIncidents} />}
                    accentColor="var(--hovercard-accent-danger)"
                    disabled={shouldDisableHoverCards}
                    maxWidth={420}
                    placement="top-start"
                    referenceAsChild
                  >
                    <span
                      aria-label={`${rowIncidents.length} bris mécanique${rowIncidents.length > 1 ? "s" : ""} ou contretemps`}
                      className="inline-flex h-5 shrink-0 items-center gap-0.5 rounded-full border border-red-300 bg-red-50 px-1.5 text-[0.58rem] font-black leading-none text-red-700"
                      data-fleet-truck-incident-count={rowIncidents.length}
                    >
                      <Wrench aria-hidden="true" className="h-2.5 w-2.5" strokeWidth={2.5} />
                      {rowIncidents.length}
                    </span>
                  </HoverCard>
                ) : null}
                {hasHaversineRouteWarning ? (
                  <HoverCard
                    title={haversineRouteWarningPresentation?.title}
                    content={haversineRouteWarningContent}
                    accentColor="var(--hovercard-accent-warning)"
                    disabled={shouldDisableHoverCards}
                    interactive
                    maxWidth={460}
                    placement="top-start"
                    referenceAsChild
                  >
                    <button
                      type="button"
                      aria-label={haversineRouteWarningPresentation?.ariaLabel}
                      className="inline-flex h-6 w-6 shrink-0 cursor-pointer appearance-none items-center justify-center rounded-full border border-[rgba(217,119,6,0.38)] bg-[rgba(255,247,237,0.98)] p-0 text-[rgb(154,52,18)] shadow-[0_0_0_3px_rgba(251,191,36,0.14),0_7px_14px_rgba(180,83,9,0.14)] ring-2 ring-[rgba(254,215,170,0.72)] motion-safe:animate-dispatch-source-changes-pulse focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-amber-700"
                      data-fleet-haversine-route-warning="true"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (haversineRouteWarningPresentation?.openRequest) {
                          onOpenError?.(haversineRouteWarningPresentation.openRequest);
                        }
                      }}
                      onMouseEnter={(event) => {
                        event.stopPropagation();
                        setIsTruckWarningHovered(true);
                      }}
                      onMouseLeave={() => setIsTruckWarningHovered(false)}
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <AlertTriangle aria-hidden="true" className="h-3 w-3 motion-safe:animate-fleet-haversine-route-warning-blink" strokeWidth={2.5} />
                    </button>
                  </HoverCard>
                ) : null}
                {excludedTruckRefreshPresentation ? (
                  <HoverCard
                    title="Mise à jour Data manager"
                    content={excludedTruckRefreshHoverCardContent}
                    accentColor={excludedTruckRefreshIsDanger
                      ? "var(--hovercard-accent-danger)"
                      : "var(--hovercard-accent-warning)"}
                    disabled={shouldDisableHoverCards}
                    maxWidth={460}
                    placement="top-start"
                    referenceAsChild
                  >
                    <button
                      type="button"
                      aria-label={excludedTruckRefreshPresentation.ariaLabel}
                      className={excludedTruckRefreshButtonClassName}
                      data-fleet-excluded-truck-refresh-warning="true"
                      onClick={(event) => {
                        event.stopPropagation();
                        if (excludedTruckRefreshPresentation.openRequest) {
                          onOpenError?.(excludedTruckRefreshPresentation.openRequest);
                        }
                      }}
                      onMouseEnter={(event) => {
                        event.stopPropagation();
                        setIsTruckWarningHovered(true);
                      }}
                      onMouseLeave={() => setIsTruckWarningHovered(false)}
                      onPointerDown={(event) => event.stopPropagation()}
                    >
                      <AlertTriangle aria-hidden="true" className="h-3 w-3" strokeWidth={2.5} />
                    </button>
                  </HoverCard>
                ) : null}
                {displayedTotalDurationLabel !== "0 min" && displayedTruckDistanceLabel ? (
                  <span
                    aria-label={`Distance prévue ${displayedTruckDistanceLabel}`}
                    className="ml-auto shrink-0 whitespace-nowrap text-[0.68rem] font-normal leading-none tabular-nums text-content-copy"
                    data-fleet-truck-distance="true"
                  >
                    {displayedTruckDistanceLabel}
                  </span>
                ) : null}
              </div>
              <div
                className={joinClasses([FLEET_TIMELINE_TRUCK_DRIVER_CLASS_NAME, "col-start-1 row-start-2"])}
              >
                {row.isTractorMissing ? (
                  <span className={FLEET_TRACTOR_MISSING_CHIP_CLASS_NAME} data-fleet-tractor-missing="true" title={row.missingTractorTitle}>{MISSING_TRACTOR_LABEL}</span>
                ) : null}
                {row.driverLabel || "Chauffeur non renseigné"}
                {/* Avis Geotab : cloche pour un chauffeur réglé à part, cloche barrée pour un chauffeur sans avis. */}
                {{ custom: <Bell aria-label="Avis Geotab réglés à part" className="ml-1 inline h-3 w-3 shrink-0 align-[-1px] text-accent" strokeWidth={2.5} data-fleet-driver-alerts="custom" />,
                  none: <BellOff aria-label="Aucun avis Geotab" className="ml-1 inline h-3 w-3 shrink-0 align-[-1px] text-[#c2410c]" strokeWidth={2.5} data-fleet-driver-alerts="none" /> }[assignmentAlertStatus(alertDisplay, row.assignment)] ?? null}
              </div>
              {timelineTruckProductPills || geotabRealSummary || geotabGap ? (
                <div className="col-span-2 col-start-1 row-start-3 flex min-w-0 items-center gap-1 pr-10">
                  {timelineTruckProductPills}
                  {geotabRealSummary?.total || geotabGap ? <span aria-hidden="true" className="ml-auto h-4 min-w-[30px] shrink-0" /> : null}
                </div>
              ) : null}
            </div>
          </button>
        </HoverCard>
        {geotabRealSummary?.total || geotabGap ? (
          <div className="pointer-events-none col-span-2 col-start-1 row-start-3 z-[2] flex min-w-0 items-center pr-10.5">
            <GeotabRealProgressChip summary={geotabRealSummary} gap={geotabGap} />
          </div>
        ) : null}
        {displayedTotalDurationLabel !== "0 min" ? (
          <HoverCard
            accentColor="var(--color-accent)"
            content={totalDurationHoverCardContent}
            delay={TRUCK_TOTAL_DURATION_HOVERCARD_DELAY_MS}
            disabled={totalDurationHoverCardContent == null}
            maxWidth={totalDurationHoverCardMaxWidth}
            placement="top-end"
            referenceAsChild
            title="Total camion"
          >
            <span
              aria-label={`Total affiché ${displayedTotalDurationLabel}`}
              className={joinClasses([
                "col-start-2 row-start-1 z-[2] max-w-28 cursor-help self-center text-right leading-none tabular-nums outline-hidden transition-colors focus-visible:ring-2 focus-visible:ring-accent/25",
                // Dépassement d'heures : la durée devient une pastille orange, la rangée reste blanche.
                row.hasOvertimeWarning
                  ? "rounded-[4px] border border-orange-300 bg-orange-50 px-1 py-0.5 text-[0.7rem] font-bold text-orange-800"
                  : "pr-0.5 font-serif text-[0.78rem] font-normal text-content-ink hover:text-text focus-visible:text-text",
                isTimelineUnrenderable ? "whitespace-normal" : "whitespace-nowrap",
              ])}
              tabIndex={0}
            >
              {displayedTotalDurationLabel.replace(/(\d+\s*h\s+\d+)\s+min$/, "$1")}
            </span>
          </HoverCard>
        ) : null}
        {hasGanttPortalIcon ? (
          <div className={joinClasses(["col-start-2 z-[2] grid h-5 w-10 grid-cols-2 items-center justify-self-end self-center", truckStatusIconRowClassName])}>
            <FleetTruckStatusIconHoverCard
              accentColor={driverPortalStatusHoverCard?.accentColor}
              description={driverPortalStatusHoverCard?.description}
              disabled={shouldDisableHoverCards}
              title={driverPortalStatusHoverCard?.title}
            >
              <span
                className={joinClasses([
                  "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border",
                  portalPublicationRoute.status === "published"
                    ? DRIVER_PORTAL_FLUORESCENT_BADGE_CLASS_NAME
                    : "border-slate-200 bg-slate-50 text-slate-400",
                ])}
                data-fleet-driver-portal-status={portalPublicationRoute.status}
                title={driverPortalStatusHoverCard?.title}
              >
                <Smartphone
                  aria-hidden="true"
                  className={joinClasses([
                    "h-3 w-3",
                    portalPublicationRoute.status === "published"
                      ? DRIVER_PORTAL_FLUORESCENT_ICON_CLASS_NAME
                      : "fill-slate-100",
                  ])}
                  strokeWidth={2.2}
                />
              </span>
            </FleetTruckStatusIconHoverCard>
            {shell.dispatchDisabledBadge ? (
              <FleetTruckStatusIconHoverCard
                accentColor={dispatchDisabledHoverCard?.accentColor}
                className="hidden min-[1120px]:inline-flex"
                description={dispatchDisabledHoverCard?.description}
                disabled={shouldDisableHoverCards}
                title={dispatchDisabledHoverCard?.title}
              >
                <FleetTruckStatusIndicator
                  title={shell.dispatchDisabledBadge.title}
                  isEndOfDayLoadTransit={shell.dispatchDisabledBadge.isEndOfDayLoadTransit}
                  variant="dispatch-disabled"
                />
              </FleetTruckStatusIconHoverCard>
            ) : shell.truckProtectionBadge ? (
              <FleetTruckStatusIconHoverCard
                accentColor={truckProtectionHoverCard?.accentColor}
                className="hidden min-[1120px]:inline-flex"
                description={truckProtectionHoverCard?.description}
                disabled={shouldDisableHoverCards}
                title={truckProtectionHoverCard?.title}
              >
                <FleetTruckStatusIndicator
                  title={shell.truckProtectionBadge.title}
                  variant="protected"
                />
              </FleetTruckStatusIconHoverCard>
            ) : (
              <span aria-hidden="true" className="h-5 w-5" />
            )}
          </div>
        ) : shell.dispatchDisabledBadge ? (
          <FleetTruckStatusIconHoverCard
            accentColor={dispatchDisabledHoverCard?.accentColor}
            className={joinClasses(["col-start-2 z-[2] hidden justify-self-end self-center min-[1120px]:inline-flex", truckStatusIconRowClassName])}
            description={dispatchDisabledHoverCard?.description}
            disabled={shouldDisableHoverCards}
            title={dispatchDisabledHoverCard?.title}
          >
            <FleetTruckStatusIndicator
              title={shell.dispatchDisabledBadge.title}
              isEndOfDayLoadTransit={shell.dispatchDisabledBadge.isEndOfDayLoadTransit}
              variant="dispatch-disabled"
            />
          </FleetTruckStatusIconHoverCard>
        ) : shell.truckProtectionBadge ? (
          <FleetTruckStatusIconHoverCard
            accentColor={truckProtectionHoverCard?.accentColor}
            className={joinClasses(["col-start-2 z-[2] hidden justify-self-end self-center min-[1120px]:inline-flex", truckStatusIconRowClassName])}
            description={truckProtectionHoverCard?.description}
            disabled={shouldDisableHoverCards}
            title={truckProtectionHoverCard?.title}
          >
            <FleetTruckStatusIndicator
              title={shell.truckProtectionBadge.title}
              variant="protected"
            />
          </FleetTruckStatusIconHoverCard>
        ) : null}
        </div>
        ) : (
          <HoverCard
            title=""
            content={<TruckHoverCardContent row={row} shell={shell} trip={rowActiveTrip} truckSourceChanges={truckSourceChanges} />}
            accentColor="var(--hovercard-accent-neutral)"
            disabled={shouldDisableHoverCards || isTruckActionsOpen}
            maxWidth={560}
            placement="left-start"
            referenceAsChild
          >
            <button
              type="button"
              ref={truckActionsAnchorRef}
              onClick={handleTruckHeaderClick}
              data-dispatch-truck-context="true"
              onContextMenu={handleTruckHeaderContextMenu}
              onPointerCancel={handleTruckHeaderPointerEnd}
              onPointerDown={handleTruckHeaderPointerDown}
              onPointerLeave={handleTruckHeaderPointerEnd}
              onPointerMove={handleTruckHeaderPointerMove}
              onPointerUp={handleTruckHeaderPointerEnd}
              className={joinClasses([
                "grid min-w-0 content-center gap-px self-stretch overflow-hidden rounded-[5px] px-1.5 py-0.5 text-left transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-indigo-600",
                row.isSelected
                  ? "bg-[rgba(224,231,255,0.96)]"
                  : "bg-[rgba(238,242,255,0.56)] hover:bg-[rgba(224,231,255,0.82)]",
              ])}
              data-evening-truck-identity="true"
            >
              <div className="flex min-w-0 items-center gap-1 overflow-hidden">
                <h3 className={joinClasses([FLEET_TIMELINE_TRUCK_TITLE_CLASS_NAME, "text-text"])} data-evening-truck-name="true">
                  {shell.truckTitle}
                </h3>
                {hasRowIncidents ? (
                  <HoverCard
                    title="Bris mécaniques / Contretemps"
                    content={<FleetIncidentsSummaryHoverCardContent incidents={rowIncidents} />}
                    accentColor="var(--hovercard-accent-danger)"
                    disabled={shouldDisableHoverCards}
                    maxWidth={420}
                    placement="top-start"
                    referenceAsChild
                  >
                    <span
                      aria-label={`${rowIncidents.length} bris mécanique${rowIncidents.length > 1 ? "s" : ""} ou contretemps`}
                      className="inline-flex h-4 shrink-0 items-center gap-0.5 rounded-full border border-red-300 bg-red-50 px-1 text-[0.54rem] font-black leading-none text-red-700"
                      data-fleet-truck-incident-count={rowIncidents.length}
                    >
                      <Wrench aria-hidden="true" className="h-2.5 w-2.5" strokeWidth={2.5} />
                      {rowIncidents.length}
                    </span>
                  </HoverCard>
                ) : null}
              </div>
              <div
                className={FLEET_TIMELINE_TRUCK_DRIVER_CLASS_NAME}
                data-evening-truck-driver="true"
              >
                {row.isTractorMissing ? (
                  <span className={FLEET_TRACTOR_MISSING_CHIP_CLASS_NAME} data-fleet-tractor-missing="true" title={row.missingTractorTitle}>{MISSING_TRACTOR_LABEL}</span>
                ) : null}
                {row.driverLabel || "Chauffeur non renseigné"}
              </div>
              {timelineTruckProductPills ? (
                <div
                  className="flex min-w-0 items-center overflow-hidden"
                  data-evening-truck-product="true"
                >
                  {timelineTruckProductPills}
                </div>
              ) : null}
            </button>
          </HoverCard>
        )}

        {/* Piste au bas de la rangée : l'espace libre au-dessus reçoit les onglets produit. */}
        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)] items-center self-end">
        <div className="min-w-0 w-full">
            <div
              data-fleet-timeline-track="true"
              data-fleet-timeline-extra-lane={needsRetainedPmLane ? "true" : undefined}
              data-timeline-start={timelineStartMinutes}
              data-timeline-scale={scaleMinutes}
              data-timeline-lead={leadingPaddingPercent}
              data-timeline-trail={trailingPaddingPercent}
              style={endOfDayLoadTransitTrackStyle}
              className={joinClasses([
              "relative overflow-visible rounded-[4px] border border-[rgba(148,163,184,0.16)] bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.94))] px-0 [&:has([data-geotab-real-gap])]:mb-3.5",
              needsRetainedPmLane ? "h-18" : FLEET_TIMELINE_ROW_TRACK_HEIGHT_CLASS_NAME,
              isEveningTimelineRail
                ? "border-[rgba(99,102,241,0.24)] bg-[linear-gradient(180deg,rgba(252,252,255,0.99),rgba(239,242,255,0.96))]"
                : "",
                isReassignmentDragReady && isActionableDropTarget
                ? "border-[rgba(217,119,6,0.28)] bg-[linear-gradient(180deg,rgba(255,251,235,0.98),rgba(255,247,237,0.92))]"
                : isReassignmentDragActive && isDragSourceRow
                  ? "border-[rgba(217,119,6,0.18)] bg-[linear-gradient(180deg,rgba(255,251,235,0.94),rgba(255,255,255,0.96))]"
                  : "",
              ])}>
              {timelineTickLines.map((tick) => (
                <span
                  key={tick.minutes}
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-y-1 w-px bg-[rgba(148,163,184,0.18)]"
                  style={{ left: `${tick.percent}%`, transform: "translateX(-50%)" }}
                />
              ))}

              {positionedIncidents.map(({ incident, placement }, incidentIndex) => {
                const incidentLabel = `${formatFleetIncidentTime(incident?.start_time, placement.startMinutes)} — ${formatFleetIncidentTime(incident?.end_time, placement.endMinutes)}`;
                return (
                  <button
                    key={normalizeFleetIncidentText(incident?.id) || `${placement.startMinutes}-${incidentIndex}`}
                    type="button"
                    aria-label={`Bris mécanique / Contretemps de ${shell.truckTitle}, ${incidentLabel}`}
                    title="Double-cliquer pour gérer les bris mécaniques / contretemps"
                    onClick={handleTruckHeaderClick}
                    data-dispatch-truck-context="true"
                    onContextMenu={handleTruckHeaderContextMenu}
                    onDoubleClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      closeTruckActions();
                      onManageTruckIncidents?.({
                        dispatchDate: activeDispatchDate,
                        dispatchPeriod: normalizedDisplayDispatchPeriod,
                        truckId: resolveFleetTimelineRowTruckKey(row),
                        truckName: shell.truckTitle,
                      });
                    }}
                    className={joinClasses([
                      "pointer-events-auto absolute top-0.5 z-[18] flex h-8 cursor-pointer items-center justify-center overflow-hidden rounded-[4px] border px-0.5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent",
                      hasMutedIncidentBars
                        ? "border-stone-200 bg-stone-50 text-stone-500"
                        : "border-red-700/80 bg-red-200/75 text-red-900 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.45),0_2px_5px_rgba(185,28,28,0.18)]",
                    ])}
                    data-fleet-truck-incident="true"
                    style={{
                      backgroundImage: hasMutedIncidentBars
                        ? "repeating-linear-gradient(135deg, #eeebe5 0, #eeebe5 7px, #faf9f6 7px, #faf9f6 14px)"
                        : "repeating-linear-gradient(135deg, rgba(185,28,28,0.16) 0, rgba(185,28,28,0.16) 4px, rgba(254,226,226,0.58) 4px, rgba(254,226,226,0.58) 8px)",
                      left: `${placement.leftPercent}%`,
                      width: `${placement.widthPercent}%`,
                    }}
                  >
                    {placement.widthPercent >= 2.4 ? <Wrench aria-hidden="true" className={hasMutedIncidentBars ? "h-3.5 w-3.5 shrink-0" : "h-5 w-5 shrink-0"} strokeWidth={hasMutedIncidentBars ? 1.75 : 2.5} /> : null}
                    {placement.widthPercent >= 8 ? <span className={hasMutedIncidentBars ? "ml-2 truncate text-xs font-normal" : "ml-1 truncate text-[0.56rem] font-black"}>{hasMutedIncidentBars ? "Entretien · " : ""}{incidentLabel}</span> : null}
                  </button>
                );
              })}

              {!hasOptimizationPreview && (
                <LunchBreakTimelineBlock block={lunchBreakBlock} disabled={shouldDisableHoverCards} />
              )}

              {isHoveredDropTarget ? (
                <div className="pointer-events-none absolute inset-0.5 z-10 flex items-center justify-center rounded-[5px] border border-dashed border-[rgba(217,119,6,0.42)] bg-[rgba(255,247,237,0.78)] text-[0.68rem] font-semibold text-[rgb(154,52,18)] shadow-none">
                  {isSuspendedDeliveryDrag ? `Réactiver sur ${shell.truckTitle}` : `Déposer sur ${shell.truckTitle}`}
                </div>
              ) : null}

              {hasOptimizationPreview ? (
                <OptimizationPreviewOverlay
                  belowRetainedSegments={needsRetainedPmLane}
                  leadingPaddingPercent={leadingPaddingPercent}
                  preview={optimizationPreview}
                  scaleMinutes={scaleMinutes}
                  timelineStartMinutes={timelineStartMinutes}
                  trailingPaddingPercent={trailingPaddingPercent}
                />
              ) : null}

              {isTimelineUnrenderable ? (
                <button
                  type="button"
                  onClick={() => {
                    notifyTimelineGanttInteraction();
                    onSelectAssignment?.(row.assignmentId);
                  }}
                  className="absolute inset-0.5 flex min-w-0 items-center justify-center gap-1.5 rounded-[5px] border border-dashed border-amber-300/80 bg-amber-50/75 px-2 text-[0.64rem] font-semibold text-amber-800 transition hover:border-amber-400 hover:bg-amber-50 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-amber-400/35 focus-visible:ring-offset-2"
                  title={unrenderableTimelineHoverTitle || undefined}
                >
                  <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5 shrink-0" strokeWidth={2.3} />
                  <span className="shrink-0">
                    Tournée à vérifier — {unrenderableTimelineItemCount} commande{unrenderableTimelineItemCount > 1 ? "s" : ""}
                  </span>
                  {unrenderableTimelineListLabel ? (
                    <span className="min-w-0 truncate font-medium text-amber-700/90">
                      {unrenderableTimelineListLabel}
                    </span>
                  ) : null}
                </button>
              ) : !row.hasTrips && !hasRowIncidents && !hasOptimizationPreview ? (
                <button
                  type="button"
                  onClick={() => {
                    notifyTimelineGanttInteraction();
                    onSelectAssignment?.(row.assignmentId);
                  }}
                  className="absolute inset-0.5 flex items-center justify-center rounded-[5px] border border-dashed border-[rgba(148,163,184,0.3)] px-2 text-[0.66rem] font-medium text-content-copy transition hover:border-border-accent-strong hover:text-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2"
                >
                  <span>Aucune étape planifiée</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      notifyTimelineGanttInteraction();
                      onSelectAssignment?.(row.assignmentId);
                    }}
                    className="absolute inset-0 rounded-[5px]"
                    aria-label={truckProductLabel ? `Sélectionner ${shell.truckTitle}, produit ${truckProductLabel}` : `Sélectionner ${shell.truckTitle}`}
                  />

                  {timelineRenderItems.map((item, itemIndex) => {
                    if (item.kind === "idle") {
                      const idlePlacement = resolveTimelineItemPlacement(item.leftPercent, item.widthPercent);
                      const idleWidthPercent = idlePlacement.widthPercent;
                      const idleDurationLabel = formatDurationShort(item.durationMinutes) || "0 min";
                      const idleWindowLabel = Number.isFinite(Number(item.startMinutes)) && Number.isFinite(Number(item.endMinutes))
                        ? `${formatTimelineClockLabel(item.startMinutes)} → ${formatTimelineClockLabel(item.endMinutes)}`
                        : "";
                      const showIdleLabel = idleWidthPercent >= 7.5;

                      return (
                        <div
                          key={item.key}
                          aria-label={idleWindowLabel
                            ? `Attente ou inactivité ${idleDurationLabel}, de ${idleWindowLabel}`
                            : `Attente ou inactivité ${idleDurationLabel}`}
                          className="pointer-events-none absolute top-1 flex h-6.5 items-center overflow-hidden rounded-[6px] border border-dashed border-[rgba(148,163,184,0.34)] bg-white/96 px-1 text-[0.58rem] font-bold uppercase tracking-[0.08em] text-content-muted"
                          style={{
                            left: `${idlePlacement.leftPercent}%`,
                            width: `${idleWidthPercent}%`,
                          }}
                        >
                          {showIdleLabel ? <span className="truncate">Attente {idleDurationLabel}</span> : null}
                        </div>
                      );
                    }

                    if (item.kind === "group") {
                      const groupItem = item;
                      const memberSegments = Array.isArray(groupItem.memberSegments) ? groupItem.memberSegments : [];
                      const groupManualBufferMinutes = memberSegments.reduce((totalMinutes, segment) => {
                        const segmentManualBufferMinutes = Number(segment?.manualBufferMinutes);
                        return Number.isFinite(segmentManualBufferMinutes) && segmentManualBufferMinutes > 0
                          ? totalMinutes + segmentManualBufferMinutes
                          : totalMinutes;
                      }, 0);
                      const isInformationalInitialPreloadGroup = Boolean(groupItem.isInitialPreload);
                      const isInformationalEndOfDayLoadGroup = Boolean(groupItem.isEndOfDayLoad || groupItem.isEndOfDayLoadSourcePreload);
                      const isInformationalGroup = isInformationalInitialPreloadGroup || isInformationalEndOfDayLoadGroup;
                      const optimizationPreviewSourceOrderIds = hasPlanningResetPreview || (isInformationalInitialPreloadGroup && !isInformationalEndOfDayLoadGroup)
                        ? normalizeOptimizationPreviewOrderIds(
                            memberSegments.map((segment) => segment?.operationOrderId || segment?.orderLabel),
                          )
                        : [];
                      const groupPlacement = resolveTimelineItemPlacement(groupItem.leftPercent, groupItem.widthPercent);
                      const groupWidthPercent = groupPlacement.widthPercent;
                      const useCompactGroupLayout = groupWidthPercent < 8.5;
                      const isSelectedGroup = memberSegments.some((segment) => Boolean(segment?.isSelectedClient || (selectedClientId && segment?.clientId === selectedClientId)));
                      const isLinkedGroup = memberSegments.some((segment) => isLinkedCommandCompanionSegment(segment, linkedSegmentSelection));
                      const isActivationHighlightedGroup = memberSegments.some((segment) => segment?.isActivationHighlighted);
                      const groupStatusMarkers = getGroupedSegmentStatusMarkers(memberSegments);
                      const groupIsDispatcherFrozen = isDispatcherFrozenOrDeliveredTimeline(groupStatusMarkers);
                      const groupSequenceLabel = getGroupedSegmentSequenceLabel(groupItem);
                      const groupRequiresAspirator = memberSegments.some((segment) => segment?.requiresAspirator);
                      const groupHasScheduledTimeAnchor = memberSegments.some((segment) => hasTimelineScheduledTimeAnchor(segment));
                      const groupManualMixBoundarySegment = memberSegments.find((segment) => segment?.hasManualMixBoundaryBefore) || null;
                      const groupHasManualMixBoundaryBefore = Boolean(groupManualMixBoundarySegment);
                      const groupManualMixBoundaryTitle = groupManualMixBoundarySegment?.manualMixBoundaryTitle || "Changement de produit";
                      const groupProductLabels = resolveGroupedSegmentProductLabels(memberSegments);
                      const groupHasMultipleProducts = groupProductLabels.length > 1;
                      const groupProductRunLabel = groupHasMultipleProducts ? groupProductLabels[0] : productRunStartLabels.get(groupItem) || "";
                      const groupDisplayedProductLabels = groupHasMultipleProducts
                        ? groupProductLabels
                        : groupProductRunLabel ? [groupProductRunLabel] : [];
                      const groupOutlineTone = getTimelineOutlineTone({
                        hasScheduledTimeAnchor: groupHasScheduledTimeAnchor,
                        requiresAspirator: groupRequiresAspirator,
                      });
                      const groupedStatusSummaryLabel = groupStatusMarkers.map((marker) => marker.label).join(" · ");
                      const groupedStatusMaxVisible = groupWidthPercent >= 16 ? 3 : groupWidthPercent >= 11 ? 2 : 1;
                      const groupPriorityLabels = getGroupedSegmentPriorityLabels(groupItem);
                      const groupPriorityLabel = groupPriorityLabels[0] || "";
                      const compactGroupLabel = isInformationalGroup
                        ? ""
                        : groupItem.toLabel || "Arrêt groupé";
                      const groupPriorityTitle = groupPriorityLabels.length > 1
                        ? `Priorités du groupe : ${groupPriorityLabels.join(", ")}`
                        : groupPriorityLabel
                          ? `Priorité ${groupPriorityLabel}`
                          : "";
                      const groupContextBadges = buildGroupedTimelineContextBadges(memberSegments, { routeAlternativesFeatureEnabled })
                        .filter((badge) => badge?.tone !== "product-change");
                      const groupFleetRibbon = groupItem?.isEndOfDayLoad || groupItem?.isEndOfDayLoadSourcePreload
                        ? <EndOfDayLoadFleetRibbon compact showChargedPmLabel={isInclined} visualMode={resolveEndOfDayLoadDisplayMode(groupItem)} {...resolveEndOfDayLoadLabelOptions(groupItem)} />
                        : groupItem?.isInitialPreload
                          ? <InitialPreloadFleetRibbon compact compartmentFillBadge={isInclined ? null : buildCompartmentFillGroupTimelineBadge(memberSegments)} />
                          : null;
                      const groupHasManualBufferBadge = groupManualBufferMinutes > 0;
                      const groupPortalStatusMarker = getTimelinePortalStatusMarker(groupStatusMarkers);
                      const groupProtectionStatusMarker = getTimelineProtectionStatusMarker(groupStatusMarkers);
                      const groupFrozenLockMarker = getTimelineFrozenLockStatusMarker(groupStatusMarkers);
                      const compactGroupContextBadge = groupContextBadges[0] || null;
                      const showCompactGroupFrozenMarker = Boolean(groupFrozenLockMarker);
                      const showCompactGroupPriorityBadge = !showCompactGroupFrozenMarker && !groupHasManualBufferBadge && Boolean(groupPriorityLabel) && groupWidthPercent >= 6.5;
                      const showCompactGroupContextBadge = !showCompactGroupFrozenMarker && !showCompactGroupPriorityBadge && Boolean(compactGroupContextBadge) && groupWidthPercent >= 6.5;
                      const showCompactGroupPortalMarker = !showCompactGroupFrozenMarker && useCompactGroupLayout && Boolean(groupPortalStatusMarker);
                      const showCompactGroupProtectionMarker = !showCompactGroupFrozenMarker && Boolean(groupProtectionStatusMarker) && !showCompactGroupPortalMarker;
                      const showCompactGroupStatusMarker = useCompactGroupLayout
                        && !showCompactGroupFrozenMarker
                        && !showCompactGroupPriorityBadge
                        && !showCompactGroupPortalMarker
                        && !showCompactGroupProtectionMarker
                        && !showCompactGroupContextBadge
                        && groupStatusMarkers.length > 0
                        && groupWidthPercent >= 6.5;
                      const compactGroupIndicatorCount = (showCompactGroupFrozenMarker ? 1 : 0)
                        + (showCompactGroupPriorityBadge ? 1 : 0)
                        + (showCompactGroupPortalMarker ? 1 : 0)
                        + (showCompactGroupProtectionMarker ? 1 : 0)
                        + (!showCompactGroupFrozenMarker && !showCompactGroupPriorityBadge && !showCompactGroupPortalMarker && !showCompactGroupProtectionMarker && showCompactGroupContextBadge ? 1 : 0)
                        + (!showCompactGroupFrozenMarker && !showCompactGroupPriorityBadge && !showCompactGroupPortalMarker && !showCompactGroupProtectionMarker && !showCompactGroupContextBadge && showCompactGroupStatusMarker ? 1 : 0);
                      const compactGroupContentPaddingClassName = compactGroupIndicatorCount > 1
                        ? "pr-11"
                        : compactGroupIndicatorCount === 1
                          ? "pr-6"
                          : "";
                      const hoverRouteSegmentIndex = getFleetGroupedRouteHoverSegmentIndex(groupItem);
                      const groupRouteNumber = Number(memberSegments[0]?.globalSequence || groupItem?.startSequence);
                      const canZoomToGroupStart = Number.isInteger(groupRouteNumber) && groupRouteNumber > 0;
                      const groupHasReorderMembers = memberSegments.some((seg) => Boolean(seg?.segmentReorder?.stepId && seg?.segmentReorder?.stepType && seg?.segmentReorder?.tripId));
                      const groupReorderAnchor = groupHasReorderMembers
                        ? memberSegments.find((seg) => Boolean(seg?.segmentReorder?.stepId && seg?.segmentReorder?.stepType && seg?.segmentReorder?.tripId)) || null
                        : null;
                      const groupReorderConfig = groupReorderAnchor?.segmentReorder || null;
                      const groupReorderMembers = groupHasReorderMembers
                        ? memberSegments.filter((seg) => Boolean(seg?.segmentReorder?.stepId && seg?.segmentReorder?.stepType && seg?.segmentReorder?.tripId))
                        : [];
                      const groupReorderFirstMember = groupReorderMembers[0] || null;
                      const groupReorderLastMember = groupReorderMembers[groupReorderMembers.length - 1] || null;
                      const endOfDayLoadGroupActionSegment = isInformationalEndOfDayLoadGroup
                        ? memberSegments.find((segment) => normalizeSegmentMetaValue(segment?.endOfDayLoadMarkerKey))
                          || memberSegments[0]
                          || null
                        : null;
                      const endOfDayLoadGroupActionKey = endOfDayLoadGroupActionSegment
                        ? resolveRowSegmentActionKey(endOfDayLoadGroupActionSegment)
                        : "";
                      const canOpenEndOfDayLoadGroupAction = Boolean(endOfDayLoadGroupActionKey);
                      const initialPreloadGroupActionSegment = isInformationalInitialPreloadGroup
                        ? memberSegments[0] || null
                        : null;
                      const initialPreloadGroupActionKey = initialPreloadGroupActionSegment
                        ? resolveRowSegmentActionKey(initialPreloadGroupActionSegment)
                        : "";
                      const canOpenInitialPreloadGroupAction = Boolean(initialPreloadGroupActionKey);
                      const groupActionPresentation = !isInformationalGroup
                        ? buildTimelineGroupActionPresentation(groupItem, memberSegments[0] || null, {
                            assignmentId: row.assignmentId,
                          })
                        : null;
                      const groupActionKey = groupActionPresentation
                        ? resolveRowSegmentActionKey(groupActionPresentation)
                        : "";
                      const canOpenGroupAction = Boolean(groupActionPresentation && groupActionKey);
                      const groupInteractionOptions = {
                        blockedPlanningMovementFeedback,
                        reassignmentDragState,
                        segmentReorderDragState,
                        fleetSegmentPressState,
                        assignmentId: row.assignmentId,
                        displayDispatchPeriod: normalizedDisplayDispatchPeriod,
                        groupKey: groupItem.groupKey,
                      };
                      const isBlockedPlanningMovementGroup = isFleetTimelineBlockedPlanningMovementSource(
                        groupInteractionOptions,
                      );
                      const groupInteractionClassName = getFleetTimelineSegmentInteractionClassName(
                        groupInteractionOptions,
                      );
                      const isGroupReorderDropTarget = Boolean(
                        isSegmentReorderDragVisibleForDisplayPeriod
                        && groupReorderConfig
                        && segmentReorderDragState?.sourceAssignmentId === row.assignmentId
                        && segmentReorderDragState?.hoveredStepId
                        && (
                          segmentReorderDragState.hoveredStepId === groupReorderConfig.stepId
                          || (
                            segmentReorderDragState?.hoveredGroupKey
                            && segmentReorderDragState.hoveredGroupKey === groupItem.groupKey
                          )
                        ),
                      );
                      const groupReorderInsertionStyle = isGroupReorderDropTarget
                        ? resolveSegmentReorderInsertionStyle(segmentReorderDragState?.hoveredPlacement)
                        : null;

                      return (
                        <HoverCard
                          key={groupItem.key}
                          title={isInformationalEndOfDayLoadGroup
                            ? (groupSequenceLabel
                              ? `${getEndOfDayLoadModeLongLabelForSegment(groupItem)} · ${groupSequenceLabel}`
                              : `${getEndOfDayLoadModeLongLabelForSegment(groupItem)} · ${groupItem.sameSiteGroupSize} élément${groupItem.sameSiteGroupSize > 1 ? "s" : ""}`)
                            : (groupSequenceLabel ? `Groupe · ${groupSequenceLabel}` : `Groupe · ${groupItem.sameSiteGroupSize} étape${groupItem.sameSiteGroupSize > 1 ? "s" : ""}`)}
                          content={(
                            <GroupedSegmentHoverCardContent
                              row={row}
                              groupItem={groupItem}
                              displayOverrides={sequenceDisplayOverrides}
                              hovercardContext={fleetSegmentHovercardContext}
                              comparisonPayloadByKey={fleetSegmentComparisonByKey}
                              dispatchDate={activeDispatchDate}
                              sequenceOpeningVisibilityByItemKey={sequenceOpeningVisibilityByItemKey}
                              sequenceClosingVisibilityByItemKey={sequenceClosingVisibilityByItemKey}
                              sequenceRenderItems={sequenceRenderItems}
                              truckLabelById={truckLabelById}
                            />
                          )}
                          accentColor={getGroupedSegmentAccentColor(
                            groupItem.stopType,
                            groupItem.isInitialPreload,
                            groupItem.isEndOfDayLoad || groupItem.isEndOfDayLoadSourcePreload,
                            resolveEndOfDayLoadVisualMode(groupItem),
                          )}
                          disabled={shouldDisableHoverCards || (!isInformationalGroup && expandedTimelineGroupKey === groupItem.groupKey)}
                          delay={240}
                          freezeAfterMs={1600}
                          placement="top"
                          {...FLEET_SEGMENT_HOVER_CARD_LAYOUT_PROPS}
                          referenceAsChild
                        >
                          <button
                            type="button"
                            onClick={(event) => {
                              if (isBlockedPlanningMovementGroup) {
                                event.preventDefault();
                                event.stopPropagation();
                                return;
                              }

                              if (consumeSegmentReorderClickSuppression?.()) {
                                event.preventDefault();
                                event.stopPropagation();
                                return;
                              }

                              if (consumeGroupedSegmentClickSuppression?.()) {
                                event.preventDefault();
                                event.stopPropagation();
                                return;
                              }

                              event.preventDefault();
                              event.stopPropagation();

                              if (isInformationalGroup) {
                                if (isInformationalEndOfDayLoadGroup && canOpenEndOfDayLoadGroupAction) {
                                  onOpenSegmentPopover?.(endOfDayLoadGroupActionKey, event.currentTarget);
                                } else if (isInformationalInitialPreloadGroup && canOpenInitialPreloadGroupAction) {
                                  onOpenSegmentPopover?.(initialPreloadGroupActionKey, event.currentTarget);
                                }
                                return;
                              }

                              onExpandTimelineGroup?.(row, groupItem, "click");

                              if (canZoomToGroupStart) {
                                onRouteSequenceSelect?.({
                                  routeNumber: groupRouteNumber,
                                  routeSegmentIndex: hoverRouteSegmentIndex,
                                });
                              }
                            }}
                            onContextMenu={(event) => {
                              if (isInformationalGroup) {
                                return;
                              }

                              event.preventDefault();
                              event.stopPropagation();
                              if (canOpenGroupAction) {
                                onOpenSegmentPopover?.(
                                  groupActionKey,
                                  event.currentTarget,
                                  groupActionPresentation,
                                );
                              }
                            }}
                            onBlur={() => {
                              onRouteHoverEnd?.();
                            }}
                            onFocus={() => {
                              if (row.isSelected && hoverRouteSegmentIndex != null) {
                                onRouteSegmentHoverStart?.(hoverRouteSegmentIndex);
                              }
                            }}
                            onMouseEnter={() => {
                              if (row.isSelected && hoverRouteSegmentIndex != null) {
                                onRouteSegmentHoverStart?.(hoverRouteSegmentIndex);
                              }
                            }}
                            onMouseLeave={() => {
                              onRouteHoverEnd?.();
                            }}
                            onPointerDown={(event) => {
                              if (isInclined || isInformationalGroup) {
                                return;
                              }

                              onGroupedSegmentPointerDown?.(event, row, groupItem);
                            }}
                            className={joinClasses([
                              "fleet-timeline-segment absolute top-0.5 flex h-7.5 items-center rounded-[4px] px-1 transition focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2",
                              isInformationalGroup ? "justify-center text-center" : "text-left",
                              groupItem?.isInitialPreload || groupItem?.isEndOfDayLoad || groupItem?.isEndOfDayLoadSourcePreload || (useCompactGroupLayout && (showCompactGroupProtectionMarker || showCompactGroupFrozenMarker)) ? "overflow-visible" : "overflow-hidden",
                              groupIsDispatcherFrozen && !isInformationalGroup
                                ? getFrozenTimelineSegmentClassName(groupItem.stopType, isSelectedGroup)
                                : getGroupedSegmentClassName(
                                  groupItem.stopType,
                                  isSelectedGroup,
                                  groupItem.isInitialPreload,
                                  groupItem.isEndOfDayLoad || groupItem.isEndOfDayLoadSourcePreload,
                                  resolveEndOfDayLoadVisualMode(groupItem),
                                ),
                              isLinkedGroup ? "ring-2 ring-[rgba(56,99,142,0.22)] animate-linked-step-emphasis" : "",
                              isActivationHighlightedGroup ? "z-10 ring-2 ring-[rgba(217,119,6,0.42)] shadow-[0_0_0_1px_rgba(217,119,6,0.14)] motion-safe:animate-[fleet-timeline-drop-highlight_0.92s_ease-in-out_5]" : "",
                              isInformationalEndOfDayLoadGroup && canOpenEndOfDayLoadGroupAction
                                ? "cursor-pointer"
                                : isInformationalInitialPreloadGroup && canOpenInitialPreloadGroupAction
                                  ? "cursor-pointer"
                                : isInformationalGroup ? "cursor-help" : "",
                              groupHasReorderMembers && !isInformationalGroup ? "cursor-grab active:cursor-grabbing" : "",
                              groupInteractionClassName,
                            ])}
                            style={{
                              ...(groupReorderInsertionStyle || {}),
                              left: `${groupPlacement.leftPercent}%`,
                              // Groupe qui porte la pause dîner : son texte s'arrête au bloc « Dîner ».
                              paddingRight: groupItem.lunchBreakInsetPercent > 0 ? `${groupItem.lunchBreakInsetPercent}%` : undefined,
                              width: `${groupWidthPercent}%`,
                            }}
                            draggable={false}
                            data-fleet-timeline-group="true"
                            data-group-key={groupItem.groupKey}
                            data-fleet-timeline-item-key={groupItem.key}
                            data-planning-movement-feedback={isBlockedPlanningMovementGroup ? "blocked" : undefined}
                            data-optimization-preview-source-order-ids={optimizationPreviewSourceOrderIds.length ? optimizationPreviewSourceOrderIds.join(",") : undefined}
                            data-fleet-segment-reorder={groupReorderConfig ? "true" : undefined}
                            data-segment-reorder-assignment-id={groupReorderConfig ? row.assignmentId : undefined}
                            data-segment-reorder-step-id={groupReorderConfig?.stepId || undefined}
                            data-segment-reorder-step-type={groupReorderConfig?.stepType || undefined}
                            data-segment-reorder-trip-id={groupReorderConfig?.tripId || undefined}
                            data-segment-reorder-scope={groupReorderConfig?.scope || undefined}
                            data-segment-reorder-delivery-block-id={groupReorderConfig?.deliveryBlockId || undefined}
                            data-segment-reorder-order-id={groupReorderConfig?.orderId || undefined}
                            data-segment-reorder-delivery-id={groupReorderConfig?.deliveryId || undefined}
                            data-segment-reorder-order-label={groupReorderConfig ? (groupReorderAnchor?.orderLabel || undefined) : undefined}
                            data-segment-reorder-parent-loading-step-id={groupReorderConfig?.parentLoadingStepId || undefined}
                            data-segment-reorder-segment-id={groupReorderConfig?.segmentId || undefined}
                            data-segment-reorder-segment-label={groupReorderConfig ? getSequenceOperationLabel(groupItem, groupReorderAnchor) : undefined}
                            data-segment-reorder-before-step-id={groupReorderFirstMember?.segmentReorder?.stepId || undefined}
                            data-segment-reorder-before-order-id={groupReorderFirstMember?.segmentReorder?.orderId || undefined}
                            data-segment-reorder-before-delivery-id={groupReorderFirstMember?.segmentReorder?.deliveryId || undefined}
                            data-segment-reorder-before-parent-loading-step-id={groupReorderFirstMember?.segmentReorder?.parentLoadingStepId || undefined}
                            data-segment-reorder-before-manual-mix-block-index={
                              groupReorderFirstMember?.segmentReorder && Number.isInteger(groupReorderFirstMember.segmentReorder.manualMixBlockIndex)
                                ? String(groupReorderFirstMember.segmentReorder.manualMixBlockIndex)
                                : undefined
                            }
                            data-segment-reorder-after-step-id={groupReorderLastMember?.segmentReorder?.stepId || undefined}
                            data-segment-reorder-after-order-id={groupReorderLastMember?.segmentReorder?.orderId || undefined}
                            data-segment-reorder-after-delivery-id={groupReorderLastMember?.segmentReorder?.deliveryId || undefined}
                            data-segment-reorder-after-parent-loading-step-id={groupReorderLastMember?.segmentReorder?.parentLoadingStepId || undefined}
                            data-segment-reorder-after-manual-mix-block-index={
                              groupReorderLastMember?.segmentReorder && Number.isInteger(groupReorderLastMember.segmentReorder.manualMixBlockIndex)
                                ? String(groupReorderLastMember.segmentReorder.manualMixBlockIndex)
                                : undefined
                            }
                            data-segment-reorder-group-key={groupReorderConfig ? (groupItem.groupKey || "") : undefined}
                            data-segment-reorder-joinable={groupReorderConfig?.scope === "loads" && isJoinableLoadSegment(groupReorderFirstMember) ? "true" : undefined}
                            aria-description={groupProductLabels.length ? `Produits : ${groupProductLabels.join(", ")}` : undefined}
                            aria-label={`${groupItem.sameSiteGroupSize} étapes au même arrêt ${groupItem.toLabel || "groupé"}.${groupHasManualMixBoundaryBefore ? ` ${groupManualMixBoundaryTitle}.` : ""}${groupManualBufferMinutes > 0 ? ` Buffer manuel: +${groupManualBufferMinutes} min.` : ""}${groupRequiresAspirator ? " Aspiration requise sur au moins une commande." : ""}${groupedStatusSummaryLabel ? ` Statuts: ${groupedStatusSummaryLabel}.` : ""}${groupPriorityLabel ? ` ${groupPriorityLabels.length > 1 ? `Priorités: ${groupPriorityLabels.join(", ")}.` : `Priorité: ${groupPriorityLabel}.`}` : ""}${isInformationalEndOfDayLoadGroup ? ` Mode ${getEndOfDayLoadModeLongLabelForSegment(groupItem)}. Cliquer pour retirer le PM.` : isInformationalInitialPreloadGroup ? " Cliquer pour annuler le Preload." : " Cliquer pour éclater le groupe. Clic droit pour ouvrir les actions du groupe."}`}
                          >
                            {isInclined ? (
                              <TimelinePerspectiveIndicators
                                markers={groupStatusMarkers} contextBadges={groupContextBadges}
                                productLabels={groupDisplayedProductLabels}
                                productTitle={groupManualMixBoundaryTitle}
                                priorityLabel={groupPriorityLabel} priorityTitle={groupPriorityTitle}
                                ribbon={groupFleetRibbon}
                              />
                            ) : null}
                            {isBlockedPlanningMovementGroup ? (
                              <span
                                key={blockedPlanningMovementFeedback.attemptNonce}
                                aria-hidden="true"
                                className={BLOCKED_PLANNING_MOVEMENT_PULSE_CLASS_NAME}
                              />
                            ) : null}
                            <TimelineProductRunStripe label={groupProductRunLabel} labels={groupProductLabels} />
                            {groupItem.servicePercent > 0 ? (
                              <span
                                aria-hidden="true"
                                className={joinClasses([
                                  "pointer-events-none absolute inset-y-0 left-0 rounded-l-[4px] border-r border-[color-mix(in_srgb,currentColor_18%,transparent)]",
                                  getGroupedTimelineServiceOverlayClassName(
                                    groupItem.stopType,
                                    groupIsDispatcherFrozen && !isInformationalGroup,
                                  ),
                                ])}
                                style={{ width: getGroupedTimelineServiceOverlayWidth(groupItem.servicePercent) }}
                              />
                            ) : null}
                            {groupPriorityLabel && !groupHasScheduledTimeAnchor ? (
                              <span
                                aria-hidden="true"
                                className={joinClasses([
                                  "pointer-events-none absolute inset-x-0 top-0 z-10 h-[2px]",
                                  TIMELINE_PRIORITY_STRIPE_CLASS_NAMES[groupPriorityLabel] || TIMELINE_PRIORITY_STRIPE_CLASS_NAMES.P3,
                                ])}
                              />
                            ) : null}
                            {groupOutlineTone
                              ? getTimelineOutlineLayerClassNames(groupOutlineTone).map((className, outlineIndex) => (
                                <span
                                  key={`${groupOutlineTone}:${outlineIndex}`}
                                  aria-hidden="true"
                                  className={joinClasses([
                                    "pointer-events-none",
                                    className,
                                  ])}
                                />
                              ))
                              : null}
                            {useCompactGroupLayout ? (
                              <>
                                <span data-fleet-timeline-segment-copy="true" className={joinClasses([
                                  "relative z-10 flex w-full min-w-0 items-center gap-0.75 uppercase",
                                  FLEET_TIMELINE_SEGMENT_TEXT_CLASS_NAME,
                                  isInformationalGroup ? "justify-center" : "",
                                  isInclined ? "" : compactGroupContentPaddingClassName,
                                ])}>
                                  <TimelineSequenceBadge compact label={groupSequenceLabel} tone="default" />
                                  {compactGroupLabel ? <span className="min-w-0 truncate normal-case font-bold tracking-normal">{compactGroupLabel}</span> : null}
                                </span>
                                {showCompactGroupFrozenMarker || showCompactGroupPriorityBadge || showCompactGroupPortalMarker || showCompactGroupProtectionMarker ? (
                                  <span className="pointer-events-none absolute right-1 top-1/2 z-20 flex -translate-y-1/2 items-center gap-0.5">
                                    {showCompactGroupFrozenMarker ? <TimelineSegmentStatusMarkers markers={[groupFrozenLockMarker]} maxVisible={1} size="compact" /> : null}
                                    {showCompactGroupPriorityBadge ? <TimelinePriorityBadge compact label={groupPriorityLabel} title={groupPriorityTitle} /> : null}
                                    {showCompactGroupPortalMarker ? <TimelineSegmentStatusMarkers markers={[groupPortalStatusMarker]} maxVisible={1} size="compact" /> : null}
                                    {showCompactGroupProtectionMarker ? <TimelineSegmentStatusMarkers markers={[groupProtectionStatusMarker]} maxVisible={1} size="compact" /> : null}
                                  </span>
                                ) : showCompactGroupContextBadge ? (
                                  <span className="pointer-events-none absolute right-0.75 top-1/2 z-10 -translate-y-1/2">
                                    <TimelineContextBadge compact className={compactGroupContextBadge.className} icon={compactGroupContextBadge.icon} label={compactGroupContextBadge.label} style={compactGroupContextBadge.style} title={compactGroupContextBadge.title} tone={compactGroupContextBadge.tone} />
                                  </span>
                                ) : showCompactGroupStatusMarker ? (
                                  <span className="pointer-events-none absolute right-0.75 top-1/2 z-10 -translate-y-1/2">
                                    <TimelineSegmentStatusMarkers markers={groupStatusMarkers} maxVisible={1} size="compact" />
                                  </span>
                                ) : null}
                              </>
                            ) : (
                              <span data-fleet-timeline-segment-copy="true" className={joinClasses([
                                "relative z-10 flex w-full min-w-0 items-center justify-between gap-1 uppercase",
                                FLEET_TIMELINE_SEGMENT_TEXT_CLASS_NAME,
                              ])}>
                                <span className="flex min-w-0 flex-1 items-center gap-1">
                                  <TimelineSequenceBadge label={groupSequenceLabel} tone="default" />
                                  <span className="truncate normal-case font-bold tracking-normal">{groupItem.toLabel || "Arrêt groupé"}</span>
                                </span>
                                <TimelineInlineIndicators
                                  priorityLabel={groupPriorityLabel}
                                  priorityTitle={groupPriorityTitle}
                                  contextBadges={groupContextBadges}
                                  markers={groupStatusMarkers}
                                  maxVisible={groupedStatusMaxVisible}
                                  compact={groupWidthPercent < 11}
                                />
                              </span>
                            )}
                          </button>
                        </HoverCard>
                      );
                    }

                    const segment = item.segment;
                    const hoverRouteSegmentIndexes = resolveAspiratorMissionRouteSegmentIndexes(segment);
                    const isSelectedSegment = Boolean(segment.isSelectedClient || (selectedClientId && segment.clientId === selectedClientId));
                    const isLinkedCommandSegment = isLinkedCommandCompanionSegment(segment, linkedSegmentSelection);
                    const isInformationalEndOfDayLoadSegment = Boolean(segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload);
                    const isPmOperationalLoading = segment?.isPmOperationalLoading === true;
                    const isInformationalSegment = Boolean(segment?.isInitialPreload || isInformationalEndOfDayLoadSegment);
                    const optimizationPreviewSourceOrderId = hasPlanningResetPreview || (segment?.isInitialPreload && !isInformationalEndOfDayLoadSegment)
                      ? normalizeOptimizationPreviewText(segment?.operationOrderId || segment?.orderLabel)
                      : "";
                    const segmentRequiresAspirator = Boolean(segment?.requiresAspirator);
                    const segmentHasScheduledTimeAnchor = hasTimelineScheduledTimeAnchor(segment);
                    const segmentOutlineTone = getTimelineOutlineTone({
                      hasScheduledTimeAnchor: segmentHasScheduledTimeAnchor,
                      requiresAspirator: segmentRequiresAspirator,
                    });
                    const segmentActionKey = resolveRowSegmentActionKey(segment);
                    const isExpandedGroupMember = Boolean(expandedTimelineGroupKey && segment.sameSiteGroupKey === expandedTimelineGroupKey);
                    const segmentPlacement = resolveTimelineItemPlacement(segment.leftPercent, segment.widthPercent);
                    const widthPercent = segmentPlacement.widthPercent;
                    const routeNumber = Number(segment.globalSequence);
                    const canZoomToRouteBadge = !segment.isSelectable && Number.isInteger(routeNumber) && routeNumber > 0;
                    const hasManualMixBoundaryBefore = Boolean(segment?.hasManualMixBoundaryBefore);

                    if (
                      segment.isTerminalRouteMarker
                      || shouldHideOptimizationPreviewInspection(segment, {
                        preview: optimizationPreview,
                        previewActive: hasOptimizationPreview,
                      })
                    ) {
                      return null;
                    }

                    const allStatusMarkers = Array.isArray(segment.statusMarkers) ? segment.statusMarkers : [];
                    const segmentIsDispatcherFrozen = isDispatcherFrozenOrDeliveredTimeline(allStatusMarkers);
                    const useMicroSequenceLayout = widthPercent < 1.4;
                    const useCompactSequenceLayout = widthPercent < 6.5;
                    const useCenteredSequenceLayout = widthPercent < 8.5;
                    const isMergedSameAddressSegment = isMergedOperationalMissionSegment(segment);
                    const endOfDayLoadVisualMode = resolveEndOfDayLoadVisualMode(segment);
                    const segmentHoverAccentColor = isMergedSameAddressSegment
                      ? getGroupedSegmentAccentColor("mixed", segment.isInitialPreload, isInformationalEndOfDayLoadSegment || isPmOperationalLoading, endOfDayLoadVisualMode)
                      : getSegmentHoverCardAccentColor(segment.stopType, segment.isInitialPreload, isInformationalEndOfDayLoadSegment || isPmOperationalLoading, endOfDayLoadVisualMode);
                    const segmentClassName = isPmOperationalLoading
                      ? getSegmentClassName(segment.stopType, isSelectedSegment, false, true, endOfDayLoadVisualMode)
                      : segmentIsDispatcherFrozen && !isInformationalSegment
                      ? getFrozenTimelineSegmentClassName(segment.stopType, isSelectedSegment, isMergedSameAddressSegment)
                      : isMergedSameAddressSegment
                        ? getMergedSameAddressSegmentClassName(isSelectedSegment)
                        : getSegmentClassName(segment.stopType, isSelectedSegment, segment.isInitialPreload, isInformationalEndOfDayLoadSegment, endOfDayLoadVisualMode);
                    const segmentServiceOverlayClassName = isPmOperationalLoading
                      ? "bg-current/10"
                      : isMergedSameAddressSegment
                      ? getGroupedTimelineServiceOverlayClassName("mixed", segmentIsDispatcherFrozen && !isInformationalSegment)
                      : getTimelineServiceOverlayClassName(segment.stopType, segmentIsDispatcherFrozen && !isInformationalSegment);
                    const displaySequenceLabel = getDisplayedTimelineSequenceLabel(segment) || segment.globalSequence;
                    const timelineSequenceAnnouncement = getTimelineSegmentSequenceAnnouncement(segment, displaySequenceLabel);
                    const mergedSameAddressBadgeLabel = getSameAddressMergedTimelineBadgeLabel(segment, widthPercent);
                    const inlineStatusMarkers = allStatusMarkers;
                    const statusMaxVisible = widthPercent >= 16 ? 3 : widthPercent >= 10 ? 2 : 1;
                    const compactSequenceTone = useCenteredSequenceLayout ? "default" : getTimelineSequenceBadgeTone(allStatusMarkers);
                    const segmentProductRunLabel = productRunStartLabels.get(item) || "";
                    const segmentPriorityLabel = getSegmentPriorityLabel(segment);
                    const segmentPriorityTitle = segmentPriorityLabel ? `Priorité ${segmentPriorityLabel}` : "";
                    const timelineContextBadges = buildTimelineContextBadges(segment, { routeAlternativesFeatureEnabled })
                      .filter((badge) => badge?.tone !== "product-change");
                    const segmentFleetRibbon = isPmOperationalLoading
                      ? <EndOfDayLoadFleetRibbon compact showChargedPmLabel={isInclined} visualMode={resolveEndOfDayLoadDisplayMode(segment)} {...resolveEndOfDayLoadLabelOptions(segment)} title={`${getEndOfDayLoadModeLongLabelForSegment(segment)} · Chargé PM`} />
                      : segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload
                        ? <EndOfDayLoadFleetRibbon compact showChargedPmLabel={isInclined} visualMode={resolveEndOfDayLoadDisplayMode(segment)} {...resolveEndOfDayLoadLabelOptions(segment)} />
                        : segment?.isInitialPreload
                          ? <InitialPreloadFleetRibbon compact compartmentFillBadge={isInclined ? null : buildCompartmentFillTimelineBadge(segment)} />
                          : null;
                    const hasManualBufferBadge = Number(segment?.manualBufferMinutes) > 0 && !isLunchBreakSegment(segment);
                    const compactPortalStatusMarker = getTimelinePortalStatusMarker(inlineStatusMarkers);
                    const compactProtectionStatusMarker = getTimelineProtectionStatusMarker(inlineStatusMarkers);
                    const compactFrozenLockMarker = getTimelineFrozenLockStatusMarker(inlineStatusMarkers);
                    const compactContextBadge = timelineContextBadges[0] || null;
                    const manualMixBoundaryAnnouncement = hasManualMixBoundaryBefore
                      ? ` ${segment.manualMixBoundaryTitle || "Changement de produit"}.`
                      : "";
                    const montrealRiskAnnouncement = routeAlternativesFeatureEnabled && segment?.hasMontrealRiskAlert
                      ? ` Alerte: ${segment?.montrealRiskAlert?.suggestedAction || MONTREAL_RISK_BADGE_TITLE}`
                      : "";
                    const segmentTimeAnchorLabel = segment?.hasPriorityTimeAnchor && Number.isFinite(Number(segment?.scheduledAtMinutes))
                      ? formatTimelineClockLabel(segment.scheduledAtMinutes)
                      : "";
                    const showExpandedGroupMemberAspiratorInlineBadge = useCenteredSequenceLayout
                      && !useMicroSequenceLayout
                      && isExpandedGroupMember
                      && segmentRequiresAspirator;
                    const compactSequenceOperationLabel = isInformationalSegment
                      ? ""
                      : getSequenceOperationLabel(segment);
                    const showCompactSequenceFrozenMarker = Boolean(compactFrozenLockMarker);
                    const showCompactSequencePriorityBadge = !showCompactSequenceFrozenMarker
                      && !useMicroSequenceLayout
                      && !hasManualBufferBadge
                      && Boolean(segmentPriorityLabel)
                      && widthPercent >= 3.6;
                    const showCompactSequencePortalMarker = !showCompactSequenceFrozenMarker && Boolean(compactPortalStatusMarker);
                    const showCompactSequenceProtectionMarker = !showCompactSequenceFrozenMarker && Boolean(compactProtectionStatusMarker) && !showCompactSequencePortalMarker;
                    const showCompactSequenceContextBadge = !showCompactSequenceFrozenMarker
                      && !useMicroSequenceLayout
                      && !showCompactSequencePriorityBadge
                      && !showCompactSequencePortalMarker
                      && !showExpandedGroupMemberAspiratorInlineBadge
                      && Boolean(compactContextBadge)
                      && widthPercent >= 3.6;
                    const showCompactSequenceStatusMarker = !showCompactSequenceFrozenMarker
                      && !useMicroSequenceLayout
                      && !showCompactSequencePriorityBadge
                      && !showCompactSequencePortalMarker
                      && !showCompactSequenceContextBadge
                      && inlineStatusMarkers.length > 0
                      && widthPercent >= 3.6;
                    const compactSequenceIndicatorCount = (showCompactSequenceFrozenMarker ? 1 : 0)
                      + (showCompactSequencePriorityBadge ? 1 : 0)
                      + (showCompactSequencePortalMarker ? 1 : 0)
                      + (showCompactSequenceProtectionMarker ? 1 : 0)
                      + (!showCompactSequenceFrozenMarker && !showCompactSequencePriorityBadge && !showCompactSequencePortalMarker && !showCompactSequenceProtectionMarker && showCompactSequenceContextBadge ? 1 : 0)
                      + (!showCompactSequenceFrozenMarker && !showCompactSequencePriorityBadge && !showCompactSequencePortalMarker && !showCompactSequenceProtectionMarker && !showCompactSequenceContextBadge && showCompactSequenceStatusMarker ? 1 : 0);
                    const segmentInteractionStepId = segment?.reassignmentDrag?.stepId || segment?.segmentReorder?.stepId || "";
                    const segmentInteractionOptions = {
                      blockedPlanningMovementFeedback,
                      reassignmentDragState,
                      segmentReorderDragState,
                      fleetSegmentPressState,
                      assignmentId: row.assignmentId,
                      displayDispatchPeriod: normalizedDisplayDispatchPeriod,
                      stepId: segmentInteractionStepId,
                    };
                    const isBlockedPlanningMovementSegment = isFleetTimelineBlockedPlanningMovementSource(
                      segmentInteractionOptions,
                    );
                    const segmentInteractionClassName = getFleetTimelineSegmentInteractionClassName(
                      segmentInteractionOptions,
                    );
                    const isSegmentReorderDropTarget = Boolean(
                      isSegmentReorderDragVisibleForDisplayPeriod
                      && segmentReorderDragState?.sourceAssignmentId === row.assignmentId
                      && segment.segmentReorder?.stepId
                      && segment.segmentReorder.stepId === segmentReorderDragState?.hoveredStepId,
                    );
                    const segmentReorderInsertionStyle = isSegmentReorderDropTarget
                      ? resolveSegmentReorderInsertionStyle(segmentReorderDragState?.hoveredPlacement)
                      : null;
                    const compactSequenceContentPaddingClassName = compactSequenceIndicatorCount > 1
                      ? "pr-11"
                      : compactSequenceIndicatorCount === 1
                        ? "pr-6"
                        : "";
                    // Protection has the same trailing position for single steps and groups.
                    const shouldAnchorStatusAfterService = !useCenteredSequenceLayout
                      && inlineStatusMarkers.length > 0
                      && !compactProtectionStatusMarker
                      && Number(segment.servicePercent) > 0;
                    const serviceEndPercent = shouldAnchorStatusAfterService
                      ? Math.min(Math.max(Number(segment.servicePercent), 0), 100)
                      : 0;
                    // Le bloc « Dîner » recouvre la sortie du service de l'arrêt qui porte la pause.
                    const shouldRightAnchorStatusMarkers = shouldAnchorStatusAfterService
                      && (
                        hasManualBufferBadge
                        || segment.lunchBreakInsetPercent > 0
                        || serviceEndPercent >= 94
                        || (100 - serviceEndPercent) < 12
                      );
                    const statusMarkerAnchorStyle = shouldAnchorStatusAfterService
                      ? shouldRightAnchorStatusMarkers
                        ? {
                            left: "auto",
                            right: "2px",
                            transform: "none",
                          }
                        : {
                            left: `${serviceEndPercent}%`,
                            transform: "translateX(3px)",
                          }
                      : null;
                    const hasTrailingProtectionStacking = showCompactSequenceFrozenMarker
                      || showCompactSequenceProtectionMarker
                      || (shouldAnchorStatusAfterService && Boolean(compactProtectionStatusMarker));
                    const nonCenteredIndicators = !useCenteredSequenceLayout
                      ? (
                        <TimelineInlineIndicators
                          priorityLabel={segmentPriorityLabel}
                          priorityTitle={segmentPriorityTitle}
                          contextBadges={timelineContextBadges}
                          markers={inlineStatusMarkers}
                          maxVisible={statusMaxVisible}
                          compact={widthPercent < 10.5}
                        />
                      )
                      : null;

                    return (
                      <HoverCard
                        key={`${row.assignmentId}::${segment.tripId}::${segment.globalSequence}`}
                        title={buildSegmentHoverCardTitle(segment)}
                        titleTrailing={renderSegmentHoverCardOrderTitleTrailing(segment, row)}
                        content={(
                          <SegmentHoverCardContent
                            row={row}
                            segment={segment}
                            displayOverrides={sequenceDisplayOverrides}
                            hovercardContext={fleetSegmentHovercardContext}
                            comparisonPayloadByKey={fleetSegmentComparisonByKey}
                            dispatchDate={activeDispatchDate}
                            sequenceOpeningVisibilityByItemKey={sequenceOpeningVisibilityByItemKey}
                            sequenceClosingVisibilityByItemKey={sequenceClosingVisibilityByItemKey}
                            sequenceRenderItems={sequenceRenderItems}
                            truckLabelById={truckLabelById}
                          />
                        )}
                        accentColor={segmentHoverAccentColor}
                        disabled={shouldDisableHoverCards}
                        delay={1000}
                        freezeAfterMs={2000}
                        placement="top"
                        {...FLEET_SEGMENT_HOVER_CARD_LAYOUT_PROPS}
                        referenceAsChild
                      >
                        <button
                          ref={(element) => {
                            registerSegmentActionAnchor?.(segmentActionKey, element);
                          }}
                          type="button"
                          onClick={(event) => {
                            if (isBlockedPlanningMovementSegment) {
                              event.preventDefault();
                              event.stopPropagation();
                              return;
                            }

                            if (consumeSegmentReorderClickSuppression?.()) {
                              event.preventDefault();
                              event.stopPropagation();
                              return;
                            }

                            if (consumeReassignmentClickSuppression?.()) {
                              event.preventDefault();
                              event.stopPropagation();
                              return;
                            }

                            event.stopPropagation();
                            if (event.detail > 1) {
                              return;
                            }

                            notifyTimelineGanttInteraction();

                            if (isInformationalSegment && segmentActionKey) {
                              onOpenSegmentPopover?.(segmentActionKey, event.currentTarget, segment);
                              return;
                            }

                            if (segmentActionKey && segment?.businessStep) {
                              onOpenSegmentPopover?.(segmentActionKey, event.currentTarget, segment);
                              if (segment.isSelectable) {
                                onSelectSegment?.(row, segment);
                              }
                              return;
                            }

                            if (isSelectedSegment && segment.isSelectable) {
                              onOpenSegmentPopover?.(segmentActionKey, event.currentTarget, segment);
                              onSelectSegment?.(row, segment);
                              return;
                            }

                            if (isSelectedSegment) {
                              onOpenSegmentPopover?.(segmentActionKey, event.currentTarget, segment);
                              return;
                            }

                            if (segment.isSelectable) {
                              onOpenSegmentPopover?.(segmentActionKey, event.currentTarget, segment);
                              onSelectSegment?.(row, segment);
                              return;
                            }

                              if (canZoomToRouteBadge) {
                                onRouteSequenceSelect?.({
                                  routeNumber,
                                  routeSegmentIndex: segment.routeSegmentIndex,
                                });
                                return;
                              }

                            onSelectAssignment?.(row.assignmentId);
                          }}
                          onDoubleClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onDoubleClickSegment?.(row, segment);
                          }}
                          onContextMenu={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            notifyTimelineGanttInteraction();

                            if (isInformationalSegment && segmentActionKey) {
                              onOpenSegmentPopover?.(segmentActionKey, event.currentTarget, segment);
                              return;
                            }
                            if (segmentActionKey && segment?.businessStep) {
                              onOpenSegmentPopover?.(segmentActionKey, event.currentTarget, segment);
                              if (segment.isSelectable) onSelectSegment?.(row, segment);
                              return;
                            }
                            if (isSelectedSegment && segment.isSelectable) {
                              onOpenSegmentPopover?.(segmentActionKey, event.currentTarget, segment);
                              onSelectSegment?.(row, segment);
                              return;
                            }
                            if (isSelectedSegment) {
                              onOpenSegmentPopover?.(segmentActionKey, event.currentTarget, segment);
                              return;
                            }
                            if (segment.isSelectable) {
                              onOpenSegmentPopover?.(segmentActionKey, event.currentTarget, segment);
                              onSelectSegment?.(row, segment);
                              return;
                            }
                          }}
                            onBlur={() => {
                              onRouteHoverEnd?.();
                            }}
                            onFocus={() => {
                                if (row.isSelected) {
                                  onRouteSegmentHoverStart?.(segment.routeSegmentIndex, hoverRouteSegmentIndexes);
                                }
                            }}
                            onMouseEnter={() => {
                                if (row.isSelected) {
                                  onRouteSegmentHoverStart?.(segment.routeSegmentIndex, hoverRouteSegmentIndexes);
                                }
                            }}
                            onMouseLeave={() => {
                              onRouteHoverEnd?.();
                            }}
                          onPointerDown={(segment.reassignmentDrag || segment.segmentReorder)
                            ? (event) => {
                                if (isInclined || isInformationalSegment) {
                                  return;
                                }
                                onSegmentPointerDown?.(event, row, segment);
                              }
                            : undefined}
                          onDragStart={segment.reassignmentDrag
                            ? (event) => {
                                if (isInclined) { event.preventDefault(); return; }
                                onReassignmentDragStart?.(event, row, segment);
                              }
                            : undefined}
                          onDragEnd={segment.reassignmentDrag ? onReassignmentDragEnd : undefined}
                          className={joinClasses([
                            "fleet-timeline-segment absolute top-0.5 flex h-7.5 items-center rounded-[4px] px-1 transition focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2",
                            isInformationalSegment ? "justify-center text-center" : "text-left",
                            isInformationalSegment || isPmOperationalLoading || showCompactSequenceFrozenMarker || (useCenteredSequenceLayout && showCompactSequenceProtectionMarker) ? "overflow-visible" : "overflow-hidden",
                            segmentClassName,
                            isMergedSameAddressSegment ? "ring-1 ring-white/80 shadow-[0_12px_20px_rgba(15,23,42,0.12)]" : "",
                            isLinkedCommandSegment ? "ring-2 ring-[rgba(56,99,142,0.22)] animate-linked-step-emphasis" : "",
                            hasTrailingProtectionStacking ? "z-20" : "",
                            segment.isActivationHighlighted
                              ? joinClasses([
                                hasTrailingProtectionStacking ? "" : "z-10",
                                "ring-2 ring-[rgba(217,119,6,0.42)] shadow-[0_0_0_1px_rgba(217,119,6,0.14)] motion-safe:animate-[fleet-timeline-drop-highlight_0.92s_ease-in-out_5]",
                              ])
                              : "",
                            segmentInteractionClassName,
                            isInformationalEndOfDayLoadSegment ? "cursor-pointer" : isInformationalSegment ? "cursor-help" : "",
                            segment.reassignmentDrag && !isInformationalSegment ? "cursor-grab active:cursor-grabbing" : "",
                          ])}
                          style={{
                            ...(segmentReorderInsertionStyle || {}),
                            borderWidth: useMicroSequenceLayout ? 0 : undefined,
                            boxShadow: useMicroSequenceLayout ? "none" : undefined,
                            left: `${segmentPlacement.leftPercent}%`,
                            paddingLeft: useMicroSequenceLayout ? 0 : undefined,
                            // Arrêt qui porte la pause dîner : son texte s'arrête au bloc « Dîner ».
                            paddingRight: segment.lunchBreakInsetPercent > 0
                              ? `${segment.lunchBreakInsetPercent}%`
                              : useMicroSequenceLayout && !showCompactSequenceFrozenMarker ? 0 : undefined,
                            width: `${widthPercent}%`,
                          }}
                          draggable={false}
                          data-fleet-timeline-group-member={isExpandedGroupMember ? "true" : undefined}
                          data-group-key={isExpandedGroupMember ? segment.sameSiteGroupKey : undefined}
                          data-fleet-timeline-item-key={item.key}
                          data-planning-movement-feedback={isBlockedPlanningMovementSegment ? "blocked" : undefined}
                          data-optimization-preview-source-order-id={optimizationPreviewSourceOrderId || undefined}
                          data-fleet-timeline-merged-same-address={isMergedSameAddressSegment ? "true" : undefined}
                          data-fleet-segment-reorder={segment.segmentReorder ? "true" : undefined}
                          data-segment-reorder-assignment-id={segment.segmentReorder ? row.assignmentId : undefined}
                          data-segment-reorder-step-id={segment.segmentReorder?.stepId || undefined}
                          data-segment-reorder-step-type={segment.segmentReorder?.stepType || undefined}
                          data-segment-reorder-trip-id={segment.segmentReorder?.tripId || undefined}
                          data-segment-reorder-scope={segment.segmentReorder?.scope || undefined}
                          data-segment-reorder-delivery-block-id={segment.segmentReorder?.deliveryBlockId || undefined}
                          data-segment-reorder-order-id={segment.segmentReorder?.orderId || undefined}
                          data-segment-reorder-delivery-id={segment.segmentReorder?.deliveryId || undefined}
                          data-segment-reorder-order-label={segment.segmentReorder ? (segment.orderLabel || undefined) : undefined}
                          data-segment-reorder-parent-loading-step-id={segment.segmentReorder?.parentLoadingStepId || undefined}
                          data-segment-reorder-segment-id={segment.segmentReorder?.segmentId || undefined}
                          data-segment-reorder-segment-label={segment.segmentReorder ? getSequenceOperationLabel(segment) : undefined}
                          data-segment-reorder-group-key={segment.segmentReorder ? (segment.sameSiteGroupKey || "") : undefined}
                          data-segment-reorder-joinable={segment.segmentReorder?.scope === "loads" && isJoinableLoadSegment(segment) ? "true" : undefined}
                          data-segment-reorder-manual-mix-block-index={
                            segment.segmentReorder && Number.isInteger(segment.segmentReorder.manualMixBlockIndex)
                              ? String(segment.segmentReorder.manualMixBlockIndex)
                              : undefined
                          }
                          data-timeline-segment-action-key={segmentActionKey}
                          aria-label={isInformationalEndOfDayLoadSegment
                            ? `${getEndOfDayLoadModeLongLabelForSegment(segment)}: ${getSequenceOperationLabel(segment)}. Cliquer pour retirer le PM.`
                            : segment.statusSummaryLabel
                            ? `${timelineSequenceAnnouncement} opération ${getSequenceOperationLabel(segment)}. Trajet ${getSequenceRouteSummaryLabel(segment)}.${manualMixBoundaryAnnouncement}${montrealRiskAnnouncement}${Number(segment?.manualBufferMinutes) > 0 ? ` Buffer manuel: +${Number(segment.manualBufferMinutes)} min.` : ""}${segmentRequiresAspirator ? " Aspiration requise." : ""}${segmentPriorityLabel ? ` Priorité: ${segmentPriorityLabel}.` : ""}${segmentTimeAnchorLabel ? ` Horaire cible: ${segmentTimeAnchorLabel}.` : ""} Statuts: ${segment.statusSummaryLabel}`
                            : `${timelineSequenceAnnouncement} opération ${getSequenceOperationLabel(segment)}. Trajet ${getSequenceRouteSummaryLabel(segment)}.${manualMixBoundaryAnnouncement}${montrealRiskAnnouncement}${Number(segment?.manualBufferMinutes) > 0 ? ` Buffer manuel: +${Number(segment.manualBufferMinutes)} min.` : ""}${segmentRequiresAspirator ? " Aspiration requise." : ""}${segmentPriorityLabel ? ` Priorité: ${segmentPriorityLabel}.` : ""}${segmentTimeAnchorLabel ? ` Horaire cible: ${segmentTimeAnchorLabel}.` : ""}`}
                        >
                          {isInclined ? (
                            <TimelinePerspectiveIndicators
                              markers={allStatusMarkers} contextBadges={timelineContextBadges}
                              productLabel={segmentProductRunLabel}
                              productLabels={isPmOperationalLoading && segment.endOfDayLoadProductLabels?.length > 1 ? segment.endOfDayLoadProductLabels : []}
                              productTitle={segment.manualMixBoundaryTitle || segmentProductRunLabel}
                              priorityLabel={segmentPriorityLabel} priorityTitle={segmentPriorityTitle}
                              ribbon={segmentFleetRibbon}
                            />
                          ) : null}
                          {isBlockedPlanningMovementSegment ? (
                            <span
                              key={blockedPlanningMovementFeedback.attemptNonce}
                              aria-hidden="true"
                              className={BLOCKED_PLANNING_MOVEMENT_PULSE_CLASS_NAME}
                            />
                          ) : null}
                          <TimelineProductRunStripe label={segmentProductRunLabel} />
                          {segment.servicePercent > 0 ? (
                            <span
                              aria-hidden="true"
                              className={joinClasses([
                                "pointer-events-none absolute inset-y-0 left-0 rounded-l-[4px] border-r border-[color-mix(in_srgb,currentColor_18%,transparent)]",
                                segmentServiceOverlayClassName,
                              ])}
                              style={{ width: `${segment.servicePercent}%` }}
                            />
                          ) : null}
                          {segment.isActivationHighlighted ? (
                            <span
                              aria-hidden="true"
                              className="pointer-events-none absolute inset-0 bg-[rgba(255,237,213,0.76)]"
                            />
                          ) : null}
                          {segmentPriorityLabel && !segmentHasScheduledTimeAnchor ? (
                            <span
                              aria-hidden="true"
                              className={joinClasses([
                                "pointer-events-none absolute inset-x-0 top-0 z-10 h-[2px]",
                                TIMELINE_PRIORITY_STRIPE_CLASS_NAMES[segmentPriorityLabel] || TIMELINE_PRIORITY_STRIPE_CLASS_NAMES.P3,
                              ])}
                            />
                          ) : null}
                          {segmentOutlineTone
                            ? getTimelineOutlineLayerClassNames(segmentOutlineTone).map((className, outlineIndex) => (
                              <span
                                key={`${segmentOutlineTone}:${outlineIndex}`}
                                aria-hidden="true"
                                className={joinClasses([
                                  "pointer-events-none",
                                  className,
                                ])}
                              />
                            ))
                            : null}
                          {useCenteredSequenceLayout ? (
                            <>
                              <span data-fleet-timeline-segment-copy="true" className={joinClasses([
                                "relative z-10 flex w-full min-w-0 items-center gap-0.75 uppercase",
                                FLEET_TIMELINE_SEGMENT_TEXT_CLASS_NAME,
                                isInformationalSegment ? "justify-center" : "",
                                isInclined ? "" : compactSequenceContentPaddingClassName,
                              ])}>
                                <TimelineSequenceBadge compact={useCompactSequenceLayout} label={displaySequenceLabel} tone={compactSequenceTone} />
                                {showExpandedGroupMemberAspiratorInlineBadge ? (
                                  <span data-fleet-context-badge="true" className="-ml-1.5 inline-flex shrink-0 items-center justify-center rounded-[4px] border border-[rgba(234,88,12,0.22)] bg-[rgba(255,247,237,0.98)] px-1 py-[1px] text-[0.42rem] font-black normal-case leading-none text-[rgb(194,65,12)] shadow-none">
                                    Asp
                                  </span>
                                ) : null}
                                {compactSequenceOperationLabel ? <span className="min-w-0 truncate normal-case font-bold tracking-normal">{compactSequenceOperationLabel}</span> : null}
                                {mergedSameAddressBadgeLabel && !useMicroSequenceLayout ? (
                                  <span className={MERGED_SAME_ADDRESS_BADGE_CLASS_NAME}>
                                    {mergedSameAddressBadgeLabel}
                                  </span>
                                ) : null}
                              </span>
                              {showCompactSequenceFrozenMarker || showCompactSequencePriorityBadge || showCompactSequencePortalMarker || showCompactSequenceProtectionMarker ? (
                                <span className="pointer-events-none absolute right-1 top-1/2 z-20 flex -translate-y-1/2 items-center gap-0.5">
                                  {showCompactSequenceFrozenMarker ? <TimelineSegmentStatusMarkers markers={[compactFrozenLockMarker]} maxVisible={1} size="compact" /> : null}
                                  {showCompactSequencePriorityBadge ? <TimelinePriorityBadge compact label={segmentPriorityLabel} title={segmentPriorityTitle} /> : null}
                                  {showCompactSequencePortalMarker ? <TimelineSegmentStatusMarkers markers={[compactPortalStatusMarker]} maxVisible={1} size="compact" /> : null}
                                  {showCompactSequenceProtectionMarker ? <TimelineSegmentStatusMarkers markers={[compactProtectionStatusMarker]} maxVisible={1} size="compact" /> : null}
                                </span>
                              ) : showCompactSequenceContextBadge ? (
                                <span className="pointer-events-none absolute right-0.75 top-1/2 z-10 -translate-y-1/2 items-center">
                                  <TimelineContextBadge compact className={compactContextBadge.className} icon={compactContextBadge.icon} label={compactContextBadge.label} style={compactContextBadge.style} title={compactContextBadge.title} tone={compactContextBadge.tone} />
                                </span>
                              ) : showCompactSequenceStatusMarker ? (
                                <span
                                  className="pointer-events-none absolute top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center"
                                  style={{ left: "calc(50% + 1rem)" }}
                                >
                                  <TimelineSegmentStatusMarkers markers={inlineStatusMarkers} maxVisible={1} size="compact" />
                                </span>
                              ) : null}
                            </>
                          ) : (
                            <>
                              <span
                                data-fleet-timeline-segment-copy="true"
                                className={joinClasses([
                                  "relative z-10 flex w-full min-w-0 items-center gap-1 uppercase",
                                  FLEET_TIMELINE_SEGMENT_TEXT_CLASS_NAME,
                                  shouldAnchorStatusAfterService ? "justify-start" : "justify-between",
                                ])}
                              >
                                <span className="flex min-w-0 items-center gap-1">
                                  <TimelineSequenceBadge label={displaySequenceLabel} />
                                  {widthPercent >= 8.5 ? <span className="truncate normal-case font-bold tracking-normal">{getSequenceOperationLabel(segment)}</span> : null}
                                  {mergedSameAddressBadgeLabel ? (
                                    <span className={MERGED_SAME_ADDRESS_BADGE_CLASS_NAME}>
                                      {mergedSameAddressBadgeLabel}
                                    </span>
                                  ) : null}
                                </span>
                                {shouldAnchorStatusAfterService ? null : nonCenteredIndicators}
                              </span>
                              {shouldAnchorStatusAfterService ? (
                                <span
                                  className="pointer-events-none absolute inset-y-0 z-10 flex items-center"
                                  style={statusMarkerAnchorStyle || undefined}
                                >
                                  {nonCenteredIndicators}
                                </span>
                              ) : null}
                            </>
                          )}
                        </button>
                      </HoverCard>
                    );
                  })}
                  {!isInclined ? timelineRenderItems.map((item, itemIndex) => {
                    const groupProductLabels = item.kind === "group" ? resolveGroupedSegmentProductLabels(item.memberSegments) : [];
                    const productRunLabel = resolveItemProductTabLabel(item);
                    const itemSource = item.kind === "group" ? item : item.segment;
                    const previousItemSource = timelineRenderItems[itemIndex - 1]?.kind === "group" ? timelineRenderItems[itemIndex - 1] : timelineRenderItems[itemIndex - 1]?.segment;
                    const isEndOfDayLoadSource = (source) => Boolean(source?.isPmOperationalLoading || source?.isEndOfDayLoad || source?.isEndOfDayLoadSourcePreload);
                    // Une seule bannière PM par suite de segments PM consécutifs.
                    const isEndOfDayLoadItem = isEndOfDayLoadSource(itemSource) && !isEndOfDayLoadSource(previousItemSource);
                    // Une seule bannière « Veille » par suite de segments préchargés.
                    const isInitialPreloadItem = !isEndOfDayLoadSource(itemSource) && Boolean(itemSource?.isInitialPreload) && !previousItemSource?.isInitialPreload;
                    if (!productRunLabel && !isEndOfDayLoadItem && !isInitialPreloadItem) {
                      return null;
                    }
                    const itemPlacement = resolveTimelineItemPlacement(itemSource.leftPercent, 0);
                    return (
                      <TimelineSegmentTabs
                        key={`segment-tabs:${item.key}`}
                        endOfDayLoad={isEndOfDayLoadItem
                          ? {
                            isSourcePreload: Boolean(itemSource.isEndOfDayLoadSourcePreload) && !itemSource.isPmOperationalLoading,
                            visualMode: resolveEndOfDayLoadDisplayMode(itemSource),
                            ...resolveEndOfDayLoadLabelOptions(itemSource),
                          }
                          : null}
                        initialPreload={isInitialPreloadItem
                          ? { compartmentFillBadge: item.kind === "group" ? buildCompartmentFillGroupTimelineBadge(item.memberSegments || []) : buildCompartmentFillTimelineBadge(item.segment) }
                          : null}
                        itemKey={item.key}
                        leftPercent={itemPlacement.leftPercent}
                        productLabel={productRunLabel}
                        productLabels={showProductTags ? groupProductLabels : []}
                        roomPercent={productTabRoomPercents.get(item)}
                      />
                    );
                  }) : null}
                  {!isInclined ? (
                    <JoinedLoadsTimelineMarks
                      items={timelineRenderItems}
                      dragState={isSegmentReorderDragVisibleForDisplayPeriod && segmentReorderDragState?.sourceAssignmentId === row.assignmentId ? segmentReorderDragState : null}
                      disabled={Boolean(segmentReorderDragState?.sourceStepId)}
                    />
                  ) : null}
                  {geotabRealMarks.length ? (
                    <GeotabRealTimelineMarks marks={geotabRealMarks} showProgress={geotabReal.showProgress} showDurations={geotabReal.showDurations} links={geotabReal.links} />
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
    {isTruckActionsOpen && canShowTruckActions ? (
      <TruckActionsPopover
        assignment={row.assignment}
        anchorElement={truckActionsAnchorRef.current}
        bulkReassignmentTargets={truckReassignmentTargets}
        currentDriverName={row.assignment?.driver_name || ""}
        dispatchDate={activeDispatchDate}
        dispatchPeriod={normalizedDisplayDispatchPeriod}
        incidents={rowIncidents}
        onClose={closeTruckActions}
        onManageTruckIncidents={closeThen(onManageTruckIncidents)}
        onOptimizeTruckRoutes={closeThen(onOptimizeTruckRoutes)}
        onPrintTruckSheet={typeof onPrintTruckSheet === "function"
          ? (payload = {}) => {
              closeTruckActions();
              onPrintTruckSheet(payload);
            }
          : null}
        onPublishTruckPortal={typeof onPublishTruckPortal === "function"
          ? (route) => {
              closeTruckActions();
              onPublishTruckPortal(route);
            }
          : null}
        onReassignAllTruckOrders={closeThen(onReassignAllTruckOrders)}
        onSetTruckPreloadTime={closeThen(onSetTruckPreloadTime)}
        onUpdateTruckDriverName={closeThen(onUpdateTruckDriverName)}
        onUpdateTruckTractor={closeThen(onUpdateTruckTractor)}
        onUpdateTruckStartTime={closeThen(onUpdateTruckStartTime)}
        onUpdateTruckTimeSettings={typeof onUpdateTruckTimeSettings === "function"
          ? (payload = {}) => {
              closeTruckActions();
              onUpdateTruckTimeSettings(payload);
            }
          : null}
        onToggleTruckDispatchDisabled={closeThen(onToggleTruckDispatchDisabled)}
        onToggleTruckDompeurMixedLoad={closeThen(onToggleTruckDompeurMixedLoad)}
        onToggleTruckOrdersProtection={closeThen(onToggleTruckOrdersProtection)}
        onToggleTruckOvertime={closeThen(onToggleTruckOvertime)}
        onToggleTruckProtection={closeThen(onToggleTruckProtection)}
        onUnpublishTruckPortal={typeof onUnpublishTruckPortal === "function"
          ? (route) => {
              closeTruckActions();
              onUnpublishTruckPortal(route);
            }
          : null}
        portalPublications={portalPublications}
        truckSourceChanges={truckSourceChanges}
        truckSheetPrintDisabledReason={truckSheetPrintDisabledReason}
        truckSheetPrintIsBusy={truckSheetPrintIsBusy}
        truckTimeSettingsDisabledReason={truckTimeSettingsDisabledReason}
      />
    ) : null}
    </>
  );
}

function EveningTimelineRailScale({
  endMinutes = 0,
  errorMessage = "",
  isLoading = false,
  leadingPaddingPercent = 0,
  onRetry = null,
  startMinutes = 0,
  trailingPaddingPercent = 0,
}) {
  const hourTicks = resolveTimelineHourTicks(startMinutes, endMinutes, {
    leadingPaddingPercent,
    trailingPaddingPercent,
  });
  const tickStride = Math.max(Math.ceil(hourTicks.length / 4), 1);
  const displayedTicks = hourTicks.filter((tick, index) => (
    index === 0 || index === hourTicks.length - 1 || index % tickStride === 0
  ));

  return (
    <div
      data-evening-gantt-scale="true"
      className={joinClasses([
        "grid gap-px overflow-hidden rounded-[8px] border border-[rgba(99,102,241,0.18)] bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(238,242,255,0.86))] py-1.4 pr-1.5 text-[0.76rem] font-bold text-text",
        EVENING_TIMELINE_TRUCK_COLUMN_GRID,
      ])}
    >
      <div className="grid content-center gap-px overflow-hidden px-1.5 text-[0.56rem] font-extrabold uppercase tracking-[0.08em] text-indigo-700/80">
        <span>Camion</span>
        <span className="truncate normal-case font-semibold tracking-normal text-content-muted">Chauffeur</span>
      </div>
      <div className="relative h-8 overflow-hidden pr-0.5">
        <span className="absolute bottom-0 left-1 z-10 inline-flex items-center gap-1 rounded-full border border-[rgba(99,102,241,0.24)] bg-[rgba(238,242,255,0.96)] px-1.5 py-0.5 text-[0.56rem] font-extrabold uppercase tracking-[0.1em] text-indigo-700 shadow-[0_1px_2px_rgba(79,70,229,0.08)]">
          <Moon aria-hidden="true" className="h-2.75 w-2.75" strokeWidth={2.2} />
          Soir
        </span>
        <span className="absolute inset-x-0 top-[1.3rem] h-px bg-[rgba(148,163,184,0.28)]" />
        {!isLoading && !errorMessage ? displayedTicks.map((tick) => (
          <React.Fragment key={tick.minutes}>
            <span
              className="absolute top-0 whitespace-nowrap text-[0.64rem] leading-none font-semibold normal-case tracking-normal text-content-copy"
              style={getEveningTimelineTickLabelStyle(tick)}
            >
              {tick.label}
            </span>
            <span
              aria-hidden="true"
              className="absolute top-[1.05rem] h-3.5 w-px bg-[rgba(148,163,184,0.4)]"
              style={{
                left: tick.percent <= 0 ? 0 : tick.percent >= 100 ? "100%" : `${tick.percent}%`,
                transform: tick.percent <= 0 ? "none" : tick.percent >= 100 ? "translateX(-100%)" : "translateX(-50%)",
              }}
            />
          </React.Fragment>
        )) : null}
        {isLoading ? (
          <span className="absolute inset-x-1 bottom-0 truncate text-right text-[0.6rem] font-semibold text-indigo-700">
            Chargement du Soir…
          </span>
        ) : errorMessage ? (
          <span className="absolute inset-x-1 bottom-0 flex items-center justify-end gap-1.5">
            <span className="min-w-0 truncate text-[0.58rem] font-semibold text-danger" title={errorMessage}>{errorMessage}</span>
            <button
              type="button"
              onClick={() => onRetry?.()}
              className="shrink-0 rounded-[5px] border border-[rgba(148,163,184,0.22)] bg-white px-1.5 py-0.5 text-[0.58rem] font-semibold text-content-copy hover:border-border-accent-muted hover:text-text"
            >
              Réessayer
            </button>
          </span>
        ) : null}
      </div>
    </div>
  );
}

function EveningTimelineRailRow({
  row = null,
  ...timelineRowProps
}) {
  if (!row) {
    return null;
  }

  const eveningRow = row.dispatchPeriod === "evening"
    ? row
    : { ...row, dispatchPeriod: "evening" };

  return (
    <FleetTimelineRow
      {...timelineRowProps}
      displayDispatchPeriod="evening"
      isCompactTimelineRail
      row={eveningRow}
      segmentActionKeyPrefix={EVENING_TIMELINE_ACTION_KEY_PREFIX}
    />
  );
}

const FLEET_SEQUENCE_ALIGNED_STOP_PILL_CLASS_NAME = "min-w-[7.1rem] shrink-0 justify-center";

const SEQUENCE_CELL_PILL_TONE_CLASS_NAMES = {
  active: "border-border-accent-strong bg-surface-accent-tint text-(--color-legacy-teal)",
  aspiration: "border-[rgba(234,88,12,0.22)] bg-[rgba(255,247,237,0.98)] text-[rgb(194,65,12)]",
  expansion: "border-[rgba(192,38,211,0.24)] bg-[rgba(253,244,255,0.98)] text-[rgb(162,28,175)]",
  ferry: "border-[rgba(15,118,110,0.24)] bg-[rgba(240,253,250,0.98)] text-[rgb(15,118,110)] shadow-[0_2px_6px_rgba(13,148,136,0.14)]",
  neutral: "border-[rgba(148,163,184,0.2)] bg-[rgba(248,250,252,0.86)] text-content-muted",
  opening: "border-[rgba(37,99,235,0.22)] bg-white text-[rgb(29,78,216)]",
  "product-change": "border-[rgba(13,148,136,0.32)] bg-[rgba(204,251,241,0.98)] text-[rgb(15,118,110)] shadow-[0_2px_6px_rgba(13,148,136,0.16)]",
  schedule: "border-[rgba(220,38,38,0.22)] bg-[rgba(254,242,242,0.98)] text-[rgb(185,28,28)]",
  warning: "border-[rgba(217,119,6,0.22)] bg-[rgba(255,247,237,0.96)] text-[rgb(154,52,18)]",
};

function SequenceCellPill({ active = false, className = "", label = "", style = undefined, title = "", tone = "" }) {
  const resolvedTone = tone || (active ? "active" : "neutral");

  return (
    <span
      title={title || undefined}
      data-route-pill="true"
      className={joinClasses([
        `inline-flex items-center border px-1.75 py-0.5 text-[0.62rem] font-semibold uppercase tracking-[0.06em] ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`,
        SEQUENCE_CELL_PILL_TONE_CLASS_NAMES[resolvedTone] || SEQUENCE_CELL_PILL_TONE_CLASS_NAMES.neutral,
        className,
      ])}
      style={style}
    >
      {label}
    </span>
  );
}

function FleetSequenceSupplementaryMeta({ connectorPill = null, counterpartyLabel = "", indicators = [], summaryLabel = "" }) {
  const resolvedIndicators = Array.isArray(indicators) ? indicators.filter(Boolean) : [];
  const resolvedCounterpartyLabel = typeof counterpartyLabel === "string" ? counterpartyLabel.trim() : "";
  const resolvedSummaryLabel = typeof summaryLabel === "string" ? summaryLabel.trim() : "";
  const hasPillRow = connectorPill || resolvedCounterpartyLabel || resolvedIndicators.length > 0;

  if (!hasPillRow && !resolvedSummaryLabel) {
    return null;
  }

  return (
    <div className="grid min-w-0 gap-0.5 text-[0.63rem] leading-4 text-content-copy">
      {hasPillRow ? (
        <div className="flex min-w-0 flex-wrap items-center gap-1.25 overflow-hidden">
          {connectorPill}
          {resolvedCounterpartyLabel ? (
            <span className="min-w-0 truncate text-[0.63rem] font-medium leading-4 text-content-copy" title={resolvedCounterpartyLabel}>
              {resolvedCounterpartyLabel}
            </span>
          ) : null}
          <SequenceTableIndicatorPills
            indicators={resolvedIndicators}
            pillClassName="shrink-0 px-1.25 py-px text-[0.58rem] tracking-[0.04em]"
          />
        </div>
      ) : null}
      {resolvedSummaryLabel ? (
        <span className="min-w-0 truncate" title={resolvedSummaryLabel}>
          {resolvedSummaryLabel}
        </span>
      ) : null}
    </div>
  );
}

function SequenceTableIndicatorPills({ className = "", indicators = [], pillClassName = "" }) {
  const resolvedIndicators = Array.isArray(indicators) ? indicators.filter(Boolean) : [];

  if (!resolvedIndicators.length) {
    return null;
  }

  return (
    <div className={joinClasses(["flex flex-wrap items-center gap-1", className])}>
      {resolvedIndicators.map((indicator) => {
        const pill = (
          <SequenceCellPill
            key={indicator.key || indicator.label}
            active={indicator.active === true}
            className={pillClassName}
            label={indicator.label || ""}
            style={indicator.style}
            tone={indicator.tone || ""}
            title={indicator.title || ""}
          />
        );
        return indicator.compartmentFill ? (
          <CompartmentFillHoverCard key={indicator.key} {...indicator.compartmentFill}>
            <span className="inline-flex">{pill}</span>
          </CompartmentFillHoverCard>
        ) : pill;
      })}
    </div>
  );
}

export default function FleetViewPanel({
  eveningGanttRailState = { payload: null, isLoading: false, errorMessage: "" },
  assignment = null,
  includeSuspendedDeliveriesInOptimization = false,
  isOptimizationInProgress = false,
  isActiveView = false,
  isEveningRailOpen = false,
  interactions = {},
  mountNode = null,
  onClientSelect = null,
  onEveningRailOpenChange = null,
  onIncludeSuspendedDeliveriesInOptimizationChange = null,
  onMapControllerReady = null,
  onMapRenderMetaChange = null,
  onOptimizationPreviewInteractionLockChange = null,
  onRouteHighlightChange = null,
  selectionRequest = null,
  selectedClientId = "",
  state = null,
  stateRef = null,
  trip = null,
}) {
  const { access, hasPermission, isReady: isAccessReady } = useAccess();
  const canChooseTruckSheetCompartments = isAccessReady && canShowReportCompartments(access, "truck_sheets");
  const floatingMapAnchorRef = React.useRef(null);
  const floatingSuspendedAnchorRef = React.useRef(null);
  const routeMapControllerRef = React.useRef(null);
  const timelineRowsContainerRef = React.useRef(null);
  const haversineRouteWarningDelayTimersRef = React.useRef(new Map());
  const latestHaversineRouteWarningTruckIdsRef = React.useRef(EMPTY_TRUCK_ID_SET);
  const pendingSegmentMapFocusRef = React.useRef(null);
  const activeMapZoomSegmentKeyRef = React.useRef("");
  const openSegmentActionPresentationRef = React.useRef(null);
  const assignmentInteractionsRef = React.useRef(interactions?.assignmentCard || {});
  const hasShownPictureInPictureRestorePromptRef = React.useRef(false);
  const isPageUnloadingRef = React.useRef(false);
  const optimizationPreviewVisitStateRef = React.useRef({
    jobId: "",
    observedActive: false,
  });
  const fullscreenMountNode = getReactPortalRoot();
  const { showToast } = useToast();
  const {
    dismissOptimizationNotice,
    jobState: optimizationJobState,
    planningResetLaunchScope,
    requestOptimizationCancel,
  } = useOptimizationJobTracker();
  const {
    exportState: selectedTruckSheetPdfExportState,
    launchTruckSheetPdfExport: launchSelectedTruckSheetPdfExport,
  } = useTruckSheetPdfExportTracker("selected");
  const { optimizationAnimationEnabled, userId } = useUserUiPreferences();
  const handleColumnPreferencesSaveError = React.useCallback((error) => {
    const errorDetail = error instanceof Error && error.message
      ? error.message
      : "Impossible de sauvegarder les préférences de colonnes.";
    showToast({
      duration: 7000,
      title: "Préférences de colonnes non enregistrées",
      message: `${errorDetail} La dernière configuration synchronisée a été restaurée.`,
      type: "error",
    });
  }, [showToast]);

  const [hideUnusedTrucks, setHideUnusedTrucks] = React.useState(false);
  const [truckIncidentsDialogScope, setTruckIncidentsDialogScope] = React.useState(null);
  const [truckSheetPrintRequest, setTruckSheetPrintRequest] = React.useState(null);
  React.useEffect(() => {
    if (!canChooseTruckSheetCompartments) {
      setTruckSheetPrintRequest((current) => current && current.showCompartments !== true ? { ...current, showCompartments: true } : current);
    }
  }, [canChooseTruckSheetCompartments]);
  const [openSegmentActionKey, setOpenSegmentActionKey] = React.useState("");
  const [expandedSequenceGroupKeys, setExpandedSequenceGroupKeys] = React.useState([]);
  const [expandedTimelineGroupState, setExpandedTimelineGroupState] = React.useState(null);
  const timelineGroupSyncTargetRef = React.useRef(null);
  const suppressedTimelineSelectionSyncRef = React.useRef(null);
  const [routeMapRenderMeta, setRouteMapRenderMeta] = React.useState(null);
  const [routeAlternativePreview, setRouteAlternativePreview] = React.useState(null);
  const [routeMapPictureInPictureMountNode, setRouteMapPictureInPictureMountNode] = React.useState(null);
  const [isRouteMapPictureInPictureLinked, setIsRouteMapPictureInPictureLinked] = React.useState(false);
  const [isRouteMapPictureInPictureExpanded, setIsRouteMapPictureInPictureExpanded] = React.useState(false);
  const [isFleetFullscreen, setIsFleetFullscreen] = React.useState(false);
  const [optimizationProgressOverlayElement, setOptimizationProgressOverlayElement] = React.useState(null);
  const [optimizationProgressOverlayHeight, setOptimizationProgressOverlayHeight] = React.useState(0);
  const [fleetSelection, setFleetSelection] = React.useState(() => ({
    assignmentId: typeof assignment?.truck_id === "string" ? assignment.truck_id : "",
    dispatchPeriod: "day",
  }));
  const [eveningSelectedClientId, setEveningSelectedClientId] = React.useState("");
  const handleCrossPeriodReassignmentSelection = React.useCallback(({
    assignmentId = "",
    dispatchPeriod = "evening",
  } = {}) => {
    const normalizedAssignmentId = typeof assignmentId === "string" ? assignmentId.trim() : "";
    if (!normalizedAssignmentId) {
      return;
    }

    const normalizedDispatchPeriod = dispatchPeriod === "day" ? "day" : "evening";
    setFleetSelection({
      assignmentId: normalizedAssignmentId,
      dispatchPeriod: normalizedDispatchPeriod,
    });
    setEveningSelectedClientId("");
    if (normalizedDispatchPeriod === "day") {
      assignmentInteractionsRef.current?.onSelectAssignment?.(normalizedAssignmentId);
    }
  }, []);
  const [timelineZoomKey, setTimelineZoomKey] = React.useState(DEFAULT_FLEET_TIMELINE_ZOOM_KEY);
  const [timelineQuery, setTimelineQuery] = React.useState("");
  const [persistentHaversineRouteWarningTruckIds, setPersistentHaversineRouteWarningTruckIds] = React.useState(() => new Set());
  const [dashboardTruckCompartmentsByTruckId, setDashboardTruckCompartmentsByTruckId] = React.useState(() => new Map());
  const deferredTimelineQuery = React.useDeferredValue(timelineQuery);
  const normalizedTimelineQuery = normalizeFleetSearch(deferredTimelineQuery);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const markPageUnloading = () => {
      isPageUnloadingRef.current = true;
    };
    const clearPageUnloading = () => {
      isPageUnloadingRef.current = false;
    };

    window.addEventListener("beforeunload", markPageUnloading, true);
    window.addEventListener("pagehide", markPageUnloading, true);
    window.addEventListener("pageshow", clearPageUnloading, true);
    return () => {
      window.removeEventListener("beforeunload", markPageUnloading, true);
      window.removeEventListener("pagehide", markPageUnloading, true);
      window.removeEventListener("pageshow", clearPageUnloading, true);
    };
  }, []);
  const hasTimelineSearch = normalizedTimelineQuery.length > 0;
  const timelineQueryLabel = typeof deferredTimelineQuery === "string" ? deferredTimelineQuery.trim() : "";
  const groupedSegmentCollapseTimerRef = React.useRef(null);
  const groupedSegmentHoldGestureRef = React.useRef(null);
  const segmentPointerGestureRef = React.useRef(null);
  const [fleetSegmentPressState, setFleetSegmentPressState] = React.useState(null);
  const suppressGroupedSegmentClickRef = React.useRef(false);
  const isSharedPlanningMutationActive = React.useSyncExternalStore(
    subscribePlanningMutationActivity,
    getIsPlanningMutationActivityActive,
    () => false,
  );
  const sourceChangesByPeriod = interactions?.chrome?.sourceChangesByPeriod
    || interactions?.sourceChangesByPeriod
    || {};
  const dayTruckSourceChanges = sourceChangesByPeriod?.day?.truckChanges
    || interactions?.chrome?.sourceChanges?.truckChanges
    || interactions?.sourceChanges?.truckChanges
    || state?.resultsData?.source_changes?.truck_changes
    || null;
  // Première vérification M-à-J en cours : les valeurs camion en attente ne sont pas encore connues.
  const activeSourceChanges = interactions?.chrome?.sourceChanges || interactions?.sourceChanges || null;
  const truckTimeSettingsDisabledReason = activeSourceChanges?.canMutate === true
    && activeSourceChanges.truckChanges?.state !== "ready"
    && (activeSourceChanges.isVerifying === true || activeSourceChanges.isLoading === true)
    ? "Vérification M-à-J en cours."
    : "";
  const isSourceChangesPlanningMutationActive = Boolean(
    interactions?.chrome?.sourceChanges?.isMutating === true
    || interactions?.sourceChanges?.isMutating === true
    || sourceChangesByPeriod?.day?.isMutating === true
    || sourceChangesByPeriod?.evening?.isMutating === true,
  );
  const isPlanningMovementLocked = isSharedPlanningMutationActive
    || isSourceChangesPlanningMutationActive;
  const blockedPlanningMovementAttemptNonceRef = React.useRef(0);
  const blockedPlanningMovementNoticeTimerRef = React.useRef(null);
  const blockedPlanningMovementPointerCleanupRef = React.useRef(null);
  const [blockedPlanningMovementFeedback, setBlockedPlanningMovementFeedback] = React.useState(null);

  const clearBlockedPlanningMovementNoticeTimer = React.useCallback(() => {
    if (blockedPlanningMovementNoticeTimerRef.current && typeof window !== "undefined") {
      window.clearTimeout(blockedPlanningMovementNoticeTimerRef.current);
    }
    blockedPlanningMovementNoticeTimerRef.current = null;
  }, []);

  const clearBlockedPlanningMovementPointerTracking = React.useCallback(() => {
    blockedPlanningMovementPointerCleanupRef.current?.();
    blockedPlanningMovementPointerCleanupRef.current = null;
  }, []);

  const resetBlockedPlanningMovementFeedback = React.useCallback(() => {
    clearBlockedPlanningMovementNoticeTimer();
    clearBlockedPlanningMovementPointerTracking();
    setBlockedPlanningMovementFeedback(null);
  }, [clearBlockedPlanningMovementNoticeTimer, clearBlockedPlanningMovementPointerTracking]);

  const startBlockedPlanningMovementFeedback = React.useCallback((event, source = {}) => {
    if (!isPlanningMovementLocked || typeof window === "undefined") {
      return false;
    }

    const sourceElement = event?.currentTarget;
    const sourceRect = typeof sourceElement?.getBoundingClientRect === "function"
      ? sourceElement.getBoundingClientRect()
      : null;
    const pointerClientX = Number.isFinite(event?.clientX)
      ? event.clientX
      : sourceRect
        ? sourceRect.left + (sourceRect.width / 2)
        : 24;
    const pointerClientY = Number.isFinite(event?.clientY)
      ? event.clientY
      : sourceRect
        ? sourceRect.top + (sourceRect.height / 2)
        : 24;
    const dispatchPeriod = source?.dispatchPeriod === "evening" ? "evening" : "day";
    const attemptNonce = blockedPlanningMovementAttemptNonceRef.current + 1;
    blockedPlanningMovementAttemptNonceRef.current = attemptNonce;

    event?.preventDefault?.();
    event?.stopPropagation?.();
    clearBlockedPlanningMovementNoticeTimer();
    clearBlockedPlanningMovementPointerTracking();
    setBlockedPlanningMovementFeedback({
      ...source,
      attemptNonce,
      dispatchPeriod,
      isPointerActive: event?.type === "pointerdown",
      noticeVisible: true,
      pointerClientX,
      pointerClientY,
    });

    const scheduleNoticeHide = () => {
      clearBlockedPlanningMovementNoticeTimer();
      blockedPlanningMovementNoticeTimerRef.current = window.setTimeout(() => {
        blockedPlanningMovementNoticeTimerRef.current = null;
        setBlockedPlanningMovementFeedback((currentFeedback) => (
          currentFeedback?.attemptNonce === attemptNonce
            ? { ...currentFeedback, noticeVisible: false }
            : currentFeedback
        ));
      }, BLOCKED_PLANNING_MOVEMENT_NOTICE_DURATION_MS);
    };

    const hasTrackedPointer = event?.type === "pointerdown"
      && Number.isFinite(Number(event?.pointerId));
    if (!hasTrackedPointer) {
      setBlockedPlanningMovementFeedback((currentFeedback) => (
        currentFeedback?.attemptNonce === attemptNonce
          ? { ...currentFeedback, isPointerActive: false }
          : currentFeedback
      ));
      scheduleNoticeHide();
      return true;
    }

    const pointerId = Number(event.pointerId);
    const cleanupPointerTracking = () => {
      window.removeEventListener("pointermove", handlePointerMove, true);
      window.removeEventListener("pointerup", handlePointerUp, true);
      window.removeEventListener("pointercancel", handlePointerCancel, true);
      window.removeEventListener("blur", handleWindowBlur);
    };
    const updatePointerPosition = (pointerEvent) => {
      if (pointerEvent?.pointerId !== pointerId) {
        return;
      }
      setBlockedPlanningMovementFeedback((currentFeedback) => (
        currentFeedback?.attemptNonce === attemptNonce
          ? {
              ...currentFeedback,
              pointerClientX: Number.isFinite(pointerEvent.clientX) ? pointerEvent.clientX : currentFeedback.pointerClientX,
              pointerClientY: Number.isFinite(pointerEvent.clientY) ? pointerEvent.clientY : currentFeedback.pointerClientY,
            }
          : currentFeedback
      ));
    };
    const finishPointerTracking = (pointerEvent = null) => {
      if (pointerEvent?.pointerId != null && pointerEvent.pointerId !== pointerId) {
        return;
      }
      updatePointerPosition(pointerEvent);
      cleanupPointerTracking();
      if (blockedPlanningMovementPointerCleanupRef.current === cleanupPointerTracking) {
        blockedPlanningMovementPointerCleanupRef.current = null;
      }
      setBlockedPlanningMovementFeedback((currentFeedback) => (
        currentFeedback?.attemptNonce === attemptNonce
          ? { ...currentFeedback, isPointerActive: false }
          : currentFeedback
      ));
      scheduleNoticeHide();
    };
    function handlePointerMove(pointerEvent) {
      updatePointerPosition(pointerEvent);
    }
    function handlePointerUp(pointerEvent) {
      finishPointerTracking(pointerEvent);
    }
    function handlePointerCancel(pointerEvent) {
      finishPointerTracking(pointerEvent);
    }
    function handleWindowBlur() {
      finishPointerTracking();
    }

    window.addEventListener("pointermove", handlePointerMove, true);
    window.addEventListener("pointerup", handlePointerUp, true);
    window.addEventListener("pointercancel", handlePointerCancel, true);
    window.addEventListener("blur", handleWindowBlur);
    blockedPlanningMovementPointerCleanupRef.current = cleanupPointerTracking;
    return true;
  }, [
    clearBlockedPlanningMovementNoticeTimer,
    clearBlockedPlanningMovementPointerTracking,
    isPlanningMovementLocked,
  ]);

  React.useEffect(() => {
    if (!isPlanningMovementLocked) {
      resetBlockedPlanningMovementFeedback();
    }
  }, [isPlanningMovementLocked, resetBlockedPlanningMovementFeedback]);

  React.useEffect(() => () => {
    clearBlockedPlanningMovementNoticeTimer();
    clearBlockedPlanningMovementPointerTracking();
  }, [clearBlockedPlanningMovementNoticeTimer, clearBlockedPlanningMovementPointerTracking]);

  const handleIncludeSuspendedDeliveriesChange = typeof onIncludeSuspendedDeliveriesInOptimizationChange === "function"
    ? onIncludeSuspendedDeliveriesInOptimizationChange
    : null;
  const fleetSegmentHovercardContext = state?.resultsData && typeof state.resultsData.fleet_segment_hovercard_context === "object"
    ? state.resultsData.fleet_segment_hovercard_context
    : null;
  const routeAlternativesFeatureEnabled = isSorelTracyRouteAlternativesFeatureEnabled(fleetSegmentHovercardContext);
  const activeDispatchDate = typeof state?.resultsData?.dispatch_date === "string" && state.resultsData.dispatch_date.trim()
    ? state.resultsData.dispatch_date.trim()
    : readCurrentDispatchDate() || "";
  const truckIncidentsDialogDispatchDate = normalizeFleetIncidentText(truckIncidentsDialogScope?.dispatchDate);
  const isTruckIncidentsDialogCurrentDate = !truckIncidentsDialogDispatchDate
    || !activeDispatchDate
    || truckIncidentsDialogDispatchDate === activeDispatchDate;
  React.useEffect(() => {
    if (!isTruckIncidentsDialogCurrentDate) {
      setTruckIncidentsDialogScope(null);
    }
  }, [isTruckIncidentsDialogCurrentDate]);
  React.useEffect(() => {
    if (!truckSheetPrintRequest) {
      return;
    }

    const requestDispatchDate = normalizeSegmentMetaValue(truckSheetPrintRequest.dispatchDate);
    if (!isActiveView || (requestDispatchDate && activeDispatchDate && requestDispatchDate !== activeDispatchDate)) {
      setTruckSheetPrintRequest(null);
    }
  }, [activeDispatchDate, isActiveView, truckSheetPrintRequest]);
  const activeDispatchPeriod = normalizeSegmentMetaValue(
    state?.resultsData?.ui_dispatch_period || state?.resultsData?.dispatch_period,
  ).toLowerCase();
  const perspective = useFleetTimelinePerspective({
    scopeKey: `${activeDispatchDate}:${activeDispatchPeriod}`,
    enabled: isActiveView,
  });
  const dispatchPeriodToggleEnabled = state?.resultsData?.dispatch_period_toggle_enabled === true;
  const optimizationPreviewMode = typeof optimizationJobState?.previewMode === "string"
    ? optimizationJobState.previewMode.trim().toLowerCase()
    : "off";
  const isOptimizationPreviewActive = optimizationPreviewMode !== "off"
    && ["running", "cancelling"].includes(optimizationJobState?.status);
  const optimizationPreviewEvents = Array.isArray(optimizationJobState?.assignmentPreviewEvents)
    ? optimizationJobState.assignmentPreviewEvents
    : [];
  const optimizationPreviewEventBatchAvailable = optimizationPreviewEvents.length > 0;
  const optimizationPreviewAuthoritativePaintValue = optimizationJobState?.referenceProbe?.latestToFirstPaintMs;
  const eveningRailAuthoritativeRunId = eveningGanttRailState.payload?.optimization_diagnostics?.run_id;
  const isEveningRailResultCurrent = optimizationJobState?.dispatchPeriod === "evening"
    && eveningGanttRailState.payload?.dispatch_date === optimizationJobState?.dispatchDate
    && Boolean(eveningRailAuthoritativeRunId)
    && eveningRailAuthoritativeRunId === optimizationJobState?.optimizationDiagnostics?.run_id;
  const optimizationPreviewAuthoritativePaintSeen = isEveningRailResultCurrent || (optimizationPreviewAuthoritativePaintValue !== null
    && optimizationPreviewAuthoritativePaintValue !== undefined
    && Number.isFinite(Number(optimizationPreviewAuthoritativePaintValue)));
  const optimizationPreviewJobId = typeof optimizationJobState?.jobId === "string"
    ? optimizationJobState.jobId.trim()
    : "";
  const optimizationPreviewVisitState = updateOptimizationPreviewVisitState(
    optimizationPreviewVisitStateRef.current,
    {
      // Observation is a visit/job concern, not a motion-preference concern.
      // A reduced-motion visit must still drain its server cursor and must not
      // replay a terminal batch after a remount.
      active: isOptimizationPreviewActive,
      jobId: optimizationPreviewJobId,
    },
  );
  optimizationPreviewVisitStateRef.current = optimizationPreviewVisitState;
  const optimizationPreviewVisitObserved = optimizationPreviewVisitState.observedActive === true;
  const terminalPreviewMode = optimizationAnimationEnabled ? optimizationPreviewMode : "off";
  const shouldPrioritizeTerminalPreview = shouldPrioritizeOptimizationTerminalPreview({
    assignmentPreview: optimizationJobState?.assignmentPreview,
    observedActive: optimizationPreviewVisitState.observedActive,
    previewMode: terminalPreviewMode,
    resultCommitted: optimizationJobState?.resultCommitted === true,
    status: optimizationJobState?.status,
  });
  const optimizationAssignmentPreviewForPresentation = shouldDeferOptimizationTerminalPreview({
    assignmentPreview: optimizationJobState?.assignmentPreview,
    prioritizeTerminal: shouldPrioritizeTerminalPreview,
  })
    ? null
    : optimizationJobState?.assignmentPreview;
  const optimizationPreviewEventsForPresentation = React.useMemo(() => (
    optimizationPreviewVisitObserved
      ? optimizationPreviewEvents.filter((event) => {
        const preview = event?.preview && typeof event.preview === "object"
          ? event.preview
          : event;
        return !isCanonicalOptimizationTerminalPreview(preview) || shouldPrioritizeTerminalPreview;
        })
      : []
  ), [optimizationPreviewEvents, optimizationPreviewVisitObserved, shouldPrioritizeTerminalPreview]);
  const {
    displayedAt: displayedOptimizationPreviewAt,
    preview: displayedOptimizationPreview,
    queueDepth: optimizationPreviewQueueDepth,
  } = useDisplayedOptimizationPreview({
    active: optimizationAnimationEnabled
      && (isOptimizationPreviewActive
        || optimizationJobState?.assignmentPreviewHasMore === true
        || (optimizationPreviewVisitObserved && optimizationPreviewEventBatchAvailable)
        || shouldPrioritizeTerminalPreview),
    assignmentPreview: optimizationAssignmentPreviewForPresentation,
    assignmentPreviewEvents: optimizationPreviewEventsForPresentation,
    jobId: optimizationPreviewJobId,
    prioritizeTerminal: shouldPrioritizeTerminalPreview,
  });
  const isDisplayedOptimizationPreviewTerminal = shouldPrioritizeTerminalPreview
    && isCanonicalOptimizationTerminalPreview(displayedOptimizationPreview)
    && Number(displayedOptimizationPreview?.revision || 0)
      === Number(optimizationJobState?.assignmentPreview?.revision || 0);
  const isOptimizationPreviewTerminalHandoffActive = useOptimizationPreviewTerminalHandoff({
    assignmentPreview: optimizationJobState?.assignmentPreview,
    authoritativePaintSeen: optimizationPreviewAuthoritativePaintSeen,
    displayedAt: displayedOptimizationPreviewAt,
    displayedPreview: displayedOptimizationPreview,
    jobId: optimizationPreviewJobId,
    observedActive: optimizationPreviewVisitState.observedActive,
    previewMode: terminalPreviewMode,
    resultCommitted: optimizationJobState?.resultCommitted === true,
    status: optimizationJobState?.status,
  });
  const optimizationPreviewLastReceivedRevision = Number(
    optimizationJobState?.assignmentPreviewLastReceivedRevision
      ?? optimizationJobState?.assignmentPreviewCursor
      ?? 0,
  );
  const optimizationPreviewLatestRevision = Number(
    optimizationJobState?.assignmentPreviewLatestRevision || 0,
  );
  const isOptimizationPreviewDiscardedTerminal = ["cancelled", "failed"].includes(
    optimizationJobState?.status,
  ) && optimizationJobState?.resultCommitted !== true;
  const optimizationPreviewRevisionDrainTarget = Math.max(
    Number.isFinite(optimizationPreviewLastReceivedRevision) ? optimizationPreviewLastReceivedRevision : 0,
    Number.isFinite(optimizationPreviewLatestRevision) ? optimizationPreviewLatestRevision : 0,
  );
  const optimizationPreviewDisplayedRevision = optimizationAnimationEnabled
    ? Number(displayedOptimizationPreview?.revision || 0)
    // When motion is disabled there is no presentation queue to advance. The
    // committed assignment remains authoritative, so consider the server
    // cursor painted synchronously for drain/lock purposes.
    : optimizationPreviewRevisionDrainTarget;
  const optimizationPreviewRevisionDrainPending = !isOptimizationPreviewDiscardedTerminal
    && optimizationPreviewRevisionDrainTarget > optimizationPreviewDisplayedRevision;
  const optimizationPreviewDrainPending = !isOptimizationPreviewDiscardedTerminal
    && optimizationPreviewVisitObserved && (
    optimizationJobState?.assignmentPreviewHasMore === true
      || optimizationPreviewQueueDepth > 0
      || optimizationPreviewRevisionDrainPending
  );
  const isOptimizationPreviewVisualActive = optimizationAnimationEnabled
    && (isOptimizationPreviewActive
      || optimizationPreviewDrainPending
      || isOptimizationPreviewTerminalHandoffActive);
  const isOptimizationPreviewInteractionLocked = isOptimizationPreviewActive
    || optimizationPreviewDrainPending
    || isOptimizationPreviewTerminalHandoffActive;
  React.useLayoutEffect(() => {
    onOptimizationPreviewInteractionLockChange?.(isOptimizationPreviewInteractionLocked);
  }, [isOptimizationPreviewInteractionLocked, onOptimizationPreviewInteractionLockChange]);
  React.useLayoutEffect(() => () => {
    onOptimizationPreviewInteractionLockChange?.(false);
  }, [onOptimizationPreviewInteractionLockChange]);
  const displayedOptimizationJobState = React.useMemo(() => (
    isOptimizationPreviewVisualActive && displayedOptimizationPreview
      ? { ...optimizationJobState, assignmentPreview: displayedOptimizationPreview }
      : optimizationJobState
  ), [displayedOptimizationPreview, isOptimizationPreviewVisualActive, optimizationJobState]);
  const optimizationPreviewJobDate = typeof optimizationJobState?.dispatchDate === "string"
    ? optimizationJobState.dispatchDate.trim()
    : "";
  const optimizationPreviewJobPeriod = optimizationJobState?.dispatchPeriod === "evening"
    ? "evening"
    : optimizationJobState?.dispatchPeriod === "day" ? "day" : "";
  const isOptimizationPreviewDateActive = isOptimizationPreviewVisualActive
    && Boolean(activeDispatchDate)
    && optimizationPreviewJobDate === activeDispatchDate;
  const isOptimizationDayPreviewActive = isOptimizationPreviewDateActive
    && optimizationPreviewJobPeriod === "day";
  const isOptimizationEveningPreviewActive = isOptimizationPreviewDateActive
    && optimizationPreviewJobPeriod === "evening";
  const optimizationPreviewRevealedOrderIds = React.useMemo(
    () => collectOptimizationPreviewRevealedOrderIds(displayedOptimizationPreview),
    [displayedOptimizationPreview],
  );
  const optimizationPreviewProtectedOrderIds = React.useMemo(
    () => collectOptimizationPreviewProtectedOrderIds(displayedOptimizationPreview),
    [displayedOptimizationPreview],
  );
  const firstOptimizationOrderCount = Number.isFinite(Number(state?.resultsData?.ui_first_optimization_order_count))
    ? Math.max(0, Math.round(Number(state.resultsData.ui_first_optimization_order_count)))
    : 0;
  const optimizationPreviewResults = optimizationPreviewJobPeriod === "evening" && activeDispatchPeriod !== "evening"
    ? eveningGanttRailState.payload
    : state?.resultsData;
  const optimizationPreviewReserve = React.useMemo(() => {
    const reserve = collectOptimizationPreviewReserveState(
      optimizationPreviewResults?.assignments,
      optimizationPreviewResults || {},
      optimizationPreviewRevealedOrderIds,
      optimizationPreviewProtectedOrderIds,
      { operationKind: optimizationJobState?.operationKind },
    );

    return applyOptimizationPreviewReserveFallback(
      reserve,
      displayedOptimizationPreview,
      optimizationPreviewRevealedOrderIds,
      firstOptimizationOrderCount,
    );
  }, [displayedOptimizationPreview, firstOptimizationOrderCount, optimizationJobState?.operationKind, optimizationPreviewProtectedOrderIds, optimizationPreviewRevealedOrderIds, optimizationPreviewResults]);
  const optimizationExcludedTruckIds = React.useMemo(
    () => new Set(optimizationPreviewReserve.excludedTruckIds),
    [optimizationPreviewReserve.excludedTruckIds],
  );
  const isDayDispatchPeriod = activeDispatchPeriod !== "evening" && activeDispatchPeriod !== "soir";
  // Menu Affichage : réel Geotab (avancement, écarts de durée) et étiquettes de produits.
  const handleDisplaySaveError = React.useCallback(() => {
    showToast({ title: "Affichage non enregistré", message: "Le choix précédent a été rétabli.", type: "error" });
  }, [showToast]);
  const [ganttDisplay, setGanttDisplayOption] = useDispatchDisplayPreferences({ onSaveError: handleDisplaySaveError });
  const canSeeGeotabReal = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.dispatchGeotabRealRead);
  const isTodayDispatch = activeDispatchDate === getLocalTodayDispatchDate();
  const productTagsDefaultOff = isTodayDispatch && canSeeGeotabReal && ganttDisplay.geotab_progress;
  const [sessionProductTags, setSessionProductTags] = useDispatchProductTags(productTagsDefaultOff ? false : ganttDisplay.product_tags, userId);
  const showProductTags = !isTodayDispatch || sessionProductTags;
  const handleGanttDisplayOption = React.useCallback((option, value) => {
    if (option === "product_tags") setSessionProductTags(value);
    else setGanttDisplayOption(option, value);
  }, [setGanttDisplayOption, setSessionProductTags]);
  // Même tolérance que les avis et la section Geotab, réglable dans le menu Affichage.
  const geotabTolerance = useGeotabTolerance(canSeeGeotabReal);
  const canOpenGeotab = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.geotabRead);
  const canOpenGeotabCalibration = isAccessReady && hasPermission(ACCESS_PERMISSION_KEYS.geotabCalibrationRead);
  const geotabRealPeriod = isDayDispatchPeriod ? "day" : "evening";
  const geotabReal = useDispatchGeotabReal({
    date: activeDispatchDate,
    period: geotabRealPeriod,
    revision: state?.resultsData?.runtime_revision || "",
    enabled: canSeeGeotabReal && (ganttDisplay.geotab_progress || ganttDisplay.geotab_durations),
  });
  const geotabGaps = useDispatchGeotabGaps(activeDispatchDate);
  const geotabRealStatus = !geotabReal.key
    ? "Pour aujourd'hui et les journées passées."
    : geotabReal.error || (geotabReal.data ? "" : "Lecture de Geotab…");
  // Liens vers la section Geotab, seulement avec ses propres droits.
  const geotabRealLinks = React.useMemo(() => (canOpenGeotab || canOpenGeotabCalibration ? (mark) => (
    <span className="flex flex-wrap gap-2 text-[0.72rem] font-semibold">
      {canOpenGeotab ? <a className="text-accent underline" href={geotabUrl({ date: activeDispatchDate, period: geotabRealPeriod, truckId: mark.truckId })}>Voir dans Geotab</a> : null}
      {canOpenGeotabCalibration ? <a className="text-accent underline" href={geotabUrl({ view: "calibration" })}>Calibrage</a> : null}
    </span>
  ) : null), [activeDispatchDate, canOpenGeotab, canOpenGeotabCalibration, geotabRealPeriod]);
  const shouldShowEveningRail = isDayDispatchPeriod && isEveningRailOpen;
  const eveningRailWidth = useEveningRailWidth({ enabled: shouldShowEveningRail && isActiveView, userId });
  const activeSuspendedDispatchPeriod = isDayDispatchPeriod ? "day" : "evening";
  const optimizationPreviewByDispatchPeriod = React.useMemo(() => ({
    day: buildOptimizationPreviewByTruckId(displayedOptimizationPreview, {
      dispatchDate: activeDispatchDate,
      dispatchPeriod: "day",
      jobDispatchDate: optimizationJobState?.dispatchDate,
      jobDispatchPeriod: optimizationJobState?.dispatchPeriod,
      retainedOrderIds: optimizationPreviewReserve.retainedOrderIds,
    }),
    evening: buildOptimizationPreviewByTruckId(displayedOptimizationPreview, {
      dispatchDate: activeDispatchDate,
      dispatchPeriod: "evening",
      jobDispatchDate: optimizationJobState?.dispatchDate,
      jobDispatchPeriod: optimizationJobState?.dispatchPeriod,
      retainedOrderIds: optimizationPreviewReserve.retainedOrderIds,
    }),
  }), [
    activeDispatchDate,
    displayedOptimizationPreview,
    optimizationJobState?.dispatchDate,
    optimizationJobState?.dispatchPeriod,
    optimizationPreviewReserve.retainedOrderIds,
  ]);
  const mainOptimizationPreviewByTruckId = isDayDispatchPeriod
    ? optimizationPreviewByDispatchPeriod.day
    : optimizationPreviewByDispatchPeriod.evening;
  const optimizationPreviewActivityPhase = resolveOptimizationPreviewVisualPhase(
    optimizationJobState?.progress?.currentStepKey || displayedOptimizationPreview,
  );
  const isOptimizationMainPreviewActive = isDayDispatchPeriod
    ? isOptimizationDayPreviewActive
    : isOptimizationEveningPreviewActive;
  const shouldShowFirstOptimizationGanttWaiting = state?.resultsData?.ui_first_optimization_waiting === true
    && mainOptimizationPreviewByTruckId.size === 0;
  useOptimizationPreviewMotion({
    active: isOptimizationPreviewDateActive,
    assignmentPreview: displayedOptimizationPreview,
    jobId: optimizationPreviewJobId,
    operationKind: optimizationJobState?.operationKind,
    prioritizeTerminal: shouldPrioritizeTerminalPreview,
    protectedOrderIds: optimizationPreviewReserve.stationaryOrderIds,
    retainedOrderIds: optimizationPreviewReserve.retainedOrderIds,
    queueDepth: optimizationPreviewQueueDepth,
  });
  const transientActivatedDispatchPeriod = state?.transientActivatedDispatchPeriod === "evening"
    ? "evening"
    : state?.transientActivatedDispatchPeriod === "day" ? "day" : "";
  const canHighlightActiveDispatchPeriod = !transientActivatedDispatchPeriod
    || transientActivatedDispatchPeriod === activeSuspendedDispatchPeriod;
  const canHighlightEveningDispatchPeriod = !transientActivatedDispatchPeriod
    || transientActivatedDispatchPeriod === "evening";
  const shouldHighlightSuspendedPanel = state?.transientActivatedHighlightSurface === "suspended";
  const suspendedArrivalHighlightRequest = {
    dispatchDate: activeDispatchDate,
    requestedStepKeys: state?.transientActivatedStepKeys,
    requestedPeriod: transientActivatedDispatchPeriod,
    highlightSurface: state?.transientActivatedHighlightSurface,
  };
  const daySuspendedArrivalKeys = useSuspendedArrivalHighlight({
    ...suspendedArrivalHighlightRequest,
    dispatchPeriod: activeSuspendedDispatchPeriod,
    deliveries: state?.resultsData?.suspended_deliveries,
  });
  const eveningSuspendedArrivalKeys = useSuspendedArrivalHighlight({
    ...suspendedArrivalHighlightRequest,
    dispatchPeriod: "evening",
    deliveries: eveningGanttRailState.payload?.suspended_deliveries,
  });
  const suspendedPanelModel = selectSuspendedPanelData({
    ...(state || {}),
    transientActivatedStepKeys: daySuspendedArrivalKeys,
  }, {
    dispatchPeriod: activeSuspendedDispatchPeriod,
    includeSuspendedDeliveriesInOptimization,
  });
  const dayActivationHighlightTimerRef = React.useRef(null);
  const [retainedDayActivatedStepKeys, setRetainedDayActivatedStepKeys] = React.useState(() => new Set());
  const [capturedDayActivatedStepKeysSignature, setCapturedDayActivatedStepKeysSignature] = React.useState("");
  const eveningActivationHighlightTimerRef = React.useRef(null);
  const [retainedEveningActivatedStepKeys, setRetainedEveningActivatedStepKeys] = React.useState(() => new Set());
  const [capturedEveningActivatedStepKeysSignature, setCapturedEveningActivatedStepKeysSignature] = React.useState("");
  React.useEffect(() => {
    if (dayActivationHighlightTimerRef.current) {
      window.clearTimeout(dayActivationHighlightTimerRef.current);
      dayActivationHighlightTimerRef.current = null;
    }

    if (!isDayDispatchPeriod || !state?.resultsData) {
      setRetainedDayActivatedStepKeys((currentKeys) => (currentKeys.size ? new Set() : currentKeys));
      setCapturedDayActivatedStepKeysSignature("");
      return undefined;
    }

    const currentStepKeys = canHighlightActiveDispatchPeriod && state?.transientActivatedStepKeys instanceof Set
      ? Array.from(state.transientActivatedStepKeys)
      : [];
    if (!currentStepKeys.length) {
      setRetainedDayActivatedStepKeys((currentKeys) => (currentKeys.size ? new Set() : currentKeys));
      setCapturedDayActivatedStepKeysSignature("");
      return undefined;
    }

    // Le délai commence quand le nouveau payload Jour est réellement commis dans le Gantt.
    setRetainedDayActivatedStepKeys(new Set(currentStepKeys));
    setCapturedDayActivatedStepKeysSignature(buildMutableSetSignature(new Set(currentStepKeys)));
    dayActivationHighlightTimerRef.current = window.setTimeout(() => {
      dayActivationHighlightTimerRef.current = null;
      setRetainedDayActivatedStepKeys(new Set());
    }, RENDERED_ACTIVATION_HIGHLIGHT_DURATION_MS);

    return () => {
      if (dayActivationHighlightTimerRef.current) {
        window.clearTimeout(dayActivationHighlightTimerRef.current);
        dayActivationHighlightTimerRef.current = null;
      }
    };
  }, [
    activeDispatchDate,
    canHighlightActiveDispatchPeriod,
    isDayDispatchPeriod,
    state?.resultsData,
  ]);
  React.useEffect(() => {
    if (eveningActivationHighlightTimerRef.current) {
      window.clearTimeout(eveningActivationHighlightTimerRef.current);
      eveningActivationHighlightTimerRef.current = null;
    }

    if (!shouldShowEveningRail || eveningGanttRailState.isLoading || !eveningGanttRailState.payload) {
      setRetainedEveningActivatedStepKeys((currentKeys) => (currentKeys.size ? new Set() : currentKeys));
      setCapturedEveningActivatedStepKeysSignature("");
      return undefined;
    }

    const currentStepKeys = canHighlightEveningDispatchPeriod && state?.transientActivatedStepKeys instanceof Set
      ? Array.from(state.transientActivatedStepKeys)
      : [];
    if (!currentStepKeys.length) {
      setRetainedEveningActivatedStepKeys((currentKeys) => (currentKeys.size ? new Set() : currentKeys));
      setCapturedEveningActivatedStepKeysSignature("");
      return undefined;
    }

    // Le délai commence quand le nouveau payload Soir est réellement commis dans le Gantt.
    setRetainedEveningActivatedStepKeys(new Set(currentStepKeys));
    setCapturedEveningActivatedStepKeysSignature(buildMutableSetSignature(new Set(currentStepKeys)));
    eveningActivationHighlightTimerRef.current = window.setTimeout(() => {
      eveningActivationHighlightTimerRef.current = null;
      setRetainedEveningActivatedStepKeys(new Set());
    }, RENDERED_ACTIVATION_HIGHLIGHT_DURATION_MS);

    return () => {
      if (eveningActivationHighlightTimerRef.current) {
        window.clearTimeout(eveningActivationHighlightTimerRef.current);
        eveningActivationHighlightTimerRef.current = null;
      }
    };
  }, [
    activeDispatchDate,
    canHighlightEveningDispatchPeriod,
    eveningGanttRailState.isLoading,
    eveningGanttRailState.payload,
    shouldShowEveningRail,
  ]);
  const enforceP2P3TimeWindowsAsHard = state?.resultsData?.optimization_diagnostics
    ?.optimization_settings?.enforce_p2_p3_time_windows_as_hard === true;
  const [fleetSegmentComparisonByKey, setFleetSegmentComparisonByKey] = React.useState(() => new Map());

  const clearGroupedSegmentCollapseTimer = React.useCallback(() => {
    if (groupedSegmentCollapseTimerRef.current) {
      window.clearTimeout(groupedSegmentCollapseTimerRef.current);
      groupedSegmentCollapseTimerRef.current = null;
    }
  }, []);

  const clearGroupedSegmentClickSuppression = React.useCallback(() => {
    suppressGroupedSegmentClickRef.current = false;
  }, []);

  const consumeGroupedSegmentClickSuppression = React.useCallback(() => {
    if (!suppressGroupedSegmentClickRef.current) {
      return false;
    }

    suppressGroupedSegmentClickRef.current = false;
    return true;
  }, []);

  const resetFleetSegmentPressState = React.useCallback(() => {
    setFleetSegmentPressState(null);
  }, []);

  const clearGroupedSegmentHoldGesture = React.useCallback(() => {
    const gesture = groupedSegmentHoldGestureRef.current;
    if (!gesture) {
      return;
    }

    if (gesture.timerId) {
      window.clearTimeout(gesture.timerId);
    }

    gesture.cleanup?.();
    groupedSegmentHoldGestureRef.current = null;
    resetFleetSegmentPressState();
  }, [resetFleetSegmentPressState]);

  const clearSegmentPointerGesture = React.useCallback(() => {
    const gesture = segmentPointerGestureRef.current;
    if (!gesture) {
      return;
    }

    if (gesture.timerId) {
      window.clearTimeout(gesture.timerId);
    }

    gesture.cleanup?.();
    segmentPointerGestureRef.current = null;
    resetFleetSegmentPressState();
  }, [resetFleetSegmentPressState]);

  const suppressCurrentTimelineSelectionSync = React.useCallback(() => {
    const syncTarget = timelineGroupSyncTargetRef.current;
    if (syncTarget?.source !== "selection-sync" || !syncTarget?.groupKey) {
      suppressedTimelineSelectionSyncRef.current = null;
      return;
    }

    suppressedTimelineSelectionSyncRef.current = {
      assignmentId: syncTarget.assignmentId || "",
      groupKey: syncTarget.groupKey,
    };
  }, []);

  const closeExpandedTimelineGroup = React.useCallback(() => {
    clearGroupedSegmentCollapseTimer();
    setExpandedTimelineGroupState(null);
  }, [clearGroupedSegmentCollapseTimer]);

  React.useEffect(() => {
    assignmentInteractionsRef.current = interactions?.assignmentCard || {};
  }, [interactions]);

  const closeFleetFullscreen = React.useCallback(() => {
    setIsFleetFullscreen(false);
  }, []);

  React.useEffect(() => {
    if (isActiveView) {
      return;
    }

    setIsFleetFullscreen(false);
  }, [isActiveView]);

  React.useEffect(() => {
    if (!isFleetFullscreen || typeof document === "undefined") {
      return undefined;
    }

    const { body, documentElement } = document;
    const previousBodyOverflow = body.style.overflow;
    const previousDocumentOverflow = documentElement.style.overflow;
    const handleWindowKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      setIsFleetFullscreen(false);
    };

    body.style.overflow = "hidden";
    documentElement.style.overflow = "hidden";
    window.addEventListener("keydown", handleWindowKeyDown);

    return () => {
      body.style.overflow = previousBodyOverflow;
      documentElement.style.overflow = previousDocumentOverflow;
      window.removeEventListener("keydown", handleWindowKeyDown);
    };
  }, [isFleetFullscreen]);

  React.useEffect(() => {
    setFleetFullscreenOverlayActive(isFleetFullscreen);

    return () => {
      setFleetFullscreenOverlayActive(false);
    };
  }, [isFleetFullscreen]);

  const expandedTimelineGroupKey = expandedTimelineGroupState?.groupKey || "";
  const latestExpandedSequenceGroupKey = expandedSequenceGroupKeys[expandedSequenceGroupKeys.length - 1] || "";

  const toggleExpandedSequenceGroup = React.useCallback((groupKey) => {
    if (!groupKey) {
      return;
    }

    setExpandedSequenceGroupKeys((currentKeys) => (
      currentKeys.includes(groupKey)
        ? currentKeys.filter((currentKey) => currentKey !== groupKey)
        : [...currentKeys, groupKey]
    ));
  }, []);

  const handleRouteMapControllerReady = React.useCallback((controller) => {
    routeMapControllerRef.current = controller || null;
    onMapControllerReady?.(controller);
  }, [onMapControllerReady]);

  const handleRouteMapRenderMetaChange = React.useCallback((nextMapRenderMeta = null) => {
    setRouteMapRenderMeta(nextMapRenderMeta || null);
    onMapRenderMetaChange?.(nextMapRenderMeta || null);
  }, [onMapRenderMetaChange]);

  const applyPendingSegmentMapFocus = React.useCallback(() => {
    const pendingFocus = pendingSegmentMapFocusRef.current;
    const controller = routeMapControllerRef.current;
    if (!pendingFocus || !controller) {
      return false;
    }

    if (pendingFocus.mode === "restore") {
      const restored = controller.restoreView?.() === true;
      if (!restored) {
        return false;
      }

      activeMapZoomSegmentKeyRef.current = "";
      pendingSegmentMapFocusRef.current = null;
      return true;
    }

    const zoomed = controller.zoomToClient?.(pendingFocus.clientId, pendingFocus.stopType) === true;
    if (!zoomed) {
      return false;
    }

    activeMapZoomSegmentKeyRef.current = pendingFocus.focusKey;
    pendingSegmentMapFocusRef.current = null;
    return true;
  }, []);

  const {
    dismissPopover: closeSegmentActionPopover,
    isOpen: isSegmentActionPopoverOpen,
    popoverRefs: segmentActionPopoverRefs,
    togglePopover: toggleSegmentActionPopover,
  } = useFleetSegmentPopover({
    openSegmentActionKey,
    onTogglePopover: setOpenSegmentActionKey,
  });
  const openTimelineSegmentActionPopover = React.useCallback((segmentKey, anchorElement, presentationSegment = null) => {
    openSegmentActionPresentationRef.current = {
      segment: presentationSegment,
      segmentKey: typeof segmentKey === "string" ? segmentKey : "",
    };
    toggleSegmentActionPopover(segmentKey, anchorElement);
  }, [toggleSegmentActionPopover]);

  const selectedAssignmentId = typeof assignment?.truck_id === "string" ? assignment.truck_id : "";
  const selectedTripId = typeof trip?.trip_id === "string" ? trip.trip_id : "";
  React.useEffect(() => {
    if (!selectedAssignmentId) {
      return;
    }

    setFleetSelection((currentSelection) => (
      currentSelection.dispatchPeriod === "evening"
        ? currentSelection
        : { assignmentId: selectedAssignmentId, dispatchPeriod: "day" }
    ));
  }, [selectedAssignmentId]);
  React.useEffect(() => {
    setFleetSelection({ assignmentId: selectedAssignmentId, dispatchPeriod: "day" });
    setEveningSelectedClientId("");
  }, [activeDispatchDate]);
  const canManageDispatcherFreeze = typeof interactions?.assignmentCard?.onFreezeDelivery === "function"
    || typeof interactions?.assignmentCard?.onUnfreezeDelivery === "function";
  const pendingDeliveryMutationSignature = buildMutableSetSignature(state?.pendingDeliveryMutationById);
  const pendingLoadingMutationSignature = buildMutableSetSignature(state?.pendingLoadingMutationById);
  const transientActivatedStepKeysSignature = buildMutableSetSignature(state?.transientActivatedStepKeys);
  const retainedDayActivatedStepKeysSignature = buildMutableSetSignature(retainedDayActivatedStepKeys);
  const dayActivatedStepKeys = React.useMemo(() => new Set([
    ...(transientActivatedStepKeysSignature !== capturedDayActivatedStepKeysSignature
      && canHighlightActiveDispatchPeriod
      && state?.transientActivatedStepKeys instanceof Set
      ? state.transientActivatedStepKeys
      : []),
    ...retainedDayActivatedStepKeys,
  ]), [
    canHighlightActiveDispatchPeriod,
    capturedDayActivatedStepKeysSignature,
    retainedDayActivatedStepKeysSignature,
    transientActivatedStepKeysSignature,
  ]);
  const timelineAssignments = React.useMemo(
    () => mergeOptimizationPreviewAssignments(
      state?.assignmentList,
      mainOptimizationPreviewByTruckId,
      { active: isOptimizationMainPreviewActive },
    ),
    [isOptimizationMainPreviewActive, mainOptimizationPreviewByTruckId, state?.assignmentList],
  );
  const timelineData = React.useMemo(() => buildFleetTimelineData({
    ...(state || {}),
    canManageDispatcherFreeze,
    transientActivatedStepKeys: shouldHighlightSuspendedPanel ? new Set() : dayActivatedStepKeys,
  }, timelineAssignments), [
    canManageDispatcherFreeze,
    dayActivatedStepKeys,
    timelineAssignments,
    selectedAssignmentId,
    selectedClientId,
    selectedTripId,
    state?.openOperationalReassignmentKey,
    state?.resultsData,
    state?.workspaceRevision,
    pendingDeliveryMutationSignature,
    pendingLoadingMutationSignature,
    shouldHighlightSuspendedPanel,
    transientActivatedStepKeysSignature,
  ]);
  const rows = React.useMemo(() => (Array.isArray(timelineData.rows) ? timelineData.rows : []), [timelineData]);
  const routingIssueByTruckId = React.useMemo(
    () => buildRoutingIssueByTruckId(state?.resultsData || {}),
    [state?.resultsData?.error_entries],
  );
  const haversineRouteWarningTruckIds = React.useMemo(() => {
    const nextTruckIds = new Set();
    rows.forEach((row) => {
      if (!row?.hasHaversineRouteWarning) {
        return;
      }

      const truckId = resolveFleetTimelineRowTruckKey(row);
      if (truckId) {
        nextTruckIds.add(truckId);
      }
    });
    return nextTruckIds;
  }, [rows]);
  const haversineRouteWarningTruckIdsSignature = React.useMemo(
    () => Array.from(haversineRouteWarningTruckIds).sort().join("|"),
    [haversineRouteWarningTruckIds],
  );
  latestHaversineRouteWarningTruckIdsRef.current = haversineRouteWarningTruckIds;

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const timers = haversineRouteWarningDelayTimersRef.current;

    timers.forEach((timerId, truckId) => {
      if (!haversineRouteWarningTruckIds.has(truckId)) {
        window.clearTimeout(timerId);
        timers.delete(truckId);
      }
    });

    setPersistentHaversineRouteWarningTruckIds((currentTruckIds) => {
      let didChange = false;
      const nextTruckIds = new Set(currentTruckIds);

      currentTruckIds.forEach((truckId) => {
        if (!haversineRouteWarningTruckIds.has(truckId)) {
          nextTruckIds.delete(truckId);
          didChange = true;
        }
      });

      return didChange ? nextTruckIds : currentTruckIds;
    });

    haversineRouteWarningTruckIds.forEach((truckId) => {
      if (persistentHaversineRouteWarningTruckIds.has(truckId) || timers.has(truckId)) {
        return;
      }

      const timerId = window.setTimeout(() => {
        timers.delete(truckId);
        if (!latestHaversineRouteWarningTruckIdsRef.current.has(truckId)) {
          return;
        }

        setPersistentHaversineRouteWarningTruckIds((currentTruckIds) => {
          if (currentTruckIds.has(truckId)) {
            return currentTruckIds;
          }

          const nextTruckIds = new Set(currentTruckIds);
          nextTruckIds.add(truckId);
          return nextTruckIds;
        });
      }, HAVERSINE_ROUTE_WARNING_PERSISTENCE_DELAY_MS);

      timers.set(truckId, timerId);
    });

    return undefined;
  }, [haversineRouteWarningTruckIdsSignature, persistentHaversineRouteWarningTruckIds]);

  React.useEffect(() => () => {
    if (typeof window === "undefined") {
      return;
    }

    haversineRouteWarningDelayTimersRef.current.forEach((timerId) => {
      window.clearTimeout(timerId);
    });
    haversineRouteWarningDelayTimersRef.current.clear();
  }, []);

  const truckLabelById = React.useMemo(() => {
    const labelsById = {};
    rows.forEach((row) => {
      const truckId = String(row?.assignmentId || row?.truckId || "").trim();
      if (!truckId) {
        return;
      }
      labelsById[truckId] = String(row?.truckLabel || truckId).trim() || truckId;
    });
    return labelsById;
  }, [rows]);
  const fleetSegmentComparisonRowsSignature = React.useMemo(() => {
    return rows.map((row) => {
      const rowSegments = Array.isArray(row?.segments) ? row.segments : [];
      return rowSegments.map((segment) => [
        segment?.tripId ?? "",
        segment?.routeSegmentIndex ?? "",
        segment?.scheduledAtMinutes ?? "",
        segment?.segmentStartMinutes ?? "",
        segment?.segmentEndMinutes ?? "",
        segment?.visualSegmentEndMinutes ?? "",
        segment?.displayTravelDurationMinutes ?? "",
        segment?.travelDurationMinutes ?? "",
        segment?.routeScheduledTravelDurationMinutes ?? "",
        segment?.distanceLabel ?? "",
      ].join(":"));
    }).flat().join("|");
  }, [rows]);
  const sortedRows = React.useMemo(() => [...rows].sort(compareTimelineRowsByDisplayedWorkload), [rows]);
  const retainedEveningActivatedStepKeysSignature = buildMutableSetSignature(retainedEveningActivatedStepKeys);
  const eveningActivatedStepKeys = React.useMemo(() => new Set([
    ...(transientActivatedStepKeysSignature !== capturedEveningActivatedStepKeysSignature
      && canHighlightEveningDispatchPeriod
      && state?.transientActivatedStepKeys instanceof Set
      ? state.transientActivatedStepKeys
      : []),
    ...retainedEveningActivatedStepKeys,
  ]), [
    canHighlightEveningDispatchPeriod,
    capturedEveningActivatedStepKeysSignature,
    retainedEveningActivatedStepKeysSignature,
    transientActivatedStepKeysSignature,
  ]);
  const eveningPayload = eveningGanttRailState.payload;
  const eveningResultsState = React.useMemo(
    () => buildResultsState(eveningPayload),
    [eveningPayload],
  );
  const eveningSuspendedPanelModel = selectSuspendedPanelData({
    assignmentList: Array.isArray(eveningGanttRailState.payload?.assignments)
      ? eveningGanttRailState.payload.assignments
      : [],
    pendingDeliveryMutationById: state?.pendingDeliveryMutationById,
    resultsData: eveningGanttRailState.payload,
    transientActivatedStepKeys: eveningSuspendedArrivalKeys,
  }, {
    dispatchPeriod: "evening",
  });
  const eveningTimelineData = React.useMemo(() => {
    if (!eveningPayload || typeof eveningPayload !== "object") {
      return { rows: [] };
    }
    const eveningAssignments = mergeOptimizationPreviewAssignments(
      eveningPayload.assignments,
      optimizationPreviewByDispatchPeriod.evening,
      { active: isOptimizationEveningPreviewActive },
    );
    return buildFleetTimelineData({
      ...eveningResultsState,
      canManageDispatcherFreeze: false,
      currentAssignment: null,
      currentTrip: null,
      pendingDeliveryMutationById: state?.pendingDeliveryMutationById,
      pendingLoadingMutationById: state?.pendingLoadingMutationById,
      selectedClientId: eveningSelectedClientId,
      transientActivatedStepKeys: shouldHighlightSuspendedPanel ? new Set() : eveningActivatedStepKeys,
    }, eveningAssignments);
  }, [
    eveningActivatedStepKeys,
    eveningPayload,
    eveningResultsState,
    eveningSelectedClientId,
    isOptimizationEveningPreviewActive,
    optimizationPreviewByDispatchPeriod.evening,
    pendingDeliveryMutationSignature,
    pendingLoadingMutationSignature,
    shouldHighlightSuspendedPanel,
    state?.pendingDeliveryMutationById,
    state?.pendingLoadingMutationById,
    transientActivatedStepKeysSignature,
  ]);
  const eveningTimelineRows = React.useMemo(
    () => (Array.isArray(eveningTimelineData?.rows) ? eveningTimelineData.rows : []),
    [eveningTimelineData],
  );
  const sortedEveningTimelineRows = React.useMemo(
    () => [...eveningTimelineRows].sort(compareTimelineRowsByDisplayedWorkload),
    [eveningTimelineRows],
  );
  const eveningTruckLabelById = React.useMemo(() => {
    const labelsById = {};
    sortedEveningTimelineRows.forEach((row) => {
      const truckId = String(row?.assignmentId || row?.truckId || "").trim();
      if (!truckId) {
        return;
      }
      labelsById[truckId] = String(row?.truckLabel || truckId).trim() || truckId;
    });
    return labelsById;
  }, [sortedEveningTimelineRows]);
  const mergedTimelineRows = React.useMemo(
    () => mergeDayAndEveningTimelineRows(sortedRows, sortedEveningTimelineRows),
    [sortedEveningTimelineRows, sortedRows],
  );
  const timelineFilterRows = shouldShowEveningRail ? mergedTimelineRows : sortedRows;
  React.useEffect(() => {
    setFleetSegmentComparisonByKey((currentMap) => (currentMap.size ? new Map() : currentMap));
  }, [activeDispatchDate, fleetSegmentComparisonRowsSignature]);
  React.useEffect(() => {
    if (fleetSegmentHovercardContext?.fleet_segment_hovercard_comparison_enabled !== true) {
      setFleetSegmentComparisonByKey((currentMap) => (currentMap.size ? new Map() : currentMap));
      return undefined;
    }

    const requestTargets = [];
    const seenKeys = new Set();

    rows.forEach((row) => {
      const rowSegments = Array.isArray(row?.segments) ? row.segments : [];

      rowSegments.forEach((segment) => {
        const comparisonKey = buildFleetSegmentComparisonCacheKey({
          dispatchDate: activeDispatchDate,
          routeSegmentIndex: segment?.routeSegmentIndex,
          tripId: segment?.tripId,
        });

        if (!comparisonKey || seenKeys.has(comparisonKey)) {
          return;
        }

        seenKeys.add(comparisonKey);
        requestTargets.push({
          comparisonKey,
          routeSegmentIndex: segment?.routeSegmentIndex,
          tripId: segment?.tripId,
        });
      });
    });

    const pendingTargets = requestTargets.filter(({ comparisonKey }) => !fleetSegmentComparisonByKey.has(comparisonKey));
    if (!pendingTargets.length) {
      return undefined;
    }

    let isDisposed = false;
    const abortControllers = [];

    Promise.all(
      pendingTargets.map(async ({ comparisonKey, routeSegmentIndex, tripId }) => {
        const abortController = new AbortController();
        abortControllers.push(abortController);

        try {
          const payload = await fetchFleetSegmentComparison({
            routeSegmentIndex,
            signal: abortController.signal,
            tripId,
          });
          return { comparisonKey, payload };
        } catch {
          return null;
        }
      }),
    ).then((entries) => {
      if (isDisposed) {
        return;
      }

      const resolvedEntries = entries.filter(Boolean);
      if (!resolvedEntries.length) {
        return;
      }

      setFleetSegmentComparisonByKey((currentMap) => {
        const nextMap = new Map(currentMap);
        let didChange = false;

        resolvedEntries.forEach(({ comparisonKey, payload }) => {
          const currentPayload = nextMap.get(comparisonKey);
          const currentDurationMinutes = Number(currentPayload?.current_duration_minutes);
          const nextDurationMinutes = Number(payload?.current_duration_minutes);
          const payloadChanged = currentPayload?.status !== payload?.status
            || currentPayload?.here_time_aware_routing_enabled !== payload?.here_time_aware_routing_enabled
            || currentPayload?.comparison_duration_minutes !== payload?.comparison_duration_minutes
            || currentDurationMinutes !== nextDurationMinutes;

          if (!payloadChanged) {
            return;
          }

          nextMap.set(comparisonKey, payload);
          didChange = true;
        });

        return didChange ? nextMap : currentMap;
      });
    });

    return () => {
      isDisposed = true;
      abortControllers.forEach((abortController) => {
        abortController.abort();
      });
    };
  }, [activeDispatchDate, fleetSegmentHovercardContext?.fleet_segment_hovercard_comparison_enabled, rows]);
  const hasMountNode = mountNode instanceof HTMLElement;
  const {
    filterMessage: timelineFilterMessage,
    isDropdownOpen: isTruckFilterOpen,
    panelRef: truckFilterPanelRef,
    selectAllTrucks: handleSelectAllTrucks,
    selectedTruckIds,
    toggleDropdown: toggleTruckFilterDropdown,
    toggleTruckSelection: handleToggleTruckSelection,
    triggerRef: truckFilterButtonRef,
  } = useFleetTruckFilter({
    hideUnusedTrucks,
    rows: timelineFilterRows,
  });
  const selectedTruckIdSet = new Set(selectedTruckIds);
  const truckOptions = timelineFilterRows.map((row) => ({
    assignmentId: row.assignmentId,
    hasTrips: Boolean(
      row.hasTrips
      || row.hasIncidents
      || resolveFleetRowIncidents(row).length
      || timelineRowHasOptimizationPreview(
        row.dayRow || row,
        mainOptimizationPreviewByTruckId,
        { active: isOptimizationMainPreviewActive },
      )
      || timelineRowHasOptimizationPreview(
        row.eveningRow || row,
        optimizationPreviewByDispatchPeriod.evening,
        { active: isOptimizationEveningPreviewActive },
      )
    ),
    label: row.truckLabel || row.assignmentId || "Camion",
    meta: getTruckFilterMetaLabel(row.dayRow || row.eveningRow || row, { includeTripCount: false }),
  }));
  const truckReassignmentTargets = React.useMemo(
    () => sortedRows.map((row) => {
      const rowAssignment = row?.assignment && typeof row.assignment === "object" ? row.assignment : null;
      return {
        aspirator: rowAssignment?.aspirator,
        // Le Chargé PM se nomme par la remorque de la ligne.
        compartmentName: rowAssignment?.compartment_name,
        isDispatchDisabled: Boolean(row.isDispatchDisabled),
        isDompeur: rowAssignment?.is_dompeur,
        isInitialPreloadEnabled: rowAssignment?.is_initial_preload_enabled,
        isTractorMissing: Boolean(row.isTractorMissing),
        isTruckProtected: Boolean(row.isTruckProtected ?? rowAssignment?.is_truck_protected),
        meta: getTruckFilterMetaLabel(row),
        ontario: rowAssignment?.ontario,
        preferredGrainType: rowAssignment?.preferred_grain_type,
        truckType: rowAssignment?.truck_type,
        truckId: row.assignmentId,
        truckName: row.truckLabel || row.assignmentId || "Camion",
        truck_raw_data: rowAssignment?.truck_raw_data,
      };
    }),
    [sortedRows],
  );
  const eveningTruckReassignmentTargets = React.useMemo(
    () => sortedEveningTimelineRows.map((row) => {
      const rowAssignment = row?.assignment && typeof row.assignment === "object" ? row.assignment : null;
      return {
        aspirator: rowAssignment?.aspirator,
        // Le Chargé PM se nomme par la remorque de la ligne.
        compartmentName: rowAssignment?.compartment_name,
        isDispatchDisabled: Boolean(row.isDispatchDisabled),
        isDompeur: rowAssignment?.is_dompeur,
        isInitialPreloadEnabled: rowAssignment?.is_initial_preload_enabled,
        isTractorMissing: Boolean(row.isTractorMissing),
        isTruckProtected: Boolean(row.isTruckProtected ?? rowAssignment?.is_truck_protected),
        meta: getTruckFilterMetaLabel(row),
        ontario: rowAssignment?.ontario,
        preferredGrainType: rowAssignment?.preferred_grain_type,
        truckType: rowAssignment?.truck_type,
        truckId: row.assignmentId,
        truckName: row.truckLabel || row.assignmentId || "Camion",
        truck_raw_data: rowAssignment?.truck_raw_data,
      };
    }),
    [sortedEveningTimelineRows],
  );
  const truckCapabilityById = React.useMemo(
    () => buildReassignmentVisibleTruckCapabilityIndex(sortedRows),
    [sortedRows],
  );
  const isTimelineRowVisible = (row, previewByTruckId, previewActive) => {
    if (!selectedTruckIdSet.has(row.assignmentId)) {
      return false;
    }

    const hasOptimizationPreview = timelineRowHasOptimizationPreview(
      row,
      previewByTruckId,
      { active: previewActive },
    );
    if (hideUnusedTrucks && !row.hasTrips && row?.hasIncidents !== true
      && resolveFleetRowIncidents(row).length === 0 && !hasOptimizationPreview) {
      return false;
    }

    if (hasTimelineSearch) {
      if (!normalizeFleetSearch(buildFleetSearchValue(row?.assignment)).includes(normalizedTimelineQuery)) {
        return false;
      }
    }

    return true;
  };
  const filteredTimelineRows = sortedRows.filter((row) => isTimelineRowVisible(
    row,
    mainOptimizationPreviewByTruckId,
    isOptimizationMainPreviewActive,
  ));
  const filteredEveningTimelineRows = shouldShowEveningRail
    ? sortedEveningTimelineRows.filter((row) => isTimelineRowVisible(
        row,
        optimizationPreviewByDispatchPeriod.evening,
        isOptimizationEveningPreviewActive,
      ))
    : [];
  const currentSelectedRow = timelineData.selectedRow || sortedRows.find((row) => row.assignmentId === assignment?.truck_id) || sortedRows[0] || null;
  const {
    closeFloating: closeFloatingMap,
    dimensions: floatingMapDimensions,
    handleKeyDown: handleFloatingMapKeyDown,
    handlePointerEnd: handleFloatingMapPointerEnd,
    handlePointerMove: handleFloatingMapPointerMove,
    isDragging: isFloatingMapDragging,
    isFloating: isMapFloating,
    isResizing: isFloatingMapResizing,
    mountNode: floatingMapMountNode,
    openFloating: openFloatingMap,
    panelRef: floatingMapPanelRef,
    panelStyle: floatingMapPanelStyle,
    setSize: handleFloatingMapSizeChange,
    size: floatingMapSize,
    startDrag: handleFloatingMapPointerDown,
    startResize: handleFloatingMapResizePointerDown,
  } = useFleetFloatingMapState({
    initialPosition: FLOATING_MAP_INITIAL_POSITION,
    initialSize: "compact",
    persistStorageKey: FLEET_FLOATING_ROUTE_MAP_STORAGE_KEY,
    sizePresets: FLEET_FLOATING_MAP_SIZE_PRESETS,
  });
  const {
    closeFloating: closeFloatingSuspendedPanel,
    dimensions: floatingSuspendedDimensions,
    handleKeyDown: handleFloatingSuspendedKeyDown,
    handlePointerEnd: handleFloatingSuspendedPointerEnd,
    handlePointerMove: handleFloatingSuspendedPointerMove,
    isDragging: isFloatingSuspendedDragging,
    isFloating: isSuspendedPanelFloating,
    isResizing: isFloatingSuspendedResizing,
    mountNode: floatingSuspendedMountNode,
    openFloating: openFloatingSuspendedPanel,
    panelRef: floatingSuspendedPanelRef,
    panelStyle: floatingSuspendedPanelStyle,
    setSize: handleFloatingSuspendedSizeChange,
    size: floatingSuspendedSize,
    startDrag: handleFloatingSuspendedPointerDown,
    startResize: handleFloatingSuspendedResizePointerDown,
  } = useFleetFloatingMapState({
    initialPosition: {
      x: FLOATING_MAP_INITIAL_POSITION.x + 40,
      y: FLOATING_MAP_INITIAL_POSITION.y + 44,
    },
    initialSize: "standard",
  });
  const {
    clearReassignmentClickSuppression,
    consumeReassignmentClickSuppression,
    dragNoticeState,
    finishImmediateSegmentReassignment,
    handleReassignmentDragEnd,
    handleReassignmentDragOver,
    handleReassignmentDrop,
    handleSegmentReassignmentDragStart,
    handleSuspendedDeliveryDragStart,
    handleSuspendedDeliveryPointerDown,
    handleSuspendedDeliveryPreview,
    handleSuspendedDropZoneDragOver,
    handleSuspendedDropZoneDrop,
    handleUndoReassignment,
    reassignmentDragState,
    resetReassignmentDragState,
    startImmediateSegmentReassignment,
    undoReassignmentState,
    updateImmediateSegmentReassignment,
  } = useFleetReassignmentDrag({
    assignmentInteractionsRef,
    closeSegmentActionPopover,
    dispatchDate: activeDispatchDate,
    eveningRuntimeRevision: eveningGanttRailState.payload?.runtime_revision || "",
    eveningTimelineRows: filteredEveningTimelineRows,
    onTargetAssignmentSelect: handleCrossPeriodReassignmentSelection,
    runtimeRevision: state?.resultsData?.runtime_revision || "",
    timelineDispatchPeriod: activeSuspendedDispatchPeriod,
    timelineRows: filteredTimelineRows,
    timelineRowsContainerRef,
    toggleTruckFilterDropdown,
  });
  const {
    clearSegmentReorderClickSuppression,
    consumeSegmentReorderClickSuppression,
    finishImmediateSegmentReorder,
    resetSegmentReorderDragState,
    segmentReorderDragState,
    startImmediateSegmentReorder,
    updateImmediateSegmentReorder,
  } = useFleetSegmentReorderDrag({
    assignmentInteractionsRef,
    closeSegmentActionPopover,
    toggleTruckFilterDropdown,
  });
  React.useEffect(() => {
    if (!perspective.isInclined) return;
    resetReassignmentDragState();
    resetSegmentReorderDragState();
    clearSegmentPointerGesture();
    clearGroupedSegmentHoldGesture();
  }, [perspective.isInclined, resetReassignmentDragState, resetSegmentReorderDragState, clearSegmentPointerGesture, clearGroupedSegmentHoldGesture]);
  React.useEffect(() => {
    if (!isOptimizationPreviewInteractionLocked) {
      return;
    }

    closeSegmentActionPopover();
    resetReassignmentDragState();
    resetSegmentReorderDragState();
    clearSegmentPointerGesture();
    clearGroupedSegmentHoldGesture();
  }, [
    clearGroupedSegmentHoldGesture,
    clearSegmentPointerGesture,
    closeSegmentActionPopover,
    isOptimizationPreviewInteractionLocked,
    resetReassignmentDragState,
    resetSegmentReorderDragState,
  ]);
  const reassignmentNoticeState = blockedPlanningMovementFeedback?.noticeVisible
    ? {
        ...blockedPlanningMovementFeedback,
        dragKind: blockedPlanningMovementFeedback.sourceSurface === "suspended" ? "suspended" : "assignment",
        status: "blocked",
      }
    : dragNoticeState;
  const mainResetPresentation = resolvePlanningResetTruckPresentation({
    dispatchDate: activeDispatchDate,
    dispatchPeriod: activeSuspendedDispatchPeriod,
    launchScope: planningResetLaunchScope,
    jobState: optimizationJobState,
    resultsData: state?.resultsData,
    appliedResetKey: state?.lastOvertimeWarningResetKey,
  });
  const eveningResetPresentation = resolvePlanningResetTruckPresentation({
    dispatchDate: activeDispatchDate,
    dispatchPeriod: "evening",
    launchScope: planningResetLaunchScope,
    jobState: optimizationJobState,
    resultsData: eveningGanttRailState.payload,
    appliedResetKey: state?.lastEveningTruckStatusResetKey,
  });
  const eveningCompletedResetKey = eveningResetPresentation.completedResetKey;
  React.useLayoutEffect(() => {
    if (state && eveningCompletedResetKey) {
      state.lastEveningTruckStatusResetKey = eveningCompletedResetKey;
    }
  }, [eveningCompletedResetKey, state]);
  const suppressMainTruckStatus = mainResetPresentation.suppressPreviousStatus;
  const suppressEveningTruckStatus = eveningResetPresentation.suppressPreviousStatus;
  const completedResetKey = mainResetPresentation.completedResetKey;
  const shouldClearOvertimeWarnings = Boolean(completedResetKey)
    && state?.lastOvertimeWarningResetKey !== completedResetKey;
  React.useLayoutEffect(() => {
    if (state && shouldClearOvertimeWarnings) {
      state.overtimeWarningTruckIds = new Set();
      state.lastOvertimeWarningResetKey = completedResetKey;
    }
  }, [completedResetKey, shouldClearOvertimeWarnings, state]);
  const overtimeWarningTruckIdSet = !shouldClearOvertimeWarnings && state?.overtimeWarningTruckIds instanceof Set
    ? state.overtimeWarningTruckIds
    : EMPTY_TRUCK_ID_SET;
  const baseVisibleTimelineRows = React.useMemo(() => {
    return filteredTimelineRows.map((row) => {
      const overtimeState = getAssignmentOvertimeState(row.assignment);
      const hasOvertimeWarning = !suppressMainTruckStatus && shouldShowAssignmentOvertimeChrome(row.assignment, {
        authorizedTruckIds: overtimeWarningTruckIdSet,
        truckId: row.assignmentId,
      });

      return {
        ...row,
        dispatchPeriod: activeSuspendedDispatchPeriod,
        suppressPreviousTruckStatus: suppressMainTruckStatus,
        exceedsMaxWork: overtimeState.exceedsMaxWork,
        hasOvertimeWarning,
      };
    });
  }, [activeSuspendedDispatchPeriod, filteredTimelineRows, overtimeWarningTruckIdSet, suppressMainTruckStatus]);
  const baseVisibleEveningTimelineRows = React.useMemo(
    () => filteredEveningTimelineRows.map((row) => {
      const overtimeState = getAssignmentOvertimeState(row.assignment);
      const hasOvertimeWarning = !suppressEveningTruckStatus && shouldShowAssignmentOvertimeChrome(row.assignment, {
        authorizedTruckIds: EMPTY_TRUCK_ID_SET,
        truckId: row.assignmentId,
      });

      return {
        ...row,
        suppressPreviousTruckStatus: suppressEveningTruckStatus,
        exceedsMaxWork: overtimeState.exceedsMaxWork,
        hasOvertimeWarning,
      };
    }),
    [filteredEveningTimelineRows, suppressEveningTruckStatus],
  );
  const requestedEveningRow = fleetSelection.dispatchPeriod === "evening" && shouldShowEveningRail
    ? baseVisibleEveningTimelineRows.find((row) => row.assignmentId === fleetSelection.assignmentId) || null
    : null;
  const requestedMainRow = fleetSelection.dispatchPeriod === activeSuspendedDispatchPeriod
    ? baseVisibleTimelineRows.find((row) => row.assignmentId === fleetSelection.assignmentId) || null
    : null;
  const fallbackMainRow = baseVisibleTimelineRows.find((row) => row.assignmentId === currentSelectedRow?.assignmentId)
    || baseVisibleTimelineRows[0]
    || null;
  const fallbackEveningRow = shouldShowEveningRail ? baseVisibleEveningTimelineRows[0] || null : null;
  const selectedDispatchPeriod = requestedEveningRow || (!requestedMainRow && !fallbackMainRow && fallbackEveningRow)
    ? "evening"
    : activeSuspendedDispatchPeriod;
  const selectedRowBase = requestedEveningRow || requestedMainRow || fallbackMainRow || fallbackEveningRow || null;
  const visibleTimelineRows = React.useMemo(
    () => baseVisibleTimelineRows.map((row) => ({
      ...row,
      isSelected: selectedDispatchPeriod === activeSuspendedDispatchPeriod && row.assignmentId === selectedRowBase?.assignmentId,
    })),
    [activeSuspendedDispatchPeriod, baseVisibleTimelineRows, selectedDispatchPeriod, selectedRowBase?.assignmentId],
  );
  const visibleEveningTimelineRows = React.useMemo(
    () => baseVisibleEveningTimelineRows.map((row) => ({
      ...row,
      isSelected: selectedDispatchPeriod === "evening" && row.assignmentId === selectedRowBase?.assignmentId,
    })),
    [baseVisibleEveningTimelineRows, selectedDispatchPeriod, selectedRowBase?.assignmentId],
  );
  const timelineLegendProductLabels = React.useMemo(
    () => resolveTimelineLegendProductLabels([...baseVisibleTimelineRows, ...(shouldShowEveningRail ? baseVisibleEveningTimelineRows : [])]),
    [baseVisibleEveningTimelineRows, baseVisibleTimelineRows, shouldShowEveningRail],
  );
  const shouldShowMainResetWaiting = usePlanningResetGanttWaiting({
    dispatchDate: activeDispatchDate,
    dispatchPeriod: activeSuspendedDispatchPeriod,
    launchScope: planningResetLaunchScope,
    jobState: optimizationJobState,
    hasDisplayedPreview: isOptimizationMainPreviewActive && visibleTimelineRows.some((row) => (
      !optimizationExcludedTruckIds.has(row.assignmentId) && mainOptimizationPreviewByTruckId.has(row.assignmentId)
    )),
    resultInstalled: optimizationPreviewAuthoritativePaintSeen,
  });
  const shouldShowEveningResetWaiting = usePlanningResetGanttWaiting({
    dispatchDate: activeDispatchDate,
    dispatchPeriod: "evening",
    launchScope: planningResetLaunchScope,
    jobState: optimizationJobState,
    hasDisplayedPreview: shouldShowEveningRail && isOptimizationEveningPreviewActive && visibleEveningTimelineRows.some((row) => (
      !optimizationExcludedTruckIds.has(row.assignmentId) && optimizationPreviewByDispatchPeriod.evening.has(row.assignmentId)
    )),
    resultInstalled: isEveningRailResultCurrent,
  });
  const shouldShowOptimizationGanttWaiting = shouldShowFirstOptimizationGanttWaiting || shouldShowMainResetWaiting;
  const activationHighlightTarget = React.useMemo(() => {
    const highlightedEveningRow = visibleEveningTimelineRows.find((row) => (
      row?.segments?.some((segment) => segment.isActivationHighlighted)
    ));
    if (highlightedEveningRow?.assignmentId) {
      return { assignmentId: highlightedEveningRow.assignmentId, dispatchPeriod: "evening" };
    }

    const highlightedDayRow = visibleTimelineRows.find((row) => (
      row?.segments?.some((segment) => segment.isActivationHighlighted)
    ));
    return highlightedDayRow?.assignmentId
      ? { assignmentId: highlightedDayRow.assignmentId, dispatchPeriod: activeSuspendedDispatchPeriod }
      : null;
  }, [activeSuspendedDispatchPeriod, visibleEveningTimelineRows, visibleTimelineRows]);
  const selectedRow = (selectedDispatchPeriod === activeSuspendedDispatchPeriod ? visibleTimelineRows : visibleEveningTimelineRows)
    .find((row) => row.assignmentId === selectedRowBase?.assignmentId)
    || null;
  const isSelectedMainTimeline = selectedDispatchPeriod === activeSuspendedDispatchPeriod;
  const selectedFleetScopeKey = `${selectedDispatchPeriod}:${selectedRow?.assignmentId || ""}`;
  React.useEffect(() => {
    if (!selectedRow?.assignmentId) {
      return;
    }

    setFleetSelection((currentSelection) => (
      currentSelection.dispatchPeriod === selectedDispatchPeriod
      && currentSelection.assignmentId === selectedRow.assignmentId
        ? currentSelection
        : { assignmentId: selectedRow.assignmentId, dispatchPeriod: selectedDispatchPeriod }
    ));
  }, [selectedDispatchPeriod, selectedRow?.assignmentId]);
  const persistFleetPictureInPictureRestore = React.useCallback((pictureInPictureTitle = "Carte flotte") => {
    persistRouteMapPictureInPictureRestoreState({
      assignmentId: selectedRow?.assignmentId || selectedRow?.assignment?.truck_id || "",
      scope: "fleet",
      title: pictureInPictureTitle,
      tripId: selectedRow?.selectedTripId || selectedRow?.activeTrip?.trip_id || "",
    });
  }, [selectedRow]);
  const openFloatingMapOnSecondScreen = React.useCallback(async ({ restore = false } = {}) => {
    try {
      const pictureInPictureTitle = selectedRow?.truckLabel ? `Carte flotte - ${selectedRow.truckLabel}` : "Carte flotte";
      const pictureInPictureSize = resolveRouteMapPictureInPictureDefaultSize();
      const result = await openRouteMapPictureInPictureWindow({
        height: pictureInPictureSize.height,
        onClose: () => {
          setRouteMapPictureInPictureMountNode(null);
          setIsRouteMapPictureInPictureLinked(false);
          setIsRouteMapPictureInPictureExpanded(false);
          if (!isPageUnloadingRef.current) {
            clearRouteMapPictureInPictureRestoreState();
          }
        },
        title: pictureInPictureTitle,
        width: pictureInPictureSize.width,
      });

      if (result.ok) {
        setRouteMapPictureInPictureMountNode(result.mountNode || null);
        setIsRouteMapPictureInPictureLinked(true);
        setIsRouteMapPictureInPictureExpanded(false);
        persistFleetPictureInPictureRestore(pictureInPictureTitle);
        closeFloatingMap();
        return;
      }

      showToast({
        type: "info",
        title: "Fenêtre détachée indisponible",
        message: result.reason === "detached-window-unsupported"
          ? "Le navigateur ne permet pas d'ouvrir la carte dans une fenêtre détachée."
          : "Le navigateur a bloqué l'ouverture de la fenêtre détachée. Autorise les fenêtres pop-up puis réessaie.",
        duration: 7000,
      });
      if (restore) {
        clearRouteMapPictureInPictureRestoreState();
      }
    } catch (error) {
      if (restore) {
        clearRouteMapPictureInPictureRestoreState();
      }
      showToast({
        type: "error",
        title: "Ouverture impossible",
        message: error instanceof Error && error.message
          ? error.message
          : "La fenêtre détachée n'a pas pu être créée.",
        duration: 7000,
      });
    }
  }, [closeFloatingMap, persistFleetPictureInPictureRestore, selectedRow?.truckLabel, showToast]);

  React.useEffect(() => {
    if (!isActiveView || isRouteMapPictureInPictureLinked || typeof window === "undefined") {
      return undefined;
    }

    const restoreState = readRouteMapPictureInPictureRestoreState();
    if (restoreState?.scope !== "fleet") {
      return undefined;
    }

    if (!hasShownPictureInPictureRestorePromptRef.current) {
      hasShownPictureInPictureRestorePromptRef.current = true;
      showToast({
        type: "info",
        title: "Carte détachée prête",
        message: "Après l'actualisation, clique dans Dispatch pour rouvrir la fenêtre détachée.",
        duration: 7000,
      });
    }

    let restoreRequested = false;
    const handleRestoreActivation = (event) => {
      const isHardRefreshKey = event.type === "keydown" && (
        event.key === "F5"
          || ((event.ctrlKey || event.metaKey) && String(event.key || "").toLowerCase() === "r")
      );

      if (restoreRequested || isHardRefreshKey) {
        return;
      }

      restoreRequested = true;
      window.removeEventListener("pointerdown", handleRestoreActivation, true);
      window.removeEventListener("keydown", handleRestoreActivation, true);
      openFloatingMapOnSecondScreen({ restore: true });
    };

    window.addEventListener("pointerdown", handleRestoreActivation, true);
    window.addEventListener("keydown", handleRestoreActivation, true);
    return () => {
      window.removeEventListener("pointerdown", handleRestoreActivation, true);
      window.removeEventListener("keydown", handleRestoreActivation, true);
    };
  }, [isActiveView, isRouteMapPictureInPictureLinked, openFloatingMapOnSecondScreen, showToast]);

  const scheduleGroupedSegmentCollapse = React.useCallback((groupKey = "", delayMs = GROUPED_SEGMENT_DRAG_COLLAPSE_DELAY_MS) => {
    if (!groupKey || typeof window === "undefined") {
      closeExpandedTimelineGroup();
      return;
    }

    clearGroupedSegmentCollapseTimer();
    groupedSegmentCollapseTimerRef.current = window.setTimeout(() => {
      groupedSegmentCollapseTimerRef.current = null;
      setExpandedTimelineGroupState((currentState) => {
        if (!currentState || currentState.groupKey !== groupKey) {
          return currentState;
        }

        return null;
      });
    }, delayMs);
  }, [clearGroupedSegmentCollapseTimer, closeExpandedTimelineGroup]);

  const openExpandedTimelineGroup = React.useCallback((row, groupItem, source = "click") => {
    if (!groupItem?.groupKey || groupItem?.isInitialPreload || groupItem?.isEndOfDayLoad || groupItem?.isEndOfDayLoadSourcePreload) {
      return;
    }

    clearGroupedSegmentCollapseTimer();
    closeSegmentActionPopover();
    suppressedTimelineSelectionSyncRef.current = null;
    setExpandedTimelineGroupState({
      assignmentId: row?.assignmentId || "",
      groupKey: groupItem.groupKey,
      source,
    });
  }, [clearGroupedSegmentCollapseTimer, closeSegmentActionPopover]);

  const handleTimelineSegmentPointerDown = React.useCallback((event, row, segment) => {
    if (event.button !== 0 || !event.isPrimary || typeof window === "undefined") {
      return;
    }

    const canStartReassignment = Boolean(segment?.reassignmentDrag?.stepId && segment?.reassignmentDrag?.stepType);
    const sourceDispatchPeriod = row?.dispatchPeriod === "evening" ? "evening" : "day";
    const canStartReorder = Boolean(
      segment?.segmentReorder?.stepId
      && segment?.segmentReorder?.stepType
      && segment?.segmentReorder?.tripId,
    );
    if (!canStartReassignment && !canStartReorder) {
      return;
    }

    if (isPlanningMovementLocked && startBlockedPlanningMovementFeedback(event, {
      assignmentId: row?.assignmentId || "",
      dispatchPeriod: sourceDispatchPeriod,
      sourceSurface: "gantt",
      sourceType: "segment",
      stepId: segment?.reassignmentDrag?.stepId || segment?.segmentReorder?.stepId || "",
    })) {
      return;
    }

    clearSegmentPointerGesture();
    clearReassignmentClickSuppression();
    clearSegmentReorderClickSuppression();
    setFleetSegmentPressState({
      assignmentId: row.assignmentId,
      stepId: segment?.reassignmentDrag?.stepId || segment?.segmentReorder?.stepId || "",
    });

    const gesture = {
      activeMode: "",
      cleanup: null,
      dragStarted: false,
      holdTimerId: null,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
      pointerId: event.pointerId,
      row,
      segment,
      startX: event.clientX,
      startY: event.clientY,
    };

    const clearHoldTimer = () => {
      if (!gesture.holdTimerId) {
        return;
      }

      window.clearTimeout(gesture.holdTimerId);
      gesture.holdTimerId = null;
    };

    const stopActiveDragMode = (pointerPosition, { shouldCommitReassignment = false } = {}) => {
      if (!gesture.dragStarted) {
        if (gesture.activeMode === "reassignment") {
          resetReassignmentDragState();
        } else if (gesture.activeMode === "reorder") {
          resetSegmentReorderDragState();
        }
        gesture.activeMode = "";
        return;
      }

      if (gesture.activeMode === "reorder") {
        finishImmediateSegmentReorder({ shouldCommit: false, pointerPosition });
      } else {
        finishImmediateSegmentReassignment({ shouldCommit: shouldCommitReassignment, pointerPosition });
      }

      gesture.dragStarted = false;
      gesture.activeMode = "";
    };

    const startReassignmentDrag = (pointerPosition) => {
      if (!canStartReassignment) {
        return false;
      }

      if (gesture.activeMode === "reorder") {
        stopActiveDragMode(pointerPosition);
      }

      gesture.activeMode = "reassignment";
      gesture.dragStarted = startImmediateSegmentReassignment(row, segment, pointerPosition);
      if (gesture.dragStarted) {
        updateImmediateSegmentReassignment(pointerPosition);
      }
      return gesture.dragStarted;
    };

    const startReorderDrag = (pointerPosition) => {
      if (!canStartReorder) {
        return false;
      }

      if (gesture.activeMode === "reassignment") {
        stopActiveDragMode(pointerPosition);
      }

      const groupDragMembers = normalizeGroupDragMembers(null, segment);
      const reorderGroupSegments = Array.isArray(groupDragMembers.reorderGroupSegments)
        && groupDragMembers.reorderGroupSegments.length > 0
        ? groupDragMembers.reorderGroupSegments
        : [segment];
      const groupStepIds = Array.isArray(groupDragMembers.groupStepIds)
        ? groupDragMembers.groupStepIds
        : [];
      const isGroupedDrag = isMergedOperationalMissionSegment(segment)
        && groupStepIds.length > 1;
      const groupMemberCount = groupDragMembers.memberCount || groupStepIds.length;

      gesture.activeMode = "reorder";
      gesture.dragStarted = startImmediateSegmentReorder(row, segment, pointerPosition, {
        ...(isGroupedDrag ? {
          groupStepIds,
          isGroupedDrag: true,
          label: groupDragMembers.label || "Groupe",
          meta: `${groupMemberCount} étape${groupMemberCount > 1 ? "s" : ""}`,
          sourceGroupKey: groupDragMembers.sourceGroupKey
            || segment?.aspiratorMissionGroupKey
            || segment?.sameSiteGroupKey
            || "",
        } : {}),
        timePriorityResetUpdates: buildSegmentTimePriorityResetUpdates(reorderGroupSegments, {
          enforceP2P3TimeWindowsAsHard,
        }),
      });
      if (gesture.dragStarted) {
        updateImmediateSegmentReorder(pointerPosition);
      }
      return gesture.dragStarted;
    };

    const ensureDragMode = (pointerPosition) => {
      const deltaX = pointerPosition.clientX - gesture.startX;
      const deltaY = pointerPosition.clientY - gesture.startY;
      const intent = resolveFleetTimelinePointerDragIntent(deltaX, deltaY);
      const isSuspendedDropTarget = isFleetSuspendedDropZoneClientPoint(pointerPosition.clientX, pointerPosition.clientY);
      const isCrossPeriodDropTarget = isFleetCrossPeriodTimelineDropTargetClientPoint(
        pointerPosition.clientX,
        pointerPosition.clientY,
        sourceDispatchPeriod,
      );

      if ((isSuspendedDropTarget || isCrossPeriodDropTarget) && canStartReassignment) {
        if (gesture.activeMode === "reassignment" && gesture.dragStarted) {
          return true;
        }
        return startReassignmentDrag(pointerPosition);
      }

      if (intent === "reorder" && canStartReorder) {
        if (gesture.activeMode === "reorder" && gesture.dragStarted) {
          return true;
        }
        return startReorderDrag(pointerPosition);
      }

      if (canStartReassignment) {
        if (gesture.activeMode === "reassignment" && gesture.dragStarted) {
          return true;
        }
        return startReassignmentDrag(pointerPosition);
      }

      if (canStartReorder) {
        if (gesture.activeMode === "reorder" && gesture.dragStarted) {
          return true;
        }
        return startReorderDrag(pointerPosition);
      }

      return false;
    };

    const finishGesture = (shouldCommit, pointerEvent) => {
      if (pointerEvent?.pointerId !== gesture.pointerId) {
        return;
      }

      const pointerPosition = {
        clientX: Number.isFinite(Number(pointerEvent?.clientX)) ? Number(pointerEvent.clientX) : gesture.lastClientX,
        clientY: Number.isFinite(Number(pointerEvent?.clientY)) ? Number(pointerEvent.clientY) : gesture.lastClientY,
      };
      const shouldCommitMovedGesture = shouldCommit && hasReassignmentPointerMoved(
        { clientX: gesture.startX, clientY: gesture.startY },
        pointerPosition,
      );
      clearSegmentPointerGesture();

      if (!gesture.dragStarted) {
        return;
      }

      if (
        shouldCommitMovedGesture
        && gesture.activeMode === "reorder"
        && canStartReassignment
        && (
          isFleetSuspendedDropZoneClientPoint(pointerPosition.clientX, pointerPosition.clientY)
          || isFleetCrossPeriodTimelineDropTargetClientPoint(
            pointerPosition.clientX,
            pointerPosition.clientY,
            sourceDispatchPeriod,
          )
        )
      ) {
        startReassignmentDrag(pointerPosition);
      }

      if (!gesture.dragStarted) {
        return;
      }

      if (gesture.activeMode === "reorder") {
        finishImmediateSegmentReorder({ shouldCommit: shouldCommitMovedGesture, pointerPosition });
        return;
      }

      finishImmediateSegmentReassignment({ shouldCommit: shouldCommitMovedGesture, pointerPosition });
    };

    const handleWindowPointerMove = (moveEvent) => {
      if (moveEvent.pointerId !== gesture.pointerId) {
        return;
      }

      gesture.lastClientX = moveEvent.clientX;
      gesture.lastClientY = moveEvent.clientY;
      const pointerPosition = {
        clientX: moveEvent.clientX,
        clientY: moveEvent.clientY,
      };
      const deltaX = moveEvent.clientX - gesture.startX;
      const deltaY = moveEvent.clientY - gesture.startY;
      const intent = resolveFleetTimelinePointerDragIntent(deltaX, deltaY);
      const isSuspendedDropTarget = isFleetSuspendedDropZoneClientPoint(moveEvent.clientX, moveEvent.clientY);

      if (intent || isSuspendedDropTarget) {
        clearHoldTimer();
        ensureDragMode(pointerPosition);
      }

      if (gesture.dragStarted) {
        if (gesture.activeMode === "reorder") {
          updateImmediateSegmentReorder(pointerPosition);
        } else {
          updateImmediateSegmentReassignment(pointerPosition);
        }
      }
    };

    const handleWindowPointerUp = (upEvent) => {
      finishGesture(true, upEvent);
    };

    const handleWindowPointerCancel = (cancelEvent) => {
      finishGesture(false, cancelEvent);
    };

    gesture.cleanup = () => {
      clearHoldTimer();
      window.removeEventListener("pointermove", handleWindowPointerMove, true);
      window.removeEventListener("pointerup", handleWindowPointerUp, true);
      window.removeEventListener("pointercancel", handleWindowPointerCancel, true);
    };

    if (canStartReassignment || canStartReorder) {
      gesture.holdTimerId = window.setTimeout(() => {
        gesture.holdTimerId = null;
        if (gesture.dragStarted) {
          return;
        }

        const pointerPosition = {
          clientX: gesture.lastClientX,
          clientY: gesture.lastClientY,
        };

        if (canStartReassignment) {
          startReassignmentDrag(pointerPosition);
          return;
        }

        startReorderDrag(pointerPosition);
      }, REASSIGNMENT_POINTER_HOLD_DELAY_MS);
    }

    window.addEventListener("pointermove", handleWindowPointerMove, true);
    window.addEventListener("pointerup", handleWindowPointerUp, true);
    window.addEventListener("pointercancel", handleWindowPointerCancel, true);
    segmentPointerGestureRef.current = gesture;
  }, [
    clearReassignmentClickSuppression,
    clearSegmentPointerGesture,
    clearSegmentReorderClickSuppression,
    enforceP2P3TimeWindowsAsHard,
    finishImmediateSegmentReassignment,
    finishImmediateSegmentReorder,
    isPlanningMovementLocked,
    resetReassignmentDragState,
    resetSegmentReorderDragState,
    startBlockedPlanningMovementFeedback,
    startImmediateSegmentReassignment,
    startImmediateSegmentReorder,
    updateImmediateSegmentReassignment,
    updateImmediateSegmentReorder,
  ]);

  const handleGroupedSegmentPointerDown = React.useCallback((event, row, groupItem) => {
    if (event.button !== 0 || !event.isPrimary || !groupItem?.groupKey || typeof window === "undefined") {
      return;
    }

    const canStartReassignment = Array.isArray(groupItem?.memberSegments)
      && groupItem.memberSegments.some((segment) => Boolean(segment?.reassignmentDrag?.stepId && segment?.reassignmentDrag?.stepType))
      && !groupItem.memberSegments.some((segment) => segment?.isEndOfDayLoadSourceStep);
    const sourceDispatchPeriod = row?.dispatchPeriod === "evening" ? "evening" : "day";
    const canStartReorder = Array.isArray(groupItem?.memberSegments)
      && groupItem.memberSegments.some((segment) => Boolean(segment?.segmentReorder?.stepId && segment?.segmentReorder?.stepType && segment?.segmentReorder?.tripId));
    if (!canStartReassignment && !canStartReorder) {
      return;
    }

    if (isPlanningMovementLocked && startBlockedPlanningMovementFeedback(event, {
      assignmentId: row?.assignmentId || "",
      dispatchPeriod: sourceDispatchPeriod,
      groupKey: groupItem.groupKey,
      sourceSurface: "gantt",
      sourceType: "group",
    })) {
      return;
    }

    clearGroupedSegmentHoldGesture();
    clearGroupedSegmentCollapseTimer();
    clearSegmentPointerGesture();
    clearReassignmentClickSuppression();
    clearSegmentReorderClickSuppression();
    suppressGroupedSegmentClickRef.current = false;

    setFleetSegmentPressState({
      assignmentId: row.assignmentId,
      groupKey: groupItem.groupKey,
    });

    const gesture = {
      activeMode: "",
      cleanup: null,
      dragStarted: false,
      groupElement: event.currentTarget instanceof HTMLElement ? event.currentTarget : null,
      groupItem,
      holdTimerId: null,
      lastClientX: event.clientX,
      lastClientY: event.clientY,
      pointerId: event.pointerId,
      row,
      startX: event.clientX,
      startY: event.clientY,
    };

    const clearHoldTimer = () => {
      if (!gesture.holdTimerId) {
        return;
      }

      window.clearTimeout(gesture.holdTimerId);
      gesture.holdTimerId = null;
    };

    const cancelGesture = (pointerEvent = null) => {
      if (pointerEvent && pointerEvent.pointerId !== gesture.pointerId) {
        return;
      }

      clearGroupedSegmentHoldGesture();
    };

    const stopActiveDragMode = (pointerPosition, { shouldCommitReassignment = false } = {}) => {
      if (!gesture.dragStarted) {
        if (gesture.activeMode === "reassignment") {
          resetReassignmentDragState();
        } else if (gesture.activeMode === "reorder") {
          resetSegmentReorderDragState();
        }
        gesture.activeMode = "";
        return;
      }

      if (gesture.activeMode === "reorder") {
        finishImmediateSegmentReorder({ shouldCommit: false, pointerPosition });
      } else {
        finishImmediateSegmentReassignment({ shouldCommit: shouldCommitReassignment, pointerPosition });
      }

      gesture.dragStarted = false;
      gesture.activeMode = "";
    };

    const startDragFromGroup = (pointerPosition, mode = "reassignment") => {
      const candidateSegment = resolveGroupedSegmentDragCandidate(gesture.groupElement, gesture.groupItem, pointerPosition?.clientX);
      if (!candidateSegment) {
        return false;
      }

      const isReorder = mode === "reorder";

      suppressGroupedSegmentClickRef.current = true;

      if (gesture.activeMode && gesture.activeMode !== mode) {
        stopActiveDragMode(pointerPosition);
      }

      if (isReorder) {
        if (!canStartReorder) {
          return false;
        }

        gesture.activeMode = "reorder";

        const groupDragMembers = normalizeGroupDragMembers(gesture.groupItem, candidateSegment);
        const groupPreviewLabel = groupDragMembers.label || "Groupe";
        const groupMemberCount = groupDragMembers.memberCount || 1;
        const groupPreviewMeta = `${groupMemberCount} étape${groupMemberCount > 1 ? "s" : ""}`;
        const groupStepIds = groupDragMembers.groupStepIds;
        const timePriorityResetUpdates = buildSegmentTimePriorityResetUpdates(groupDragMembers.reorderGroupSegments, {
          enforceP2P3TimeWindowsAsHard,
          stepType: candidateSegment?.businessStep?.stepType || candidateSegment?.segmentReorder?.stepType || "",
        });

        const beginReorderDrag = () => {
          gesture.dragStarted = startImmediateSegmentReorder(row, candidateSegment, pointerPosition, {
            isGroupedDrag: true,
            label: groupPreviewLabel,
            meta: groupPreviewMeta,
            groupStepIds,
            sourceGroupKey: groupDragMembers.sourceGroupKey || gesture.groupItem?.groupKey || "",
            timePriorityResetUpdates,
          });
          if (gesture.dragStarted) {
            updateImmediateSegmentReorder(pointerPosition);
          }
        };

        if (typeof window === "undefined") {
          beginReorderDrag();
          return gesture.dragStarted;
        }

        const snapshotPointerId = gesture.pointerId;
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            if (groupedSegmentHoldGestureRef.current?.pointerId !== snapshotPointerId || gesture.dragStarted) {
              return;
            }

            beginReorderDrag();
          });
        });
        return true;
      }

      if (!canStartReassignment) {
        return false;
      }

      gesture.activeMode = "reassignment";
      const groupDragMembers = normalizeGroupDragMembers(gesture.groupItem, candidateSegment);
      gesture.dragStarted = startImmediateSegmentReassignment(row, candidateSegment, pointerPosition, {
        groupDrag: groupDragMembers,
      });
      if (gesture.dragStarted) {
        updateImmediateSegmentReassignment(pointerPosition);
      }

      return gesture.dragStarted;
    };

    const ensureDragMode = (pointerPosition) => {
      const deltaX = pointerPosition.clientX - gesture.startX;
      const deltaY = pointerPosition.clientY - gesture.startY;
      const intent = resolveFleetTimelinePointerDragIntent(deltaX, deltaY);
      const isSuspendedDropTarget = isFleetSuspendedDropZoneClientPoint(pointerPosition.clientX, pointerPosition.clientY);
      const isCrossPeriodDropTarget = isFleetCrossPeriodTimelineDropTargetClientPoint(
        pointerPosition.clientX,
        pointerPosition.clientY,
        sourceDispatchPeriod,
      );

      if ((isSuspendedDropTarget || isCrossPeriodDropTarget) && canStartReassignment) {
        if (gesture.activeMode === "reassignment" && gesture.dragStarted) {
          return true;
        }
        return startDragFromGroup(pointerPosition, "reassignment");
      }

      if (intent === "reorder" && canStartReorder) {
        if (gesture.activeMode === "reorder" && gesture.dragStarted) {
          return true;
        }
        return startDragFromGroup(pointerPosition, "reorder");
      }

      if (canStartReassignment) {
        if (gesture.activeMode === "reassignment" && gesture.dragStarted) {
          return true;
        }
        return startDragFromGroup(pointerPosition, "reassignment");
      }

      if (canStartReorder) {
        if (gesture.activeMode === "reorder" && gesture.dragStarted) {
          return true;
        }
        return startDragFromGroup(pointerPosition, "reorder");
      }

      return false;
    };

    const handleWindowPointerMove = (moveEvent) => {
      if (moveEvent.pointerId !== gesture.pointerId) {
        return;
      }

      gesture.lastClientX = moveEvent.clientX;
      gesture.lastClientY = moveEvent.clientY;
      const pointerPosition = {
        clientX: moveEvent.clientX,
        clientY: moveEvent.clientY,
      };
      const deltaX = moveEvent.clientX - gesture.startX;
      const deltaY = moveEvent.clientY - gesture.startY;
      const intent = resolveFleetTimelinePointerDragIntent(deltaX, deltaY);
      const isSuspendedDropTarget = isFleetSuspendedDropZoneClientPoint(moveEvent.clientX, moveEvent.clientY);

      if (intent || isSuspendedDropTarget) {
        clearHoldTimer();
        ensureDragMode(pointerPosition);
      }

      if (gesture.dragStarted) {
        if (gesture.activeMode === "reorder") {
          updateImmediateSegmentReorder(pointerPosition);
        } else {
          updateImmediateSegmentReassignment(pointerPosition);
        }
      }
    };

    const handleWindowPointerUp = (upEvent) => {
      if (upEvent.pointerId !== gesture.pointerId) {
        return;
      }

      const pointerPosition = {
        clientX: Number.isFinite(Number(upEvent?.clientX)) ? Number(upEvent.clientX) : gesture.lastClientX,
        clientY: Number.isFinite(Number(upEvent?.clientY)) ? Number(upEvent.clientY) : gesture.lastClientY,
      };
      const shouldCommitMovedGesture = hasReassignmentPointerMoved(
        { clientX: gesture.startX, clientY: gesture.startY },
        pointerPosition,
      );
      const didStartDrag = gesture.dragStarted;
      const dragGroupKey = gesture.groupItem?.groupKey || "";
      const shouldFinishAsReassignmentDrop = didStartDrag
        && shouldCommitMovedGesture
        && gesture.activeMode === "reorder"
        && canStartReassignment
        && (
          isFleetSuspendedDropZoneClientPoint(pointerPosition.clientX, pointerPosition.clientY)
          || isFleetCrossPeriodTimelineDropTargetClientPoint(
            pointerPosition.clientX,
            pointerPosition.clientY,
            sourceDispatchPeriod,
          )
        );
      cancelGesture(upEvent);

      if (didStartDrag) {
        if (shouldFinishAsReassignmentDrop) {
          startDragFromGroup(pointerPosition, "reassignment");
        }

        if (!gesture.dragStarted) {
          scheduleGroupedSegmentCollapse(dragGroupKey, GROUPED_SEGMENT_DRAG_COLLAPSE_DELAY_MS);
          return;
        }

        if (gesture.activeMode === "reorder") {
          finishImmediateSegmentReorder({ shouldCommit: shouldCommitMovedGesture, pointerPosition });
        } else {
          finishImmediateSegmentReassignment({ shouldCommit: shouldCommitMovedGesture, pointerPosition });
        }
        scheduleGroupedSegmentCollapse(dragGroupKey, GROUPED_SEGMENT_DRAG_COLLAPSE_DELAY_MS);
      }
    };

    const handleWindowPointerCancel = (cancelEvent) => {
      if (cancelEvent.pointerId !== gesture.pointerId) {
        return;
      }

      const pointerPosition = {
        clientX: Number.isFinite(Number(cancelEvent?.clientX)) ? Number(cancelEvent.clientX) : gesture.lastClientX,
        clientY: Number.isFinite(Number(cancelEvent?.clientY)) ? Number(cancelEvent.clientY) : gesture.lastClientY,
      };
      const didStartDrag = gesture.dragStarted;
      const dragGroupKey = gesture.groupItem?.groupKey || "";
      cancelGesture(cancelEvent);

      if (didStartDrag) {
        if (gesture.activeMode === "reorder") {
          finishImmediateSegmentReorder({ shouldCommit: false, pointerPosition });
        } else {
          finishImmediateSegmentReassignment({ shouldCommit: false, pointerPosition });
        }
        scheduleGroupedSegmentCollapse(dragGroupKey, GROUPED_SEGMENT_DRAG_COLLAPSE_DELAY_MS);
      }
    };

    gesture.cleanup = () => {
      clearHoldTimer();
      window.removeEventListener("pointermove", handleWindowPointerMove, true);
      window.removeEventListener("pointerup", handleWindowPointerUp, true);
      window.removeEventListener("pointercancel", handleWindowPointerCancel, true);
    };

    if (canStartReassignment || canStartReorder) {
      gesture.holdTimerId = window.setTimeout(() => {
        gesture.holdTimerId = null;
        if (gesture.dragStarted) {
          return;
        }

        suppressGroupedSegmentClickRef.current = true;
        const pointerPosition = {
          clientX: gesture.lastClientX,
          clientY: gesture.lastClientY,
        };

        if (canStartReassignment) {
          startDragFromGroup(pointerPosition, "reassignment");
          return;
        }

        startDragFromGroup(pointerPosition, "reorder");
      }, REASSIGNMENT_POINTER_HOLD_DELAY_MS);
    }

    window.addEventListener("pointermove", handleWindowPointerMove, true);
    window.addEventListener("pointerup", handleWindowPointerUp, true);
    window.addEventListener("pointercancel", handleWindowPointerCancel, true);
    groupedSegmentHoldGestureRef.current = gesture;
  }, [
    clearGroupedSegmentCollapseTimer,
    clearGroupedSegmentHoldGesture,
    clearReassignmentClickSuppression,
    clearSegmentPointerGesture,
    clearSegmentReorderClickSuppression,
    enforceP2P3TimeWindowsAsHard,
    finishImmediateSegmentReassignment,
    finishImmediateSegmentReorder,
    isPlanningMovementLocked,
    resetReassignmentDragState,
    resetSegmentReorderDragState,
    scheduleGroupedSegmentCollapse,
    startBlockedPlanningMovementFeedback,
    startImmediateSegmentReassignment,
    startImmediateSegmentReorder,
    updateImmediateSegmentReassignment,
    updateImmediateSegmentReorder,
  ]);

  const cancelActiveFleetSegmentInteraction = React.useCallback(() => {
    const segmentGesture = segmentPointerGestureRef.current;
    if (segmentGesture?.dragStarted) {
      const pointerPosition = {
        clientX: segmentGesture.lastClientX,
        clientY: segmentGesture.lastClientY,
      };

      if (segmentGesture.activeMode === "reorder") {
        finishImmediateSegmentReorder({ shouldCommit: false, pointerPosition });
      } else {
        finishImmediateSegmentReassignment({ shouldCommit: false, pointerPosition });
      }
    }

    clearSegmentPointerGesture();

    const groupedGesture = groupedSegmentHoldGestureRef.current;
    if (groupedGesture?.dragStarted) {
      const pointerPosition = {
        clientX: groupedGesture.lastClientX,
        clientY: groupedGesture.lastClientY,
      };

      if (groupedGesture.activeMode === "reorder") {
        finishImmediateSegmentReorder({ shouldCommit: false, pointerPosition });
      } else {
        finishImmediateSegmentReassignment({ shouldCommit: false, pointerPosition });
      }
    }

    clearGroupedSegmentHoldGesture();
    resetReassignmentDragState();
    resetSegmentReorderDragState();
  }, [
    clearGroupedSegmentHoldGesture,
    clearSegmentPointerGesture,
    finishImmediateSegmentReassignment,
    finishImmediateSegmentReorder,
    resetReassignmentDragState,
    resetSegmentReorderDragState,
  ]);

  React.useEffect(() => {
    const hasActiveInteraction = Boolean(
      reassignmentDragState
      || segmentReorderDragState
      || fleetSegmentPressState,
    );

    if (!isActiveView || !hasActiveInteraction || typeof window === "undefined") {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      cancelActiveFleetSegmentInteraction();
    };

    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    cancelActiveFleetSegmentInteraction,
    fleetSegmentPressState,
    isActiveView,
    reassignmentDragState,
    segmentReorderDragState,
  ]);

  React.useEffect(() => {
    if (!isActiveView) {
      resetReassignmentDragState();
      resetSegmentReorderDragState();
      closeFloatingSuspendedPanel();
      setExpandedSequenceGroupKeys([]);
      closeExpandedTimelineGroup();
      toggleTruckFilterDropdown(false);
      closeSegmentActionPopover();
      clearGroupedSegmentClickSuppression();
      clearReassignmentClickSuppression();
      clearSegmentReorderClickSuppression();
      clearGroupedSegmentHoldGesture();
      clearSegmentPointerGesture();
      clearRouteMapPictureInPictureRestoreState();
      closeRouteMapPictureInPictureWindow();
      setRouteMapPictureInPictureMountNode(null);
      setIsRouteMapPictureInPictureLinked(false);
      setIsRouteMapPictureInPictureExpanded(false);
    }
  }, [
    clearGroupedSegmentClickSuppression,
    clearReassignmentClickSuppression,
    clearSegmentPointerGesture,
    clearSegmentReorderClickSuppression,
    clearGroupedSegmentHoldGesture,
    closeRouteMapPictureInPictureWindow,
    closeExpandedTimelineGroup,
    closeFloatingSuspendedPanel,
    closeSegmentActionPopover,
    isActiveView,
    resetReassignmentDragState,
    resetSegmentReorderDragState,
    toggleTruckFilterDropdown,
  ]);

  React.useEffect(() => {
    return () => {
      clearGroupedSegmentCollapseTimer();
      clearGroupedSegmentHoldGesture();
      clearSegmentPointerGesture();
    };
  }, [clearGroupedSegmentCollapseTimer, clearGroupedSegmentHoldGesture, clearSegmentPointerGesture]);

  React.useEffect(() => {
    if (!expandedTimelineGroupKey) {
      return;
    }

    const hasExpandedGroup = [...filteredTimelineRows, ...visibleEveningTimelineRows]
      .some((row) => Array.isArray(row?.segments) && row.segments.some((segment) => segment?.sameSiteGroupKey === expandedTimelineGroupKey));
    if (!hasExpandedGroup) {
      closeExpandedTimelineGroup();
    }
  }, [closeExpandedTimelineGroup, expandedTimelineGroupKey, filteredTimelineRows, visibleEveningTimelineRows]);

  React.useEffect(() => {
    if (!expandedTimelineGroupKey || typeof document === "undefined") {
      return undefined;
    }

    const handlePointerDown = (event) => {
      const target = event.target;
      if (shouldKeepExpandedTimelineGroupOpenOnPointerDown(target, expandedTimelineGroupKey)) {
        return;
      }

      suppressCurrentTimelineSelectionSync();
      closeExpandedTimelineGroup();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        suppressCurrentTimelineSelectionSync();
        closeExpandedTimelineGroup();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [closeExpandedTimelineGroup, expandedTimelineGroupKey, suppressCurrentTimelineSelectionSync]);

  React.useEffect(() => {
    if (!isActiveView || !isSelectedMainTimeline || !visibleTimelineRows.length) {
      return;
    }

    const currentSelectedAssignmentId = selectedRow?.assignmentId || currentSelectedRow?.assignmentId || "";
    if (currentSelectedAssignmentId && visibleTimelineRows.some((row) => row.assignmentId === currentSelectedAssignmentId)) {
      return;
    }

    assignmentInteractionsRef.current?.onSelectAssignment?.(visibleTimelineRows[0].assignmentId);
  }, [currentSelectedRow?.assignmentId, isActiveView, isSelectedMainTimeline, selectedRow?.assignmentId, visibleTimelineRows]);

  const selectedAssignment = selectedRow?.assignment || null;
  const activeSelectedClientId = isSelectedMainTimeline ? selectedClientId : eveningSelectedClientId;
  const selectedTruckCompartmentProfile = React.useMemo(() => {
    if (Array.isArray(selectedAssignment?.truck_compartments) && selectedAssignment.truck_compartments.length) {
      return selectedAssignment.truck_compartments;
    }

    const fallbackTruckId = typeof selectedAssignment?.truck_id === "string" ? selectedAssignment.truck_id.trim() : "";
    if (!fallbackTruckId) {
      return [];
    }

    const fallbackCompartments = dashboardTruckCompartmentsByTruckId.get(fallbackTruckId);
    return Array.isArray(fallbackCompartments) ? fallbackCompartments : [];
  }, [dashboardTruckCompartmentsByTruckId, selectedAssignment?.truck_compartments, selectedAssignment?.truck_id]);
  React.useEffect(() => {
    const fallbackTruckId = typeof selectedAssignment?.truck_id === "string" ? selectedAssignment.truck_id.trim() : "";

    // Une liste fournie, même vide (dompeur), rend inutile le rechargement complet de /api/dashboard.
    if (!isActiveView || !fallbackTruckId || selectedTruckCompartmentProfile.length || Array.isArray(selectedAssignment?.truck_compartments)) {
      return undefined;
    }

    let isDisposed = false;

    const loadDashboardTruckCompartments = async () => {
      try {
        const response = await fetchWithSupabaseAuth("/api/dashboard");
        if (!response.ok) {
          return;
        }

        const payload = await response.json().catch(() => null);
        const nextCompartmentsByTruckId = buildTruckCompartmentsByTruckId(payload?.dashboard?.raw_trucks || []);

        if (isDisposed || !nextCompartmentsByTruckId.size) {
          return;
        }

        setDashboardTruckCompartmentsByTruckId((currentCompartmentsByTruckId) => {
          const mergedCompartmentsByTruckId = new Map(currentCompartmentsByTruckId);
          nextCompartmentsByTruckId.forEach((compartments, truckId) => {
            if (!mergedCompartmentsByTruckId.has(truckId)) {
              mergedCompartmentsByTruckId.set(truckId, compartments);
            }
          });

          return mergedCompartmentsByTruckId.size === currentCompartmentsByTruckId.size
            ? currentCompartmentsByTruckId
            : mergedCompartmentsByTruckId;
        });
      } catch {}
    };

    void loadDashboardTruckCompartments();

    return () => {
      isDisposed = true;
    };
  }, [isActiveView, selectedTruckCompartmentProfile.length, selectedAssignment?.truck_compartments, selectedAssignment?.truck_id]);
  const selectedShell = selectedAssignment
    ? buildAssignmentShellViewModel({
        assignment: selectedAssignment,
        assignmentId: selectedRow?.assignmentId || selectedAssignment?.truck_id || "",
        distanceSummary: selectedRow?.distanceLabel || selectedAssignment?.total_estimated_distance_label || "Distance estimée indisponible",
        timelineRenderState: selectedRow?.timelineRenderState,
      })
    : null;
  const selectedSequenceSourceSegments = React.useMemo(
    () => resolveVisibleRouteSequenceItems(
      buildFleetTimelineSourceSegments(selectedRow, {
        activeDispatchDate,
        fleetSegmentComparisonByKey,
        fleetSegmentHovercardContext,
      }),
    ),
    [activeDispatchDate, fleetSegmentComparisonByKey, fleetSegmentHovercardContext?.here_time_aware_routing_enabled, selectedFleetScopeKey, selectedRow?.segments],
  );
  const sequenceRows = React.useMemo(
    () => buildFleetSequenceDisplayItems(selectedSequenceSourceSegments),
    [selectedSequenceSourceSegments],
  );
  const sequenceTruckLoadByKey = React.useMemo(
    () => buildSequenceTruckLoadByKey(sequenceRows),
    [sequenceRows],
  );
  const sequenceDisplayOverrides = buildSameSiteSequenceDisplayOverrides(sequenceRows);
  const sequenceRenderItems = buildSequenceTableRenderItems(sequenceRows, {
    expandedGroupKeys: expandedSequenceGroupKeys,
  });
  // Pause dîner : ligne « Dîner » après l'arrêt (ou le groupe) qui la porte.
  const sequenceLunchBreak = resolveLunchBreakWindow(selectedRow?.assignment, sequenceRows.find(isLunchBreakSegment));
  const sequenceLunchItemIndex = sequenceLunchBreak
    ? sequenceRenderItems.findLastIndex((item) => [item.segment, ...(item.memberSegments || [])].some(isLunchBreakSegment))
    : -1;
  const sequenceTableItems = sequenceLunchItemIndex < 0
    ? sequenceRenderItems
    : sequenceRenderItems.toSpliced(sequenceLunchItemIndex + 1, 0, { key: "lunch-break", kind: "lunch", lunchBreak: sequenceLunchBreak });
  const sequenceOpeningVisibilityByItemKey = React.useMemo(
    () => buildRouteSequenceOpeningVisibilityByItemKey(sequenceRenderItems),
    [sequenceRenderItems],
  );
  const sequenceClosingVisibilityByItemKey = React.useMemo(
    () => buildRouteSequenceClosingVisibilityByItemKey(sequenceRenderItems),
    [sequenceRenderItems],
  );
  const sequenceCompartmentOccupancyByItem = React.useMemo(
    () => buildSequenceCompartmentOccupancyMap(sequenceRenderItems, selectedShell?.totalCompartments || 0),
    [selectedShell?.totalCompartments, sequenceRenderItems],
  );
  const sequenceCompartmentFillRateByItem = React.useMemo(
    () => buildSequenceCompartmentFillRateMap(sequenceRenderItems, selectedTruckCompartmentProfile),
    [selectedTruckCompartmentProfile, sequenceRenderItems],
  );
  const compartmentFillTruck = {
    truckCapacityTons: selectedAssignment?.capacity_tons,
    truckCompartments: selectedTruckCompartmentProfile,
  };
  const sequenceScrollRegionRef = React.useRef(null);
  const [savedSequenceColumnPrefs, setSavedSequenceColumnPrefs] = React.useState(null);
  const [sequenceNeedsHorizontalScroll, setSequenceNeedsHorizontalScroll] = React.useState(false);

  React.useEffect(() => {
    let isDisposed = false;
    fetchColumnPreferences(FLEET_SEQUENCE_COLUMN_PREFERENCE_KEY)
      .then((preferences) => {
        if (!isDisposed) {
          setSavedSequenceColumnPrefs(preferences && typeof preferences === "object" ? preferences : null);
        }
      })
      .catch(() => {
        if (!isDisposed) {
          setSavedSequenceColumnPrefs({
            table_key: FLEET_SEQUENCE_COLUMN_PREFERENCE_KEY,
            column_order: [],
            column_widths: {},
            column_labels: {},
          });
        }
      });
    return () => {
      isDisposed = true;
    };
  }, []);

  const sequenceColumnPrefs = useDataManagerColumnPreferences({
    baseColumns: FLEET_SEQUENCE_BASE_COLUMNS,
    onSaveError: handleColumnPreferencesSaveError,
    saveColumnPreferences,
    savedPrefs: savedSequenceColumnPrefs,
    tableKey: FLEET_SEQUENCE_COLUMN_PREFERENCE_KEY,
  });
  const sequenceColumnWidths = sequenceColumnPrefs.columnWidths;
  // Colonnes optionnelles vides pour tout le trajet : masquées, elles reviennent dès qu'une étape a une valeur.
  const hiddenSequenceColumnKeys = React.useMemo(() => {
    const hiddenKeys = new Set();
    if (sequenceRows.every((segment) => !resolveSequenceSegmentSiloLabel(segment))) {
      hiddenKeys.add("silo");
    }
    if (sequenceRows.every((segment) => !resolveSegmentWorkOrderLabel({ segment }))) {
      hiddenKeys.add("workOrder");
    }
    const displayOverrides = buildSameSiteSequenceDisplayOverrides(sequenceRows);
    const durationChipsBySegment = sequenceRows.map((segment, segmentIndex) => buildSequenceSegmentDurationChips(segment, displayOverrides, segmentIndex, {
      assignment: selectedRow?.assignment || null,
      hovercardContext: fleetSegmentHovercardContext,
      sequenceItems: sequenceRows,
    }));
    ["meunerie", "aspirator", "buffer"].forEach((columnKey) => {
      if (durationChipsBySegment.every((chips) => !getFleetSequenceDurationChip(chips, columnKey))) {
        hiddenKeys.add(columnKey);
      }
    });
    return hiddenKeys;
  }, [fleetSegmentHovercardContext, selectedRow?.assignment, sequenceRows]);
  const sequenceColumns = React.useMemo(
    () => sequenceColumnPrefs.resolvedColumns.filter((column) => !hiddenSequenceColumnKeys.has(column.key)),
    [hiddenSequenceColumnKeys, sequenceColumnPrefs.resolvedColumns],
  );

  React.useLayoutEffect(() => {
    const scrollRegion = sequenceScrollRegionRef.current;
    if (!scrollRegion) {
      return undefined;
    }

    const updateSequenceLayoutMetrics = () => {
      setSequenceNeedsHorizontalScroll(scrollRegion.scrollWidth > scrollRegion.clientWidth + 1);
    };

    updateSequenceLayoutMetrics();
    const resizeObserver = new ResizeObserver(updateSequenceLayoutMetrics);
    resizeObserver.observe(scrollRegion);
    const tableElement = scrollRegion.querySelector("table");
    if (tableElement instanceof HTMLElement) {
      resizeObserver.observe(tableElement);
    }
    return () => resizeObserver.disconnect();
  }, [selectedFleetScopeKey, sequenceColumnWidths, sequenceColumns, sequenceRows.length]);
  const sequenceTableWidth = React.useMemo(
    () => getFleetSequenceTableWidth(sequenceColumnWidths, sequenceColumns),
    [sequenceColumnWidths, sequenceColumns],
  );
  const sequenceTableStyle = React.useMemo(() => ({
    minWidth: `${sequenceTableWidth}px`,
    width: `${sequenceTableWidth}px`,
  }), [sequenceTableWidth]);
  const sequenceTableColumnStyles = React.useMemo(() => (
    sequenceColumns.reduce((styles, column) => {
      const columnKey = column.key;
      styles[columnKey] = buildFleetSequenceColumnStyle(resolveFleetSequenceColumnWidth(columnKey, sequenceColumnWidths));
      return styles;
    }, {})
  ), [sequenceColumnWidths, sequenceColumns]);
  const activeTrip = getRowActiveTrip(selectedRow, selectedRow?.selectedTripId || "")
    || (isSelectedMainTimeline ? trip : null)
    || null;
  const selectedSegment = sequenceRows.find((segment) => Boolean(segment.isSelectedClient || (activeSelectedClientId && segment.clientId === activeSelectedClientId))) || null;
  const routeMapPictureInPictureTitle = selectedRow?.truckLabel ? `Carte flotte - ${selectedRow.truckLabel}` : "Carte flotte";
  const raiseDetachedRouteMapWindow = React.useCallback((truckLabel = "") => {
    if (!isRouteMapPictureInPictureLinked) {
      return;
    }

    const normalizedLabel = String(truckLabel || selectedRow?.truckLabel || "").trim();
    void focusRouteMapPictureInPictureWindow({
      preserveExpanded: isRouteMapPictureInPictureExpanded,
      title: normalizedLabel ? `Carte flotte - ${normalizedLabel}` : routeMapPictureInPictureTitle,
    });
  }, [
    isRouteMapPictureInPictureExpanded,
    isRouteMapPictureInPictureLinked,
    routeMapPictureInPictureTitle,
    selectedRow?.truckLabel,
  ]);
  const handleTimelineGanttInteraction = React.useCallback(({ truckLabel = "" } = {}) => {
    raiseDetachedRouteMapWindow(truckLabel);
  }, [raiseDetachedRouteMapWindow]);
  const routeMapPictureInPictureDistanceLabel = selectedRow?.distanceLabel || selectedAssignment?.total_estimated_distance_label || "";
  const closeRouteMapPictureInPicture = React.useCallback(() => {
    clearRouteMapPictureInPictureRestoreState();
    closeRouteMapPictureInPictureWindow();
    setRouteMapPictureInPictureMountNode(null);
    setIsRouteMapPictureInPictureLinked(false);
    setIsRouteMapPictureInPictureExpanded(false);
  }, []);
  const handleRouteMapPictureInPictureExpandedChange = React.useCallback(async (nextExpanded, options = {}) => {
    if (options?.resizedInPlace === true) {
      setIsRouteMapPictureInPictureExpanded(nextExpanded);
      persistFleetPictureInPictureRestore(routeMapPictureInPictureTitle);
      closeFloatingMap();
      return;
    }

    if (options?.unavailable === true) {
      showToast({
        type: "info",
        title: "Plein écran indisponible",
        message: "Le navigateur n'a pas permis de passer la fenêtre détachée en plein écran. La fenêtre reste ouverte au même endroit.",
        duration: 7000,
      });
      return;
    }

    try {
      const pictureInPictureSize = nextExpanded
        ? resolveRouteMapPictureInPictureScreenSize()
        : resolveRouteMapPictureInPictureDefaultSize();
      const result = await openRouteMapPictureInPictureWindow({
        height: pictureInPictureSize.height,
        onClose: () => {
          setRouteMapPictureInPictureMountNode(null);
          setIsRouteMapPictureInPictureLinked(false);
          setIsRouteMapPictureInPictureExpanded(false);
          if (!isPageUnloadingRef.current) {
            clearRouteMapPictureInPictureRestoreState();
          }
        },
        preferInitialWindowPlacement: true,
        replaceExisting: true,
        title: routeMapPictureInPictureTitle,
        width: pictureInPictureSize.width,
      });

      if (result.ok) {
        setRouteMapPictureInPictureMountNode(result.mountNode || null);
        setIsRouteMapPictureInPictureLinked(true);
        setIsRouteMapPictureInPictureExpanded(nextExpanded);
        persistFleetPictureInPictureRestore(routeMapPictureInPictureTitle);
        closeFloatingMap();
        return;
      }

      showToast({
        type: "info",
        title: "Redimensionnement indisponible",
        message: "Le navigateur n'a pas permis d'agrandir la fenêtre détachée. Tu peux quand même l'agrandir manuellement depuis ses coins.",
        duration: 7000,
      });
    } catch (error) {
      showToast({
        type: "error",
        title: "Redimensionnement impossible",
        message: error instanceof Error && error.message
          ? error.message
          : "La fenêtre détachée n'a pas pu être redimensionnée.",
        duration: 7000,
      });
    }
  }, [closeFloatingMap, persistFleetPictureInPictureRestore, routeMapPictureInPictureTitle, showToast]);
  const activeRouteAlternativePreview = React.useMemo(() => {
    if (!routeAlternativePreview || typeof routeAlternativePreview !== "object") {
      return null;
    }

    const previewTripId = typeof routeAlternativePreview.tripId === "string" ? routeAlternativePreview.tripId.trim() : "";
    const activeTripId = typeof activeTrip?.trip_id === "string" ? activeTrip.trip_id.trim() : "";
    return !previewTripId || !activeTripId || previewTripId === activeTripId ? routeAlternativePreview : null;
  }, [activeTrip?.trip_id, routeAlternativePreview]);
  const selectedRouteAlternativePreview = React.useMemo(() => {
    if (!activeRouteAlternativePreview || !selectedSegment?.routeSegmentIndex) {
      return null;
    }

    const previewSegmentIndexes = normalizeFleetRouteSegmentIndexes(
      activeRouteAlternativePreview.routeSegmentIndex,
      activeRouteAlternativePreview.routeSegmentIndexes,
    );
    const selectedRouteSegmentIndex = Number(selectedSegment.routeSegmentIndex);
    return Number.isInteger(selectedRouteSegmentIndex) && previewSegmentIndexes.includes(selectedRouteSegmentIndex)
      ? activeRouteAlternativePreview
      : null;
  }, [activeRouteAlternativePreview, selectedSegment?.routeSegmentIndex]);
  const timelineGroupSyncTarget = React.useMemo(() => resolveTimelineGroupSyncTarget({
    expandedSequenceGroupKey: latestExpandedSequenceGroupKey,
    selectedRow,
    selectedSegment,
  }), [latestExpandedSequenceGroupKey, selectedRow, selectedSegment]);
  timelineGroupSyncTargetRef.current = timelineGroupSyncTarget;
  const isOpenEveningSegmentAction = openSegmentActionKey.startsWith(EVENING_TIMELINE_ACTION_KEY_PREFIX);
  const openSegmentActionLookupKey = isOpenEveningSegmentAction
    ? openSegmentActionKey.slice(EVENING_TIMELINE_ACTION_KEY_PREFIX.length)
    : openSegmentActionKey;
  const openSegmentContext = isSegmentActionPopoverOpen
    ? findSegmentContextByActionKey(
        isOpenEveningSegmentAction ? visibleEveningTimelineRows : visibleTimelineRows,
        openSegmentActionLookupKey,
      )
    : null;
  const openSegmentActionPresentation = openSegmentActionPresentationRef.current;
  const openSegmentActionSegment = (
    openSegmentActionPresentation?.segmentKey === openSegmentActionKey
      ? mergeTimelineSegmentActionPresentation(
          openSegmentContext?.segment || null,
          openSegmentActionPresentation?.segment || null,
        )
      : openSegmentContext?.segment || null
  );
  const selectedRowHasOvertimeWarning = Boolean(
    selectedRow?.exceedsMaxWork && selectedRow?.hasOvertimeWarning,
  );
  const selectedTimelineZoomOption = FLEET_TIMELINE_ZOOM_OPTIONS.find((option) => option.key === timelineZoomKey) || FLEET_TIMELINE_ZOOM_OPTIONS[0];
  const timelineContentStyle = selectedTimelineZoomOption.factor > 1
    ? { minWidth: `${Math.round(selectedTimelineZoomOption.factor * 100)}%` }
    : undefined;
  const {
    gutterPercent: timelineInformationalGutterPercent,
    segmentWidthPercent: timelineInformationalSegmentWidthPercent,
    trailingGutterPercent: timelineTrailingPaddingPercent,
  } = resolveInformationalTimelineLayout(visibleTimelineRows);
  const timelineLeadingPaddingPercent = timelineInformationalGutterPercent;
  const timelineBoundRows = React.useMemo(
    () => mergeOptimizationPreviewTimelineBounds(
      buildFleetTimelineBoundRows(visibleTimelineRows, {
        activeDispatchDate,
        fleetSegmentComparisonByKey,
        fleetSegmentHovercardContext,
      }),
      mainOptimizationPreviewByTruckId,
      { active: isOptimizationMainPreviewActive },
    ),
    [
      activeDispatchDate,
      fleetSegmentComparisonByKey,
      fleetSegmentHovercardContext?.here_time_aware_routing_enabled,
      isOptimizationMainPreviewActive,
      mainOptimizationPreviewByTruckId,
      visibleTimelineRows,
    ],
  );
  const timelineBounds = resolveTimelineBounds(timelineBoundRows);
  const timelineScaleMinutes = timelineBounds.durationMinutes;
  const {
    gutterPercent: eveningTimelineInformationalGutterPercent,
    segmentWidthPercent: eveningTimelineInformationalSegmentWidthPercent,
    trailingGutterPercent: eveningTimelineTrailingPaddingPercent,
  } = resolveInformationalTimelineLayout(visibleEveningTimelineRows);
  const eveningTimelineBoundRows = React.useMemo(
    () => mergeOptimizationPreviewTimelineBounds(
      buildFleetTimelineBoundRows(visibleEveningTimelineRows, { activeDispatchDate }),
      optimizationPreviewByDispatchPeriod.evening,
      { active: isOptimizationEveningPreviewActive },
    ),
    [
      activeDispatchDate,
      isOptimizationEveningPreviewActive,
      optimizationPreviewByDispatchPeriod.evening,
      visibleEveningTimelineRows,
    ],
  );
  const eveningTimelineBounds = resolveTimelineBounds(eveningTimelineBoundRows);
  const eveningTimelineScaleMinutes = eveningTimelineBounds.durationMinutes;
  // Comme la ligne du Gantt : une tournée à vérifier n'a pas de durée.
  const selectedRowDisplayedDurationLabel = selectedRow?.timelineRenderState?.status === "unrenderable"
    ? "Durée non calculable"
    : selectedRow
    ? resolveRowDisplayedTotalDurationLabel(selectedRow, {
        activeDispatchDate,
        fleetSegmentComparisonByKey,
        fleetSegmentHovercardContext,
      })
    : "";
  // Distance arrondie comme le Gantt (« 516 km » et non « 515.5 km »).
  const selectedRowDistanceKm = parseSequenceDistanceKm(selectedRow?.distanceLabel);
  const selectedRowDistanceLabel = selectedRowDistanceKm != null
    ? formatTruckTotalHoverDistance(selectedRowDistanceKm)
    : selectedRow?.distanceLabel;
  const selectedRouteSignalLabel = selectedRouteAlternativePreview?.mode === "current"
    ? "Actuel affiché"
    : selectedRouteAlternativePreview?.mode === "alternative"
      ? "Traverse affichée"
      : (
          selectedSegment?.routeAlternativeApplied
            ? "Traverse appliquée"
            : selectedSegment?.routeAlternativeAvailable
              ? "Alternative disponible"
              : routeAlternativesFeatureEnabled && selectedSegment?.hasMontrealRiskAlert
                ? "Risque Montréal"
                : ""
        );
  const selectedRouteSignalTone = routeAlternativesFeatureEnabled && selectedSegment?.hasMontrealRiskAlert && !selectedRouteAlternativePreview ? "warning" : "outline";
  const assignmentInteractions = interactions?.assignmentCard || {};
  const chromeInteractions = interactions?.chrome || {};
  const previewSafeChromeInteractions = isOptimizationPreviewInteractionLocked
    ? {
        ...chromeInteractions,
        isOptimizationPreviewInteractionLocked,
        canConfigureReassignmentProtection: chromeInteractions.canMutatePlanning === true,
        canApplyDispatchSourceChanges: false,
        canCloseDispatchDay: false,
        canInvalidateDispatchDay: false,
        canManagePullForward: false,
        canMutatePlanning: false,
        canReopenDispatchDay: false,
        canResetDispatchRevalidation: false,
        canResetPlanning: false,
        canRunOptimization: false,
        canSaveDispatchSnapshot: false,
        canRestoreDispatchSnapshot: false,
      }
    : chromeInteractions;
  const handleManageTruckIncidents = React.useCallback((payload = {}) => {
    const truckId = normalizeFleetIncidentText(payload?.truckId || payload?.truck_id);
    if (!truckId) {
      return;
    }
    setTruckIncidentsDialogScope({
      dispatchDate: normalizeFleetIncidentText(payload?.dispatchDate) || activeDispatchDate,
      dispatchPeriod: normalizeFleetIncidentText(payload?.dispatchPeriod).toLowerCase() === "evening" ? "evening" : "day",
      truckId,
      truckLabel: normalizeFleetIncidentText(payload?.truckName || payload?.truckLabel) || truckId,
    });
  }, [activeDispatchDate]);
  const handleSelectDayAssignment = React.useCallback((assignmentId = "") => {
    if (!assignmentId) {
      return;
    }

    setFleetSelection({ assignmentId, dispatchPeriod: activeSuspendedDispatchPeriod });
    setEveningSelectedClientId("");
    assignmentInteractions.onSelectAssignment?.(assignmentId);
  }, [activeSuspendedDispatchPeriod, assignmentInteractions]);
  const handleSelectEveningAssignment = React.useCallback((assignmentId = "") => {
    if (!assignmentId) {
      return;
    }

    setFleetSelection({ assignmentId, dispatchPeriod: "evening" });
    setEveningSelectedClientId("");
  }, []);
  const handleScopedRouteClientSelect = React.useCallback((selection = null) => {
    if (isSelectedMainTimeline) {
      onClientSelect?.(selection);
      return;
    }

    const clientId = typeof selection?.clientId === "string" ? selection.clientId : "";
    setEveningSelectedClientId(clientId);
    if (selection?.centerMap && clientId) {
      routeMapControllerRef.current?.zoomToClient?.(clientId, selection?.stopType || "");
    }
  }, [isSelectedMainTimeline, onClientSelect]);
  const shouldRenderEmbeddedMapRail = !isMapFloating && !isRouteMapPictureInPictureLinked && Boolean(selectedRow);
  const shouldRenderInlineSuspendedPanel = !isSuspendedPanelFloating;
  const shouldShowOptimizationProgressCard = isActiveView
    && isFleetOptimizationProgressCardVisible(optimizationJobState, {
      isPreviewDrainActive: isOptimizationPreviewInteractionLocked,
    });
  const optimizationPreviewRemainingMovementCount = resolveOptimizationPreviewCount(
    readOptimizationPreviewValue(optimizationJobState?.assignmentPreview,
      "remaining_movement_count",
      "remainingMovementCount",
      "remaining_movements",
      "remainingMovements",
      "moves_remaining",
      "movesRemaining",
    ),
    readOptimizationPreviewValue(optimizationJobState,
      "remaining_movement_count",
      "remainingMovementCount",
      "remaining_movements",
      "remainingMovements",
      "moves_remaining",
      "movesRemaining",
    ),
    optimizationPreviewVisitObserved
      ? Math.max(
        Number(optimizationPreviewQueueDepth) || 0,
        Math.max(0, optimizationPreviewRevisionDrainTarget - Math.max(0, optimizationPreviewDisplayedRevision)),
      )
      : null,
  );
  const shouldOverlayEmbeddedOptimizationProgressCard = shouldShowOptimizationProgressCard
    && (shouldRenderEmbeddedMapRail || shouldRenderInlineSuspendedPanel);
  const shouldOverlayFloatingOptimizationProgressCard = shouldShowOptimizationProgressCard;
  React.useLayoutEffect(() => {
    const overlay = optimizationProgressOverlayElement;
    if (!overlay) {
      setOptimizationProgressOverlayHeight(0);
      return undefined;
    }

    // Le panneau absolu doit réserver sa hauteur dans le rail, marges haute et basse comprises.
    const updateOverlayHeight = () => {
      setOptimizationProgressOverlayHeight(Math.ceil(overlay.getBoundingClientRect().height + overlay.offsetTop * 2));
    };
    updateOverlayHeight();
    const resizeObserver = new ResizeObserver(updateOverlayHeight);
    resizeObserver.observe(overlay);
    return () => resizeObserver.disconnect();
  }, [optimizationProgressOverlayElement]);
  const renderOptimizationProgressCard = (overlay = false) => {
    const card = (
      <OptimizationProgressCard
        jobState={displayedOptimizationJobState}
        onCancel={requestOptimizationCancel}
        onDismiss={dismissOptimizationNotice}
        optimizationPreviewReserve={optimizationPreviewReserve}
        optimizationPreviewQueueDepth={optimizationPreviewQueueDepth}
        optimizationPreviewRemainingMovementCount={optimizationPreviewRemainingMovementCount}
        isPreviewDrainActive={isOptimizationPreviewInteractionLocked}
        placement="fleet"
      />
    );

    return overlay ? (
      <div
        ref={setOptimizationProgressOverlayElement}
        className="pointer-events-none absolute inset-x-1.5 top-1.5 z-[60] h-fit overflow-visible"
        data-fleet-optimization-progress-overlay="true"
      >
        {card}
      </div>
    ) : card;
  };
  // Keep Suspendues visible even when search/filters hide every truck row; otherwise
  // matches that exist only in Suspendues disappear with the inspector rail.
  const shouldRenderEmbeddedInspectorRail = Boolean(
    !isMapFloating && (shouldRenderEmbeddedMapRail || shouldRenderInlineSuspendedPanel || shouldShowOptimizationProgressCard),
  );
  const shouldRenderFloatingSuspendedRail = Boolean(
    isMapFloating && (shouldRenderInlineSuspendedPanel || shouldShowOptimizationProgressCard),
  );
  const shouldRenderInlineInspectorRail = shouldRenderEmbeddedInspectorRail || shouldRenderFloatingSuspendedRail;
  const openFleetRouteMapPreview = React.useCallback(() => {
    const anchorElement = floatingMapAnchorRef.current;
    const fallbackPosition = typeof window !== "undefined"
      ? {
          x: window.scrollX + FLOATING_MAP_INITIAL_POSITION.x,
          y: window.scrollY + FLOATING_MAP_INITIAL_POSITION.y,
        }
      : FLOATING_MAP_INITIAL_POSITION;
    const anchorPosition = shouldRenderInlineSuspendedPanel && anchorElement instanceof HTMLElement && typeof window !== "undefined"
      ? (() => {
          const anchorRect = anchorElement.getBoundingClientRect();
          return {
            x: anchorRect.left + window.scrollX - FLOATING_MAP_COMPACT_WIDTH - FLOATING_MAP_INLINE_OPEN_GAP_PX,
            y: anchorRect.top + window.scrollY,
          };
        })()
      : fallbackPosition;
    openFloatingMap(anchorElement, anchorPosition);
  }, [openFloatingMap, shouldRenderInlineSuspendedPanel]);
  const handlePreviewRouteAlternativeMap = React.useCallback((preview) => {
    if (!preview || typeof preview !== "object") {
      return;
    }

    const current = preview.current && typeof preview.current === "object" ? preview.current : null;
    const alternative = preview.alternative && typeof preview.alternative === "object" ? preview.alternative : null;
    if (!current || !alternative) {
      return;
    }

    setRouteAlternativePreview({
      ...preview,
      alternative,
      current,
      mode: preview.mode === "current" ? "current" : "alternative",
      requestedAt: Date.now(),
    });
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(openFleetRouteMapPreview);
      return;
    }

    openFleetRouteMapPreview();
  }, [openFleetRouteMapPreview]);
  const segmentActionAssignmentInteractions = React.useMemo(() => ({
    ...assignmentInteractions,
    onPreviewRouteAlternative: handlePreviewRouteAlternativeMap,
  }), [assignmentInteractions, handlePreviewRouteAlternativeMap]);
  const renderSuspendedGroupTrailingAction = React.useCallback((suspendedItem) => (
    <SuspendedTransferAction
      assignmentInteractions={assignmentInteractions}
      dispatchDate={activeDispatchDate}
      item={suspendedItem}
      onTargetAssignmentSelect={handleCrossPeriodReassignmentSelection}
      sourceDispatchPeriod={suspendedItem?.dispatchPeriod}
      truckCapabilityById={truckCapabilityById}
      truckReassignmentTargets={truckReassignmentTargets}
    />
  ), [
    activeDispatchDate,
    assignmentInteractions,
    handleCrossPeriodReassignmentSelection,
    truckCapabilityById,
    truckReassignmentTargets,
  ]);
  const fullscreenDispatchRibbonModel = React.useMemo(() => selectDispatchRibbonData(state, {
    buildPlanningOptimizeConfirmationContent,
    includeSuspendedDeliveriesInOptimization,
  }), [includeSuspendedDeliveriesInOptimization, state]);
  const isDispatchTruckSheetPrintBusy = selectedTruckSheetPdfExportState.status === "launching"
    || selectedTruckSheetPdfExportState.status === "pending_async";
  const isDispatchTruckSheetSnapshotBlocked = fullscreenDispatchRibbonModel?.truckSheetPdf?.snapshotStatus === "stale"
    && fullscreenDispatchRibbonModel?.allowStaleSnapshotActions !== true;
  const dispatchTruckSheetPrintBlockedReason = isDispatchTruckSheetSnapshotBlocked
    ? "Dispatch à revalider : relancez « Optimiser » avant d'imprimer l'horaire."
    : "";
  const dispatchTruckSheetPrintDisabledReason = isDispatchTruckSheetPrintBusy
    ? "Un horaire chauffeur est déjà en préparation."
    : dispatchTruckSheetPrintBlockedReason
      || (!activeDispatchDate ? "Sélectionnez une date de dispatch avant d'imprimer l'horaire chauffeur." : "");
  const handleRequestTruckSheetPrint = React.useCallback((payload = {}) => {
    const truckId = normalizeSegmentMetaValue(payload?.truckId);
    const dispatchDate = normalizeSegmentMetaValue(payload?.dispatchDate) || activeDispatchDate;
    if (!truckId || !dispatchDate || isDispatchTruckSheetPrintBusy || isDispatchTruckSheetSnapshotBlocked) {
      return;
    }

    setTruckSheetPrintRequest({
      showCompartments: true,
      dispatchDate,
      dispatchPeriod: payload?.dispatchPeriod === "evening" ? "evening" : "day",
      fitOnePage: payload?.fitOnePage === true,
      orientation: payload?.orientation === "portrait" ? "portrait" : "landscape",
      showMap: payload?.showMap !== false,
      truckId,
      truckName: normalizeSegmentMetaValue(payload?.truckName) || truckId,
    });
  }, [activeDispatchDate, isDispatchTruckSheetPrintBusy, isDispatchTruckSheetSnapshotBlocked]);
  const handleCloseTruckSheetPrintDialog = React.useCallback(() => {
    setTruckSheetPrintRequest(null);
  }, []);
  const handleConfirmTruckSheetPrint = React.useCallback((confirmation = {}) => {
    if (!truckSheetPrintRequest) {
      return;
    }
    if (isDispatchTruckSheetPrintBusy) {
      showToast({
        duration: 5000,
        message: "Attendez la fin de l'export actuel avant d'en préparer un autre.",
        title: "Horaire déjà en préparation",
        type: "info",
      });
      return;
    }

    const notes = normalizeSegmentMetaValue(confirmation?.textPromptValue);
    const showCompartments = canChooseTruckSheetCompartments ? truckSheetPrintRequest.showCompartments !== false : true;
    const resultUrl = appendDispatchDateToUrl(
      buildDispatchTruckSheetPdfReadyRoute(
        truckSheetPrintRequest.truckId,
        truckSheetPrintRequest.dispatchPeriod,
      ),
      truckSheetPrintRequest.dispatchDate,
      truckSheetPrintRequest.dispatchPeriod,
    );
    const fingerprint = buildDispatchTruckSheetPdfFingerprint(
      fullscreenDispatchRibbonModel?.resultsExport?.fingerprint,
      {
        dispatchDate: truckSheetPrintRequest.dispatchDate,
        dispatchPeriod: truckSheetPrintRequest.dispatchPeriod,
        fitOnePage: truckSheetPrintRequest.fitOnePage === true,
        generationNonce: String(Date.now()),
        notes,
        orientation: truckSheetPrintRequest.orientation,
        truckId: truckSheetPrintRequest.truckId,
        showCompartments,
        showMap: truckSheetPrintRequest.showMap,
      },
    );
    const launchPromise = launchSelectedTruckSheetPdfExport({
      blocked: isDispatchTruckSheetSnapshotBlocked,
      blockedReason: dispatchTruckSheetPrintBlockedReason,
      dispatchPeriod: truckSheetPrintRequest.dispatchPeriod,
      fingerprint,
      fitOnePage: truckSheetPrintRequest.fitOnePage === true,
      notes,
      openDocumentWindow: true,
      orientation: truckSheetPrintRequest.orientation,
      resultUrl,
      truckIds: [truckSheetPrintRequest.truckId],
      showCompartments,
      showMap: truckSheetPrintRequest.showMap,
    });
    setTruckSheetPrintRequest(null);
    void launchPromise;
  }, [
    canChooseTruckSheetCompartments,
    dispatchTruckSheetPrintBlockedReason,
    fullscreenDispatchRibbonModel?.resultsExport?.fingerprint,
    isDispatchTruckSheetPrintBusy,
    isDispatchTruckSheetSnapshotBlocked,
    launchSelectedTruckSheetPdfExport,
    showToast,
    truckSheetPrintRequest,
  ]);
  const fleetDispatchDayControls = fullscreenDispatchRibbonModel?.dispatchDayControls && typeof fullscreenDispatchRibbonModel.dispatchDayControls === "object"
    ? fullscreenDispatchRibbonModel.dispatchDayControls
    : null;
  const fleetActiveDispatchStatusKey = String(
    fleetDispatchDayControls?.activeDispatchStatus
    || fleetDispatchDayControls?.activeStatus?.key
    || "",
  ).trim().toLowerCase();
  const fleetActiveDispatchState = String(fleetDispatchDayControls?.activeDispatchState || "").trim().toLowerCase();
  const fleetActiveItemDispatchState = String(
    fleetDispatchDayControls?.activeItem?.dispatchDayState
    || fleetDispatchDayControls?.activeItem?.dispatch_day_state
    || "",
  ).trim().toLowerCase();
  const isFleetArchivedDispatchDay = fleetActiveDispatchState === "archived"
    || fleetActiveItemDispatchState === "archived";
  const isFleetClosedDispatchDay = fleetDispatchDayControls?.activeItem?.isClosed === true
    || fleetActiveDispatchStatusKey === "cloture"
    || fleetActiveDispatchStatusKey === "closed"
    || fleetActiveDispatchState === "closed";
  // Bouton « Ajouter » et lignes vides de la journée affichée ; jamais sur une journée fermée.
  const isStandaloneUnitsDayOpen = !isFleetClosedDispatchDay && !isFleetArchivedDispatchDay;
  const standaloneUnits = useStandaloneUnitsCatalog({ dispatchDate: activeDispatchDate, dispatchPeriod: activeSuspendedDispatchPeriod,
    enabled: isActiveView && isStandaloneUnitsDayOpen });
  const ganttTruckIds = React.useMemo(() => new Set(sortedRows.map(resolveFleetTimelineRowTruckKey)), [sortedRows]);
  const canWriteStandaloneUnits = assignmentInteractions.canMutatePlanning === true && !isOptimizationPreviewInteractionLocked;
  const standaloneUnitRows = isStandaloneUnitsDayOpen ? (
    <GanttStandaloneUnitRows catalog={standaloneUnits.catalog} ganttTruckIds={ganttTruckIds} dispatchDate={activeDispatchDate}
      dispatchPeriod={activeSuspendedDispatchPeriod} canWrite={canWriteStandaloneUnits}
      onUpdateTruckTractor={canWriteStandaloneUnits ? (payload = {}) => assignmentInteractions.onUpdateTruckTractor?.({
        ...payload, expectedRuntimeRevision: state?.resultsData?.runtime_revision || "",
      }) : null} />
  ) : null;
  const truckIncidentsDialogPeriod = truckIncidentsDialogScope?.dispatchPeriod === "evening" ? "evening" : "day";
  const truckIncidentsDialogRows = truckIncidentsDialogPeriod === activeSuspendedDispatchPeriod
    ? sortedRows
    : sortedEveningTimelineRows;
  const truckIncidentsDialogRow = truckIncidentsDialogRows.find(
    (row) => resolveFleetTimelineRowTruckKey(row) === truckIncidentsDialogScope?.truckId,
  ) || null;
  const truckIncidentsDialogRuntimeRevision = truckIncidentsDialogPeriod === activeSuspendedDispatchPeriod
    ? normalizeFleetIncidentText(state?.resultsData?.runtime_revision)
    : normalizeFleetIncidentText(eveningGanttRailState?.payload?.runtime_revision);
  const isTruckIncidentsDialogReadOnly = assignmentInteractions.canMutatePlanning !== true
    || isFleetClosedDispatchDay
    || isFleetArchivedDispatchDay
    || isOptimizationPreviewInteractionLocked;
  const truckIncidentsDialogReadOnlyReason = isFleetArchivedDispatchDay
    ? "Journée archivée : les incidents sont consultables, mais ne peuvent plus être modifiés."
    : isFleetClosedDispatchDay
      ? "Journée clôturée : rouvrez-la avant d'ajouter, modifier ou supprimer un incident."
      : assignmentInteractions.canMutatePlanning !== true
        ? "Votre rôle permet de consulter ces incidents, sans les modifier."
        : isOptimizationPreviewInteractionLocked
          ? "Aperçu provisoire : les incidents sont consultables, sans modification."
        : "";
  const handleSegmentMapZoomToggle = React.useCallback((row, segment) => {
    if (!segment?.isSelectable || !segment?.clientId) {
      return;
    }

    const focusKey = getTimelineSegmentActionKey(row?.assignmentId || "", segment);
    const currentSegmentKey = selectedSegment
      ? getTimelineSegmentActionKey(selectedRow?.assignmentId || "", selectedSegment)
      : "";
    if (!focusKey) {
      return;
    }

    if (activeMapZoomSegmentKeyRef.current === focusKey) {
      pendingSegmentMapFocusRef.current = { mode: "restore" };
      applyPendingSegmentMapFocus();
      return;
    }

    pendingSegmentMapFocusRef.current = {
      clientId: segment.clientId,
      focusKey,
      mode: "zoom",
      stopType: segment.stopType || "",
    };

    const targetDispatchPeriod = row?.dispatchPeriod === "evening" ? "evening" : "day";
    const isTargetMainTimeline = targetDispatchPeriod === activeSuspendedDispatchPeriod;
    const isChangingFleetScope = selectedDispatchPeriod !== targetDispatchPeriod
      || selectedRow?.assignmentId !== row?.assignmentId;
    if (isTargetMainTimeline) {
      handleSelectDayAssignment(row?.assignmentId || "");
    } else {
      handleSelectEveningAssignment(row?.assignmentId || "");
      setEveningSelectedClientId(segment.clientId);
    }

    if (currentSegmentKey !== focusKey && isTargetMainTimeline) {
      chromeInteractions.onRouteClientSelect?.({
        assignmentId: row.assignmentId,
        centerMap: true,
        clientId: segment.clientId,
        openSelectionOnly: true,
        routeSegmentIndex: segment.routeSegmentIndex,
        tripId: segment.tripId,
      });
    }

    if (isChangingFleetScope) {
      return;
    }

    if (!applyPendingSegmentMapFocus() && typeof window !== "undefined") {
      window.requestAnimationFrame(() => {
        applyPendingSegmentMapFocus();
      });
    }
  }, [activeSuspendedDispatchPeriod, applyPendingSegmentMapFocus, chromeInteractions, handleSelectDayAssignment, handleSelectEveningAssignment, selectedDispatchPeriod, selectedRow?.assignmentId, selectedSegment]);

  React.useEffect(() => {
    applyPendingSegmentMapFocus();
  }, [
    activeTrip?.trip_id,
    applyPendingSegmentMapFocus,
    routeMapRenderMeta?.markerCount,
    routeMapRenderMeta?.rendered,
    routeMapRenderMeta?.routePointCount,
    activeSelectedClientId,
  ]);

  React.useEffect(() => {
    const currentSegmentKey = selectedSegment
      ? getTimelineSegmentActionKey(selectedRow?.assignmentId || "", selectedSegment)
      : "";

    if (activeMapZoomSegmentKeyRef.current && activeMapZoomSegmentKeyRef.current !== currentSegmentKey) {
      activeMapZoomSegmentKeyRef.current = "";
    }
  }, [selectedRow?.assignmentId, selectedSegment]);

  React.useEffect(() => {
    if (!isActiveView || !activationHighlightTarget?.assignmentId || typeof window === "undefined") {
      return;
    }

    const containerElement = timelineRowsContainerRef.current;
    if (!(containerElement instanceof HTMLElement)) {
      return;
    }

    const escapedAssignmentId = window.CSS?.escape
      ? window.CSS.escape(activationHighlightTarget.assignmentId)
      : activationHighlightTarget.assignmentId;
    const escapedDispatchPeriod = window.CSS?.escape
      ? window.CSS.escape(activationHighlightTarget.dispatchPeriod)
      : activationHighlightTarget.dispatchPeriod;
    const rowElement = containerElement.querySelector(`[data-fleet-timeline-row="true"][data-assignment-id="${escapedAssignmentId}"][data-dispatch-period="${escapedDispatchPeriod}"]`);

    if (rowElement instanceof HTMLElement) {
      rowElement.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
    }
  }, [activationHighlightTarget, isActiveView]);

  React.useEffect(() => {
    setExpandedSequenceGroupKeys((currentKeys) => {
      if (!currentKeys.length) {
        return currentKeys;
      }

      const validGroupKeys = new Set(
        sequenceRows
          .map((segment) => (typeof segment?.sameSiteGroupKey === "string" ? segment.sameSiteGroupKey.trim() : ""))
          .filter(Boolean),
      );
      const nextKeys = currentKeys.filter((groupKey) => validGroupKeys.has(groupKey));

      return nextKeys.length === currentKeys.length ? currentKeys : nextKeys;
    });
  }, [sequenceRows]);

  React.useEffect(() => {
    if (!timelineGroupSyncTarget?.groupKey) {
      suppressedTimelineSelectionSyncRef.current = null;
      if (expandedTimelineGroupState?.source === "sequence-sync" || expandedTimelineGroupState?.source === "selection-sync") {
        closeExpandedTimelineGroup();
      }
      return;
    }

    const suppressedSelectionSync = suppressedTimelineSelectionSyncRef.current;
    if (
      timelineGroupSyncTarget.source === "selection-sync"
      && suppressedSelectionSync?.assignmentId === timelineGroupSyncTarget.assignmentId
      && suppressedSelectionSync?.groupKey === timelineGroupSyncTarget.groupKey
    ) {
      return;
    }
    suppressedTimelineSelectionSyncRef.current = null;

    if (
      expandedTimelineGroupState?.source === "click"
      && timelineGroupSyncTarget.source === "selection-sync"
      && (
        expandedTimelineGroupState?.assignmentId !== timelineGroupSyncTarget.assignmentId
        || expandedTimelineGroupKey !== timelineGroupSyncTarget.groupKey
      )
    ) {
      return;
    }

    if (
      expandedTimelineGroupState?.assignmentId === timelineGroupSyncTarget.assignmentId
      && expandedTimelineGroupKey === timelineGroupSyncTarget.groupKey
    ) {
      return;
    }

    openExpandedTimelineGroup(selectedRow, timelineGroupSyncTarget, timelineGroupSyncTarget.source);
  }, [
    closeExpandedTimelineGroup,
    expandedTimelineGroupKey,
    expandedTimelineGroupState?.assignmentId,
    expandedTimelineGroupState?.source,
    openExpandedTimelineGroup,
    selectedRow,
    timelineGroupSyncTarget,
  ]);

  const isTimelineRowExcludedFromOptimization = (row) => (
    optimizationExcludedTruckIds.has(resolveFleetTimelineRowTruckKey(row))
  );
  const renderDayTimelineRow = (row) => (
    <FleetTimelineRow
      isInclined={perspective.isInclined}
      key={`day:${row.assignmentId}`}
      activeDispatchDate={activeDispatchDate}
      consumeGroupedSegmentClickSuppression={consumeGroupedSegmentClickSuppression}
      consumeSegmentReorderClickSuppression={consumeSegmentReorderClickSuppression}
      consumeReassignmentClickSuppression={consumeReassignmentClickSuppression}
      disableHoverCards={Boolean(reassignmentDragState || segmentReorderDragState || blockedPlanningMovementFeedback)}
      blockedPlanningMovementFeedback={blockedPlanningMovementFeedback}
      displayDispatchPeriod={activeSuspendedDispatchPeriod}
      expandedTimelineGroupKey={expandedTimelineGroupKey}
      fleetSegmentComparisonByKey={fleetSegmentComparisonByKey}
      fleetSegmentHovercardContext={fleetSegmentHovercardContext}
      fleetSegmentPressState={fleetSegmentPressState}
      geotabReal={geotabReal.data?.byTruckId.get(resolveFleetTimelineRowTruckKey(row)) ? {
        truck: geotabReal.data.byTruckId.get(resolveFleetTimelineRowTruckKey(row)),
        tolerance: geotabTolerance,
        showProgress: ganttDisplay.geotab_progress,
        showDurations: ganttDisplay.geotab_durations,
        links: geotabRealLinks,
      } : null}
      geotabGap={isDayDispatchPeriod ? geotabGaps.get(resolveFleetTimelineRowTruckKey(row)) || null : null}
      showProductTags={showProductTags}
      alertDisplay={canSeeGeotabReal && alertsEnabled(ganttDisplay) ? ganttDisplay : null}
      haversineRouteWarningIssue={routingIssueByTruckId.get(resolveFleetTimelineRowTruckKey(row)) || null}
      linkedSegmentSelection={selectedSegment}
      onDoubleClickSegment={handleSegmentMapZoomToggle}
      openSegmentActionKey={openSegmentActionKey}
      onDropReassignment={isOptimizationPreviewInteractionLocked ? null : handleReassignmentDrop}
      onExpandTimelineGroup={openExpandedTimelineGroup}
      onGroupedSegmentPointerDown={isOptimizationPreviewInteractionLocked ? null : handleGroupedSegmentPointerDown}
      onOpenSegmentPopover={isOptimizationPreviewInteractionLocked ? null : openTimelineSegmentActionPopover}
      onOpenError={chromeInteractions.onOpenError}
      onReassignmentDragEnd={isOptimizationPreviewInteractionLocked ? null : handleReassignmentDragEnd}
      onReassignmentDragOver={isOptimizationPreviewInteractionLocked ? null : handleReassignmentDragOver}
      onReassignmentDragStart={isOptimizationPreviewInteractionLocked ? null : handleSegmentReassignmentDragStart}
      onSegmentPointerDown={isOptimizationPreviewInteractionLocked ? null : handleTimelineSegmentPointerDown}
      registerSegmentActionAnchor={segmentActionPopoverRefs.registerAnchor}
      reassignmentDragState={reassignmentDragState}
      segmentReorderDragState={segmentReorderDragState}
      row={row}
      scaleMinutes={timelineScaleMinutes}
      showHaversineRouteWarning={persistentHaversineRouteWarningTruckIds.has(resolveFleetTimelineRowTruckKey(row))}
      leadingPaddingPercent={timelineLeadingPaddingPercent}
      trailingPaddingPercent={timelineTrailingPaddingPercent}
      informationalSegmentWidthPercent={timelineInformationalSegmentWidthPercent}
      timelineStartMinutes={timelineBounds.startMinutes}
      truckLabelById={truckLabelById}
      truckReassignmentTargets={truckReassignmentTargets}
      truckSourceChanges={dayTruckSourceChanges}
      selectedClientId={activeSelectedClientId}
      onOptimizeTruckRoutes={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onOptimizeTruckRoutes}
      onRecalculateTruckRoute={isOptimizationPreviewInteractionLocked || assignmentInteractions.canMutatePlanning !== true ? null : assignmentInteractions.onRecalculateTruckRoute}
      onManageTruckIncidents={handleManageTruckIncidents}
      onPrintTruckSheet={handleRequestTruckSheetPrint}
      onPublishTruckPortal={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onPublishTruckPortal}
      onReassignAllTruckOrders={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onReassignAllTruckOrders}
      onSetTruckPreloadTime={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onSetTruckPreloadTime}
      onSelectAssignment={handleSelectDayAssignment}
      onTimelineGanttInteraction={handleTimelineGanttInteraction}
      onUpdateTruckDriverName={isOptimizationPreviewInteractionLocked || assignmentInteractions.canMutatePlanning !== true ? null : (payload = {}) => assignmentInteractions.onUpdateTruckDriverName?.({
        ...payload,
        expectedRuntimeRevision: payload.dispatchPeriod === activeSuspendedDispatchPeriod
          ? state?.resultsData?.runtime_revision || ""
          : eveningGanttRailState?.payload?.runtime_revision || "",
      })}
      onUpdateTruckTractor={isOptimizationPreviewInteractionLocked || assignmentInteractions.canMutatePlanning !== true ? null : (payload = {}) => assignmentInteractions.onUpdateTruckTractor?.({
        ...payload,
        expectedRuntimeRevision: payload.dispatchPeriod === activeSuspendedDispatchPeriod
          ? state?.resultsData?.runtime_revision || ""
          : eveningGanttRailState?.payload?.runtime_revision || "",
      })}
      onUpdateTruckStartTime={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onUpdateTruckStartTime}
      onUpdateTruckTimeSettings={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onUpdateTruckTimeSettings}
      onToggleTruckDispatchDisabled={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onToggleTruckDispatchDisabled}
      onToggleTruckDompeurMixedLoad={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onToggleTruckDompeurMixedLoad}
      onToggleTruckOrdersProtection={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onToggleTruckOrdersProtection}
      onToggleTruckOvertime={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onToggleTruckOvertime}
      onToggleTruckProtection={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onToggleTruckProtection}
      onUnpublishTruckPortal={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onUnpublishTruckPortal}
      optimizationPreview={isOptimizationMainPreviewActive && !isTimelineRowExcludedFromOptimization(row)
        ? {
          ...(mainOptimizationPreviewByTruckId.get(row.assignmentId) || { labels: [] }),
          activityPhase: optimizationPreviewActivityPhase,
          operationKind: optimizationJobState?.operationKind,
        }
        : null}
      optimizationPreviewMode={isOptimizationMainPreviewActive && !isTimelineRowExcludedFromOptimization(row)}
      portalPublications={assignmentInteractions.portalPublications}
      onRouteHoverEnd={chromeInteractions.onRouteHoverEnd}
      onRouteSegmentHoverStart={chromeInteractions.onRouteSegmentHoverStart}
      onRouteSequenceSelect={chromeInteractions.onRouteSequenceSelect}
      onSelectSegment={handleSegmentMapZoomToggle}
      truckSheetPrintDisabledReason={dispatchTruckSheetPrintDisabledReason}
      truckSheetPrintIsBusy={isDispatchTruckSheetPrintBusy}
      truckTimeSettingsDisabledReason={truckTimeSettingsDisabledReason}
    />
  );
  const renderEveningTimelineRow = (row) => (
    <EveningTimelineRailRow
      isInclined={perspective.isInclined}
      key={`evening:${row.assignmentId}`}
      activeDispatchDate={activeDispatchDate}
      consumeGroupedSegmentClickSuppression={consumeGroupedSegmentClickSuppression}
      consumeSegmentReorderClickSuppression={consumeSegmentReorderClickSuppression}
      consumeReassignmentClickSuppression={consumeReassignmentClickSuppression}
      disableHoverCards={Boolean(reassignmentDragState || segmentReorderDragState || blockedPlanningMovementFeedback)}
      blockedPlanningMovementFeedback={blockedPlanningMovementFeedback}
      expandedTimelineGroupKey={expandedTimelineGroupKey}
      fleetSegmentComparisonByKey={fleetSegmentComparisonByKey}
      fleetSegmentHovercardContext={fleetSegmentHovercardContext}
      fleetSegmentPressState={fleetSegmentPressState}
      informationalSegmentWidthPercent={eveningTimelineInformationalSegmentWidthPercent}
      leadingPaddingPercent={eveningTimelineInformationalGutterPercent}
      linkedSegmentSelection={selectedSegment}
      onDoubleClickSegment={handleSegmentMapZoomToggle}
      onManageTruckIncidents={handleManageTruckIncidents}
      onDropReassignment={isOptimizationPreviewInteractionLocked ? null : handleReassignmentDrop}
      onExpandTimelineGroup={openExpandedTimelineGroup}
      onGroupedSegmentPointerDown={isOptimizationPreviewInteractionLocked ? null : handleGroupedSegmentPointerDown}
      onOpenSegmentPopover={isOptimizationPreviewInteractionLocked ? null : openTimelineSegmentActionPopover}
      onPrintTruckSheet={handleRequestTruckSheetPrint}
      onReassignmentDragEnd={isOptimizationPreviewInteractionLocked ? null : handleReassignmentDragEnd}
      onReassignmentDragOver={isOptimizationPreviewInteractionLocked ? null : handleReassignmentDragOver}
      onReassignmentDragStart={isOptimizationPreviewInteractionLocked ? null : handleSegmentReassignmentDragStart}
      onRouteHoverEnd={chromeInteractions.onRouteHoverEnd}
      onRouteSegmentHoverStart={chromeInteractions.onRouteSegmentHoverStart}
      onRouteSequenceSelect={chromeInteractions.onRouteSequenceSelect}
      onSegmentPointerDown={isOptimizationPreviewInteractionLocked ? null : handleTimelineSegmentPointerDown}
      onSelectAssignment={handleSelectEveningAssignment}
      onUpdateTruckDriverName={isOptimizationPreviewInteractionLocked || assignmentInteractions.canMutatePlanning !== true ? null : (payload = {}) => assignmentInteractions.onUpdateTruckDriverName?.({
        ...payload, dispatchPeriod: "evening", expectedRuntimeRevision: eveningGanttRailState?.payload?.runtime_revision || "",
      })}
      onUpdateTruckTractor={isOptimizationPreviewInteractionLocked || assignmentInteractions.canMutatePlanning !== true ? null : (payload = {}) => assignmentInteractions.onUpdateTruckTractor?.({
        ...payload, dispatchPeriod: "evening", expectedRuntimeRevision: eveningGanttRailState?.payload?.runtime_revision || "",
      })}
      onUpdateTruckTimeSettings={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onUpdateTruckTimeSettings}
      onSelectSegment={handleSegmentMapZoomToggle}
      onTimelineGanttInteraction={handleTimelineGanttInteraction}
      optimizationPreview={isOptimizationEveningPreviewActive && !isTimelineRowExcludedFromOptimization(row)
        ? {
          ...(optimizationPreviewByDispatchPeriod.evening.get(row.assignmentId) || { labels: [] }),
          activityPhase: optimizationPreviewActivityPhase,
          operationKind: optimizationJobState?.operationKind,
        }
        : null}
      optimizationPreviewMode={isOptimizationEveningPreviewActive && !isTimelineRowExcludedFromOptimization(row)}
      openSegmentActionKey={openSegmentActionKey}
      registerSegmentActionAnchor={segmentActionPopoverRefs.registerAnchor}
      reassignmentDragState={reassignmentDragState}
      segmentReorderDragState={segmentReorderDragState}
      row={row}
      scaleMinutes={eveningTimelineScaleMinutes}
      selectedClientId={activeSelectedClientId}
      timelineStartMinutes={eveningTimelineBounds.startMinutes}
      trailingPaddingPercent={eveningTimelineTrailingPaddingPercent}
      truckLabelById={eveningTruckLabelById}
      truckReassignmentTargets={eveningTruckReassignmentTargets}
      truckSheetPrintDisabledReason={dispatchTruckSheetPrintDisabledReason}
      truckSheetPrintIsBusy={isDispatchTruckSheetPrintBusy}
    />
  );
  const filterSuspendedPanelModelForSearch = React.useCallback((model) => {
    if (!model || typeof model !== "object") {
      return {
        countLabel: "0 commande suspendue",
        deliveryCount: 0,
        hasItems: false,
        items: [],
      };
    }
    if (!hasTimelineSearch) {
      return model;
    }

    const sourceItems = Array.isArray(model.items) ? model.items : [];
    const items = sourceItems.filter((item) => (
      normalizeFleetSearch(buildSuspendedSearchValue(item)).includes(normalizedTimelineQuery)
    ));
    const deliveryCount = items.reduce((total, item) => {
      if (item?.type === "group") {
        const memberCount = Number(item.memberCount);
        if (Number.isFinite(memberCount) && memberCount > 0) {
          return total + memberCount;
        }
        if (Array.isArray(item.memberItems) && item.memberItems.length) {
          return total + item.memberItems.length;
        }
        if (Array.isArray(item.deliveryIds) && item.deliveryIds.length) {
          return total + item.deliveryIds.length;
        }
        return total;
      }
      return total + 1;
    }, 0);

    return {
      ...model,
      countLabel: deliveryCount
        ? `${deliveryCount} commande(s) suspendue(s)`
        : "0 commande suspendue",
      deliveryCount,
      hasItems: items.length > 0,
      items,
    };
  }, [hasTimelineSearch, normalizedTimelineQuery]);
  const filteredSuspendedPanelModel = React.useMemo(
    () => filterSuspendedPanelModelForSearch(suspendedPanelModel),
    [filterSuspendedPanelModelForSearch, suspendedPanelModel],
  );
  const filteredEveningSuspendedPanelModel = React.useMemo(
    () => filterSuspendedPanelModelForSearch(eveningSuspendedPanelModel),
    [eveningSuspendedPanelModel, filterSuspendedPanelModelForSearch],
  );

  if (!hasMountNode) {
    return null;
  }

  const optimizationGanttWaitingState = shouldShowOptimizationGanttWaiting ? (
    <OptimizationGanttWaitingState
      animationEnabled={optimizationAnimationEnabled}
      isPlanningReset={shouldShowMainResetWaiting}
      jobState={!planningResetLaunchScope && optimizationPreviewJobDate === activeDispatchDate
        && optimizationPreviewJobPeriod === activeSuspendedDispatchPeriod
        ? optimizationJobState
        : null}
      orderCount={firstOptimizationOrderCount}
    />
  ) : null;

  const eveningOptimizationGanttWaitingState = shouldShowEveningRail && shouldShowEveningResetWaiting ? (
    <OptimizationGanttWaitingState
      animationEnabled={optimizationAnimationEnabled}
      isPlanningReset
      jobState={planningResetLaunchScope ? null : optimizationJobState}
    />
  ) : null;

  if (!rows.length && !eveningTimelineRows.length) {
    return createPortal(
      optimizationGanttWaitingState || eveningOptimizationGanttWaitingState || <EmptyState
        className="border border-[rgba(148,163,184,0.16)] bg-white px-5 py-5"
        message="Aucun camion exploitable n'est disponible dans la lecture courante."
        title="Vue flotte indisponible"
      />,
      mountNode,
    );
  }
  const renderRouteMapPanel = (compactMode, floatingSizeValue = null, { renderLegend = true } = {}) => {
    if (!isActiveView) {
      return null;
    }

    return (
      <RouteMapPanel
        assignment={selectedAssignment}
        compact={compactMode}
        key={`fleet-route-map-${isMapFloating ? "floating" : "inline"}`}
        customMapHeight={isMapFloating ? floatingMapDimensions.mapHeight : null}
        fillAvailableHeight={false}
        floatingSize={floatingSizeValue}
        isFloating={isMapFloating}
        isRouteViewActive={isActiveView}
        renderInline
        renderLegend={renderLegend}
        routeAlternativePreview={activeRouteAlternativePreview}
        onClientSelect={handleScopedRouteClientSelect}
        onControllerReady={handleRouteMapControllerReady}
        onMapRenderMetaChange={handleRouteMapRenderMetaChange}
        onRouteHighlightChange={onRouteHighlightChange}
        renderGroupRangeToggle={isMapFloating}
        onExpand={isMapFloating ? null : openFleetRouteMapPreview}
        overlayLabel={selectedRow?.truckLabel || ""}
        selectedClientId={activeSelectedClientId || null}
        selectionRequest={isSelectedMainTimeline ? selectionRequest : null}
        stateRef={stateRef}
        trip={activeTrip}
      />
    );
  };
  const floatingSuspendedListHeight = floatingSuspendedDimensions.mapHeight;
  const floatingSuspendedPanelHeight = floatingSuspendedListHeight + 128;
  const shouldUseDualColumnSuspendedLayout = floatingSuspendedDimensions.width >= 640;
  const embeddedSuspendedListMaxHeight = "22.25rem";
  const activeSuspendedDeliveryCount = filteredSuspendedPanelModel.deliveryCount
    || filteredSuspendedPanelModel.items.length;
  const eveningSuspendedDeliveryCount = filteredEveningSuspendedPanelModel.deliveryCount
    || filteredEveningSuspendedPanelModel.items.length;
  const visibleSuspendedDeliveryCount = activeSuspendedDeliveryCount
    + (shouldShowEveningRail ? eveningSuspendedDeliveryCount : 0);
  const hasVisibleSuspendedItems = visibleSuspendedDeliveryCount > 0;
  const renderSuspendedPeriodSection = ({
    dispatchPeriod = "day",
    errorMessage = "",
    floating = false,
    isLoading = false,
    model,
    onRetry = null,
    plain = false,
  }) => {
    const normalizedDispatchPeriod = dispatchPeriod === "evening" ? "evening" : "day";
    const dispatchPeriodLabel = normalizedDispatchPeriod === "evening" ? "Soir" : "Jour";
    const hoveredDispatchPeriod = reassignmentDragState?.hoveredDispatchPeriod === "evening"
      ? "evening"
      : reassignmentDragState?.hoveredDispatchPeriod === "day"
        ? "day"
        : "";
    const isHoveredSuspendedDropZone = reassignmentDragState?.hoveredDropZone === "suspended"
      && (!hoveredDispatchPeriod || hoveredDispatchPeriod === normalizedDispatchPeriod);
    const isForeignSuspendedDropZone = reassignmentDragState?.dragKind === "assignment"
      && reassignmentDragState?.sourceDispatchPeriod
      && reassignmentDragState.sourceDispatchPeriod !== normalizedDispatchPeriod;
    const isSuspendedPeriodTransferTarget = reassignmentDragState?.dragKind === "suspended"
      && reassignmentDragState.sourceDispatchPeriod !== normalizedDispatchPeriod;
    const suspendedPeriodTransferHint = `Déplacer vers les suspendues du ${normalizedDispatchPeriod === "evening" ? "soir" : "jour"}`;
    const previewedDeliveryId = reassignmentDragState?.dragKind === "suspended"
      && (reassignmentDragState?.sourceDispatchPeriod === "evening" ? "evening" : "day") === normalizedDispatchPeriod
      ? reassignmentDragState.deliveryId
      : "";

    return (
      <section
        className={joinClasses([
          plain
            ? "grid content-start transition duration-150"
            : "grid content-start gap-1 rounded-[4px] border p-1 transition duration-150",
          plain
            ? (isHoveredSuspendedDropZone
                ? "rounded-[4px] bg-[linear-gradient(180deg,rgba(255,247,237,0.99),rgba(255,251,245,0.98))] shadow-[0_10px_22px_rgba(217,119,6,0.1)]"
                : isForeignSuspendedDropZone
                  ? "opacity-60"
                  : "")
            : isHoveredSuspendedDropZone
              ? "border-[rgba(217,119,6,0.38)] bg-[linear-gradient(180deg,rgba(255,247,237,0.99),rgba(255,251,245,0.98))] shadow-[0_10px_22px_rgba(217,119,6,0.1)]"
              : isForeignSuspendedDropZone
                ? "border-[rgba(148,163,184,0.14)] bg-slate-50/55 opacity-60"
                : normalizedDispatchPeriod === "evening"
                  ? "border-[rgba(99,102,241,0.18)] bg-[rgba(248,250,255,0.82)]"
                  : "border-[rgba(217,119,6,0.16)] bg-[rgba(255,252,247,0.78)]",
        ])}
        data-fleet-suspended-period-section={normalizedDispatchPeriod}
        data-fleet-suspended-drop-zone="true"
        data-dispatch-period={normalizedDispatchPeriod}
        onDragOver={(event) => {
          event.stopPropagation();
          if (!isOptimizationPreviewInteractionLocked) {
            handleSuspendedDropZoneDragOver(event, normalizedDispatchPeriod);
          }
        }}
        onDrop={(event) => {
          event.stopPropagation();
          if (!isOptimizationPreviewInteractionLocked) {
            handleSuspendedDropZoneDrop(event, normalizedDispatchPeriod);
          }
        }}
      >
        {isSuspendedPeriodTransferTarget ? (
          <div className="rounded-[4px] border border-accent bg-white px-2 py-1 text-xs font-semibold text-content-ink" role="status">
            {suspendedPeriodTransferHint}
          </div>
        ) : null}
        {!plain ? (
          <div className="flex min-w-0 items-center justify-between gap-2 px-1 py-0.5">
            <h5 className="m-0 min-w-0 truncate text-[0.68rem] font-extrabold uppercase tracking-[0.07em] text-content-copy">
              {dispatchPeriodLabel}
            </h5>
            <div className="flex shrink-0 items-center gap-1.5">
              <Pill
                tone={normalizedDispatchPeriod === "evening" ? "info" : "warning"}
                className="px-1.75 py-0.75 text-[0.58rem] shadow-none"
              >
                {isLoading && !model?.hasItems ? "…" : (model?.deliveryCount || model?.items?.length || 0)}
              </Pill>
            </div>
          </div>
        ) : null}

        {isLoading && !model?.hasItems ? (
          <EmptyState
            tone="subtle"
            title={`Chargement des suspendues ${dispatchPeriodLabel}`}
            message="Lecture du dispatch en cours…"
            className="min-h-20 rounded-[4px] px-3 py-2.5"
            titleClassName="text-[0.78rem]"
            messageClassName="text-[0.68rem] leading-5"
          />
        ) : errorMessage ? (
          <EmptyState
            tone="subtle"
            title={`Suspendues ${dispatchPeriodLabel} indisponibles`}
            message={errorMessage}
            className="min-h-20 rounded-[4px] px-3 py-2.5"
            titleClassName="text-[0.78rem]"
            messageClassName="text-[0.68rem] leading-5"
          >
            {typeof onRetry === "function" ? (
              <ActionButton
                tone="ghost"
                size="sm"
                className="w-fit px-2.5 py-1 text-[0.68rem]"
                onClick={onRetry}
              >
                Réessayer
              </ActionButton>
            ) : null}
          </EmptyState>
        ) : model?.hasItems ? (
          <div className={joinClasses([
            "grid auto-rows-max gap-0.75",
            floating && shouldUseDualColumnSuspendedLayout ? "grid-cols-2 items-start" : "grid-cols-1",
          ])}>
            <div className={floating && shouldUseDualColumnSuspendedLayout ? "col-span-2" : ""}>
              <SuspendedTonnageSummary items={model.items} />
            </div>
            <SuspendedDeliveriesList
              blockedPlanningMovementFeedback={blockedPlanningMovementFeedback}
              compact
              draggableCards={!isOptimizationPreviewInteractionLocked}
              items={model.items}
              planningMovementBlocked={isPlanningMovementLocked}
              onDeliveryDragEnd={isOptimizationPreviewInteractionLocked ? null : handleReassignmentDragEnd}
              onDeliveryDragStart={isOptimizationPreviewInteractionLocked || perspective.isInclined ? null : handleSuspendedDeliveryDragStart}
              onDeliveryPointerDown={isOptimizationPreviewInteractionLocked || perspective.isInclined ? null : handleSuspendedDeliveryPointerDown}
              onBlockedPlanningMovementAttempt={startBlockedPlanningMovementFeedback}
              onPreviewDelivery={isOptimizationPreviewInteractionLocked ? null : handleSuspendedDeliveryPreview}
              consumeReassignmentClickSuppression={consumeReassignmentClickSuppression}
              onReactivateDelivery={isOptimizationPreviewInteractionLocked ? null : interactions?.chrome?.onReactivateDelivery}
              reassignmentRebuildMode={assignmentInteractions.reassignmentRebuildMode}
              renderGroupTrailingAction={renderSuspendedGroupTrailingAction}
              previewedDeliveryId={previewedDeliveryId}
            />
          </div>
        ) : (
          <EmptyState
            tone="subtle"
            title={hasTimelineSearch
              ? (plain ? "Aucun résultat" : `Aucun résultat · ${dispatchPeriodLabel}`)
              : (plain ? "Aucune commande suspendue" : `Aucune commande suspendue · ${dispatchPeriodLabel}`)}
            message={hasTimelineSearch
              ? `Aucune commande suspendue ne correspond à « ${timelineQueryLabel} ».`
              : (plain
                ? "Les commandes suspendues apparaîtront ici dès qu'elles seront retirées d'une tournée active."
                : `Les commandes suspendues ${dispatchPeriodLabel} apparaîtront ici dès qu'elles seront retirées d'une tournée active.`)}
            className="min-h-20 rounded-[4px] px-3 py-2.5"
            titleClassName="text-[0.78rem]"
            messageClassName="text-[0.68rem] leading-5"
          />
        )}
      </section>
    );
  };
  const renderSuspendedPanel = ({ floating = false, fullscreen = false } = {}) => (
    <section
      ref={floating ? null : floatingSuspendedAnchorRef}
      className={joinClasses([
        floating
          ? "grid h-full min-h-0 grid-rows-[auto_minmax(0,1fr)] gap-1.5"
          : (fullscreen
              ? "grid h-full min-h-0 content-start grid-rows-[auto_minmax(0,1fr)] gap-1 rounded-[4px] border p-1.5 shadow-none transition duration-150"
              : "grid min-h-0 content-start gap-1 rounded-[4px] border p-1.5 shadow-none transition duration-150"),
        floating
          ? ""
          : "border-border-warning-subtle bg-[linear-gradient(180deg,rgba(255,251,245,0.98),rgba(255,255,255,0.96))]",
        !isOptimizationPreviewInteractionLocked
          && reassignmentDragState?.hoveredDropZone === "suspended"
          && (!reassignmentDragState.hoveredDispatchPeriod
            || reassignmentDragState.hoveredDispatchPeriod === activeSuspendedDispatchPeriod)
          ? "rounded-[4px] ring-2 ring-inset ring-accent"
          : "",
      ])}
      data-fleet-suspended-panel="true"
      data-fleet-suspended-drop-zone="true"
      data-dispatch-period={activeSuspendedDispatchPeriod}
      onDragOver={(event) => {
        if (!isOptimizationPreviewInteractionLocked) {
          handleSuspendedDropZoneDragOver(event, activeSuspendedDispatchPeriod);
        }
      }}
      onDrop={(event) => {
        if (!isOptimizationPreviewInteractionLocked) {
          handleSuspendedDropZoneDrop(event, activeSuspendedDispatchPeriod);
        }
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="grid min-w-0 gap-0.5">
          <h4 className="m-0 text-[0.78rem] font-bold leading-tight tracking-[-0.01em] text-text">
            Commandes suspendues
          </h4>
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          <Pill tone="warning" className="px-2 py-1 text-[0.62rem] shadow-none">
            {activeSuspendedDeliveryCount}
          </Pill>
          {suspendedPanelModel.hasItems ? (
            <label
              className={joinClasses([
                "inline-flex min-h-7 items-center gap-1.25 rounded-sm border border-[rgba(217,119,6,0.24)] bg-[rgba(255,251,245,0.96)] px-2 py-1 text-[0.62rem] font-semibold text-[rgb(146,64,14)]",
                handleIncludeSuspendedDeliveriesChange ? "cursor-pointer" : "cursor-not-allowed opacity-55",
              ])}
              title="Inclure les commandes suspendues au prochain clic sur Optimiser"
            >
              <input
                type="checkbox"
                checked={includeSuspendedDeliveriesInOptimization}
                disabled={!handleIncludeSuspendedDeliveriesChange}
                onChange={(event) => {
                  handleIncludeSuspendedDeliveriesChange?.(event.target.checked);
                }}
                className="h-3.5 w-3.5 accent-[rgb(217,119,6)]"
                data-fleet-include-suspended-deliveries-toggle="true"
                data-dispatch-period={activeSuspendedDispatchPeriod}
              />
              <span className="whitespace-nowrap">Inclure</span>
            </label>
          ) : null}
          {!floating ? (
            <button
              type="button"
              onClick={() => {
                openFloatingSuspendedPanel(floatingSuspendedAnchorRef.current);
              }}
              aria-label="Agrandir les commandes suspendues"
              title="Agrandir les commandes suspendues"
              aria-pressed="false"
              className="inline-flex items-center gap-1.5 rounded-[4px] border border-[rgba(148,163,184,0.18)] bg-white px-2.5 py-1.5 text-[0.68rem] font-semibold text-content-copy transition hover:border-border-accent-strong hover:text-text focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2"
            >
              <Expand aria-hidden="true" className="h-4 w-4" strokeWidth={2.15} />
            </button>
          ) : null}
        </div>
      </div>

      <div
        className={joinClasses([
          "grid min-h-0 auto-rows-max content-start gap-1.5 overflow-y-auto pr-0.5",
          floating || fullscreen ? "h-full" : "overscroll-contain",
        ])}
        style={floating || fullscreen ? undefined : { maxHeight: embeddedSuspendedListMaxHeight }}
      >
        {renderSuspendedPeriodSection({
          dispatchPeriod: activeSuspendedDispatchPeriod,
          floating,
          model: filteredSuspendedPanelModel,
          plain: true,
        })}
        {shouldShowEveningRail ? renderSuspendedPeriodSection({
          dispatchPeriod: "evening",
          errorMessage: eveningGanttRailState.errorMessage,
          floating,
          isLoading: eveningGanttRailState.isLoading,
          model: filteredEveningSuspendedPanelModel,
          onRetry: eveningGanttRailState.retry,
        }) : null}
      </div>
    </section>
  );

  const mainContent = (
    <section
      data-react-fleet-view-panel="true"
      data-dispatch-context-surface="true"
      className={joinClasses([
        "grid min-w-0 max-w-full bg-white",
        isFleetFullscreen && shouldShowOptimizationProgressCard ? "overflow-x-hidden overflow-y-auto" : "overflow-hidden",
        isFleetFullscreen
          ? "fixed inset-3 z-50 h-[calc(100vh-1.5rem)] rounded-[8px] border border-[rgba(127,135,146,0.12)] shadow-[0_28px_90px_rgba(15,23,42,0.18)]"
          : "relative rounded-none shadow-none",
      ])}
    >
      <div className={joinClasses([
        "grid gap-1.5",
        isFleetFullscreen
          ? "h-full min-h-0 grid-rows-[auto_auto_minmax(0,1fr)] px-3 py-3 min-[981px]:px-4 min-[981px]:py-4"
          : "border-b border-[rgba(148,163,184,0.16)] px-1.25 pt-0.5 pb-1.75 min-[981px]:px-1.5 min-[981px]:pb-2",
      ])}>
        <FleetReassignmentDragNotice dragState={reassignmentNoticeState} />

        <div className={joinClasses([
          "grid min-w-0 items-start gap-1.5",
          shouldRenderInlineInspectorRail ? "xl:grid-cols-[minmax(0,1fr)_minmax(280px,320px)]" : "",
          isFleetFullscreen ? "min-h-0 auto-rows-fr" : "fleet-timeline-inspector-layout",
        ])}>
          <div className={joinClasses(["grid min-w-0 content-start gap-1.5", isFleetFullscreen ? "h-full min-h-0 grid-rows-[auto_minmax(0,1fr)]" : ""])}>
        <div className="grid gap-0.5">
          <div className="grid min-w-0 items-stretch gap-1.5" data-fleet-view-toolbar="true">
            <div className="fleet-view-toolbar-section flex min-w-0 flex-wrap items-center gap-2.5">
              {isFleetFullscreen ? (
                <DispatchFullscreenActions interactions={previewSafeChromeInteractions} model={fullscreenDispatchRibbonModel} />
              ) : null}
              <div className="flex min-w-0 flex-wrap items-center gap-2.5">
                <h2 className="sr-only">Gantt multi-camions</h2>
                <FleetTimelineSearchField
                  query={timelineQuery}
                  onQueryChange={(nextValue) => {
                    React.startTransition(() => {
                      setTimelineQuery(nextValue);
                    });
                  }}
                />
              </div>
              {selectedShell && !selectedShell.statusBadge.isHidden ? <StatusBadge tone={selectedShell.statusBadge.tone}>{selectedShell.statusBadge.label}</StatusBadge> : null}
              <FleetTruckFilterControls
                hideUnusedTrucks={hideUnusedTrucks}
                isOpen={isTruckFilterOpen}
                onSelectAll={handleSelectAllTrucks}
                onToggleHideUnusedTrucks={setHideUnusedTrucks}
                onToggleOpen={toggleTruckFilterDropdown}
                onToggleTruck={handleToggleTruckSelection}
                panelRef={truckFilterPanelRef}
                selectedTruckCount={selectedTruckIds.length}
                selectedTruckIdSet={selectedTruckIdSet}
                totalTruckCount={truckOptions.length}
                truckOptions={truckOptions}
                triggerRef={truckFilterButtonRef}
              />
              {undoReassignmentState ? (() => {
                const _sLabel = typeof undoReassignmentState.sourceLabel === "string" ? undoReassignmentState.sourceLabel.trim() : "";
                const _sName = typeof undoReassignmentState.sourceTruckName === "string" && undoReassignmentState.sourceTruckName.trim() ? undoReassignmentState.sourceTruckName.trim() : undoReassignmentState.sourceTruckId || "le camion précédent";
                const _tName = typeof undoReassignmentState.targetTruckName === "string" && undoReassignmentState.targetTruckName.trim() ? undoReassignmentState.targetTruckName.trim() : undoReassignmentState.targetTruckId || "le camion actuel";
                const _desc = _sLabel ? `${_sLabel} vers ${_tName}` : `Commande déplacée vers ${_tName}`;
                return (
                  <span className="inline-flex min-w-0 max-w-full items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => { handleUndoReassignment?.(); }}
                      disabled={!handleUndoReassignment || undoReassignmentState.isPending}
                      aria-busy={undoReassignmentState.isPending}
                      aria-label={undoReassignmentState.isPending ? "Retour au planning précédent en cours" : `Revenir vers ${_sName}`}
                      title={undoReassignmentState.isPending ? "Retour en cours" : `Revenir vers ${_sName}`}
                      className="inline-flex shrink-0 items-center gap-1.5 rounded-[4px] border border-slate-300 bg-white px-2.75 py-1.5 text-[0.68rem] font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <RotateCcw aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.15} />
                      <span>{undoReassignmentState.isPending ? "Retour…" : "Revenir"}</span>
                    </button>
                    <span className="min-w-0 truncate text-[0.72rem] text-slate-700" title={_desc}>{_desc}</span>
                  </span>
                );
              })() : null}
              <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2" data-fleet-view-display-controls="true">
                <DispatchDisplayMenu display={{ ...ganttDisplay, product_tags: sessionProductTags }} onChange={handleGanttDisplayOption} canSeeGeotab={canSeeGeotabReal} geotabStatus={geotabRealStatus} productTagsDefaultOff={productTagsDefaultOff} />
                <FleetTimelineZoomSelect onZoomChange={setTimelineZoomKey} zoomKey={timelineZoomKey} />
                {isDayDispatchPeriod ? (
                  <button
                    type="button"
                    onClick={() => onEveningRailOpenChange?.(!isEveningRailOpen)}
                    aria-pressed={isEveningRailOpen ? "true" : "false"}
                    className={joinClasses([
                      "inline-flex min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[4px] border px-2.75 py-1.5 text-[0.68rem] font-semibold transition focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2",
                      isEveningRailOpen
                        ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                        : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900",
                    ])}
                  >
                    <Moon aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.15} />
                    <span>{isEveningRailOpen ? "Masquer le soir" : "Afficher le soir"}</span>
                  </button>
                ) : null}
                {isStandaloneUnitsDayOpen ? (
                  <GanttAddUnitButton catalog={standaloneUnits.catalog} ganttTruckIds={ganttTruckIds} dispatchDate={activeDispatchDate}
                    dispatchPeriod={activeSuspendedDispatchPeriod} canWrite={canWriteStandaloneUnits} />
                ) : null}
              <button
                type="button"
                onClick={() => {
                  setIsFleetFullscreen((currentState) => !currentState);
                }}
                aria-pressed={isFleetFullscreen ? "true" : "false"}
                aria-label={isFleetFullscreen ? "Réduire la vue" : "Agrandir la vue"}
                title={isFleetFullscreen ? "Réduire la vue" : "Agrandir la vue"}
                className="inline-flex min-h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-[4px] border border-slate-300 bg-white px-2.75 py-1.5 text-[0.68rem] font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent/35 focus-visible:ring-offset-2"
              >
                {isFleetFullscreen ? <Minimize2 aria-hidden="true" className="h-4 w-4" strokeWidth={2.15} /> : <Expand aria-hidden="true" className="h-4 w-4" strokeWidth={2.15} />}
              </button>
              <FleetTimelineShortcutsHelp />
                {/* En Soir, le sélecteur reste visible : sans lui, aucun retour vers Jour. */}
                {dispatchPeriodToggleEnabled || !isDayDispatchPeriod ? <ConnectedDispatchPeriodToggle /> : null}
              </div>
            </div>


          </div>
        </div>

          <div
            data-fleet-timeline-panel="true"
            className={joinClasses([
              "grid min-w-0 self-start content-start gap-1.5 rounded-[8px] border border-[rgba(15,92,87,0.1)] bg-[linear-gradient(180deg,rgba(249,251,250,0.96),rgba(243,247,245,0.92))] p-1.25 shadow-none",
              isFleetFullscreen ? "h-full min-h-0" : "",
            ])}
          >
            {visibleTimelineRows.length || visibleEveningTimelineRows.length || shouldShowOptimizationGanttWaiting || eveningOptimizationGanttWaitingState
              || (isStandaloneUnitsDayOpen && standaloneUnits.catalog?.standalone_units?.length) ? (
              <div className={joinClasses([
                "grid content-start gap-1",
                isFleetFullscreen ? "h-full min-h-0 grid-rows-[auto_minmax(0,1fr)]" : "",
              ])}>
                <FleetTimelineToolbar productLabels={timelineLegendProductLabels} />

                <FleetTimelinePerspective
                  controller={perspective}
                  fullscreen={isFleetFullscreen}
                  contentStyle={timelineContentStyle}
                  zoom={selectedTimelineZoomOption.factor}
                >
                    {shouldShowEveningRail ? (
                      <div
                        className="grid min-w-0 items-stretch gap-1"
                        style={{ gridTemplateColumns: eveningRailWidth.gridTemplateColumns, width: eveningRailWidth.scaleWidth }}
                      >
                        <FleetTimelineScale
                          startMinutes={timelineBounds.startMinutes}
                          endMinutes={timelineBounds.endMinutes}
                          leadingPaddingPercent={timelineLeadingPaddingPercent}
                          trailingPaddingPercent={timelineTrailingPaddingPercent}
                        />
                        <EveningTimelineRailScale
                          startMinutes={eveningTimelineBounds.startMinutes}
                          endMinutes={eveningTimelineBounds.endMinutes}
                          leadingPaddingPercent={eveningTimelineInformationalGutterPercent}
                          trailingPaddingPercent={eveningTimelineTrailingPaddingPercent}
                          errorMessage={eveningGanttRailState.errorMessage}
                          isLoading={eveningGanttRailState.isLoading}
                          onRetry={eveningGanttRailState.retry}
                        />
                      </div>
                    ) : (
                      <div className="overflow-hidden pr-px [scrollbar-gutter:stable]">
                        <FleetTimelineScale
                          startMinutes={timelineBounds.startMinutes}
                          endMinutes={timelineBounds.endMinutes}
                          leadingPaddingPercent={timelineLeadingPaddingPercent}
                          trailingPaddingPercent={timelineTrailingPaddingPercent}
                        />
                      </div>
                    )}

                    <div
                      ref={timelineRowsContainerRef}
                      data-fleet-timeline-rows-canvas="true"
                      className={joinClasses([
                        FLEET_TIMELINE_ROWS_CANVAS_CLASS_NAME,
                        isFleetFullscreen ? "h-full min-h-0 max-h-none" : FLEET_TIMELINE_ROWS_EMBEDDED_MAX_HEIGHT_CLASS_NAME,
                      ])}
                    >
                      {shouldShowEveningRail ? (
                        <div
                          className="grid min-w-0 items-start gap-1"
                          data-independent-dispatch-period-columns="true"
                          ref={eveningRailWidth.containerRef}
                          style={{ gridTemplateColumns: eveningRailWidth.gridTemplateColumns }}
                        >
                          <div className="grid min-w-0 content-start gap-0.5" data-dispatch-period-column="day">
                            {optimizationGanttWaitingState}
                            {visibleTimelineRows.length ? visibleTimelineRows.map(renderDayTimelineRow) : !shouldShowOptimizationGanttWaiting && (
                              <div className="rounded-[6px] border border-dashed border-[rgba(148,163,184,0.22)] bg-white/70 px-3 py-4 text-center text-[0.66rem] font-medium text-content-muted">
                                Aucun camion Jour visible
                              </div>
                            )}
                            {standaloneUnitRows}
                          </div>
                          <div className="grid min-w-0 content-start gap-0.5" data-dispatch-period-column="evening">
                            {eveningOptimizationGanttWaitingState}
                            {visibleEveningTimelineRows.length ? visibleEveningTimelineRows.map(renderEveningTimelineRow) : !eveningOptimizationGanttWaitingState && (
                              <div className="rounded-[6px] border border-dashed border-[rgba(99,102,241,0.2)] bg-[rgba(248,250,255,0.72)] px-3 py-4 text-center text-[0.66rem] font-medium text-content-muted">
                                Aucun camion Soir visible
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          {optimizationGanttWaitingState}
                          {visibleTimelineRows.map(renderDayTimelineRow)}
                          {standaloneUnitRows}
                        </>
                      )}
                    </div>
                    <div
                      className={joinClasses(["pointer-events-none absolute inset-0 z-10 grid min-w-0 gap-1", shouldShowEveningRail ? "" : "overflow-hidden pr-px [scrollbar-gutter:stable]"])}
                      style={shouldShowEveningRail ? { gridTemplateColumns: eveningRailWidth.gridTemplateColumns, width: eveningRailWidth.scaleWidth } : undefined}
                    >
                      <FleetTimelineNowLine
                        dispatchDate={activeDispatchDate}
                        startMinutes={timelineBounds.startMinutes}
                        endMinutes={timelineBounds.endMinutes}
                        leadingPaddingPercent={timelineLeadingPaddingPercent}
                        trailingPaddingPercent={timelineTrailingPaddingPercent}
                      />
                    </div>
                    {shouldShowEveningRail && eveningRailWidth.resizable && !perspective.isInclined ? (
                      <div
                        role="separator"
                        aria-label="Largeur du rail Soir"
                        aria-orientation="vertical"
                        aria-valuemin={160}
                        aria-valuemax={Math.round(eveningRailWidth.width - 320)}
                        aria-valuenow={Math.round(eveningRailWidth.eveningWidth)}
                        aria-valuetext={`${Math.round(eveningRailWidth.eveningWidth)} pixels pour le Soir`}
                        tabIndex={0}
                        title="Glisser pour ajuster la largeur du Soir. Flèches gauche et droite au clavier."
                        data-evening-rail-resize="true"
                        className="group absolute inset-y-0 z-20 flex w-2 -translate-x-1/2 cursor-col-resize touch-none justify-center rounded-[4px] focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-accent"
                        style={{ left: eveningRailWidth.separatorLeft }}
                        onPointerDown={eveningRailWidth.onPointerDown}
                        onKeyDown={eveningRailWidth.onKeyDown}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <span className="h-full w-px bg-slate-300 group-hover:w-0.5 group-hover:bg-accent group-focus-visible:bg-accent" />
                      </div>
                    ) : null}
                </FleetTimelinePerspective>
              </div>
            ) : (
              <EmptyState
                className="border border-[rgba(148,163,184,0.16)] bg-[rgba(248,250,252,0.82)] px-5 py-5"
                message={hasTimelineSearch
                  ? (hasVisibleSuspendedItems
                    ? `Aucun camion actif ne correspond à « ${timelineQueryLabel} ». Des commandes suspendues correspondent à la recherche.`
                    : `Aucun camion ni commande ne correspond à « ${timelineQueryLabel} ».`)
                  : timelineFilterMessage}
                title={hasTimelineSearch
                  ? (hasVisibleSuspendedItems ? "Aucun camion actif" : "Aucun résultat")
                  : "Aucun camion visible"}
              />
            )}
          </div>

          </div>

          {shouldRenderEmbeddedInspectorRail ? (
            <aside
              data-fleet-inspector-rail="true"
              ref={floatingMapAnchorRef}
              style={shouldOverlayEmbeddedOptimizationProgressCard ? { minHeight: optimizationProgressOverlayHeight || undefined } : undefined}
              className={joinClasses([
                "relative grid min-h-0 min-w-0 self-start content-start gap-1.25 rounded-[8px] border border-[rgba(15,92,87,0.12)] bg-[rgba(248,250,252,0.84)] p-1.5 shadow-none",
                shouldOverlayEmbeddedOptimizationProgressCard ? "overflow-visible" : "overflow-y-auto",
                isFleetFullscreen ? "h-full" : "",
              ])}
            >
              {shouldShowOptimizationProgressCard && !shouldOverlayEmbeddedOptimizationProgressCard
                ? renderOptimizationProgressCard()
                : null}

              {shouldRenderEmbeddedMapRail ? (
                <>


                  {selectedRouteSignalLabel ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Pill tone={selectedRouteSignalTone} className="px-2.5 py-1 text-[0.68rem] shadow-none" title={selectedSegment?.montrealRiskAlert?.suggestedAction || undefined}>
                        {selectedRouteSignalLabel}
                      </Pill>
                    </div>
                  ) : null}

                  {selectedSegment?.statusMarkers?.length ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <TimelineSegmentStatusMarkers markers={selectedSegment.statusMarkers} maxVisible={4} size="table" />
                    </div>
                  ) : null}

                  {selectedSegment?.timeWindowOverride ? (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <SequenceCellPill label={selectedSegment.timeWindowOverride.label || "Fenêtre"} />
                      <span className="text-[0.7rem] leading-4 text-content-muted">
                        {selectedSegment.timeWindowOverride.reason
                          ? `Motif: ${selectedSegment.timeWindowOverride.reason}`
                          : (selectedSegment.timeWindowOverride.description || selectedSegment.timeWindowOverride.modeLabel || "Confirmation enregistrée")}
                      </span>
                    </div>
                  ) : null}

                  {renderRouteMapPanel(true)}
                </>
              ) : null}

              {shouldRenderInlineSuspendedPanel ? renderSuspendedPanel({ fullscreen: isFleetFullscreen }) : null}
              {shouldOverlayEmbeddedOptimizationProgressCard ? renderOptimizationProgressCard(true) : null}
            </aside>
          ) : null}

          {shouldRenderFloatingSuspendedRail ? (
            <aside
              data-fleet-inspector-rail="true"
              style={shouldOverlayFloatingOptimizationProgressCard ? { minHeight: optimizationProgressOverlayHeight || undefined } : undefined}
              className={joinClasses([
                "relative grid min-h-0 min-w-0 self-start content-start gap-1.25 rounded-[8px] border border-[rgba(15,92,87,0.12)] bg-[rgba(248,250,252,0.84)] p-1.5 shadow-none",
                shouldOverlayFloatingOptimizationProgressCard ? "overflow-visible" : "overflow-y-auto",
                isFleetFullscreen ? "h-full grid-rows-[minmax(0,1fr)]" : "",
              ])}
            >
              {renderSuspendedPanel({ fullscreen: isFleetFullscreen })}
              {shouldOverlayFloatingOptimizationProgressCard ? renderOptimizationProgressCard(true) : null}
            </aside>
          ) : null}
        </div>
      </div>

      {!isFleetFullscreen ? (
        <div className="grid min-w-0 gap-2 px-1.25 py-1.75 min-[981px]:px-1.5 min-[981px]:py-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <FleetSelectionHeading row={selectedRow} title="Séquence route" />

          <div className="flex flex-wrap items-center gap-2">
            {selectedRow ? (
              <Pill
                tone={selectedDispatchPeriod === "evening" ? "info" : "success"}
                className={`gap-1.5 px-3 py-1.25 text-[0.72rem] font-extrabold shadow-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`}
                data-selected-dispatch-period={selectedDispatchPeriod}
                data-route-pill="true"
              >
                {selectedDispatchPeriod === "evening"
                  ? <Moon aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.25} />
                  : <Sun aria-hidden="true" className="h-3.5 w-3.5" strokeWidth={2.25} />}
                {selectedDispatchPeriod === "evening" ? "Soir" : "Jour"}
              </Pill>
            ) : null}
            {selectedRouteSignalLabel ? <Pill tone={selectedRouteSignalTone} className={`px-3 py-1.25 text-[0.72rem] shadow-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`} data-route-pill="true">{selectedRouteSignalLabel}</Pill> : null}
            {selectedSegment?.statusMarkers?.length ? <TimelineSegmentStatusMarkers markers={selectedSegment.statusMarkers} maxVisible={4} size="table" /> : null}
            {selectedRowHasOvertimeWarning ? <Pill tone="warning" className={`px-3 py-1.25 text-[0.72rem] shadow-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`} data-route-pill="true">Heures max dépassées</Pill> : null}
            {selectedRow ? <Pill tone="outline" className={`px-3 py-1.25 text-[0.72rem] shadow-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`} data-route-pill="true">{selectedRowDisplayedDurationLabel}</Pill> : null}
            {selectedRow?.distanceLabel ? <Pill tone="outline" className={`px-3 py-1.25 text-[0.72rem] shadow-none ${DISPATCH_ROUTE_PILL_RADIUS_CLASS_NAME}`} data-route-pill="true">{selectedRowDistanceLabel}</Pill> : null}
          </div>
        </div>

        {!selectedRow ? (
          <EmptyState
            className="border border-[rgba(148,163,184,0.16)] bg-[rgba(248,250,252,0.82)] px-5 py-5"
            message={filteredTimelineRows.length ? "Sélectionnez une ligne pour afficher la séquence condensée du camion." : timelineFilterMessage}
            title={filteredTimelineRows.length ? "Aucun camion sélectionné" : "Aucun camion visible"}
          />
        ) : !sequenceRows.length ? (
          <EmptyState
            className="border border-[rgba(148,163,184,0.16)] bg-[rgba(248,250,252,0.82)] px-5 py-5"
            message="Ce camion reste visible comme ligne vide et pourra servir de cible de dépôt lors de la phase suivante."
            title="Camion non assigné"
          />
        ) : (
          <div className="grid min-w-0 gap-2">
            <div
              ref={sequenceScrollRegionRef}
              className={joinClasses([
                "max-w-full overflow-y-auto rounded-[8px] border border-[rgba(148,163,184,0.16)] bg-[rgba(248,250,252,0.82)]",
                sequenceNeedsHorizontalScroll ? "overflow-x-auto" : "overflow-x-hidden",
              ])}
              data-fleet-sequence-scroll-region="true"
            >
              <table className="table-fixed border-separate border-spacing-0 text-[0.79rem] [&_thead]:overflow-x-clip" style={sequenceTableStyle}>
                <colgroup>
                  {sequenceColumns.map((column) => (
                    <col key={column.key} style={sequenceTableColumnStyles[column.key]} />
                  ))}
                </colgroup>
                <thead>
                  <tr className="bg-[rgba(255,255,255,0.88)] text-[0.62rem] font-extrabold uppercase tracking-[0.02em] text-content-ink">
                    {sequenceColumns.map((column) => {
                      const align = FLEET_SEQUENCE_COLUMN_ALIGNMENTS[column.key] || "left";
                      return (
                        <FleetSequenceSortableHeaderCell
                          key={column.key}
                          align={align}
                          className={resolveFleetSequenceHeaderClassName(column.key, align)}
                          columnKey={column.key}
                          label={column.label}
                          onColumnMove={sequenceColumnPrefs.handleColumnMove}
                          onResizePointerDown={sequenceColumnPrefs.handleResizePointerDown}
                        />
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sequenceTableItems.map((item) => {
                    if (item.kind === "lunch") {
                      return <FleetSequenceLunchBreakRow key={item.key} columns={sequenceColumns} lunchBreak={item.lunchBreak} />;
                    }

                    if (item.kind === "group") {
                      const groupItem = item;
                      const memberSegments = Array.isArray(groupItem.memberSegments) ? groupItem.memberSegments.filter(Boolean) : [];
                      const firstSegment = memberSegments[0] || null;
                      const lastSegment = memberSegments[memberSegments.length - 1] || firstSegment;
                      const terminalMergedMemberSegment = memberSegments.find((memberSegment) => memberSegment?.isTerminalRouteMerged === true) || null;
                      const groupSequenceLabel = getGroupedSegmentSequenceLabel(groupItem);
                      const groupDurationChips = buildSequenceGroupDurationChips(groupItem, sequenceDisplayOverrides, {
                        assignment: selectedRow?.assignment || null,
                        hovercardContext: fleetSegmentHovercardContext,
                      });
                      const isExpanded = groupItem.isExpanded === true;
                      const groupOperationLabel = getSequenceOperationLabel(groupItem, firstSegment);
                      const groupRouteSummaryLabel = getSequenceRouteSummaryLabel({
                        fromLabel: terminalMergedMemberSegment?.routeFromLabel || firstSegment?.fromLabel,
                        routeFromLabel: terminalMergedMemberSegment?.routeFromLabel || firstSegment?.routeFromLabel,
                        routeToLabel: terminalMergedMemberSegment?.routeToLabel || lastSegment?.routeToLabel || firstSegment?.routeToLabel,
                        toLabel: terminalMergedMemberSegment?.routeToLabel || lastSegment?.toLabel || firstSegment?.toLabel,
                      });
                      const groupAddress = groupItem?.operationAddress || groupItem?.toAddress || firstSegment?.operationAddress || firstSegment?.toAddress || "";
                      const groupInlineSummaryLabel = (groupItem?.isEndOfDayLoad || groupItem?.isEndOfDayLoadSourcePreload)
                        ? ""
                        : getSequenceInlineSummaryLabel(groupRouteSummaryLabel, groupAddress);
                      const isActivationHighlightedGroup = memberSegments.some((segment) => segment?.isActivationHighlighted);
                      const groupInlineIndicators = buildSequenceTableGroupInlineIndicators(memberSegments, { compartmentFillTruck, routeAlternativesFeatureEnabled });
                      const {
                        inlineIndicators: groupContentIndicators,
                        promotedIndicators: groupStepIndicators,
                      } = splitSequenceTableInlineIndicators(groupInlineIndicators, {
                        promotePrioritySchedule: hasSequencePriorityTimeAnchor(memberSegments),
                      });
                      const visibleGroupContentIndicators = filterRouteSequenceOpeningIndicators(
                        groupContentIndicators,
                        shouldDisplayRouteSequenceOpeningForItem(groupItem.key, sequenceOpeningVisibilityByItemKey),
                      );
                      const groupCompartmentLabel = getSequenceGroupCompartmentLabel(memberSegments);
                      const groupCompartmentChip = groupCompartmentLabel && groupCompartmentLabel !== "—"
                        ? {
                            key: `${groupItem.groupKey || groupItem.key || "group"}:compartment`,
                            kind: "compartment",
                            label: groupCompartmentLabel,
                            stopType: groupItem?.stopType || firstSegment?.stopType || "delivery",
                            title: `Compartiment(s) : ${groupCompartmentLabel}`,
                          }
                        : null;
                      const groupCompartmentOccupancyLabel = shouldDisplaySequenceCompartmentOccupancy(groupItem)
                        ? (sequenceCompartmentOccupancyByItem.get(groupItem.key) || "—")
                        : "";
                      const groupCompartmentFillRateLabel = shouldDisplaySequenceCompartmentFillRate(groupItem)
                        ? (sequenceCompartmentFillRateByItem.get(groupItem.key) || "—")
                        : "";
                      const groupChargeLabel = formatSequenceGroupChargeLabel(memberSegments, sequenceTruckLoadByKey);
                      const hoverRouteSegmentIndex = getFleetGroupedRouteHoverSegmentIndex(groupItem);
                      const groupRouteNumber = Number(firstSegment?.routeSequenceDisplaySequence || groupItem?.startSequence || firstSegment?.globalSequence);
                      const canZoomToGroupStart = Number.isInteger(groupRouteNumber) && groupRouteNumber > 0;
                      const groupCellsByColumnKey = {
                        step: (
                          <FleetSequenceStepCell
                            indicators={groupStepIndicators}
                            startLabel={formatSequenceStepTimeLabel(firstSegment)}
                            stepLabel={groupSequenceLabel || `${memberSegments.length}`}
                          />
                        ),
                        route: (
                          <div className="flex items-start gap-2">
                            <span className="relative mt-0.5 inline-flex h-4.5 w-4.5 shrink-0 overflow-visible" aria-hidden="true">
                              {groupItem?.isEndOfDayLoad || groupItem?.isEndOfDayLoadSourcePreload
                                ? <EndOfDayLoadFleetRibbon anchor="sequence" compact visualMode={resolveEndOfDayLoadDisplayMode(groupItem)} {...resolveEndOfDayLoadLabelOptions(groupItem)} />
                                : groupItem?.isInitialPreload
                                  ? <InitialPreloadFleetRibbon anchor="sequence" compact />
                                  : null}
                              <span
                                className={joinClasses([
                                  "inline-flex h-4.5 w-4.5 items-center justify-center rounded-full border border-white text-white transition-transform duration-150",
                                ])}
                                style={getGroupedSequenceTableMarkerStyle(groupItem, firstSegment?.color)}
                              >
                                <ChevronDown className={joinClasses(["h-3 w-3", isExpanded ? "rotate-180" : ""])} strokeWidth={2.2} />
                              </span>
                            </span>
                            <div className="grid min-w-0 gap-0.75">
                              <div className="flex flex-wrap items-center gap-1.25 text-[0.79rem] leading-4.5 text-text">
                                <span className="font-bold text-text">{groupOperationLabel}</span>
                              </div>
                              <FleetSequenceSupplementaryMeta
                                connectorPill={(
                                  <SequenceCellPill
                                    className={FLEET_SEQUENCE_ALIGNED_STOP_PILL_CLASS_NAME}
                                    label={`Même arrêt ${groupSequenceLabel || memberSegments.length}`}
                                  />
                                )}
                                indicators={visibleGroupContentIndicators}
                                summaryLabel={groupInlineSummaryLabel}
                              />
                            </div>
                          </div>
                        ),
                        silo: (
                          <span className="block truncate" title={getSequenceGroupSharedValue(memberSegments, "siloLabel")}>
                            {getSequenceGroupSharedValue(memberSegments, "siloLabel")}
                          </span>
                        ),
                        order: `${memberSegments.length} étape${memberSegments.length > 1 ? "s" : ""}`,
                        workOrder: (
                          <span className="block truncate" title={getSequenceGroupSharedValue(memberSegments, "workOrderLabel") !== "—" ? getSequenceGroupSharedValue(memberSegments, "workOrderLabel") : undefined}>
                            {getSequenceGroupSharedValue(memberSegments, "workOrderLabel")}
                          </span>
                        ),
                        product: (
                          <span className="flex flex-wrap gap-0.5" data-fleet-sequence-products="true">
                            {resolveGroupedSegmentProductLabels(memberSegments).map((productLabel) => (
                              <FleetProductPill key={productLabel} label={productLabel} maxLength={Infinity} />
                            ))}
                          </span>
                        ),
                        code: getSequenceGroupSharedValue(memberSegments, "productCodeLabel"),
                        compartment: <FleetSequenceCompartmentPill chip={groupCompartmentChip} compartmentProfile={withVirtualTruckCompartment(selectedTruckCompartmentProfile, selectedAssignment?.capacity_tons)} segments={memberSegments} showEmptyCompartments />,
                        occupancy: groupCompartmentOccupancyLabel,
                        fillRate: groupCompartmentFillRateLabel,
                        tonnage: <FleetSequenceLoadCell label={formatSequenceGroupTonnageLabel(memberSegments)} stopType={groupItem.stopType} signed />,
                        charge: <FleetSequenceLoadCell label={groupChargeLabel} stopType={groupItem.stopType} />,
                        distance: <FleetSequenceDistanceCell label={formatSequenceGroupDistanceLabel(memberSegments, sequenceDisplayOverrides)} />,
                        service: <FleetSequenceDurationColumnContent chip={getFleetSequenceDurationChip(groupDurationChips, "service")} />,
                        meunerie: <FleetSequenceDurationColumnContent chip={getFleetSequenceDurationChip(groupDurationChips, "meunerie")} />,
                        aspirator: <FleetSequenceDurationColumnContent chip={getFleetSequenceDurationChip(groupDurationChips, "aspirator")} />,
                        buffer: <FleetSequenceDurationColumnContent chip={getFleetSequenceDurationChip(groupDurationChips, "buffer")} />,
                        travel: <FleetSequenceDurationColumnContent chip={getFleetSequenceDurationChip(groupDurationChips, "travel")} />,
                      };

                      return (
                        <tr
                          key={groupItem.key}
                          onClick={() => {
                            toggleExpandedSequenceGroup(groupItem.groupKey);

                            if (isExpanded) {
                              chromeInteractions.onRouteSequenceSelect?.({
                                restoreView: true,
                                routeSegmentIndex: hoverRouteSegmentIndex,
                              });
                              return;
                            }

                            if (!canZoomToGroupStart) {
                              return;
                            }

                            chromeInteractions.onRouteSequenceSelect?.({
                              routeNumber: groupRouteNumber,
                              routeSegmentIndex: hoverRouteSegmentIndex,
                            });
                          }}
                          onKeyDown={(event) => {
                            if (event.key !== "Enter" && event.key !== " ") {
                              return;
                            }

                            event.preventDefault();
                            toggleExpandedSequenceGroup(groupItem.groupKey);

                            if (isExpanded) {
                              chromeInteractions.onRouteSequenceSelect?.({
                                restoreView: true,
                                routeSegmentIndex: hoverRouteSegmentIndex,
                              });
                              return;
                            }

                            if (!canZoomToGroupStart) {
                              return;
                            }

                            chromeInteractions.onRouteSequenceSelect?.({
                              routeNumber: groupRouteNumber,
                              routeSegmentIndex: hoverRouteSegmentIndex,
                            });
                          }}
                          onBlur={() => {
                            chromeInteractions.onRouteHoverEnd?.();
                          }}
                          onFocus={() => {
                            if (hoverRouteSegmentIndex != null) {
                              chromeInteractions.onRouteSegmentHoverStart?.(hoverRouteSegmentIndex);
                            }
                          }}
                          onMouseEnter={() => {
                            if (hoverRouteSegmentIndex != null) {
                              chromeInteractions.onRouteSegmentHoverStart?.(hoverRouteSegmentIndex);
                            }
                          }}
                          onMouseLeave={() => {
                            chromeInteractions.onRouteHoverEnd?.();
                          }}
                          className={joinClasses([
                            "border-b border-[var(--shell-chrome-line)] text-text transition last:border-b-0 cursor-pointer hover:bg-white focus-within:bg-white",
                            isExpanded ? "bg-[rgba(255,255,255,0.92)]" : "bg-[var(--shell-chrome-soft)]",
                          ])}
                          data-sequence-group-key={groupItem.groupKey}
                          role="button"
                          tabIndex={0}
                        >
                          {sequenceColumns.map((column) => (
                            <td key={column.key} className={resolveFleetSequenceColumnCellClassName(column.key, "group")}>
                              {groupCellsByColumnKey[column.key] ?? null}
                            </td>
                          ))}
                        </tr>
                      );
                    }

                    const segment = item.segment;
                    const hoverRouteSegmentIndexes = resolveAspiratorMissionRouteSegmentIndexes(segment);
                    const isGroupedMember = item.kind === "group-member";
                    const isSelectedSegment = Boolean(segment.isSelectedClient || (selectedClientId && segment.clientId === selectedClientId));
                    const isLinkedCommandSegment = isLinkedCommandCompanionSegment(segment, selectedSegment);
                    const isSelectable = Boolean(segment.isSelectable);
                    const segmentSequenceIndex = sequenceRows.indexOf(segment);
                    const segmentChips = Array.isArray(segment.chips) ? segment.chips : [];
                    const compartmentChip = segmentChips.find((chip) => chip?.key === "compartment" || chip?.kind === "compartment") || null;
                    const durationChips = isGroupedMember
                      ? []
                      : buildSequenceSegmentDurationChips(segment, sequenceDisplayOverrides, segmentSequenceIndex, {
                          assignment: selectedRow?.assignment || null,
                          hovercardContext: fleetSegmentHovercardContext,
                          sequenceItems: sequenceRows,
                        });
                    const operationLabel = getSequenceOperationLabel(segment);
                    const routeSummaryLabel = getSequenceRouteSummaryLabel(segment);
                    const inlineSummaryLabel = isGroupedMember || segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload
                      ? ""
                      : getSequenceInlineSummaryLabel(routeSummaryLabel, resolveSegmentPrimaryAddress(segment));
                    const counterpartySummaryLines = buildSequenceCounterpartySummaryLines([segment]);
                    const counterpartySummaryLabel = counterpartySummaryLines.join(" · ");
                    const segmentCompartmentOccupancyLabel = shouldDisplaySequenceCompartmentOccupancy(segment)
                      ? (sequenceCompartmentOccupancyByItem.get(item.key) || "—")
                      : "";
                    const shouldDisplayLoadMetrics = shouldDisplaySequenceLoadMetrics(segment);
                    const segmentCompartmentFillRateLabel = shouldDisplaySequenceCompartmentFillRate(segment)
                      ? (sequenceCompartmentFillRateByItem.get(item.key) || "—")
                      : "";
                    const segmentChargeLabel = shouldDisplayLoadMetrics
                      ? (sequenceTruckLoadByKey.get(getFleetSequenceDisplayKey(segment, segmentSequenceIndex))?.label || "—")
                      : "";
                    const segmentQuantityLabel = shouldDisplayLoadMetrics
                      ? (segment.quantityLabel || "—")
                      : "";
                    const segmentDistanceLabel = isGroupedMember
                      ? ""
                      : getSameSiteSequenceDisplayDistanceLabel(segment, sequenceDisplayOverrides, segmentSequenceIndex);
                    const segmentTimeWindowOverride = segment?.timeWindowOverride && typeof segment.timeWindowOverride === "object"
                      ? segment.timeWindowOverride
                      : null;
                    const inlineIndicators = buildSequenceTableInlineIndicators(segment, segmentTimeWindowOverride, { compartmentFillTruck, routeAlternativesFeatureEnabled });
                    const {
                      inlineIndicators: segmentContentIndicators,
                      promotedIndicators: segmentStepIndicators,
                    } = splitSequenceTableInlineIndicators(inlineIndicators, {
                      promotePrioritySchedule: hasSequencePriorityTimeAnchor(segment),
                    });
                    const visibleSegmentContentIndicators = filterRouteSequenceOpeningIndicators(
                      segmentContentIndicators,
                      shouldDisplayRouteSequenceOpeningForItem(item.key, sequenceOpeningVisibilityByItemKey),
                    ).filter((indicator) => !(isGroupedMember && indicator?.tone === "product-change"));
                    const segmentSiloLabel = resolveSequenceSegmentSiloLabel(segment);
                    const routeNumber = Number(segment?.routeSequenceDisplaySequence || segment?.globalSequence);
                    const canZoomToRouteBadge = !isSelectable && Number.isInteger(routeNumber) && routeNumber > 0;
                    const isInteractive = isSelectable || canZoomToRouteBadge;
                    const segmentWorkOrderLabel = resolveSegmentWorkOrderLabel({ segment });
                    const segmentCellsByColumnKey = {
                      step: (
                        <FleetSequenceStepCell
                          hideStartLabel={isGroupedMember}
                          indicators={segmentStepIndicators}
                          startLabel={isGroupedMember ? "" : formatSequenceStepTimeLabel(segment)}
                          stepLabel={getDisplayedTimelineSequenceLabel(segment) || `${segment.globalSequence ?? ""}`}
                        />
                      ),
                      route: (
                        <div className={joinClasses([
                          "flex items-start gap-2",
                          isGroupedMember ? "pl-5" : "",
                        ])}>
                          <span className="relative mt-0.5 inline-flex h-4 w-4 shrink-0 overflow-visible" aria-hidden="true">
                            {segment?.isEndOfDayLoad || segment?.isEndOfDayLoadSourcePreload
                              ? <EndOfDayLoadFleetRibbon anchor="sequence" compact visualMode={resolveEndOfDayLoadDisplayMode(segment)} {...resolveEndOfDayLoadLabelOptions(segment)} />
                              : segment?.isInitialPreload
                                ? <InitialPreloadFleetRibbon anchor="sequence" compact sequenceVariant="segment" />
                                : null}
                            <span
                              className="inline-flex h-4 w-4 rounded-full border border-white"
                              style={getSequenceTableMarkerStyle(
                                segment.stopType,
                                segment.color,
                                segment.isInitialPreload,
                                segment.isEndOfDayLoad || segment.isEndOfDayLoadSourcePreload,
                                resolveEndOfDayLoadVisualMode(segment),
                              )}
                            />
                          </span>
                          <div className="grid min-w-0 gap-0.5">
                            <div className="flex flex-wrap items-center gap-1.25 text-[0.79rem] leading-4.5 text-text">
                              <span className="font-bold text-text">{operationLabel}</span>
                            </div>
                            <FleetSequenceSupplementaryMeta
                              counterpartyLabel={counterpartySummaryLabel}
                              indicators={visibleSegmentContentIndicators}
                              summaryLabel={inlineSummaryLabel}
                            />
                          </div>
                        </div>
                      ),
                      silo: (
                        <span className="block truncate" title={segmentSiloLabel || undefined}>
                          {segmentSiloLabel || "—"}
                        </span>
                      ),
                      order: segment.endOfDayLoadOrderLines?.length > 1 ? (
                        // Chargé PM de plusieurs commandes : le nombre, les codes au survol.
                        <span className="block truncate" title={segment.endOfDayLoadOrderLines.map((line) => line.orderId).join(", ")}>
                          {`${segment.endOfDayLoadOrderLines.length} commandes`}
                        </span>
                      ) : (
                        <span className="block truncate" title={segment.orderLabel || undefined}>
                          {segment.orderLabel || "—"}
                        </span>
                      ),
                      workOrder: (
                        <span className="block truncate" title={segmentWorkOrderLabel || undefined}>
                          {segmentWorkOrderLabel || "—"}
                        </span>
                      ),
                      product: segment.productLabel ? <FleetProductPill label={segment.productLabel} maxLength={Infinity} /> : "—",
                      code: segment.productCodeLabel || "—",
                      compartment: <FleetSequenceCompartmentPill chip={compartmentChip} compartmentProfile={withVirtualTruckCompartment(selectedTruckCompartmentProfile, selectedAssignment?.capacity_tons)} segments={[segment]} showEmptyCompartments />,
                      occupancy: segmentCompartmentOccupancyLabel,
                      fillRate: segmentCompartmentFillRateLabel,
                      tonnage: <FleetSequenceLoadCell label={segmentQuantityLabel} stopType={segment.stopType} signed />,
                      charge: <FleetSequenceLoadCell label={segmentChargeLabel} stopType={segment.stopType} />,
                      distance: <FleetSequenceDistanceCell label={segmentDistanceLabel} />,
                      service: (
                        <FleetSequenceDurationColumnContent
                          chip={getFleetSequenceDurationChip(durationChips, "service")}
                          showFallback={!isGroupedMember}
                        />
                      ),
                      meunerie: (
                        <FleetSequenceDurationColumnContent
                          chip={getFleetSequenceDurationChip(durationChips, "meunerie")}
                          showFallback={!isGroupedMember}
                        />
                      ),
                      aspirator: (
                        <FleetSequenceDurationColumnContent
                          chip={getFleetSequenceDurationChip(durationChips, "aspirator")}
                          showFallback={!isGroupedMember}
                        />
                      ),
                      buffer: (
                        <FleetSequenceDurationColumnContent
                          chip={getFleetSequenceDurationChip(durationChips, "buffer")}
                          showFallback={!isGroupedMember}
                        />
                      ),
                      travel: (
                        <FleetSequenceDurationColumnContent
                          chip={getFleetSequenceDurationChip(durationChips, "travel")}
                          showFallback={!isGroupedMember}
                        />
                      ),
                    };

                    return (
                      <tr
                        key={`${segment.tripId}::${segment.globalSequence}`}
                        onClick={() => {
                          if (isSelectable) {
                            handleScopedRouteClientSelect({
                              assignmentId: selectedRow.assignmentId,
                              centerMap: true,
                              clientId: segment.clientId,
                              openSelectionOnly: true,
                              tripId: segment.tripId,
                            });
                            return;
                          }

                          if (!canZoomToRouteBadge) {
                            return;
                          }

                          chromeInteractions.onRouteSequenceSelect?.({
                            routeNumber,
                            routeSegmentIndex: segment.routeSegmentIndex,
                          });
                        }}
                        onBlur={() => {
                          chromeInteractions.onRouteHoverEnd?.();
                        }}
                        onFocus={() => {
                          chromeInteractions.onRouteSegmentHoverStart?.(segment.routeSegmentIndex, hoverRouteSegmentIndexes);
                        }}
                        onKeyDown={(event) => {
                          if (event.key !== "Enter" && event.key !== " ") {
                            return;
                          }

                          if (!isInteractive) {
                            return;
                          }

                          event.preventDefault();
                          if (isSelectable) {
                            handleScopedRouteClientSelect({
                              assignmentId: selectedRow.assignmentId,
                              centerMap: true,
                              clientId: segment.clientId,
                              openSelectionOnly: true,
                              tripId: segment.tripId,
                            });
                            return;
                          }

                          chromeInteractions.onRouteSequenceSelect?.({
                            routeNumber,
                            routeSegmentIndex: segment.routeSegmentIndex,
                          });
                        }}
                        onMouseEnter={() => {
                          chromeInteractions.onRouteSegmentHoverStart?.(segment.routeSegmentIndex, hoverRouteSegmentIndexes);
                        }}
                        onMouseLeave={() => {
                          chromeInteractions.onRouteHoverEnd?.();
                        }}
                        className={joinClasses([
                          "border-b border-[var(--shell-chrome-line)] text-text transition last:border-b-0",
                          isInteractive ? "cursor-pointer hover:bg-white focus-within:bg-white" : "",
                          isGroupedMember ? "bg-[rgba(255,255,255,0.76)]" : "",
                          isLinkedCommandSegment ? "animate-linked-step-emphasis" : "",
                          isSelectedSegment ? "bg-slate-100" : "",
                        ])}
                        role={isInteractive ? "button" : undefined}
                        tabIndex={isInteractive ? 0 : undefined}
                      >
                        {sequenceColumns.map((column) => (
                          <td key={column.key} className={resolveFleetSequenceColumnCellClassName(column.key, "segment")}>
                            {segmentCellsByColumnKey[column.key] ?? null}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        </div>
      ) : null}
    </section>
  );
  const floatingMapContent = isMapFloating ? (
    <FloatingRouteMapSurface
      ariaLabel="Carte flottante de la vue flotte"
      badgeLabel={selectedRouteSignalLabel || undefined}
      closeLabel="Réduire"
      isDragging={isFloatingMapDragging}
      isResizing={isFloatingMapResizing}
      onClose={closeFloatingMap}
      onDragHandlePointerDown={handleFloatingMapPointerDown}
      onKeyDown={handleFloatingMapKeyDown}
      onPointerCancel={handleFloatingMapPointerEnd}
      onPointerMove={handleFloatingMapPointerMove}
      onPointerUp={handleFloatingMapPointerEnd}
      onResizeHandlePointerDown={handleFloatingMapResizePointerDown}
      onSecondaryAction={openFloatingMapOnSecondScreen}
      onSizeChange={handleFloatingMapSizeChange}
      panelRef={floatingMapPanelRef}
      secondaryActionLabel="Détacher"
      size={floatingMapSize}
      style={floatingMapPanelStyle}
      subtitle={[selectedRow?.driverLabel, selectedSegment ? `Étape ${selectedSegment.globalSequence}` : (activeTrip?.trip_id || "Synchronisée sur le camion sélectionné.")].filter(Boolean).join(" · ")}
      title={selectedRow?.truckLabel || "Lecture courante"}
    >
      {renderRouteMapPanel(
        floatingMapSize === "compact",
        floatingMapSize === "compact" ? null : floatingMapSize,
        { renderLegend: false },
      )}
    </FloatingRouteMapSurface>
  ) : null;
  const floatingSuspendedContent = isSuspendedPanelFloating ? (
    <FloatingRouteMapSurface
      ariaLabel="Panneau flottant des commandes suspendues"
      badgeLabel="Suspendues"
      className="min-w-md"
      closeLabel="Réduire"
      isDragging={isFloatingSuspendedDragging}
      isResizing={isFloatingSuspendedResizing}
      onClose={closeFloatingSuspendedPanel}
      onDragHandlePointerDown={handleFloatingSuspendedPointerDown}
      onKeyDown={handleFloatingSuspendedKeyDown}
      onPointerCancel={handleFloatingSuspendedPointerEnd}
      onPointerMove={handleFloatingSuspendedPointerMove}
      onPointerUp={handleFloatingSuspendedPointerEnd}
      onResizeHandlePointerDown={handleFloatingSuspendedResizePointerDown}
      onSizeChange={handleFloatingSuspendedSizeChange}
      panelRef={floatingSuspendedPanelRef}
      size={floatingSuspendedSize}
      style={{
        ...floatingSuspendedPanelStyle,
        height: `${floatingSuspendedPanelHeight}px`,
      }}
      subtitle="Glissez une carte entre les suspendues Jour et Soir pour changer sa période, ou sur un camion pour la réactiver."
      title={`${visibleSuspendedDeliveryCount} commande${visibleSuspendedDeliveryCount > 1 ? "s" : ""}`}
    >
      {renderSuspendedPanel({ floating: true })}
    </FloatingRouteMapSurface>
  ) : null;
  const mainContentMountNode = isFleetFullscreen && fullscreenMountNode instanceof HTMLElement
    ? fullscreenMountNode
    : mountNode;
  const fullscreenBackdrop = isFleetFullscreen && fullscreenMountNode instanceof HTMLElement
    ? createPortal(
        <div
          aria-hidden="true"
          className="fixed inset-0 z-40 bg-[rgba(241,245,249,0.82)] backdrop-blur-xs"
          onClick={closeFleetFullscreen}
        />,
        fullscreenMountNode,
      )
    : null;
  const pictureInPictureRouteMapContent = isRouteMapPictureInPictureLinked ? (
    <RouteMapPictureInPicturePanel
      assignment={selectedAssignment}
      isRouteViewActive={isActiveView}
      mountNode={routeMapPictureInPictureMountNode}
      onClose={closeRouteMapPictureInPicture}
      onClientSelect={handleScopedRouteClientSelect}
      onControllerReady={handleRouteMapControllerReady}
      onMapRenderMetaChange={handleRouteMapRenderMetaChange}
      onRouteHighlightChange={onRouteHighlightChange}
      onWindowExpandedChange={handleRouteMapPictureInPictureExpandedChange}
      renderLegend
      routeAlternativePreview={activeRouteAlternativePreview}
      routeDistanceLabel={routeMapPictureInPictureDistanceLabel}
      selectedClientId={activeSelectedClientId || null}
      selectionRequest={isSelectedMainTimeline ? selectionRequest : null}
      stateRef={stateRef}
      title={routeMapPictureInPictureTitle}
      trip={activeTrip}
      windowExpanded={isRouteMapPictureInPictureExpanded}
    />
  ) : null;

  return (
    <>
      {fullscreenBackdrop}
      {isActiveView ? <DispatchGanttViewMenu
        contextKey={`${activeDispatchDate}:${isDayDispatchPeriod}:${isFleetFullscreen}`}
        isDayDispatchPeriod={isDayDispatchPeriod} isEveningRailOpen={isEveningRailOpen}
        isFleetFullscreen={isFleetFullscreen} onFullscreenChange={setIsFleetFullscreen}
        onEveningRailOpenChange={onEveningRailOpenChange}
      /> : null}
      {mainContentMountNode instanceof HTMLElement ? createPortal(mainContent, mainContentMountNode) : null}
      {isActiveView && isSegmentActionPopoverOpen && !reassignmentDragState ? (
        <SegmentActionPopover
          key={openSegmentActionKey || "segment-action-popover-closed"}
          anchorElement={segmentActionPopoverRefs.anchorElement}
          assignmentInteractions={segmentActionAssignmentInteractions}
          dispatchDate={activeDispatchDate}
          dispatchPeriod={isOpenEveningSegmentAction ? "evening" : activeSuspendedDispatchPeriod}
          panelRef={segmentActionPopoverRefs.panelRef}
          row={openSegmentContext?.row || null}
          segment={openSegmentActionSegment}
          truckReassignmentTargets={truckReassignmentTargets}
        />
      ) : null}
      {isActiveView && truckSheetPrintRequest ? (
        <ConfirmationModal
          cancelLabel="Annuler"
          confirmLabel="Préparer l'horaire"
          contentDensity="compact"
          description="La note est facultative et sera imprimée dans la section Notes du PDF."
          eyebrow="Horaire chauffeur"
          isOpen
          onClose={handleCloseTruckSheetPrintDialog}
          onConfirm={handleConfirmTruckSheetPrint}
          panelSize="compact"
          summaryDensity="compact"
          summaryItems={[
            { label: "Camion", value: truckSheetPrintRequest.truckName || truckSheetPrintRequest.truckId },
            { label: "Période", value: truckSheetPrintRequest.dispatchPeriod === "evening" ? "Soir" : "Jour" },
            {
              label: "Format",
              value: `${truckSheetPrintRequest.orientation === "portrait" ? "Portrait" : "Paysage"}${truckSheetPrintRequest.orientation === "portrait" && !truckSheetPrintRequest.showMap ? " · Sans carte" : ""}${truckSheetPrintRequest.fitOnePage ? " · Une page" : ""}`,
            },
            { label: "Dispatch", value: truckSheetPrintRequest.dispatchDate || activeDispatchDate },
          ]}
          textPrompt={{
            key: "truck-sheet-note",
            label: "Note de la fiche",
            description: "Ajoutez au besoin une remarque chauffeur, une consigne opérationnelle ou un rappel de livraison.",
            placeholder: "Note facultative",
            rows: 6,
          }}
          title="Ajouter une note à la fiche"
          tone="accent"
        >
          {canChooseTruckSheetCompartments ? (
            <div className="grid gap-1">
              <span className="text-xs font-semibold text-slate-700">Compartiments</span>
              <div className="flex gap-1">
                {[{ label: "Avec", value: true }, { label: "Sans", value: false }].map((option) => (
                  <button key={option.label} type="button" aria-pressed={truckSheetPrintRequest.showCompartments === option.value}
                    className={joinClasses(["rounded-[4px] border px-2 py-1 text-xs font-semibold focus-visible:ring-2 focus-visible:ring-accent/30",
                      truckSheetPrintRequest.showCompartments === option.value
                        ? "border-accent bg-accent text-white hover:border-accent-strong hover:bg-accent-strong"
                        : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900"])}
                    onClick={() => setTruckSheetPrintRequest((current) => current ? { ...current, showCompartments: option.value } : current)}>
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </ConfirmationModal>
      ) : null}
      {isActiveView && truckIncidentsDialogScope && isTruckIncidentsDialogCurrentDate ? (
        <TruckIncidentsDialog
          dispatchDate={truckIncidentsDialogScope.dispatchDate || activeDispatchDate}
          dispatchPeriod={truckIncidentsDialogPeriod}
          incidents={resolveFleetRowIncidents(truckIncidentsDialogRow)}
          isOpen
          isReadOnly={isTruckIncidentsDialogReadOnly}
          onClose={() => setTruckIncidentsDialogScope(null)}
          onCreate={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onCreateTruckIncident}
          onDelete={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onDeleteTruckIncident}
          onUpdate={isOptimizationPreviewInteractionLocked ? null : assignmentInteractions.onUpdateTruckIncident}
          readOnlyReason={truckIncidentsDialogReadOnlyReason}
          runtimeRevision={truckIncidentsDialogRuntimeRevision}
          truckId={truckIncidentsDialogScope.truckId}
          truckLabel={truckIncidentsDialogRow?.truckLabel || truckIncidentsDialogScope.truckLabel}
        />
      ) : null}
      {isActiveView && isMapFloating && floatingMapMountNode instanceof HTMLElement
        ? createPortal(floatingMapContent, floatingMapMountNode)
        : null}
      {isSuspendedPanelFloating && floatingSuspendedMountNode instanceof HTMLElement
        ? createPortal(floatingSuspendedContent, floatingSuspendedMountNode)
        : null}
      {pictureInPictureRouteMapContent}
      {isActiveView ? <TruckLoadView /> : null}
    </>
  );
}
